/**
 * Admin page for feedback management and admin user management.
 *
 * Tabs: Dashboard, Feedback list (with moderation), Admin management.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import type { Session } from '../types/app';
import MasterDetail from '../components/MasterDetail';
import { useResponsive } from '../hooks/useResponsive';
import { useTabReselect } from '../hooks/useTabReselect';
import { PlayerAvatar } from '../components/PlayerAvatar';
import {
  ChartTrendIcon, CommentIcon, TrainingIcon, AdminShieldIcon, ChevronDownIcon,
  GraduationIcon, CrossedSwordsTableIcon, CycleIcon,
  CrosshairIcon, CardsIcon, PackageIcon, ScaleIcon, WrenchIcon,
} from '../components/Icons';
import StatistiquesDiffereePage from './admin/StatistiquesDiffereePage';
import AntiCheatConflictsPage from './admin/AntiCheatConflictsPage';
import GlobalChatLogPage from './admin/GlobalChatLogPage';
import ModerationPage, {
  MODERATION_SECTION_LABELS,
  MODERATION_SECTION_ORDER,
  type ModerationSection,
} from './admin/ModerationPage';
import AdminBetaOpsPanel from '../components/AdminBetaOpsPanel';
import AdminMaintenancePanel from '../components/AdminMaintenancePanel';
import AdminUsagePanel from '../components/AdminUsagePanel';
import '../styles/admin.css';

class AdminErrorBoundary extends React.Component<{ children: React.ReactNode; label: string }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) return <div className="admin-empty">Erreur dans {this.props.label} : {this.state.error.message}</div>;
    return this.props.children;
  }
}
import { BACKEND_URL } from '../config';
import { getCardShortName, getCardInfo } from '../utils/cards';
import { Contract, type CardNumber } from '../types/game';
import { useApp } from '../contexts/AppContext';

function downloadJsonFile(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

type AdminTab = 'dashboard' | 'feedback' | 'ml-training' | 'differe-stats' | 'admins' | 'anti-cheat' | 'chat-log' | 'moderation' | 'beta-ops' | 'maintenance' | 'usage';

type MLSection = 'overview' | 'train' | 'jobs' | 'deploy' | 'versions' | 'compare' | 'deductions';

type BetaSection = 'ops' | 'inbox';

type ModerationSectionId = ModerationSection;

type FeedbackSubTab = 'passive' | 'manual';

const ML_SECTION_ORDER: MLSection[] = ['overview', 'train', 'jobs', 'versions', 'deploy', 'compare', 'deductions'];
const BETA_SECTION_ORDER: BetaSection[] = ['ops', 'inbox'];

function isMLSection(s: string | null | undefined): s is MLSection {
  return s != null && (ML_SECTION_ORDER as string[]).includes(s);
}

function isBetaSection(s: string | null | undefined): s is BetaSection {
  return s != null && (BETA_SECTION_ORDER as string[]).includes(s);
}

function isModerationSection(s: string | null | undefined): s is ModerationSectionId {
  return s != null && (MODERATION_SECTION_ORDER as string[]).includes(s);
}

/** Titres drill-down métriques comparaison ML (admin). */
const ML_COMPARE_METRIC_TITLES: Record<string, string> = {
  petitLost: 'Petit perdu',
  petitWon: 'Petit gagné (conservé ou remporté)',
  petitConserved: 'Petit conservé',
  petitRemporte: 'Petit remporté',
  petitAuBout: 'Petit au bout',
  petitKamikaze: 'Petit kamikaze',
  honneurOffert: 'Honneur offert',
  pointsDonnes: 'Points donnés',
  petitPrenableOpportunity: 'Petit prenable',
  petitPrenableRealized: 'Chasse réalisée',
  petitChasseIncertaine: 'Chasse incertaine',
  legacyRv99: 'RV 99 (retours carte vide)',
};

const ML_COMPARE_SEAT_LABELS = ['Sud', 'Est', 'Nord', 'Ouest'];

/** 7 colonnes : Sud→Ouest puis répétition — la carte d’entame du leader va dans sa colonne (0–3), les 3 suivantes dans les colonnes consécutives. */
const ML_COMPARE_DEAL_GRID_HEADERS = ['Sud', 'Est', 'Nord', 'Ouest', 'Sud', 'Est', 'Nord'];

type MlCompareMetricHighlightRow = { trickNumber: number; positionInTrick: number; player: number; card: number };

function mlCompareMetricHighlightMatches(
  hs: MlCompareMetricHighlightRow[] | undefined,
  trickNum1: number,
  positionInTrick: number,
  card: number,
): boolean {
  return hs?.some(h => h.trickNumber === trickNum1 && h.positionInTrick === positionInTrick && h.card === card) ?? false;
}

/** Couleur du Petit (1 d’atout) dans la grille : issue du pli et du camp évalué. */
function mlComparePetitPlayTone(
  card: number,
  playedBy: number,
  winner: number,
  takerPosition: number,
  compareRole: 'defender' | 'attacker',
): 'lost' | 'wonOpp' | 'kept' | 'other' | null {
  const info = getCardInfo(card as CardNumber);
  if (!info.isTrump || info.value !== 1) return null;
  const t = ((takerPosition % 4) + 4) % 4;
  const w = ((Number(winner) % 4) + 4) % 4;
  const p = ((Number(playedBy) % 4) + 4) % 4;
  const attackWonTrick = w === t;
  const modelCampWonTrick = compareRole === 'attacker' ? attackWonTrick : !attackWonTrick;
  const petitPlayedByAttack = p === t;
  const petitOnModelCamp = compareRole === 'attacker' ? petitPlayedByAttack : !petitPlayedByAttack;
  if (petitOnModelCamp && !modelCampWonTrick) return 'lost';
  if (!petitOnModelCamp && modelCampWonTrick) return 'wonOpp';
  if (petitOnModelCamp && modelCampWonTrick) return 'kept';
  return 'other';
}

/** Colonnes d’entête (indices 0–6) dont le siège (col % 4) est piloté par le modèle évalué : attaque = preneur, défense = les 3 défenseurs. */
function mlCompareStudiedSeatColumnIndexes(
  takerPosition: number,
  compareRole: 'defender' | 'attacker',
): number[] {
  const t = ((takerPosition % 4) + 4) % 4;
  const out: number[] = [];
  for (let c = 0; c < 7; c++) {
    const seat = c % 4;
    const isAttackSeat = seat === t;
    const isStudiedTeam = compareRole === 'attacker' ? isAttackSeat : !isAttackSeat;
    if (isStudiedTeam) out.push(c);
  }
  return out;
}

/** Numéros de plis (1-based), préfixe depuis le pli 1 : même séquence des cartes (`cards`) entre deux rejouages modèle. */
function mlCompareIdenticalCardPrefixTrickNumbers(
  leftTricks: Array<Record<string, unknown>>,
  rightTricks: Array<Record<string, unknown>>,
): Set<number> {
  const out = new Set<number>();
  const L = [...leftTricks].sort((a, b) => Number(a.trickIndex ?? 0) - Number(b.trickIndex ?? 0));
  const R = [...rightTricks].sort((a, b) => Number(a.trickIndex ?? 0) - Number(b.trickIndex ?? 0));
  const n = Math.min(L.length, R.length);
  for (let i = 0; i < n; i++) {
    const lc = (L[i].cards as number[]) ?? [];
    const rc = (R[i].cards as number[]) ?? [];
    if (lc.length !== rc.length) break;
    let ok = true;
    for (let j = 0; j < lc.length; j++) {
      if (lc[j] !== rc[j]) {
        ok = false;
        break;
      }
    }
    if (!ok) break;
    out.add(Number(L[i].trickIndex ?? i) + 1);
  }
  return out;
}

function formatMlCompareEvaluatedSidePoints(p: unknown): string {
  if (typeof p !== 'number' || Number.isNaN(p)) return '—';
  return Number.isInteger(p) ? String(p) : p.toFixed(1);
}

/** Met en évidence la ou les cellules avec le plus grand nombre de points (camp évalué). */
function mlCompareMetricDrillRowPointsHighlight(
  studied: unknown,
  other: unknown,
): { studiedBest: boolean; otherBest: boolean } {
  const ps = typeof studied === 'number' && Number.isFinite(studied) ? studied : null;
  const po = typeof other === 'number' && Number.isFinite(other) ? other : null;
  if (ps === null || po === null) return { studiedBest: false, otherBest: false };
  if (ps > po) return { studiedBest: true, otherBest: false };
  if (po > ps) return { studiedBest: false, otherBest: true };
  return { studiedBest: true, otherBest: true };
}

/** Points du camp évalué + liste des métriques drill-down présentes sur ce rejouage (API `evaluatedSidePoints`, `dealDrilldownMetricsPresent`). */
function MlCompareDealDetailPaneStats({
  panel,
  drillMetricKey,
  onSelectMetric,
}: {
  panel: Record<string, unknown> | null | undefined;
  drillMetricKey: string | undefined;
  /** Changer la métrique drill affichée pour cette donne (autres métriques que la courante). */
  onSelectMetric?: (metric: string) => void;
}) {
  if (!panel) return null;
  const pts = formatMlCompareEvaluatedSidePoints(panel.evaluatedSidePoints);
  const rawMetrics = (panel.dealDrilldownMetricsPresent as string[] | undefined) ?? [];
  /** Éviter le doublon « Petit gagné (agrégat) » avec Petit conservé / remporté déjà listés. */
  const metrics =
    rawMetrics.includes('petitConserved') || rawMetrics.includes('petitRemporte')
      ? rawMetrics.filter(m => m !== 'petitWon')
      : rawMetrics;
  const role = panel.compareRole as string | undefined;
  const roleLabel =
    role === 'defender' ? 'défense' : role === 'attacker' ? 'attaque' : 'camp évalué';
  return (
    <div className="ml-compare-deal-detail-split-stats ml-compare-deal-detail-split-stats--after-grids">
      <div className="ml-compare-deal-detail-split-stats-row">
        Points ({roleLabel}, cette donne){' '}
        <strong className="ml-compare-deal-detail-split-stats-points">{pts}</strong>
      </div>
      {metrics.length > 0 ? (
        <div className="ml-compare-deal-detail-split-stats-metrics">
          <span className="ml-compare-deal-detail-split-stats-label">Métriques détectées sur cette donne :</span>{' '}
          {metrics.map((m, i) => (
            <span key={m}>
              {i > 0 ? ', ' : null}
              {onSelectMetric && m !== drillMetricKey ? (
                <button
                  type="button"
                  className="ml-compare-deal-detail-split-metric-tag ml-compare-deal-detail-split-metric-tag-pick"
                  title={`Afficher cette métrique pour cette donne (${ML_COMPARE_METRIC_TITLES[m] ?? m})`}
                  onClick={() => onSelectMetric(m)}
                >
                  {ML_COMPARE_METRIC_TITLES[m] ?? m}
                </button>
              ) : (
                <span
                  className={
                    drillMetricKey && m === drillMetricKey
                      ? 'ml-compare-deal-detail-split-metric-tag ml-compare-deal-detail-split-metric-tag-current'
                      : 'ml-compare-deal-detail-split-metric-tag'
                  }
                >
                  {ML_COMPARE_METRIC_TITLES[m] ?? m}
                </span>
              )}
            </span>
          ))}
        </div>
      ) : (
        <div className="ml-compare-deal-detail-split-stats-muted">
          Aucune métrique drill-down détectée sur cette donne (pour ce rejouage).
        </div>
      )}
    </div>
  );
}

function MlCompareDealTricksGrid({
  tricks,
  metricHighlights,
  studiedSeatColumnIndexes,
  syncHighlightTrickNumbers,
  focusedTrickNum1,
  onTrickNumberCellClick,
  takerPosition,
  compareRole,
}: {
  tricks: Array<Record<string, unknown>>;
  metricHighlights?: MlCompareMetricHighlightRow[];
  /** Colonnes Sud–Ouest (répétées) où joue le camp du modèle étudié — surbrillance d’entête */
  studiedSeatColumnIndexes?: readonly number[];
  /** Plis (numéro 1-based) identiques entre les deux tableaux — surbrillance colonne « Pli » */
  syncHighlightTrickNumbers?: ReadonlySet<number>;
  /** Pli dont les mains « début de pli » sont affichées sous la grille (les deux colonnes partagent le même état). */
  focusedTrickNum1?: number | null;
  /** Clic sur la cellule « Pli » (numéro du pli). */
  onTrickNumberCellClick?: (trickNum1: number) => void;
  /** Coloration du Petit (1 d’atout) selon le camp évalué et l’issue du pli */
  takerPosition?: number;
  compareRole?: 'defender' | 'attacker';
}) {
  const studiedSet = new Set(studiedSeatColumnIndexes ?? []);
  const rows = [...tricks].sort((a, b) => Number(a.trickIndex ?? 0) - Number(b.trickIndex ?? 0));
  return (
    <div className="ml-compare-deal-tricks-grid-wrap">
      <table className="ml-compare-deal-tricks-grid">
        <thead>
          <tr>
            <th className="ml-compare-deal-tricks-grid-pl">Pli</th>
            {ML_COMPARE_DEAL_GRID_HEADERS.map((h, i) => (
              <th
                key={`${h}-${i}`}
                className={studiedSet.has(i) ? 'ml-compare-deal-tricks-grid-th-studied' : undefined}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(tr => {
            const ti = Number(tr.trickIndex ?? 0);
            const trickNum1 = ti + 1;
            const leader = Number(tr.leader);
            const winner = Number(tr.winner);
            const cards = (tr.cards as number[]) ?? [];
            const players = (tr.players as number[]) ?? [];
            const startCol = ((leader % 4) + 4) % 4;
            const tdCols: React.ReactNode[] = [];
            for (let col = 0; col < 7; col++) {
              const posInTrick = col - startCol;
              if (posInTrick >= 0 && posInTrick < 4 && posInTrick < cards.length) {
                const card = cards[posInTrick];
                const pl = players[posInTrick];
                const isWinningCard = pl === winner;
                const isMetric = mlCompareMetricHighlightMatches(metricHighlights, trickNum1, posInTrick, card);
                let cls = 'ml-compare-deal-tricks-grid-cell';
                if (isWinningCard) cls += ' ml-compare-deal-tricks-grid-cell--winner';
                if (isMetric) cls += ' ml-compare-deal-tricks-grid-cell--metric';
                const petitTone =
                  compareRole !== undefined && typeof takerPosition === 'number'
                    ? mlComparePetitPlayTone(card, Number(pl), Number(winner), takerPosition, compareRole)
                    : null;
                const petitCls =
                  petitTone === 'lost'
                    ? 'ml-compare-deal-tricks-petit--lost'
                    : petitTone === 'wonOpp'
                      ? 'ml-compare-deal-tricks-petit--won-opp'
                      : petitTone === 'kept'
                        ? 'ml-compare-deal-tricks-petit--kept'
                        : petitTone === 'other'
                          ? 'ml-compare-deal-tricks-petit--other'
                          : '';
                tdCols.push(
                  <td key={col} className={cls}>
                    <span
                      className={[
                        isWinningCard ? 'ml-compare-deal-tricks-card-bold' : '',
                        petitCls,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {getCardShortName(card)}
                    </span>
                  </td>,
                );
              } else {
                tdCols.push(
                  <td key={col} className="ml-compare-deal-tricks-grid-cell ml-compare-deal-tricks-grid-cell--empty" />,
                );
              }
            }
            const plCls =
              syncHighlightTrickNumbers?.has(trickNum1)
                ? 'ml-compare-deal-tricks-grid-pl ml-compare-deal-tricks-grid-pl-sync'
                : 'ml-compare-deal-tricks-grid-pl';
            const plFocused = focusedTrickNum1 === trickNum1;
            const plInteractive = Boolean(onTrickNumberCellClick);
            const plCellClass = [
              plCls,
              plFocused ? 'ml-compare-deal-tricks-grid-pl-focus' : '',
              plInteractive ? 'ml-compare-deal-tricks-grid-pl-click' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <tr key={ti}>
                <td
                  className={plCellClass}
                  onClick={
                    onTrickNumberCellClick
                      ? e => {
                          e.stopPropagation();
                          onTrickNumberCellClick(trickNum1);
                        }
                      : undefined
                  }
                  onKeyDown={
                    onTrickNumberCellClick
                      ? e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onTrickNumberCellClick(trickNum1);
                          }
                        }
                      : undefined
                  }
                  role={plInteractive ? 'button' : undefined}
                  tabIndex={plInteractive ? 0 : undefined}
                  title={
                    plInteractive
                      ? `Mains au début du pli ${trickNum1} (masque les cartes des plis précédents dans les 4 lignes ci‑dessous). Même clic : tout réafficher.`
                      : undefined
                  }
                >
                  {trickNum1}
                </td>
                {tdCols}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Retire des cartes du jeu en tenant compte des doublons (multiset). */
function mlCompareMultisetRemoveFromHand(hand: number[], toRemove: number[]): number[] {
  const rm = new Map<number, number>();
  for (const c of toRemove) rm.set(c, (rm.get(c) ?? 0) + 1);
  const out: number[] = [];
  for (const c of hand) {
    const n = rm.get(c) ?? 0;
    if (n > 0) rm.set(c, n - 1);
    else out.push(c);
  }
  return out;
}

function mlCompareSortCardIds(cards: number[]): number[] {
  return [...cards].sort((a, b) => a - b);
}

/** Petite / Garde : le preneur prend le chien et fait l’écart. Garde sans / contre : le chien ne rejoint pas sa main. */
function mlCompareTakerIntegratesChienAndEcart(panel: Record<string, unknown>): boolean {
  const c = Number(panel.forcedContract);
  if (c === Contract.GardeSans || c === Contract.GardeContre) return false;
  return true;
}

/** Mains après révélation du chien et écart — avant le 1er pli (aligné sur replay Entraînement). */
function mlCompareHandsAfterEcartFromPanel(panel: Record<string, unknown>): number[][] | null {
  const initial = panel.initialHands as Record<string, number[]> | undefined;
  if (!initial || typeof initial !== 'object') return null;
  const chien = (panel.chien as number[]) ?? [];
  const ecart = (panel.ecart as number[]) ?? [];
  const taker = Number(panel.takerPosition ?? 0);
  const t = ((taker % 4) + 4) % 4;
  const withChien = mlCompareTakerIntegratesChienAndEcart(panel);
  const out: number[][] = [];
  for (let i = 0; i < 4; i++) {
    let h = [...(initial[String(i)] ?? [])];
    if (i === t && withChien) {
      h = [...h, ...chien];
      h = mlCompareMultisetRemoveFromHand(h, ecart);
    }
    out.push(mlCompareSortCardIds(h));
  }
  return out;
}

/** Mains au tirage : 18 cartes par joueur avant révélation du chien et de l’écart. */
function mlCompareHandsInitialDealFromPanel(panel: Record<string, unknown>): number[][] | null {
  const initial = panel.initialHands as Record<string, number[]> | undefined;
  if (!initial || typeof initial !== 'object') return null;
  const out: number[][] = [];
  for (let i = 0; i < 4; i++) {
    out.push(mlCompareSortCardIds([...(initial[String(i)] ?? [])]));
  }
  return out;
}

/** Cartes jouées par siège lors des plis de numéro strictement inférieur à `beforeTrickNum1`. */
function mlComparePlayedCardsBySeatBeforeTrick(
  tricks: Array<Record<string, unknown>>,
  beforeTrickNum1: number,
): number[][] {
  const bySeat: number[][] = [[], [], [], []];
  const rows = [...tricks].sort((a, b) => Number(a.trickIndex ?? 0) - Number(b.trickIndex ?? 0));
  for (const tr of rows) {
    const ti = Number(tr.trickIndex ?? 0);
    const trickNum1 = ti + 1;
    if (trickNum1 >= beforeTrickNum1) break;
    const cards = (tr.cards as number[]) ?? [];
    const players = (tr.players as number[]) ?? [];
    for (let p = 0; p < cards.length && p < players.length; p++) {
      const seat = ((Number(players[p]) % 4) + 4) % 4;
      bySeat[seat].push(cards[p]);
    }
  }
  return bySeat;
}

/** Mains au début du pli `focusTrickNum1` (1 = après écart) ; `null` = après écart sans filtre pli. */
function mlCompareHandsAtStartOfTrick(
  panel: Record<string, unknown>,
  tricks: Array<Record<string, unknown>>,
  focusTrickNum1: number | null,
): number[][] | null {
  const base = mlCompareHandsAfterEcartFromPanel(panel);
  if (!base) return null;
  if (focusTrickNum1 == null || focusTrickNum1 <= 1) return base;
  const played = mlComparePlayedCardsBySeatBeforeTrick(tricks, focusTrickNum1);
  return base.map((hand, seat) => mlCompareSortCardIds(mlCompareMultisetRemoveFromHand(hand, played[seat])));
}

function mlCompareFormatCardRowShort(cards: number[] | undefined | null): string {
  if (!cards?.length) return '—';
  const sorted = mlCompareSortCardIds(cards.filter((c): c is number => typeof c === 'number' && c >= 1 && c <= 78));
  if (sorted.length === 0) return '—';
  return sorted.map(c => getCardShortName(c as CardNumber)).join('\t');
}

/** Rang courte pour une couleur (hors atout / excuse), ex. 1, V, R */
function mlCompareRankPlainColorCard(card: CardNumber): string {
  const info = getCardInfo(card);
  if (info.isExcuse || info.isTrump) return '?';
  const v = info.value;
  if (v === 1) return '1';
  if (v === 11) return 'V';
  if (v === 12) return 'C';
  if (v === 13) return 'D';
  if (v === 14) return 'R';
  return String(v);
}

const ML_COMPARE_HAND_GROUP_SUIT_SYM = ['♥', '♠', '♦', '♣'];

/** Affichage mains : ♥ … ♠ … ♦ … ♣ … puis `A 1,2,…,EXC` ; virgules serrées, blocs espacés en CSS. */
function mlCompareRenderHandGroupedLine(hand: number[]): React.ReactNode {
  const valid = hand.filter((c): c is number => typeof c === 'number' && c >= 1 && c <= 78);
  if (valid.length === 0) return '—';
  const suits: number[][] = [[], [], [], []];
  const trumpBucket: number[] = [];
  for (const c of valid) {
    if (c === 78 || (c >= 57 && c <= 77)) {
      trumpBucket.push(c);
      continue;
    }
    if (c >= 1 && c <= 14) suits[0].push(c);
    else if (c >= 15 && c <= 28) suits[1].push(c);
    else if (c >= 29 && c <= 42) suits[2].push(c);
    else if (c >= 43 && c <= 56) suits[3].push(c);
  }
  const segments: string[] = [];
  for (let s = 0; s < 4; s++) {
    mlCompareSortCardIds(suits[s]);
    if (suits[s].length === 0) continue;
    const labels = suits[s].map(c => mlCompareRankPlainColorCard(c as CardNumber));
    segments.push(`${ML_COMPARE_HAND_GROUP_SUIT_SYM[s]} ${labels.join(',')}`);
  }
  const trumps = mlCompareSortCardIds(trumpBucket.filter(c => c >= 57 && c <= 77));
  const hasExcuse = trumpBucket.includes(78);
  const trumpLabels: string[] = trumps.map(c => String(c - 56));
  if (hasExcuse) trumpLabels.push('EXC');
  if (trumpLabels.length > 0) segments.push(`A ${trumpLabels.join(',')}`);
  return (
    <>
      {segments.map((seg, i) => (
        <span
          key={`hb-${i}`}
          className={i === 0 ? 'ml-compare-hand-block' : 'ml-compare-hand-block ml-compare-hand-block-gap'}
        >
          {seg}
        </span>
      ))}
    </>
  );
}

function mlCompareRenderCardsInline(cards: number[], highlightIds?: Set<number>): React.ReactNode {
  const sorted = mlCompareSortCardIds(cards.filter((c): c is number => typeof c === 'number' && c >= 1 && c <= 78));
  if (sorted.length === 0) return '—';
  const parts: React.ReactNode[] = [];
  sorted.forEach((c, i) => {
    if (i > 0) parts.push('\t');
    parts.push(
      <span
        key={`c-${c}-${i}`}
        className={highlightIds?.has(c) ? 'ml-compare-deal-post-tricks-card-hi' : undefined}
      >
        {getCardShortName(c as CardNumber)}
      </span>,
    );
  });
  return parts;
}

/** Chien, écart et mains sous la grille des plis — détail donne ML. */
function MlCompareDealPostTricksPanel({
  panel,
  tricks,
  trickFocusTrickNum1,
  ecartHighlightBothModels,
  studiedSeatIndexes,
}: {
  panel: Record<string, unknown>;
  tricks: Array<Record<string, unknown>>;
  trickFocusTrickNum1: number | null;
  /** Cartes présentes dans l’écart des deux modèles (surlignées sur la ligne Écart). */
  ecartHighlightBothModels?: Set<number>;
  /** Sièges pilotés par le modèle étudié, pour surligner les lignes de mains correspondantes. */
  studiedSeatIndexes?: readonly number[];
}) {
  const chien = (panel.chien as number[]) ?? [];
  const ecart = (panel.ecart as number[]) ?? [];
  const studiedSeats = new Set(studiedSeatIndexes ?? []);
  const hands =
    trickFocusTrickNum1 == null
      ? mlCompareHandsInitialDealFromPanel(panel)
      : mlCompareHandsAtStartOfTrick(panel, tricks, trickFocusTrickNum1);
  return (
    <div className="ml-compare-deal-post-tricks">
      {trickFocusTrickNum1 != null && trickFocusTrickNum1 > 1 ? (
        <div className="ml-compare-deal-post-tricks-hint">
          Mains au début du pli {trickFocusTrickNum1} — cartes déjà jouées aux plis précédents masquées.
        </div>
      ) : null}
      <div className="ml-compare-deal-post-tricks-line">
        <span className="ml-compare-deal-post-tricks-k">Chien :</span>{' '}
        <span className="ml-compare-deal-post-tricks-cards">{mlCompareFormatCardRowShort(chien)}</span>
      </div>
      <div className="ml-compare-deal-post-tricks-line">
        <span className="ml-compare-deal-post-tricks-k">Écart :</span>{' '}
        <span className="ml-compare-deal-post-tricks-cards">
          {mlCompareRenderCardsInline(ecart, ecartHighlightBothModels)}
        </span>
      </div>
      {hands ? (
        hands.map((hand, seat) => (
          <div key={seat} className="ml-compare-deal-post-tricks-line">
            <span
              className={[
                'ml-compare-deal-post-tricks-k',
                studiedSeats.has(seat) ? 'ml-compare-deal-post-tricks-k-studied' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {ML_COMPARE_SEAT_LABELS[seat] ?? seat} :
            </span>{' '}
            <span className="ml-compare-deal-post-tricks-cards ml-compare-deal-post-tricks-hand-grouped">
              {mlCompareRenderHandGroupedLine(hand)}
            </span>
          </div>
        ))
      ) : (
        <div className="ml-compare-deal-post-tricks-line ml-compare-deal-post-tricks-muted">
          Mains : données indisponibles (initialHands manquant).
        </div>
      )}
    </div>
  );
}

/** Donnes max. envoyées vers Entraînement en une fois (sessionStorage). */
const ML_COMPARE_TRAINING_MAX_DEALS = 160;

/** Un tour : à partir du joueur après le donneur, chacun parle une fois — passes puis contrat du preneur (`forcedContract`). */
function syntheticMlCompareBids(dealer: number, taker: number, contract: number, playerCount = 4) {
  const pc = playerCount;
  const ts = 0;
  const bids: Array<{ player: number; bid: number; timestamp: number }> = [];
  for (let i = 0; i < pc; i++) {
    const p = (dealer + 1 + i) % pc;
    bids.push({ player: p, bid: p === taker ? contract : Contract.Pass, timestamp: ts });
  }
  return bids;
}

function buildMlCompareTrainingGameData(detail: {
  takerPosition: number;
  dealer: number;
  forcedContract: number;
  tricks: Array<{
    trickIndex?: number;
    leader: number;
    winner: number;
    cards: number[];
    players: number[];
  }>;
  initialHands: Record<string, number[]>;
  chien: number[];
  ecart: number[];
}) {
  const pc = 4;
  const dealer = detail.dealer;
  const taker = detail.takerPosition;
  const contract = detail.forcedContract;
  const bids = syntheticMlCompareBids(dealer, taker, contract, pc);
  const players = Array.from({ length: pc }, (_, position) => ({
    position,
    displayName: ML_COMPARE_SEAT_LABELS[position] ?? `P${position}`,
    isHuman: false,
    isBot: true,
  }));
  const initialHands: Record<number, number[]> = {};
  for (const [k, v] of Object.entries(detail.initialHands ?? {})) {
    initialHands[Number(k)] = [...v];
  }
  const tricks = (detail.tricks ?? []).map(t => ({
    trickNumber: (t.trickIndex ?? 0) + 1,
    leader: t.leader,
    winner: t.winner,
    cards: (t.cards ?? []).map((card, i) => ({
      player: (t.players ?? [])[i],
      card,
      timestamp: 0,
    })),
  }));

  return {
    players,
    playerCount: pc,
    dealer,
    taker,
    contract,
    initialHands,
    chien: [...(detail.chien ?? [])],
    ecart: [...(detail.ecart ?? [])],
    bids,
    tricks,
    poignees: {},
  };
}

function mlComparePublicModelLabel(m: { label?: string; versionTag?: string } | undefined | null): string {
  if (!m || typeof m !== 'object') return 'Modèle';
  const l = typeof m.label === 'string' ? m.label.trim() : '';
  const t = typeof m.versionTag === 'string' ? m.versionTag.trim() : '';
  if (l.length > 0) return l;
  return t.length > 0 ? t : 'Modèle';
}

/** Libellés par siège : modèle évalué vs adversaire fixe (aligné sur playDealHeadless / compareRole). */
function buildMlCompareReplaySeatLabels(
  compareRole: 'defender' | 'attacker',
  takerPosition: number,
  evaluatedLabel: string,
  opponentLabel: string,
): string[] {
  const out: string[] = [];
  for (let pos = 0; pos < 4; pos++) {
    const isTaker = pos === takerPosition;
    const evaluatedControlsSeat = compareRole === 'attacker' ? isTaker : !isTaker;
    const modelName = evaluatedControlsSeat ? evaluatedLabel : opponentLabel;
    out.push(`${modelName} (${ML_COMPARE_SEAT_LABELS[pos] ?? pos})`);
  }
  return out;
}

/** Une donne prête pour le replay Entraînement (batch comparateur ML). */
function mlCompareDetailToReplayDeal(
  raw: Record<string, unknown>,
  summary: { dealIndex: number; contractLabel: string; bidScore: number },
  compareRole: 'defender' | 'attacker',
  evaluatedLabel: string,
  opponentLabel: string,
  drillMetric: string,
): {
  dealIndex: number;
  label: string;
  gameData: ReturnType<typeof buildMlCompareTrainingGameData>;
  replaySeatLabels: string[];
  metricDrill?: { metric: string; highlights: Array<{ trickNumber: number; positionInTrick: number; player: number; card: number }> };
} {
  if (typeof raw.dealer !== 'number' || typeof raw.forcedContract !== 'number') {
    throw new Error(`Donne #${summary.dealIndex} : données de replay incomplètes.`);
  }
  const gameData = buildMlCompareTrainingGameData({
    takerPosition: Number(raw.takerPosition),
    dealer: Number(raw.dealer),
    forcedContract: Number(raw.forcedContract),
    tricks:
      (raw.tricks as Array<{
        trickIndex?: number;
        leader: number;
        winner: number;
        cards: number[];
        players: number[];
      }>) ?? [],
    initialHands: (raw.initialHands as Record<string, number[]>) ?? {},
    chien: (raw.chien as number[]) ?? [],
    ecart: (raw.ecart as number[]) ?? [],
  });
  const replaySeatLabels = buildMlCompareReplaySeatLabels(
    compareRole,
    Number(raw.takerPosition),
    evaluatedLabel,
    opponentLabel,
  );
  const label = `Donne #${summary.dealIndex} — ${summary.contractLabel} — force ${summary.bidScore}`;
  const mhRaw = raw.metricHighlights as unknown;
  const highlights = Array.isArray(mhRaw)
    ? (mhRaw as Array<{ trickNumber?: number; positionInTrick?: number; player?: number; card?: number }>)
        .filter(
          h =>
            typeof h?.trickNumber === 'number'
            && typeof h?.positionInTrick === 'number'
            && typeof h?.player === 'number'
            && typeof h?.card === 'number',
        )
        .map(h => ({
          trickNumber: h.trickNumber as number,
          positionInTrick: h.positionInTrick as number,
          player: h.player as number,
          card: h.card as number,
        }))
    : [];
  const metricDrill =
    drillMetric.length > 0 ? { metric: drillMetric, highlights } : undefined;
  return { dealIndex: summary.dealIndex, label, gameData, replaySeatLabels, metricDrill };
}

const ML_SECTION_LABELS: Record<MLSection, string> = {
  overview: 'Vue d\'ensemble',
  train: 'Exporter & Entraîner',
  jobs: 'Jobs',
  versions: 'Versions',
  deploy: 'Déployer',
  compare: 'Comparer',
  deductions: 'Déductions',
};

const BETA_SECTION_LABELS: Record<BetaSection, string> = {
  ops: 'Exploitation',
  inbox: 'Retours testeurs',
};

function parseAdminTabParam(raw: string | null): AdminTab {
  if (raw === 'dashboard' || raw === 'feedback' || raw === 'ml-training' || raw === 'differe-stats' || raw === 'admins' || raw === 'anti-cheat' || raw === 'chat-log' || raw === 'moderation' || raw === 'beta-ops' || raw === 'maintenance' || raw === 'usage') return raw;
  return 'dashboard';
}

/** Sous-onglet actif pour feedback / ml-training ; null pour les autres onglets. */
function parseAdminSubParam(searchParams: URLSearchParams, tab: AdminTab): string | null {
  const raw = searchParams.get('adminSub');
  if (tab === 'feedback') return raw === 'manual' ? 'manual' : 'passive';
  if (tab === 'ml-training') return isMLSection(raw) ? raw : 'overview';
  if (tab === 'beta-ops') return isBetaSection(raw) ? raw : 'ops';
  if (tab === 'moderation') return isModerationSection(raw) ? raw : 'active';
  return null;
}

/** Rapport validation bot1 « conforme import » : bot1 présent, totaux cohérents, infra RV OK (parité replay optionnelle). */
function isBot1ReportOk(report: any): boolean {
  if (!report || report.status === 'failed') return false;
  if (report.replay?.importValidationOk != null) {
    return report.replay.importValidationOk === true;
  }
  // Rapports anciens (sans importValidationOk)
  if (!report.official?.found) return false;
  if (!report.recalculatedOfficial?.matchingOfficialTotals) return false;
  const r = report.replay;
  if (!r) return false;
  if ((r.rawScoreMissing ?? 0) > 0) return false;
  if ((r.contractMismatches ?? 0) > 0) return false;
  return true;
}

function adminPageHeading(tab: AdminTab, sub: string | null): string {
  if (tab === 'dashboard') return 'Dashboard';
  if (tab === 'admins') return 'Administrateurs';
  if (tab === 'feedback') {
    const label = sub === 'manual' ? 'Feedback manuel' : 'Feedback passif';
    return `Feedback · ${label}`;
  }
  if (tab === 'ml-training') {
    const sec: MLSection = isMLSection(sub) ? sub : 'overview';
    return `Entraînement · ${ML_SECTION_LABELS[sec]}`;
  }
  if (tab === 'differe-stats') return 'Statistiques différé';
  if (tab === 'anti-cheat') return 'Conflits anti-fraude';
  if (tab === 'chat-log') return 'Log chat';
  if (tab === 'moderation') {
    const sec: ModerationSectionId = isModerationSection(sub) ? sub : 'active';
    return `Modération · ${MODERATION_SECTION_LABELS[sec]}`;
  }
  if (tab === 'beta-ops') {
    const sec: BetaSection = isBetaSection(sub) ? sub : 'ops';
    return `Bêta-test · ${BETA_SECTION_LABELS[sec]}`;
  }
  if (tab === 'usage') return "Statistiques d'usage";
  if (tab === 'maintenance') return 'Maintenance';
  return 'Admin';
}

/** Sous-page par défaut quand on ouvre Feedback ou Entraînement depuis la sidebar « repliée ». */
function defaultSubForGroupedTab(tab: AdminTab): string {
  if (tab === 'feedback') return 'passive';
  if (tab === 'ml-training') return 'overview';
  if (tab === 'beta-ops') return 'ops';
  if (tab === 'moderation') return 'active';
  return '';
}

interface FeedbackStats {
  total: number;
  manual_count: number;
  passive_count: number;
  active_count: number;
  excluded_count: number;
  agrees_count: number;
  disagrees_count: number;
  unique_experts: number;
  avg_rating: number | null;
  by_level: Array<{ player_level: string; count: number }>;
  by_version: Array<{ version: string; count: number }>;
  by_day: Array<{ day: string; count: number }>;
  pairwise_count: number;
}


interface AdminUser {
  pseudo: string;
  granted_by: string;
  created_at: string;
}

interface AdminPageProps {
  session: Session;
}

// Card names lookup (simplified)
const SUIT_NAMES = ['Trèfle', 'Carreau', 'Cœur', 'Pique'];
const RANK_NAMES = ['As', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'Valet', 'Cavalier', 'Dame', 'Roi'];
function cardName(id: number): string {
  if (id >= 57 && id <= 78) {
    if (id === 57) return 'Petit';
    if (id === 78) return '21 d\'Atout';
    if (id === 56) return 'Excuse';
    return `${id - 56} d'Atout`;
  }
  if (id === 56) return 'Excuse';
  const suit = Math.floor((id - 1) / 14);
  const rank = (id - 1) % 14;
  if (suit >= 0 && suit < 4 && rank >= 0 && rank < 14) {
    return `${RANK_NAMES[rank]} de ${SUIT_NAMES[suit]}`;
  }
  return `Carte ${id}`;
}

/** pseudo = compte admin (droits), jamais utilisé pour filtrer des données métier. */
function adminFetch(url: string, pseudo: string, options: RequestInit = {}) {
  const separator = url.includes('?') ? '&' : '?';
  return fetch(`${url}${separator}pseudo=${encodeURIComponent(pseudo)}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
}

// ============================================
// Dashboard tab
// ============================================

function DashboardTab({ session }: { session: Session }) {
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminFetch(`${BACKEND_URL}/api/admin/feedback/stats`, session.pseudo)
      .then(r => r.json())
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [session.pseudo]);

  if (loading) return <div className="admin-loading">Chargement...</div>;
  if (!stats) return <div className="admin-empty">Erreur de chargement</div>;

  return (
    <div className="admin-dashboard">
      <div className="admin-stats-grid">
        <StatCard label="Total feedback" value={stats.total} />
        <StatCard label="Manuel" value={stats.manual_count} accent="blue" />
        <StatCard label="Passif" value={stats.passive_count} accent="green" />
        <StatCard label="Actifs" value={stats.active_count} accent="green" />
        <StatCard label="Exclus" value={stats.excluded_count} accent="red" />
        <StatCard label="Experts uniques" value={stats.unique_experts} />
        <StatCard label="Accord ML" value={stats.agrees_count} accent="green" />
        <StatCard label="Désaccord ML" value={stats.disagrees_count} accent="orange" />
        <StatCard label="Paires d'entraînement" value={stats.pairwise_count} accent="blue" />
        <StatCard
          label="Rating moyen"
          value={stats.avg_rating ? Math.round(stats.avg_rating) : '—'}
        />
      </div>

      <div className="admin-dashboard-sections">
        <div className="admin-section">
          <h3>Par niveau</h3>
          <div className="admin-bar-list">
            {stats.by_level.map(l => (
              <div key={l.player_level} className="admin-bar-row">
                <span className="admin-bar-label">{l.player_level}</span>
                <div className="admin-bar-track">
                  <div
                    className="admin-bar-fill"
                    style={{ width: `${Math.min(100, (l.count / Math.max(1, stats.total)) * 100)}%` }}
                  />
                </div>
                <span className="admin-bar-count">{l.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-section">
          <h3>Par version ML</h3>
          <div className="admin-bar-list">
            {stats.by_version.map(v => (
              <div key={v.version} className="admin-bar-row">
                <span className="admin-bar-label">{v.version}</span>
                <div className="admin-bar-track">
                  <div
                    className="admin-bar-fill version"
                    style={{ width: `${Math.min(100, (v.count / Math.max(1, stats.total)) * 100)}%` }}
                  />
                </div>
                <span className="admin-bar-count">{v.count}</span>
              </div>
            ))}
          </div>
        </div>

        {stats.by_day.length > 0 && (
          <div className="admin-section admin-section-wide">
            <h3>Derniers 30 jours</h3>
            <div className="admin-day-chart">
              {stats.by_day.slice().reverse().map(d => {
                const maxCount = Math.max(...stats.by_day.map(x => x.count));
                return (
                  <div key={d.day} className="admin-day-bar" title={`${d.day}: ${d.count}`}>
                    <div
                      className="admin-day-fill"
                      style={{ height: `${Math.max(4, (d.count / Math.max(1, maxCount)) * 100)}%` }}
                    />
                    <span className="admin-day-label">
                      {new Date(d.day).getDate()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div className={`admin-stat-card ${accent ? `accent-${accent}` : ''}`}>
      <span className="admin-stat-value">{typeof value === 'number' ? value.toLocaleString() : value}</span>
      <span className="admin-stat-label">{label}</span>
    </div>
  );
}

// ============================================
// Feedback tab (sub-tabbed: Passif stats + Manuel list)
// ============================================

interface ModelOption { model: string; role: string }

function FeedbackTab({
  session,
  subTab,
  onSubTabChange,
}: {
  session: Session;
  subTab: FeedbackSubTab;
  onSubTabChange: (t: FeedbackSubTab) => void;
}) {
  const [filterValue, setFilterValue] = useState('all');
  const [availableModels, setAvailableModels] = useState<ModelOption[]>([]);

  useEffect(() => {
    adminFetch(`${BACKEND_URL}/api/admin/feedback/models`, session.pseudo)
      .then(r => r.json())
      .then(d => setAvailableModels(d.models ?? []))
      .catch(() => {});
  }, [session.pseudo]);

  const roleFilter = filterValue === 'all' ? 'all'
    : filterValue === 'defender' ? 'defender'
    : filterValue === 'attacker' ? 'attacker'
    : 'all';
  const modelFilter = !['all', 'defender', 'attacker'].includes(filterValue) ? filterValue : 'all';

  const defModels = availableModels.filter(m => m.role === 'defender' && m.model !== 'unknown');
  const attModels = availableModels.filter(m => m.role === 'attacker' && m.model !== 'unknown');

  return (
    <div className="admin-feedback-tab">
      <div className="fb-subtabs">
        <button className={`ml-section-btn ${subTab === 'passive' ? 'active' : ''}`} onClick={() => onSubTabChange('passive')}>
          Feedback passif
        </button>
        <button className={`ml-section-btn ${subTab === 'manual' ? 'active' : ''}`} onClick={() => onSubTabChange('manual')}>
          Feedback manuel
        </button>
        <div className="fb-role-filter">
          <select value={filterValue} onChange={e => setFilterValue(e.target.value)}>
            <option value="all">Tous les modèles</option>
            <option value="defender">Défense (tous)</option>
            <option value="attacker">Attaque (tous)</option>
            {defModels.length > 0 && <optgroup label="Modèles de défense">
              {defModels.map(m => <option key={`d-${m.model}`} value={m.model}>[DEF] {m.model}</option>)}
            </optgroup>}
            {attModels.length > 0 && <optgroup label="Modèles d'attaque">
              {attModels.map(m => <option key={`a-${m.model}`} value={m.model}>[ATT] {m.model}</option>)}
            </optgroup>}
          </select>
        </div>
      </div>
      {subTab === 'passive'
        ? <PassiveFeedbackView session={session} roleFilter={roleFilter} modelFilter={modelFilter} />
        : <ManualFeedbackView session={session} roleFilter={roleFilter} modelFilter={modelFilter} />}
    </div>
  );
}

// ── Helpers ────────────────────────────────

function pct(n: number, d: number): string {
  if (d === 0) return '—';
  return `${Math.round((n / d) * 100)}%`;
}

function fmtNum(n: number | string | null | undefined): string {
  if (n == null) return '—';
  const v = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(v)) return '—';
  return v.toLocaleString('fr-FR');
}

function fmtPct(n: number | string | null | undefined): string {
  if (n == null) return '—';
  const v = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(v)) return '—';
  return `${Math.round(v * 100)}%`;
}

function safeInt(v: string | number | null | undefined): number {
  if (v == null) return 0;
  const n = typeof v === 'string' ? parseInt(v) : v;
  return isNaN(n) ? 0 : n;
}

const LEVEL_LABELS: Record<string, string> = {
  elite: 'Elite', expert: 'Expert', advanced: 'Avancé',
  intermediate: 'Intermédiaire', beginner: 'Débutant', unknown: '?',
};

const ROLE_LABELS: Record<string, string> = {
  defender: 'Déf', attacker: 'Att', unknown: '?',
};

function RateBadge({ n, d }: { n: number; d: number }) {
  const rate = d > 0 ? n / d : 0;
  return (
    <span className={`fb-rate ${rate >= 0.7 ? 'fb-rate-good' : rate >= 0.5 ? 'fb-rate-mid' : 'fb-rate-bad'}`}>
      {pct(n, d)}
    </span>
  );
}

function AgreeBadges({ agrees, disagrees }: { agrees: number; disagrees: number }) {
  return (
    <>
      <span className="badge badge-agree">{fmtNum(agrees)} ✓</span>
      {' '}
      <span className="badge badge-disagree">{fmtNum(disagrees)} ✗</span>
    </>
  );
}

function AdvancedStatsSection({ adv }: { adv: Record<string, string> }) {
  const rows: Array<{ label: string; total: number; agrees: number; category?: string }> = [
    { label: 'Plis 1-5 (début)', total: safeInt(adv.early_total), agrees: safeInt(adv.early_agrees), category: 'phase' },
    { label: 'Plis 6-13 (milieu)', total: safeInt(adv.mid_total), agrees: safeInt(adv.mid_agrees), category: 'phase' },
    { label: 'Plis 14-18 (fin)', total: safeInt(adv.late_total), agrees: safeInt(adv.late_agrees), category: 'phase' },
    { label: 'Petit — IA recommande', total: safeInt(adv.petit_ai_total), agrees: safeInt(adv.petit_ai_agrees), category: 'special' },
    { label: 'Petit — humain joue sans reco IA', total: safeInt(adv.petit_human_overrides), agrees: 0, category: 'special' },
    { label: 'Excuse — IA recommande', total: safeInt(adv.excuse_ai_total), agrees: safeInt(adv.excuse_ai_agrees), category: 'special' },
    { label: 'Excuse — humain joue sans reco IA', total: safeInt(adv.excuse_human_overrides), agrees: 0, category: 'special' },
    { label: 'Déf: entame plis 1-5', total: safeInt(adv.def_entame_early_total), agrees: safeInt(adv.def_entame_early_agrees), category: 'defense' },
    { label: 'Déf: joue avant le preneur', total: safeInt(adv.def_before_taker_total), agrees: safeInt(adv.def_before_taker_agrees), category: 'defense' },
    { label: 'Déf: joue après le preneur', total: safeInt(adv.def_after_taker_total), agrees: safeInt(adv.def_after_taker_agrees), category: 'defense' },
  ];

  const categories = [
    { key: 'phase', title: 'Par phase de jeu' },
    { key: 'special', title: 'Petit & Excuse' },
    { key: 'defense', title: 'Défense — situations' },
  ];

  return (
    <div className="admin-section admin-section-wide">
      <h3>Statistiques avancées</h3>
      <div className="fb-advanced-grid">
        {categories.map(cat => {
          const catRows = rows.filter(r => r.category === cat.key && r.total > 0);
          if (catRows.length === 0) return null;
          return (
            <div key={cat.key} className="fb-advanced-cat">
              <h4>{cat.title}</h4>
              <div className="admin-feedback-table-wrapper">
                <table className="admin-feedback-table" data-mobile="cards">
                  <thead><tr><th>Situation</th><th>Volume</th><th>Taux conformité</th></tr></thead>
                  <tbody>
                    {catRows.map(r => (
                      <tr key={r.label}>
                        <td data-label="Situation">{r.label}</td>
                        <td data-label="Volume">{fmtNum(r.total)}</td>
                        <td data-label="Taux conformité">{r.category === 'special' && r.label.includes('sans reco')
                          ? <span className="fb-rate fb-rate-bad">{fmtNum(r.total)} cas</span>
                          : <RateBadge n={r.agrees} d={r.total} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Shared stats types ─────────────────────

interface FeedbackStatsData {
  overview: Record<string, string>;
  by_model: Array<Record<string, string>>;
  by_level: Array<Record<string, string>>;
  by_model_level: Array<Record<string, string>>;
  players: Array<Record<string, string>>;
  advanced: Record<string, string>;
  manual_extras?: Record<string, string>;
}

function StatsTablesView({ data, showModelRole }: { data: FeedbackStatsData; showModelRole?: boolean }) {
  const [playerFilter, setPlayerFilter] = useState('all');
  const [playerSort, setPlayerSort] = useState<'total' | 'agree_rate'>('total');

  const filteredPlayers = (data.players ?? [])
    .filter(p => playerFilter === 'all' || p.level === playerFilter)
    .sort((a, b) => {
      if (playerSort === 'total') return safeInt(b.total) - safeInt(a.total);
      const rateA = safeInt(a.total) > 0 ? safeInt(a.agrees) / safeInt(a.total) : 0;
      const rateB = safeInt(b.total) > 0 ? safeInt(b.agrees) / safeInt(b.total) : 0;
      return rateB - rateA;
    });

  return (
    <div className="admin-dashboard-sections">
      {/* By model */}
      <div className="admin-section admin-section-wide">
        <h3>Par modèle IA</h3>
        <div className="admin-feedback-table-wrapper">
          <table className="admin-feedback-table">
            <thead>
              <tr>
                <th>Modèle</th>
                {showModelRole && <th>Rôle</th>}
                <th>Volume</th>
                <th>Accord / Désaccord</th>
                <th>Taux</th>
                <th>Confiance</th>
                <th>Entropie</th>
                <th>Joueurs</th>
                <th>Période</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_model ?? []).map((m, i) => {
                const mt = safeInt(m.total);
                const ma = safeInt(m.agrees);
                const md = safeInt(m.disagrees);
                return (
                  <tr key={`${m.model}-${m.role}-${i}`} className={showModelRole ? `row-role-${m.role}` : ''}>
                    <td className="cell-model">{m.model}</td>
                    {showModelRole && <td><span className={`badge badge-role-${m.role}`}>{ROLE_LABELS[m.role] ?? m.role}</span></td>}
                    <td>{fmtNum(mt)}</td>
                    <td><AgreeBadges agrees={ma} disagrees={md} /></td>
                    <td><RateBadge n={ma} d={mt} /></td>
                    <td>{fmtPct(m.avg_confidence)}</td>
                    <td>{m.avg_entropy ? parseFloat(m.avg_entropy).toFixed(2) : '—'}</td>
                    <td>{fmtNum(m.unique_players)}</td>
                    <td className="cell-date">
                      {m.first_at ? new Date(m.first_at).toLocaleDateString('fr-FR') : '—'}
                      {' → '}
                      {m.last_at ? new Date(m.last_at).toLocaleDateString('fr-FR') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* By level */}
      <div className="admin-section">
        <h3>Par niveau de joueur</h3>
        <div className="admin-feedback-table-wrapper">
          <table className="admin-feedback-table" data-mobile="cards">
            <thead>
              <tr><th>Niveau</th><th>Volume</th><th>Taux d'accord</th><th>Confiance moy.</th></tr>
            </thead>
            <tbody>
              {(data.by_level ?? []).map(l => {
                const lt = safeInt(l.total);
                const la = safeInt(l.agrees);
                return (
                  <tr key={l.level}>
                    <td data-label="Niveau"><span className={`badge badge-level-${l.level}`}>{LEVEL_LABELS[l.level] ?? l.level}</span></td>
                    <td data-label="Volume">{fmtNum(lt)}</td>
                    <td data-label="Taux d'accord"><RateBadge n={la} d={lt} /></td>
                    <td data-label="Confiance moy.">{fmtPct(l.avg_confidence)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Model × Level matrix */}
      {(data.by_model ?? []).length > 0 && (data.by_level ?? []).length > 0 && (
        <div className="admin-section">
          <h3>Accord modèle x niveau</h3>
          <div className="admin-feedback-table-wrapper">
            <table className="admin-feedback-table fb-matrix">
              <thead>
                <tr>
                  <th>Modèle</th>
                  {(data.by_level ?? []).map(l => <th key={l.level}>{LEVEL_LABELS[l.level] ?? l.level}</th>)}
                </tr>
              </thead>
              <tbody>
                {(data.by_model ?? []).map((m, i) => (
                  <tr key={`${m.model}-${m.role}-${i}`}>
                    <td className="cell-model">{m.model}{showModelRole && m.role !== 'unknown' ? ` (${ROLE_LABELS[m.role]})` : ''}</td>
                    {(data.by_level ?? []).map(l => {
                      const cell = (data.by_model_level ?? []).find(x => x.model === m.model && x.level === l.level);
                      if (!cell) return <td key={l.level} className="fb-matrix-empty">—</td>;
                      const ct = safeInt(cell.total);
                      const ca = safeInt(cell.agrees);
                      return (
                        <td key={l.level}>
                          <RateBadge n={ca} d={ct} />
                          <span className="fb-matrix-count"> ({ct})</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Advanced stats */}
      {data.advanced && <AdvancedStatsSection adv={data.advanced} />}

      {/* Manual-specific extras */}
      {data.manual_extras && (() => {
        const ext = data.manual_extras!;
        const total = safeInt(ext.total);
        const rows = [
          { label: 'Commentaires renseignés', value: safeInt(ext.has_comment), total },
          { label: 'Classements de cartes renseignés', value: safeInt(ext.has_card_rankings), total },
          { label: 'Raisons évaluées', value: safeInt(ext.has_reason_ratings), total },
          { label: 'Coach évalué', value: safeInt(ext.has_coach_ratings), total },
        ];
        const reasonsTotal = safeInt(ext.reasons_total);
        const reasonsApproved = safeInt(ext.reasons_approved);
        return (
          <div className="admin-section">
            <h3>Détail feedback manuel</h3>
            <div className="admin-feedback-table-wrapper">
              <table className="admin-feedback-table" data-mobile="cards">
                <thead><tr><th>Rubrique</th><th>Renseigné</th><th>Taux</th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.label}>
                      <td data-label="Rubrique">{r.label}</td>
                      <td data-label="Renseigné">{fmtNum(r.value)} / {fmtNum(r.total)}</td>
                      <td data-label="Taux"><RateBadge n={r.value} d={r.total} /></td>
                    </tr>
                  ))}
                  {reasonsTotal > 0 && (
                    <tr>
                      <td data-label="Rubrique">Raisons validées (rating positif)</td>
                      <td data-label="Renseigné">{fmtNum(reasonsApproved)} / {fmtNum(reasonsTotal)}</td>
                      <td data-label="Taux"><RateBadge n={reasonsApproved} d={reasonsTotal} /></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Player leaderboard */}
      <div className="admin-section admin-section-wide">
        <h3>Joueurs contributeurs</h3>
        <div className="admin-feedback-toolbar" style={{ marginBottom: 12 }}>
          <div className="admin-filters">
            <select value={playerFilter} onChange={e => setPlayerFilter(e.target.value)}>
              <option value="all">Tous niveaux</option>
              <option value="elite">Elite</option>
              <option value="expert">Expert</option>
              <option value="advanced">Avancé</option>
              <option value="intermediate">Intermédiaire</option>
              <option value="beginner">Débutant</option>
            </select>
            <select value={playerSort} onChange={e => setPlayerSort(e.target.value as 'total' | 'agree_rate')}>
              <option value="total">Trier par volume</option>
              <option value="agree_rate">Trier par taux d'accord</option>
            </select>
          </div>
          <span className="admin-feedback-count">{filteredPlayers.length} joueur(s)</span>
        </div>
        <div className="admin-feedback-table-wrapper" style={{ maxHeight: 400, overflowY: 'auto' }}>
          <table className="admin-feedback-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Joueur</th>
                <th>Niveau</th>
                <th>Feedback</th>
                <th>Accord / Désaccord</th>
                <th>Taux</th>
                <th>Confiance</th>
                <th>Dernier</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlayers.map((p, i) => {
                const pt = safeInt(p.total);
                const pa = safeInt(p.agrees);
                const pd = safeInt(p.disagrees);
                return (
                  <tr key={`${p.pseudo}-${i}`}>
                    <td>{i + 1}</td>
                    <td className="cell-pseudo">{p.pseudo}</td>
                    <td><span className={`badge badge-level-${p.level}`}>{LEVEL_LABELS[p.level] ?? p.level}</span></td>
                    <td>{fmtNum(pt)}</td>
                    <td><AgreeBadges agrees={pa} disagrees={pd} /></td>
                    <td><RateBadge n={pa} d={pt} /></td>
                    <td>{fmtPct(p.avg_confidence)}</td>
                    <td className="cell-date">{p.last_at ? new Date(p.last_at).toLocaleDateString('fr-FR') : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Passive feedback stats view ────────────

function PassiveFeedbackView({ session, roleFilter, modelFilter }: { session: Session; roleFilter: string; modelFilter: string }) {
  const [data, setData] = useState<FeedbackStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);

  const fetchData = useCallback(() => {
    const url = `${BACKEND_URL}/api/admin/feedback/passive-stats?role=${roleFilter}&model=${encodeURIComponent(modelFilter)}`;
    return adminFetch(url, session.pseudo)
      .then(r => r.json())
      .then(setData)
      .catch(() => {});
  }, [session.pseudo, roleFilter, modelFilter]);

  useEffect(() => {
    if (!initialLoadDone.current) {
      setLoading(true);
      fetchData().finally(() => { setLoading(false); initialLoadDone.current = true; });
    } else {
      setLoading(true);
      fetchData().finally(() => setLoading(false));
    }
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(fetchData, 60_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading && !data) return <div className="admin-loading">Chargement...</div>;
  if (!data) return <div className="admin-empty">Erreur de chargement des données de feedback passif.</div>;
  if (!data.overview) return <div className="admin-empty">Données de feedback incomplètes (overview manquant).</div>;

  const o = data.overview;
  const total = safeInt(o.total);
  const agrees = safeInt(o.agrees);
  const disagrees = safeInt(o.disagrees);

  return (
    <div className="fb-passive">
      <div className="admin-stats-grid">
        <StatCard label="Total feedback passif" value={fmtNum(total)} />
        <StatCard label="Accord" value={pct(agrees, total)} accent="green" />
        <StatCard label="Désaccord" value={pct(disagrees, total)} accent="red" />
        <StatCard label="Confiance ML moy." value={fmtPct(o.avg_confidence)} accent="blue" />
        <StatCard label="Entropie ML moy." value={o.avg_entropy ? parseFloat(o.avg_entropy).toFixed(2) : '—'} />
        <StatCard label="Joueurs uniques" value={fmtNum(o.unique_players)} />
      </div>
      <StatsTablesView data={data} showModelRole={roleFilter === 'all' && modelFilter === 'all'} />
    </div>
  );
}

// ── Manual feedback view ───────────────────

interface ManualGame {
  game_id: string;
  expert_pseudo: string;
  player_level: string;
  model: string | null;
  feedback_count: string;
  agrees: string;
  disagrees: string;
  first_at: string;
  last_at: string;
}

interface ManualFeedbackDetail {
  id: number;
  feedback_type: string;
  expert_pseudo: string;
  player_level: string;
  player_rating: number | null;
  player_role: string | null;
  game_id: string;
  game_table_type: string | null;
  trick_idx: number;
  position_in_trick: number;
  game_state: Record<string, any>;
  ml_recommended_card: number;
  ml_confidence: number | null;
  ml_entropy: number | null;
  ml_model_version: string | null;
  ml_explanation: string | null;
  expert_best_card: number | null;
  expert_agrees: boolean | null;
  expert_comment: string | null;
  expert_confidence: number | null;
  status: string;
  created_at: string;
  card_rankings: Array<{ card_id: number; expert_rank: number; ml_rank: number | null; ml_score: number | null }> | null;
  reason_ratings: Array<{ reason_category: string; reason_text: string; rating: number; is_main_reason: boolean }> | null;
  coach_ratings: Array<{ coach_advice: string; rating: number; is_misleading: boolean }> | null;
}

function ManualFeedbackView({ session, roleFilter, modelFilter }: { session: Session; roleFilter: string; modelFilter: string }) {
  const [games, setGames] = useState<ManualGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [gameFeedbacks, setGameFeedbacks] = useState<ManualFeedbackDetail[]>([]);
  const [loadingGame, setLoadingGame] = useState(false);
  const [manualStats, setManualStats] = useState<FeedbackStatsData | null>(null);
  const [viewMode, setViewMode] = useState<'stats' | 'games'>('stats');
  const initialLoadDone = useRef(false);

  const fetchData = useCallback(() => {
    const qs = `role=${roleFilter}&model=${encodeURIComponent(modelFilter)}`;
    return Promise.all([
      adminFetch(`${BACKEND_URL}/api/admin/feedback/manual-games?${qs}`, session.pseudo).then(r => r.json()),
      adminFetch(`${BACKEND_URL}/api/admin/feedback/manual-stats?${qs}`, session.pseudo).then(r => r.json()),
    ]).then(([gamesData, statsData]) => {
      setGames(gamesData.games ?? []);
      setManualStats(statsData);
    }).catch(() => {});
  }, [session.pseudo, roleFilter, modelFilter]);

  useEffect(() => {
    if (!initialLoadDone.current) {
      setLoading(true);
      fetchData().finally(() => { setLoading(false); initialLoadDone.current = true; });
    } else {
      setLoading(true);
      fetchData().finally(() => setLoading(false));
    }
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(fetchData, 60_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const loadGameDetail = useCallback((gameId: string) => {
    if (selectedGame === gameId) return;
    setSelectedGame(gameId);
    setLoadingGame(true);
    adminFetch(`${BACKEND_URL}/api/admin/feedback/game/${encodeURIComponent(gameId)}`, session.pseudo)
      .then(r => r.json())
      .then(d => setGameFeedbacks(d.feedbacks ?? []))
      .catch(() => {})
      .finally(() => setLoadingGame(false));
  }, [session.pseudo, selectedGame]);

  if (loading) return <div className="admin-loading">Chargement...</div>;

  return (
    <div className="fb-manual">
      <div className="fb-subtabs" style={{ marginBottom: 16 }}>
        <button className={`ml-section-btn ${viewMode === 'stats' ? 'active' : ''}`} onClick={() => setViewMode('stats')}>
          Statistiques
        </button>
        <button className={`ml-section-btn ${viewMode === 'games' ? 'active' : ''}`} onClick={() => setViewMode('games')}>
          Parties annotées ({games.length})
        </button>
      </div>

      {viewMode === 'stats' && manualStats && (() => {
        const o = manualStats.overview;
        const total = safeInt(o.total);
        const agrees = safeInt(o.agrees);
        const disagrees = safeInt(o.disagrees);
        return (
          <>
            <div className="admin-stats-grid">
              <StatCard label="Total feedback manuel" value={fmtNum(total)} />
              <StatCard label="Accord" value={pct(agrees, total)} accent="green" />
              <StatCard label="Désaccord" value={pct(disagrees, total)} accent="red" />
              <StatCard label="Parties annotées" value={fmtNum(o.unique_games)} accent="blue" />
              <StatCard label="Joueurs uniques" value={fmtNum(o.unique_players)} />
              <StatCard label="Confiance ML moy." value={fmtPct(o.avg_confidence)} />
            </div>
            <StatsTablesView data={manualStats} showModelRole={roleFilter === 'all' && modelFilter === 'all'} />
          </>
        );
      })()}

      {viewMode === 'games' && (
        <div className="fb-manual-layout">
          <div className="fb-manual-games">
            <h3>Parties annotées</h3>
            {games.length === 0 ? (
              <div className="admin-empty">Aucun feedback manuel</div>
            ) : (
              <div className="fb-game-list">
                {games.map(g => {
                  const gt = safeInt(g.feedback_count);
                  const ga = safeInt(g.agrees);
                  const gd = safeInt(g.disagrees);
                  return (
                    <button
                      key={g.game_id}
                      className={`fb-game-card ${selectedGame === g.game_id ? 'active' : ''}`}
                      onClick={() => loadGameDetail(g.game_id)}
                    >
                      <div className="fb-game-card-top">
                        <span className="cell-pseudo">{g.expert_pseudo}</span>
                        <span className={`badge badge-level-${g.player_level}`}>{LEVEL_LABELS[g.player_level] ?? g.player_level}</span>
                      </div>
                      {g.model && <div className="fb-game-card-model">{g.model}</div>}
                      <div className="fb-game-card-stats">
                        <span>{gt} feedback</span>
                        <AgreeBadges agrees={ga} disagrees={gd} />
                      </div>
                      <div className="fb-game-card-date">
                        {new Date(g.last_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="fb-manual-detail" style={{ minWidth: 0, flex: 1 }}>
            {!selectedGame ? (
              <div className="admin-empty">Sélectionnez une partie pour voir le détail des annotations</div>
            ) : loadingGame ? (
              <div className="admin-loading">Chargement...</div>
            ) : (
              <div className="fb-detail-entries" style={{ width: '100%' }}>
                <div className="fb-detail-header-bar">
                  <h3>Partie {selectedGame} — {gameFeedbacks.length} annotation(s)</h3>
                  <a
                    className="admin-btn admin-btn-primary admin-btn-sm"
                    href={`/training?loadGame=${encodeURIComponent(selectedGame)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ouvrir en entraînement
                  </a>
                </div>
                {gameFeedbacks.map((fb, idx) => (
                  <div key={fb.id} className="fb-detail-card">
                    <div className="fb-detail-header">
                      <span className="fb-detail-trick">Pli {fb.trick_idx + 1}, position {fb.position_in_trick + 1}</span>
                      <span className={fb.expert_agrees ? 'badge badge-agree' : 'badge badge-disagree'}>
                        {fb.expert_agrees ? '✓ Accord' : '✗ Désaccord'}
                      </span>
                      {fb.player_role && <span className={`badge badge-role-${fb.player_role}`}>{ROLE_LABELS[fb.player_role] ?? fb.player_role}</span>}
                      <span className="cell-model">{fb.ml_model_version ?? '—'}</span>
                      <span className="cell-date">
                        {new Date(fb.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    <div className="fb-detail-cards-row">
                      <div className="fb-detail-card-info">
                        <span className="fb-detail-label">IA recommande</span>
                        <span className="fb-detail-card-name">{cardName(fb.ml_recommended_card)}</span>
                        {fb.ml_confidence != null && <span className="fb-detail-confidence">Confiance: {Math.round(fb.ml_confidence * 100)}%</span>}
                      </div>
                      <div className="fb-detail-vs">→</div>
                      <div className="fb-detail-card-info">
                        <span className="fb-detail-label">Expert joue</span>
                        <span className="fb-detail-card-name">{fb.expert_best_card ? cardName(fb.expert_best_card) : '—'}</span>
                        {fb.expert_confidence != null && <span className="fb-detail-confidence">Confiance: {fb.expert_confidence}/5</span>}
                      </div>
                    </div>

                    {fb.ml_explanation && (
                      <div className="fb-detail-explanation">
                        <span className="fb-detail-label">Explication IA</span>
                        <p>{fb.ml_explanation}</p>
                      </div>
                    )}

                    {fb.expert_comment && (
                      <div className="fb-detail-comment">
                        <span className="fb-detail-label">Commentaire expert</span>
                        <p>{fb.expert_comment}</p>
                      </div>
                    )}

                    {fb.card_rankings && fb.card_rankings.length > 0 && (
                      <div className="fb-detail-rankings">
                        <span className="fb-detail-label">Classement des cartes</span>
                        <div className="fb-rankings-list">
                          {fb.card_rankings.map((r, ri) => (
                            <div key={ri} className="fb-ranking-row">
                              <span className="fb-ranking-pos">#{r.expert_rank}</span>
                              <span className="fb-ranking-card">{cardName(r.card_id)}</span>
                              {r.ml_rank != null && <span className="fb-ranking-ml">ML: #{r.ml_rank}</span>}
                              {r.ml_score != null && <span className="fb-ranking-score">{Math.round(r.ml_score * 100)}%</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {fb.reason_ratings && fb.reason_ratings.length > 0 && (
                      <div className="fb-detail-reasons">
                        <span className="fb-detail-label">Raisons évaluées</span>
                        {fb.reason_ratings.map((r, ri) => (
                          <div key={ri} className={`fb-reason-row ${r.is_main_reason ? 'fb-reason-main' : ''}`}>
                            <span className={`fb-reason-rating ${r.rating > 0 ? 'fb-rate-good' : r.rating < 0 ? 'fb-rate-bad' : ''}`}>
                              {r.rating > 0 ? '+1' : r.rating < 0 ? '-1' : '—'}
                            </span>
                            <span className="fb-reason-text">{r.reason_text}</span>
                            {r.is_main_reason && <span className="badge badge-manual">principale</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {fb.game_state && typeof fb.game_state === 'object' && (
                      <details className="fb-detail-gamestate">
                        <summary>État du jeu (données brutes)</summary>
                        <div className="fb-gamestate-grid">
                          {fb.game_state.hand && (
                            <div>
                              <span className="fb-detail-label">Main</span>
                              <div className="fb-cards-inline">
                                {(fb.game_state.hand as number[]).map((c, ci) => (
                                  <span key={ci} className="fb-card-chip">{cardName(c)}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {fb.game_state.trick_cards && (fb.game_state.trick_cards as number[]).length > 0 && (
                            <div>
                              <span className="fb-detail-label">Cartes du pli</span>
                              <div className="fb-cards-inline">
                                {(fb.game_state.trick_cards as number[]).map((c, ci) => (
                                  <span key={ci} className="fb-card-chip">{cardName(c)}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {fb.game_state.contract != null && (
                            <div>
                              <span className="fb-detail-label">Contrat: </span>
                              {['—', 'Petite', 'Garde', 'Garde sans', 'Garde contre'][fb.game_state.contract as number] ?? fb.game_state.contract}
                            </div>
                          )}
                          <div>
                            <span className="fb-detail-label">Position: </span>
                            {fb.game_state.my_position as number ?? '—'} / {fb.game_state.num_players as number ?? '—'} joueurs
                            {fb.game_state.taker_position != null && ` (preneur: pos ${fb.game_state.taker_position})`}
                          </div>
                        </div>
                      </details>
                    )}

                    {idx < gameFeedbacks.length - 1 && <hr className="fb-detail-separator" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Admins management tab
// ============================================

function AdminsTab({ session }: { session: Session }) {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPseudo, setNewPseudo] = useState('');

  const loadAdmins = useCallback(() => {
    setLoading(true);
    adminFetch(`${BACKEND_URL}/api/admin/admins`, session.pseudo)
      .then(r => r.json())
      .then(data => setAdmins(data.admins ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [session.pseudo]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  const handleAdd = async () => {
    if (!newPseudo.trim()) return;
    await adminFetch(`${BACKEND_URL}/api/admin/admins`, session.pseudo, {
      method: 'POST',
      body: JSON.stringify({ target_pseudo: newPseudo.trim() }),
    });
    setNewPseudo('');
    loadAdmins();
  };

  const handleRemove = async (pseudo: string) => {
    if (!confirm(`Retirer les droits admin à ${pseudo} ?`)) return;
    await adminFetch(`${BACKEND_URL}/api/admin/admins/${encodeURIComponent(pseudo)}`, session.pseudo, {
      method: 'DELETE',
    });
    loadAdmins();
  };

  return (
    <div className="admin-admins-tab">
      <div className="admin-add-form">
        <input
          type="text"
          placeholder="Pseudo du nouveau admin..."
          value={newPseudo}
          onChange={e => setNewPseudo(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
        />
        <button className="admin-btn admin-btn-primary" onClick={handleAdd}>
          Ajouter admin
        </button>
      </div>

      {loading ? (
        <div className="admin-loading">Chargement...</div>
      ) : (
        <div className="admin-admins-list">
          {admins.map(a => (
            <div key={a.pseudo} className="admin-admin-row">
              <PlayerAvatar pseudo={a.pseudo} size={32} />
              <div className="admin-admin-info">
                <span className="admin-admin-pseudo">{a.pseudo}</span>
                <span className="admin-admin-meta">
                  Ajouté par {a.granted_by} le {new Date(a.created_at).toLocaleDateString('fr-FR')}
                </span>
              </div>
              {a.pseudo.toLowerCase() !== 'panurge' && (
                <button
                  className="admin-btn-sm admin-btn-danger"
                  onClick={() => handleRemove(a.pseudo)}
                  title="Retirer les droits admin"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// ML Training tab
// ============================================

interface MLStatus {
  configured: boolean;
  modelVersion: string;
  feedbackReady: number;
  feedbackConsumed: number;
  lastExportAt: string | null;
  runningJobs: number;
  slots?: Record<string, { loaded: boolean; version?: string; feature_dim?: number }>;
  deductionModelLoaded?: boolean;
}

interface MLJob {
  id: string;
  name: string;
  state: string;
  info?: string;
  duration?: number;
  startedAt?: string;
  stoppedAt?: string;
  image: string;
  gpu: string;
  createdAt: string;
  logs?: string;
}

const ML_TRAINING_HIDDEN_JOBS_KEY = 'webtarot.mlTraining.hiddenJobIds';
/** Persistance des tableaux de synthèse multi-comparaisons (onglet Comparer). */
const ML_SYNTH_COMPOSERS_KEY = 'webtarot.mlSynth.composers.v1';
const ML_JOB_ACTIVE_STATES = new Set(['RUNNING', 'QUEUED', 'PENDING', 'INITIALIZING']);

function isMlJobActiveState(state: string): boolean {
  return ML_JOB_ACTIVE_STATES.has(state);
}

type MLLogViewMode = 'raw' | 'clean' | 'summary' | 'params';

interface MLLogProgress {
  current: number;
  total: number;
  percent: number;
  label?: string;
  /** Step 2 imitation : examples accumulés (ligne Processed … games, … examples so far). */
  examplesSoFar?: number;
}

interface MLLogEpochMetrics {
  epoch: number;
  total: number;
  loss: number;
  val: number;
  /** Présent quand le log utilise « Val strict » + « tolerant » (même échelle que val). */
  valTolerant?: number;
  entame: number;
  autre: number;
}

interface ImitationLogSummary {
  isImitation: boolean;
  currentStep?: { number: number; title: string };
  config?: Record<string, unknown>;
  configRaw?: string;
  datasetManifest?: { players: number; topPlayers: Array<{ name: string; games: number }> };
  dealAwareSplit?: { uniqueDeals: number; totalGames: number };
  step1Progress?: MLLogProgress;
  /** Step 2 : construction des exemples à partir des parties (Processed x/y games). */
  step2GamesProgress?: MLLogProgress;
  downloadedGames?: number;
  featureDim?: number;
  trainExamples?: number;
  valExamples?: number;
  trainGames?: number;
  valGames?: number;
  splitLabel?: string;
  modelParameters?: number;
  epochs: MLLogEpochMetrics[];
  earlyStoppingEpoch?: number;
  finalValAccuracy?: number;
  finalEntameAccuracy?: number;
  evalProgress?: MLLogProgress & { winRate?: number };
  evalComplete?: { winRate: number; avgScore: number; stdScore: number };
}

interface SelfplayIterationMetrics {
  iteration: number;
  total: number;
  temperature?: number;
  lr?: number;
  collectionDefenseWinRate?: number;
  collectionAvgScore?: number;
  duplicateTrainGames?: number;
  duplicateTrainAvgMatchpoint?: number;
  frontierPickRate?: number;
  frontierNearAvg?: number;
  frontierBufferAdds?: number;
  frontierBufferSize?: number;
  policyLoss?: number;
  entropy?: number;
  clipFraction?: number;
  /** Win rate du rôle entraîné (ATK ou DEF selon trainRole). */
  evalWinRate?: number;
  evalDefenseWinRate?: number;
  evalAvgScore?: number;
  duplicateEvalMatchpoint?: number;
  isBest?: boolean;
}

interface SelfplayLogSummary {
  isSelfplay: boolean;
  trainRole?: 'attacker' | 'defender';
  config?: {
    iterations?: number;
    gamesPerIter?: number;
    lr?: number;
    ppoEpochs?: number;
    batchSize?: number;
  };
  initialEval?: {
    defenseWinRate: number;
    attackWinRate: number;
    avgScore: number;
    duplicateEvalMatchpoint?: number;
  };
  finalEval?: {
    defenseWinRate: number;
    attackWinRate: number;
    avgScore: number;
    duplicateEvalMatchpoint?: number;
  };
  summary?: {
    initialAvgScore?: number;
    initialWinRate?: number;
    bestAvgScore?: number;
    bestIteration?: number;
    finalAvgScore?: number;
    finalWinRate?: number;
    deltaAvgScore?: number;
    significant?: boolean;
  };
  iterations: SelfplayIterationMetrics[];
  earlyStopping?: boolean;
  earlyStoppingNoImproveIters?: number;
}

function parseLogNumber(value: string): number {
  return Number(value.replace(/,/g, ''));
}

function formatLogNumber(value?: number): string {
  if (value === undefined || Number.isNaN(value)) return '-';
  return value.toLocaleString('fr-FR');
}

function formatLogPercent(value?: number): string {
  if (value === undefined || Number.isNaN(value)) return '-';
  return `${value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}%`;
}

/** Loss / métriques : accepte nan, inf, notation scientifique Python. */
function parseLogFloatToken(raw: string): number {
  const t = raw.trim().toLowerCase();
  if (t === 'nan') return Number.NaN;
  if (t === 'inf' || t === '+inf') return Number.POSITIVE_INFINITY;
  if (t === '-inf') return Number.NEGATIVE_INFINITY;
  return Number(raw);
}

function stripMLJobLogLine(line: string): string {
  return line
    .replace(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z\s+\[(?:ovh|job)\]\s*/i, '')
    .replace(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2},\d{3}\s+(?:INFO|WARNING|ERROR|DEBUG)\s+/i, '')
    .replace(/^(?:INFO|WARNING|ERROR|DEBUG)\s+/i, '');
}

function cleanMLJobLogs(logs: string): string {
  return logs.split(/\r?\n/).map(stripMLJobLogLine).join('\n');
}

/** Lignes de progression répétitives, sans valeur pour l'analyse / le débogage. */
function isMLJobLogProgressNoiseLine(line: string): boolean {
  const stripped = stripMLJobLogLine(line).trim();
  if (!stripped) return false;
  return (
    /^Progress:\s*[\d,]+\/[\d,]+\s+\([\d.]+%\)/i.test(stripped)
    || /^Progress:\s*[\d,]+\s+games\b/i.test(stripped)
    || /^Progress:\s*[\d,]+\/[\d,]+\s+games\s*\|/i.test(stripped)
    || /^Processed\s+[\d,]+\/[\d,]+\s+games,\s*[\d,]+\s+examples so far/i.test(stripped)
    || /^Processed\s+[\d,]+\/[\d,]+\s+(?:training|val)\s+games\s*→\s*[\d,]+\s+examples/i.test(stripped)
    || /^Processed\s+[\d,]+\s+games\s+\([\d,]+\s+perspectives/i.test(stripped)
    || /^Downloading:\s*[\d,]+\/[\d,]+\s+games/i.test(stripped)
    || /^Eval progress:\s*[\d,]+\/[\d,]+\s+games,\s*win_rate=/i.test(stripped)
    || /^Epoch\s+\d+\s+\[\d+\/\d+\]\s/i.test(stripped)
    || /^\d+\/\d+\s+games,\s+defense:\s+\d+\/\d+\s+\(\d+%\)/i.test(stripped)
  );
}

/** Extrait de log pour copie : sans timestamps ni lignes de progression bruyantes. */
function extractMLJobLogsForCopy(logs: string): { text: string; removedProgressLines: number } {
  const rawLines = logs.split(/\r?\n/);
  const kept = rawLines
    .filter(line => !isMLJobLogProgressNoiseLine(line))
    .map(stripMLJobLogLine);
  return {
    text: kept.join('\n').replace(/\n{3,}/g, '\n\n').trim(),
    removedProgressLines: rawLines.length - kept.length,
  };
}

function parsePythonLiteralObject(raw: string): Record<string, unknown> | undefined {
  try {
    const jsonLike = raw
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')
      .replace(/\bNone\b/g, 'null')
      .replace(/'/g, '"');
    const parsed = JSON.parse(jsonLike);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : undefined;
  } catch {
    return undefined;
  }
}

function formatLogValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'oui' : 'non';
  if (typeof value === 'number') return value.toLocaleString('fr-FR');
  if (typeof value === 'string') return value.trim() || '-';
  if (value == null) return '-';
  return JSON.stringify(value);
}

function parseImitationLogSummary(logs: string): ImitationLogSummary {
  const summary: ImitationLogSummary = {
    isImitation: /TAROT ML TRAINING PIPELINE\s+—\s+IMITATION|Loaded imitation config|Imitation Training/i.test(logs),
    epochs: [],
  };

  let activeStep: { number: number; title: string } | undefined;
  /** Cible max=… sur la ligne « Downloading games » (sources sans COUNT SQL, ex. différé). */
  let step1DownloadMaxGames: number | undefined;

  for (const rawLine of logs.split(/\r?\n/)) {
    const line = stripMLJobLogLine(rawLine);
    const stepMatch = line.match(/Step\s+(\d+):\s*([^─]+)/i);
    if (stepMatch) {
      activeStep = {
        number: Number(stepMatch[1]),
        title: stepMatch[2].trim(),
      };
      summary.currentStep = activeStep;
      continue;
    }

    if (!activeStep && /Downloading games/i.test(line)) {
      activeStep = { number: 1, title: 'Download Games' };
      summary.currentStep = activeStep;
    }

    const configMatch = line.match(/Loaded imitation config:\s*(\{.*\})/i);
    if (configMatch) {
      summary.configRaw = configMatch[1];
      summary.config = parsePythonLiteralObject(configMatch[1]);
    }

    const downloadingMatch = line.match(/Downloading games from .*\(max=([\d,]+)/i);
    if (downloadingMatch) {
      step1DownloadMaxGames = parseLogNumber(downloadingMatch[1]);
    }
    if (step1DownloadMaxGames === undefined && summary.config) {
      const cfgMax = summary.config.max_games;
      if (typeof cfgMax === 'number' && Number.isFinite(cfgMax) && cfgMax > 0) {
        step1DownloadMaxGames = cfgMax;
      }
    }

    const manifestMatch = line.match(/Dataset manifest:\s*([\d,]+)\s+players,\s*top:\s*(.+)$/i);
    if (manifestMatch) {
      summary.datasetManifest = {
        players: parseLogNumber(manifestMatch[1]),
        topPlayers: manifestMatch[2].split(',').map(part => {
          const playerMatch = part.trim().match(/^(.+)\(([\d,]+)\)$/);
          return playerMatch
            ? { name: playerMatch[1].trim(), games: parseLogNumber(playerMatch[2]) }
            : { name: part.trim(), games: 0 };
        }).filter(p => p.name),
      };
    }

    const dealSplitMatch = line.match(/Deal-aware split:\s*([\d,]+)\s+unique deals,\s*([\d,]+)\s+total games/i);
    if (dealSplitMatch) {
      summary.dealAwareSplit = {
        uniqueDeals: parseLogNumber(dealSplitMatch[1]),
        totalGames: parseLogNumber(dealSplitMatch[2]),
      };
    }

    const processedGamesMatch = line.match(
      /^\s*Processed\s+([\d,]+)\/([\d,]+)\s+games,\s*([\d,]+)\s+examples so far/i,
    );
    if (processedGamesMatch) {
      const current = parseLogNumber(processedGamesMatch[1]);
      const total = parseLogNumber(processedGamesMatch[2]);
      const examplesSoFar = parseLogNumber(processedGamesMatch[3]);
      summary.step2GamesProgress = {
        current,
        total,
        percent: total > 0 ? (current / total) * 100 : 0,
        examplesSoFar,
        label: `${formatLogNumber(current)} / ${formatLogNumber(total)} games`,
      };
    }

    if (activeStep?.number === 1) {
      const progressMatch = line.match(/Progress:\s*([\d,]+)\/([\d,]+)\s+\(([\d.]+)%\)/i);
      if (progressMatch) {
        const current = parseLogNumber(progressMatch[1]);
        const total = parseLogNumber(progressMatch[2]);
        summary.step1Progress = {
          current,
          total,
          percent: Number(progressMatch[3]),
          label: `${formatLogNumber(current)} / ${formatLogNumber(total)} games`,
        };
      } else {
        const progressGamesMatch = line.match(/^Progress:\s*([\d,]+)\s+games\b/i);
        if (progressGamesMatch) {
          const current = parseLogNumber(progressGamesMatch[1]);
          const total = step1DownloadMaxGames ?? 0;
          summary.step1Progress = {
            current,
            total: total > 0 ? total : current,
            percent: total > 0 ? Math.min(100, (current / total) * 100) : 0,
            label: total > 0
              ? `${formatLogNumber(current)} / ${formatLogNumber(total)} games`
              : `${formatLogNumber(current)} games`,
          };
        }
      }
      const downloadedMatch = line.match(/Downloaded\s+([\d,]+)\s+games/i);
      if (downloadedMatch) {
        const downloaded = parseLogNumber(downloadedMatch[1]);
        summary.downloadedGames = downloaded;
        const target = step1DownloadMaxGames && step1DownloadMaxGames > 0 ? step1DownloadMaxGames : downloaded;
        summary.step1Progress = {
          current: downloaded,
          total: target,
          percent: 100,
          label: `${formatLogNumber(downloaded)} / ${formatLogNumber(target)} games`,
        };
      }
    }

    const featureMatch = line.match(
      /Feature dim:\s*([\d,]+),\s*([\d,]+)\s+train\s+\+\s+([\d,]+)\s+val examples\s+\(([\d,]+)\s+train\s+\/\s+([\d,]+)\s+val games,\s*([^)]+)\)/i,
    );
    if (featureMatch) {
      summary.featureDim = parseLogNumber(featureMatch[1]);
      summary.trainExamples = parseLogNumber(featureMatch[2]);
      summary.valExamples = parseLogNumber(featureMatch[3]);
      summary.trainGames = parseLogNumber(featureMatch[4]);
      summary.valGames = parseLogNumber(featureMatch[5]);
      summary.splitLabel = featureMatch[6].trim();
    }

    const modelParamsMatch = line.match(/Model parameters:\s*([\d,]+)/i);
    if (modelParamsMatch) summary.modelParameters = parseLogNumber(modelParamsMatch[1]);

    const epochStrictTolerantMatch = line.match(
      /Epoch\s+(\d+)\/(\d+)\s+-\s+Loss:\s*(nan|inf|[+-]?[\d.]+(?:e[+-]?\d+)?)\s+-\s+Val\s+strict:\s*([\d.]+)%\s*\|\s*tolerant:\s*([\d.]+)%\s+-\s+Entame:\s*([\d.]+)%\s+-\s+Autre:\s*([\d.]+)%/i,
    );
    if (epochStrictTolerantMatch) {
      summary.epochs.push({
        epoch: Number(epochStrictTolerantMatch[1]),
        total: Number(epochStrictTolerantMatch[2]),
        loss: parseLogFloatToken(epochStrictTolerantMatch[3]),
        val: Number(epochStrictTolerantMatch[4]),
        valTolerant: Number(epochStrictTolerantMatch[5]),
        entame: Number(epochStrictTolerantMatch[6]),
        autre: Number(epochStrictTolerantMatch[7]),
      });
    } else {
      const epochMatch = line.match(
        /Epoch\s+(\d+)\/(\d+)\s+-\s+Loss:\s*(nan|inf|[+-]?[\d.]+(?:e[+-]?\d+)?)\s+-\s+Val:\s*([\d.]+)%\s+-\s+Entame:\s*([\d.]+)%\s+-\s+Autre:\s*([\d.]+)%/i,
      );
      if (epochMatch) {
        summary.epochs.push({
          epoch: Number(epochMatch[1]),
          total: Number(epochMatch[2]),
          loss: parseLogFloatToken(epochMatch[3]),
          val: Number(epochMatch[4]),
          entame: Number(epochMatch[5]),
          autre: Number(epochMatch[6]),
        });
      }
    }

    const earlyStoppingMatch = line.match(/Early stopping after\s+(\d+)\s+epochs/i);
    if (earlyStoppingMatch) summary.earlyStoppingEpoch = Number(earlyStoppingMatch[1]);

    const accuracyMatch = line.match(/Val accuracy:\s*([\d.]+)%,\s*Entame accuracy:\s*([\d.]+)%/i);
    if (accuracyMatch) {
      summary.finalValAccuracy = Number(accuracyMatch[1]);
      summary.finalEntameAccuracy = Number(accuracyMatch[2]);
    }

    const evalProgressMatch = line.match(/Eval progress:\s*([\d,]+)\/([\d,]+)\s+games,\s+win_rate=([\d.]+)%/i);
    if (evalProgressMatch) {
      const current = parseLogNumber(evalProgressMatch[1]);
      const total = parseLogNumber(evalProgressMatch[2]);
      summary.evalProgress = {
        current,
        total,
        percent: total > 0 ? (current / total) * 100 : 0,
        label: `${formatLogNumber(current)} / ${formatLogNumber(total)} games`,
        winRate: Number(evalProgressMatch[3]),
      };
    }

    const evalCompleteMatch = line.match(/Evaluation complete:\s*win_rate=([\d.]+)%,\s*avg_score=([+-]?[\d.]+)\s*±\s*([\d.]+)/i);
    if (evalCompleteMatch) {
      summary.evalComplete = {
        winRate: Number(evalCompleteMatch[1]),
        avgScore: Number(evalCompleteMatch[2]),
        stdScore: Number(evalCompleteMatch[3]),
      };
    }
  }

  return summary;
}

function parseSelfplayLogSummary(logs: string): SelfplayLogSummary {
  const summary: SelfplayLogSummary = {
    isSelfplay: /SELF-PLAY TRAINING\s+-\s+TAROT\s+\(PPO\)/i.test(logs),
    iterations: [],
  };

  if (!summary.isSelfplay) return summary;

  let currentIter: SelfplayIterationMetrics | undefined;
  let inInitialEval = false;
  let inFinalEval = false;

  const pushCurrentIter = () => {
    if (currentIter) {
      summary.iterations.push(currentIter);
      currentIter = undefined;
    }
  };

  for (const rawLine of logs.split(/\r?\n/)) {
    const line = stripMLJobLogLine(rawLine);

    const roleHeaderMatch = line.match(
      /SELF-PLAY TRAINING\s+-\s+TAROT\s+\(PPO\)\s+—\s+training\s+(ATK|DEF|attacker|defender)/i,
    );
    if (roleHeaderMatch) {
      const roleToken = roleHeaderMatch[1].toUpperCase();
      summary.trainRole = roleToken === 'ATK' || roleToken === 'ATTACKER' ? 'attacker' : 'defender';
      continue;
    }

    const trainingRoleMatch = line.match(/Training:\s+(attacker|defender)/i);
    if (trainingRoleMatch && !summary.trainRole) {
      summary.trainRole = trainingRoleMatch[1].toLowerCase() as 'attacker' | 'defender';
    }

    const configMatch = line.match(
      /Config:\s+(\d+)\s+iters\s+x\s+([\d,]+)\s+games,\s+lr=([\d.e+-]+),\s+PPO epochs=(\d+),\s+batch=(\d+)/i,
    );
    if (configMatch) {
      summary.config = {
        iterations: Number(configMatch[1]),
        gamesPerIter: parseLogNumber(configMatch[2]),
        lr: parseLogFloatToken(configMatch[3]),
        ppoEpochs: Number(configMatch[4]),
        batchSize: Number(configMatch[5]),
      };
    }

    if (/Initial evaluation/i.test(line)) {
      inInitialEval = true;
      inFinalEval = false;
      continue;
    }
    if (/Final evaluation/i.test(line)) {
      inFinalEval = true;
      inInitialEval = false;
      continue;
    }

    const defenseWinRateMatch = line.match(
      /Defense win rate:\s+(\d+)\/(\d+)\s+=\s+([\d.]+)%(?:\s+\(95%\s+CI:\s+\[[\d.]+%,\s*[\d.]+%\]\))?/i,
    );
    if (defenseWinRateMatch) {
      const defenseWinRate = Number(defenseWinRateMatch[3]);
      const attackWinRate = 100 - defenseWinRate;
      const evalBlock = { defenseWinRate, attackWinRate, avgScore: 0 };
      if (inInitialEval) {
        summary.initialEval = { ...evalBlock };
      } else if (inFinalEval) {
        summary.finalEval = { ...evalBlock };
      }
      continue;
    }

    const avgScoreTakerMatch = line.match(/Avg score(?: \(taker perspective\))?:\s+([+-]?[\d.]+)/i);
    if (avgScoreTakerMatch) {
      const avgScore = Number(avgScoreTakerMatch[1]);
      if (inInitialEval) {
        summary.initialEval = summary.initialEval || { defenseWinRate: 0, attackWinRate: 0, avgScore: 0 };
        summary.initialEval.avgScore = avgScore;
      } else if (inFinalEval) {
        summary.finalEval = summary.finalEval || { defenseWinRate: 0, attackWinRate: 0, avgScore: 0 };
        summary.finalEval.avgScore = avgScore;
      }
      continue;
    }

    const duplicateEvalMatch = line.match(/Duplicate eval:.*mp\/deal=([+-]?[\d.]+)/i);
    if (duplicateEvalMatch) {
      const mpDeal = Number(duplicateEvalMatch[1]);
      if (inInitialEval) {
        summary.initialEval = summary.initialEval || { defenseWinRate: 0, attackWinRate: 0, avgScore: 0 };
        summary.initialEval.duplicateEvalMatchpoint = mpDeal;
        inInitialEval = false;
      } else if (inFinalEval) {
        summary.finalEval = summary.finalEval || { defenseWinRate: 0, attackWinRate: 0, avgScore: 0 };
        summary.finalEval.duplicateEvalMatchpoint = mpDeal;
        inFinalEval = false;
      } else if (currentIter) {
        currentIter.duplicateEvalMatchpoint = mpDeal;
      }
      continue;
    }

    const iterMatch = line.match(
      /Iteration\s+(\d+)\/(\d+)\s+\(T=([\d.]+),\s*lr=([\d.e+-]+)\)/i,
    );
    if (iterMatch) {
      inInitialEval = false;
      inFinalEval = false;
      pushCurrentIter();
      currentIter = {
        iteration: Number(iterMatch[1]),
        total: Number(iterMatch[2]),
        temperature: Number(iterMatch[3]),
        lr: parseLogFloatToken(iterMatch[4]),
      };
      continue;
    }

    const dataCollectionMatch = line.match(
      /Data collection:\s+([\d,]+)\s+games,\s+defense\s+([\d.]+)%,\s+avg_score=([+-]?[\d.]+)/i,
    );
    if (dataCollectionMatch && currentIter) {
      currentIter.collectionDefenseWinRate = Number(dataCollectionMatch[2]);
      currentIter.collectionAvgScore = Number(dataCollectionMatch[3]);
    }

    const duplicateTrainMatch = line.match(
      /Duplicate train:\s+([\d,]+)\/([\d,]+)\s+games,\s+avg matchpoint reward=([+-]?[\d.]+)/i,
    );
    if (duplicateTrainMatch && currentIter) {
      currentIter.duplicateTrainGames = parseLogNumber(duplicateTrainMatch[1]);
      currentIter.duplicateTrainAvgMatchpoint = Number(duplicateTrainMatch[3]);
    }

    const frontierReplayMatch = line.match(
      /Frontier replay:\s+picks=(\d+)\/(\d+)\s+\((\d+)%\),\s+near_avg=([\d.]+),\s+adds=(\d+),\s+buffer=(\d+)/i,
    );
    if (frontierReplayMatch && currentIter) {
      currentIter.frontierPickRate = Number(frontierReplayMatch[3]);
      currentIter.frontierNearAvg = Number(frontierReplayMatch[4]);
      currentIter.frontierBufferAdds = Number(frontierReplayMatch[5]);
      currentIter.frontierBufferSize = Number(frontierReplayMatch[6]);
    }

    const ppoMatch = line.match(
      /(?:ATK|DEF):\s+loss=([\d.]+),\s+H=([\d.]+),\s+clip=([\d.]+)/i,
    );
    if (ppoMatch && currentIter) {
      currentIter.policyLoss = Number(ppoMatch[1]);
      currentIter.entropy = Number(ppoMatch[2]);
      currentIter.clipFraction = Number(ppoMatch[3]);
    }

    const evalAttackerMatch = line.match(
      /Eval:\s+ATK\s+(\d+)\/(\d+)\s+=\s+([\d.]+)%\s+\(def:\s+([\d.]+)%\),\s+avg_score=([+-]?[\d.]+)/i,
    );
    if (evalAttackerMatch && currentIter) {
      currentIter.evalWinRate = Number(evalAttackerMatch[3]);
      currentIter.evalDefenseWinRate = Number(evalAttackerMatch[4]);
      currentIter.evalAvgScore = Number(evalAttackerMatch[5]);
    }

    const evalDefenderMatch = line.match(
      /Eval:\s+(\d+)\/(\d+)\s+=\s+([\d.]+)%\s+\(95%\s+CI:\s+\[[\d.]+%,\s*[\d.]+%\]\),\s+avg_score=([+-]?[\d.]+)/i,
    );
    if (evalDefenderMatch && currentIter) {
      currentIter.evalWinRate = Number(evalDefenderMatch[3]);
      currentIter.evalDefenseWinRate = Number(evalDefenderMatch[3]);
      currentIter.evalAvgScore = Number(evalDefenderMatch[4]);
    }

    if (/\*\s+New best!/i.test(line) && currentIter) {
      currentIter.isBest = true;
    }

    const earlyStopMatch = line.match(
      /Early stopping:\s+no statistically significant improvement for\s+(\d+)\s+iterations/i,
    );
    if (earlyStopMatch) {
      summary.earlyStopping = true;
      summary.earlyStoppingNoImproveIters = Number(earlyStopMatch[1]);
    }

    if (/^SUMMARY/i.test(line.trim())) {
      pushCurrentIter();
      continue;
    }

    const initialSummaryMatch = line.match(/Initial:\s+([+-]?[\d.]+)\s+pts.*\(wr\s+([\d.]+)%/i);
    if (initialSummaryMatch) {
      summary.summary = summary.summary || {};
      summary.summary.initialAvgScore = Number(initialSummaryMatch[1]);
      summary.summary.initialWinRate = Number(initialSummaryMatch[2]);
    }

    const bestSummaryMatch = line.match(/Best:\s+([+-]?[\d.]+)\s+pts\s+\(iteration\s+(\d+)\)/i);
    if (bestSummaryMatch) {
      summary.summary = summary.summary || {};
      summary.summary.bestAvgScore = Number(bestSummaryMatch[1]);
      summary.summary.bestIteration = Number(bestSummaryMatch[2]);
    }

    const finalSummaryMatch = line.match(/Final:\s+([+-]?[\d.]+)\s+pts.*\(wr\s+([\d.]+)%/i);
    if (finalSummaryMatch) {
      summary.summary = summary.summary || {};
      summary.summary.finalAvgScore = Number(finalSummaryMatch[1]);
      summary.summary.finalWinRate = Number(finalSummaryMatch[2]);
    }

    const deltaSummaryMatch = line.match(/Delta:\s+([+-]?[\d.]+)\s+pts/i);
    if (deltaSummaryMatch) {
      summary.summary = summary.summary || {};
      summary.summary.deltaAvgScore = Number(deltaSummaryMatch[1]);
    }

    if (/STATISTICALLY SIGNIFICANT/i.test(line)) {
      summary.summary = summary.summary || {};
      summary.summary.significant = true;
    }
    if (/avg_score CIs overlap \(not significant\)/i.test(line)) {
      summary.summary = summary.summary || {};
      summary.summary.significant = false;
    }
  }

  pushCurrentIter();
  return summary;
}

function formatDiffereBenchmarkProgressLabel(bar: {
  prog: number;
  tot: number;
  pct: number | null;
  batch?: boolean;
  completedRuns?: number;
  plannedRuns?: number;
} | null, busyLabel: string): string {
  if (!bar || bar.tot <= 0 || bar.pct === null) return busyLabel;
  if (bar.batch) {
    const runs = bar.plannedRuns && bar.plannedRuns > 0
      ? ` · ${bar.completedRuns ?? 0}/${bar.plannedRuns} runs`
      : '';
    return `${bar.pct}% — ${bar.prog}/${bar.tot} donnes (batch${runs})`;
  }
  return `${bar.pct}% (${bar.prog}/${bar.tot} donnes · run en cours)`;
}

function MLLogProgressBar({ progress }: { progress?: MLLogProgress }) {
  const percent = Math.max(0, Math.min(100, progress?.percent ?? 0));
  return (
    <div className="ml-log-progress">
      <div className="ml-log-progress-bar">
        <div className="ml-log-progress-fill" style={{ width: `${percent}%` }} />
      </div>
      <div className="ml-log-progress-meta">
        <span className="ml-log-progress-label">
          {progress ? `${progress.label || `${formatLogNumber(progress.current)} / ${formatLogNumber(progress.total)}`} (${formatLogPercent(percent)})` : '-'}
        </span>
        {progress?.examplesSoFar !== undefined && (
          <span className="ml-log-progress-sublabel">{formatLogNumber(progress.examplesSoFar)} examples</span>
        )}
      </div>
    </div>
  );
}

function MLLogMetricCircle({ value, label, variant = 'default' }: { value?: number; label: string; variant?: 'default' | 'accent' }) {
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const percent = Math.max(0, Math.min(100, value ?? 0));
  const dashOffset = circumference * (1 - percent / 100);
  const fillClass = variant === 'accent' ? 'ml-log-metric-circle-fill ml-log-metric-circle-fill--accent' : 'ml-log-metric-circle-fill';
  return (
    <div className="ml-log-metric-circle">
      <svg viewBox="0 0 44 44" aria-hidden="true">
        <circle className="ml-log-metric-circle-bg" cx="22" cy="22" r={radius} />
        <circle
          className={fillClass}
          cx="22"
          cy="22"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <div>
        <strong>{formatLogPercent(value)}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

function sparklineY(value: number, min: number, range: number): number {
  return 40 - ((value - min) / range) * 32;
}

/** Segments de polyline (séries potentiellement avec trous / nan). */
function sparklineSegments(values: number[], n: number, min: number, range: number): string[] {
  if (n === 0) return [];
  const segments: string[] = [];
  let buf: string[] = [];
  const pushBuf = () => {
    if (buf.length >= 2) segments.push(buf.join(' '));
    else if (buf.length === 1) segments.push(`${buf[0]} ${buf[0]}`);
    buf = [];
  };
  values.forEach((value, index) => {
    if (!Number.isFinite(value)) {
      pushBuf();
      return;
    }
    const x = n === 1 ? 50 : (index / Math.max(n - 1, 1)) * 100;
    buf.push(`${x.toFixed(2)},${sparklineY(value, min, range).toFixed(2)}`);
  });
  pushBuf();
  return segments;
}

function MLLogSparkline({
  values,
  secondaryValues,
  highlightIndex,
  baselineIndex,
  referenceValue,
}: {
  values: number[];
  secondaryValues?: number[];
  /** Index du meilleur checkpoint (marqueur visuel). */
  highlightIndex?: number;
  /** Index du point baseline (ex. it. 0 avant entraînement). */
  baselineIndex?: number;
  /** Ligne horizontale de référence (ex. mp/deal initiale). */
  referenceValue?: number;
}) {
  const n = values.length;
  const sec = secondaryValues ?? [];
  const sameLen = sec.length === 0 || sec.length === n;
  const secondary = sameLen ? sec : [];

  const pool = [...values, ...secondary].filter(Number.isFinite);
  const refPool = referenceValue !== undefined && Number.isFinite(referenceValue)
    ? [...pool, referenceValue]
    : pool;
  if (n === 0 && secondary.length === 0) {
    return <div className="ml-log-sparkline ml-log-sparkline--empty">Pas encore de données</div>;
  }
  if (refPool.length === 0) {
    return <div className="ml-log-sparkline ml-log-sparkline--empty">Pas encore de données</div>;
  }

  const min = Math.min(...refPool);
  const max = Math.max(...refPool);
  const range = max - min || 1;

  const primarySegs = n > 0 ? sparklineSegments(values, n, min, range) : [];
  const secondarySegs = secondary.length > 0 ? sparklineSegments(secondary, n, min, range) : [];

  if (primarySegs.length === 0 && secondarySegs.length === 0) {
    return <div className="ml-log-sparkline ml-log-sparkline--empty">Pas encore de données</div>;
  }

  let highlightDot: { cx: number; cy: number } | undefined;
  if (highlightIndex !== undefined && highlightIndex >= 0 && highlightIndex < n) {
    const hv = values[highlightIndex];
    if (Number.isFinite(hv)) {
      const x = n === 1 ? 50 : (highlightIndex / Math.max(n - 1, 1)) * 100;
      highlightDot = { cx: x, cy: sparklineY(hv, min, range) };
    }
  }

  let baselineDot: { cx: number; cy: number } | undefined;
  if (baselineIndex !== undefined && baselineIndex >= 0 && baselineIndex < n) {
    const bv = values[baselineIndex];
    if (Number.isFinite(bv)) {
      const x = n === 1 ? 50 : (baselineIndex / Math.max(n - 1, 1)) * 100;
      baselineDot = { cx: x, cy: sparklineY(bv, min, range) };
    }
  }

  const referenceY = referenceValue !== undefined && Number.isFinite(referenceValue)
    ? sparklineY(referenceValue, min, range)
    : undefined;

  return (
    <svg className="ml-log-sparkline" viewBox="0 0 100 44" preserveAspectRatio="none" aria-hidden="true">
      {referenceY !== undefined && (
        <line
          className="ml-log-sparkline-reference"
          x1="0"
          y1={referenceY}
          x2="100"
          y2={referenceY}
        />
      )}
      {primarySegs.map((points, i) => (
        <polyline key={`p-${i}`} className="ml-log-sparkline-line" points={points} />
      ))}
      {secondarySegs.map((points, i) => (
        <polyline key={`s-${i}`} className="ml-log-sparkline-line ml-log-sparkline-line--secondary" points={points} />
      ))}
      {baselineDot && (
        <circle
          className="ml-log-sparkline-baseline"
          cx={baselineDot.cx}
          cy={baselineDot.cy}
          r={2.8}
        />
      )}
      {highlightDot && (
        <circle
          className="ml-log-sparkline-marker"
          cx={highlightDot.cx}
          cy={highlightDot.cy}
          r={2.8}
        />
      )}
    </svg>
  );
}

function MLLogMetricTrend({
  label,
  value,
  values,
  suffix = '',
  secondaryValues,
  secondaryValue,
  highlightIndex,
  baselineIndex,
  referenceValue,
  baselineLabel,
}: {
  label: string;
  value?: number;
  values: number[];
  suffix?: string;
  /** Deuxième courbe (ex. tolérant) sur le même graphique que values. */
  secondaryValues?: number[];
  secondaryValue?: number;
  highlightIndex?: number;
  baselineIndex?: number;
  referenceValue?: number;
  baselineLabel?: string;
}) {
  const primaryStr =
    value === undefined || Number.isNaN(value)
      ? '-'
      : `${value.toLocaleString('fr-FR', { maximumFractionDigits: 3 })}${suffix}`;
  const secondaryStr =
    secondaryValue === undefined || Number.isNaN(secondaryValue)
      ? undefined
      : `${secondaryValue.toLocaleString('fr-FR', { maximumFractionDigits: 3 })}${suffix}`;

  return (
    <div className="ml-log-trend-card">
      <div className="ml-log-trend-card-header">
        <span>{label}</span>
        <strong title={secondaryStr !== undefined ? 'strict · tolérant' : undefined}>
          {secondaryStr !== undefined ? `${primaryStr} · ${secondaryStr}` : primaryStr}
        </strong>
      </div>
      <MLLogSparkline
        values={values}
        secondaryValues={secondaryValues}
        highlightIndex={highlightIndex}
        baselineIndex={baselineIndex}
        referenceValue={referenceValue}
      />
      {baselineLabel && (
        <span className="ml-log-trend-baseline-hint">{baselineLabel}</span>
      )}
    </div>
  );
}

/**
 * Même libellés longs que le sélecteur « Source » de l’onglet Exporter & Entraîner
 * (section « 1. Source des donnes & sélection des joueurs »).
 */
const ML_PLAYER_SELECTION_MODE_LABELS: Record<string, string> = {
  competition_team: 'Championnat par équipes — toutes équipes (baseline)',
  competition_team_top_teams: 'Championnat par équipes — top divisions/phases + sampling',
  competition_cia: 'Championnat Individuel Attaque — 1.5M donnes (attaquant uniquement)',
  competition_cia_top_divisions: 'Championnat Individuel Attaque — top divisions/phases (attaquant)',
  competition_quadrette: 'Coupe Quadrette — 10k donnes, 4 humains',
  freedefi_elite: 'Défis libres — pseudos élite (classement glissant général)',
  freedefi_top_live: 'Défis libres — pseudos top live ranking',
  differe_indiv_top_per_donne: 'Différé individuel — top X meilleurs résultats par donne',
  differe_indiv_top_general_role: 'Différé individuel — donnes jouées par les top N (rôle attaque/défense)',
  bot1_distill: '★ Distillation Bot1 pure (4 sièges Bot1, dataset généré dans le job)',
  bot1_soft_distill: '★ Distillation Bot1 + humains (mix soft, source humains au choix)',
  magnus_attacker: '★ Mode Magnus (attaque) — 1 à 5 joueurs, différé + CIA + défis libres',
};

const MAGNUS_MAX_PSEUDOS = 5;

type MagnusVolumeBreakdown = {
  differeAttaque: number;
  differeMixteMaxi: number;
  cia: number;
  freedefiAttacker: number;
  differesByType?: Record<string, number>;
};

type MagnusCandidate = {
  rank: number;
  pseudo: string;
  label: string;
  averagePoints: number | null;
  playedDifferes: number | null;
  totalDifferes: number | null;
  volumes: MagnusVolumeBreakdown | null;
  /** legacy field — ignored if volumes present */
  estimatedDiffereDonnes?: number | null;
};

function magnusExpectedDonnes(
  volumes: MagnusVolumeBreakdown | null | undefined,
  sources: {
    differeAttaque: boolean;
    differeMixteMaxi: boolean;
    cia: boolean;
    freedefiAttacker: boolean;
  },
): number {
  if (!volumes) return 0;
  let n = 0;
  if (sources.differeAttaque) n += volumes.differeAttaque || 0;
  if (sources.differeMixteMaxi) n += volumes.differeMixteMaxi || 0;
  if (sources.cia) n += volumes.cia || 0;
  if (sources.freedefiAttacker) n += volumes.freedefiAttacker || 0;
  return n;
}

function formatMagnusDonnes(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `~${n.toLocaleString('fr-FR')}`;
}

const ML_LOG_TEAM_DIVISION_LABELS: Record<number, string> = {
  0: 'Toutes les divisions',
  1: 'D1 seulement (élite)',
  2: 'D1 à D2',
  3: 'D1 à D3',
  4: 'D1 à D4',
  5: 'D1 à D5',
  6: 'D1 à D6',
  7: 'D1 à D7',
};

/** Regroupe le JSON `imitation_config` comme dans l’UI d’export (filtres → sampling → hyperparams). */
const ML_LOG_PARAM_SECTIONS: ReadonlyArray<{ title: string; keys: readonly string[] }> = [
  {
    title: 'Source des donnes & filtres',
    keys: [
      'player_selection_mode',
      'humans_inner_mode',
      'model_role',
      'data_table',
      'idcompetition',
      'etats',
      'pseudos',
      'date_from',
      'date_to',
      'team_scope',
      'competition_scope',
      'elite_pool_size',
      'elite_source',
      'differe_scope',
      'differe_top_per_donne',
      'differe_min_participants_per_donne',
      'differe_subtypes',
      'differe_strict_role_filter',
    ],
  },
  {
    title: 'Championnat — divisions, phases & échantillonnage',
    keys: [
      'team_division_hierarchy_max',
      'team_phase_min_label',
      'team_phase_max_label',
      'team_require_both',
      'team_max_games_per_match',
      'team_one_table_per_deal',
      'team_duplicate_strategy',
      'sample_seed',
    ],
  },
  {
    title: 'Distillation Bot1',
    keys: [
      'bot_distill_n_games',
      'bot_distill_seed',
      'bot_distill_workers',
      'bot_distill_min_contract',
      'bot_soft_distill_alpha',
    ],
  },
  {
    title: 'Hyperparamètres imitation',
    keys: [
      'max_games',
      'feature_version',
      'im_epochs',
      'im_batch_size',
      'im_lr',
      'im_patience',
      'im_entame_weight',
      'im_hidden_dim',
      'suit_augmentation',
      'regime_gate',
    ],
  },
  {
    title: 'Déductions (MLP)',
    keys: [
      'mask_strategy',
      'mask_certain_weight',
      'initial_dim_weight',
      'consistency_weight',
      'mariage_dim_boost',
    ],
  },
];

/**
 * Clés présentes dans la config log qui correspondent aux choix directs dans l’UI
 * « Exporter & Entraîner » (hors champs dérivés : scopes SQL, id compétition, table…).
 */
const ML_LOG_USER_CONFIG_KEYS = new Set<string>([
  'player_selection_mode',
  'humans_inner_mode',
  'model_role',
  'etats',
  'date_from',
  'date_to',
  'elite_pool_size',
  'elite_source',
  'team_division_hierarchy_max',
  'team_phase_min_label',
  'team_phase_max_label',
  'team_require_both',
  'team_max_games_per_match',
  'team_one_table_per_deal',
  'team_duplicate_strategy',
  'sample_seed',
  'differe_top_per_donne',
  'differe_min_participants_per_donne',
  'differe_subtypes',
  'differe_strict_role_filter',
  'max_games',
  'feature_version',
  'im_epochs',
  'im_batch_size',
  'im_lr',
  'im_patience',
  'im_entame_weight',
  'im_hidden_dim',
  'suit_augmentation',
  'regime_gate',
  'bot_distill_n_games',
  'bot_distill_seed',
  'bot_distill_workers',
  'bot_distill_min_contract',
  'bot_soft_distill_alpha',
  'mask_strategy',
  'mask_certain_weight',
  'initial_dim_weight',
  'consistency_weight',
  'mariage_dim_boost',
]);

const ML_LOG_CONFIG_LABELS: Record<string, string> = {
  data_table: 'Table SQL source',
  max_games: 'Nombre max de parties téléchargées',
  feature_version: 'Version des features',
  im_epochs: 'Epochs',
  im_batch_size: 'Taille de batch',
  im_lr: 'Learning rate',
  im_patience: 'Early stopping (patience)',
  im_hidden_dim: 'Largeur cachée (BeliefPolicy)',
  player_selection_mode: 'Source',
  humans_inner_mode: 'Source humains (sous-mode soft distill)',
  idcompetition: 'Identifiant compétition',
  etats: 'États de match retenus',
  pseudos: 'Filtre pseudos (SQL)',
  date_from: 'Date début',
  date_to: 'Date fin',
  model_role: 'Rôle du modèle',
  im_entame_weight: 'Poids relatif des coups d’entame',
  suit_augmentation: 'Augmentation par symétrie des couleurs',
  regime_gate: 'Gate multi-régime (position)',
  elite_pool_size: 'Taille du pool élite (top N)',
  elite_source: 'Source de la liste élite',
  team_scope: 'Scope équipes (filtre SQL)',
  competition_scope: 'Scope compétition (filtre SQL)',
  team_division_hierarchy_max: 'Divisions retenues',
  team_phase_min_label: 'Phase minimum',
  team_phase_max_label: 'Phase maximum',
  team_require_both: 'Éligibilité du match',
  team_max_games_per_match: 'Max donnes par match',
  team_one_table_per_deal: 'Duplicate (tables par donne)',
  team_duplicate_strategy: 'Choix de la table duplicate',
  sample_seed: 'Seed random (reproductibilité)',
  differe_scope: 'Scope différé (filtre SQL)',
  differe_top_per_donne: 'Top X meilleurs résultats par donne',
  differe_min_participants_per_donne: 'Min participants par donne',
  differe_subtypes: 'Sous-types de différé',
  differe_strict_role_filter: 'Filtrage rôle strict (Python)',
  bot_distill_n_games: 'Nombre de parties Bot1 (distill)',
  bot_distill_seed: 'Seed Bot1 (distill)',
  bot_distill_workers: 'Parallélisme Bot1 (distill)',
  bot_distill_min_contract: 'Contrat minimum Bot1 (distill)',
  bot_soft_distill_alpha: 'Part Bot1 dans le mix soft (α)',
  mask_strategy: 'Masquage des dimensions (déductions)',
  mask_certain_weight: 'Poids BCE « certain »',
  initial_dim_weight: 'Poids initial par dimension',
  consistency_weight: 'Poids cohérence',
  mariage_dim_boost: 'Boost BCE mariages',
};

function formatMlLogTrainingConfigValue(key: string, raw: unknown): string {
  const isBlank = (v: unknown): boolean =>
    v === '' || v === null || v === undefined;

  switch (key) {
    case 'player_selection_mode':
      return ML_PLAYER_SELECTION_MODE_LABELS[String(raw)] || formatLogValue(raw);
    case 'humans_inner_mode':
      return ML_PLAYER_SELECTION_MODE_LABELS[String(raw)] || formatLogValue(raw);
    case 'model_role':
      if (raw === 'attacker') return 'Attaquant';
      if (raw === 'defender') return 'Défenseur';
      return formatLogValue(raw);
    case 'team_division_hierarchy_max': {
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isFinite(n)) return formatLogValue(raw);
      return ML_LOG_TEAM_DIVISION_LABELS[n] ?? `D1 à D${String(n)}`;
    }
    case 'team_one_table_per_deal':
      return raw === true
        ? 'Une seule table par donne duplicate'
        : 'Conserver les deux tables d\'une même donne';
    case 'team_require_both':
      return raw === true
        ? 'Les deux équipes dans le scope'
        : 'Au moins une équipe dans le scope';
    case 'team_duplicate_strategy': {
      const map: Record<string, string> = {
        random: 'Aléatoire reproductible',
        attack_best: 'Meilleure réussite de l\'attaque',
        defense_best: 'Meilleure réussite de la défense',
      };
      const s = String(raw);
      return map[s] || (s || '—');
    }
    case 'elite_source': {
      const s = String(raw).trim();
      if (!s) return '—';
      const map: Record<string, string> = {
        live: 'Top live 4 joueurs',
        differe_general: 'Classement glissant général',
        differe_general_attack: 'Top attaque (classement_att)',
        differe_general_defense: 'Top défense (classement_def)',
      };
      return map[s] || s;
    }
    case 'team_phase_min_label':
    case 'team_phase_max_label':
      if (isBlank(raw)) return 'Aucune';
      return String(raw).trim() || '—';
    case 'differe_strict_role_filter':
      return raw === true
        ? 'Strict — uniquement les donnes où l\'humain a le bon rôle'
        : 'Toutes — pas de filtrage côté pipeline';
    case 'differe_subtypes': {
      if (Array.isArray(raw)) {
        return raw.length > 0 ? raw.join(', ') : 'Auto (tous les sous-types du rôle)';
      }
      if (typeof raw === 'string') {
        const t = raw.trim();
        return t.length > 0 ? t : 'Auto (tous les sous-types du rôle)';
      }
      return formatLogValue(raw);
    }
    case 'etats': {
      const s = typeof raw === 'string' ? raw.trim() : '';
      if (!s) return '—';
      return s.split(',').map(x => x.trim()).filter(Boolean).join(', ');
    }
    case 'pseudos':
    case 'date_from':
    case 'date_to':
    case 'idcompetition':
    case 'sample_seed':
    case 'differe_scope':
      if (typeof raw === 'string' && !raw.trim()) return '—';
      return formatLogValue(raw);
    default:
      break;
  }

  if (isBlank(raw) && raw !== 0 && raw !== false) return '—';
  return formatLogValue(raw);
}

function groupMlLogConfigEntries(
  entries: Array<[string, unknown]>,
): Array<{ title: string; rows: Array<[string, unknown]> }> {
  const used = new Set<string>();
  const out: Array<{ title: string; rows: Array<[string, unknown]> }> = [];

  for (const section of ML_LOG_PARAM_SECTIONS) {
    const rows: Array<[string, unknown]> = [];
    for (const key of section.keys) {
      const pair = entries.find(([k]) => k === key);
      if (pair) {
        rows.push(pair);
        used.add(key);
      }
    }
    if (rows.length > 0) out.push({ title: section.title, rows });
  }

  const rest = entries.filter(([k]) => !used.has(k));
  if (rest.length > 0) {
    rest.sort((a, b) => a[0].localeCompare(b[0]));
    out.push({ title: 'Autres paramètres', rows: rest });
  }

  return out;
}

function MLLogParametersView({ summary }: { summary: ImitationLogSummary }) {
  const configEntries = summary.config
    ? Object.entries(summary.config).filter(([key]) => !['api_url', 'api_fallback_url'].includes(key))
    : [];

  if (!summary.config && !summary.datasetManifest && !summary.dealAwareSplit) {
    return (
      <div className="ml-log-summary ml-log-summary-empty">
        Paramètres non détectés dans les logs.
      </div>
    );
  }

  return (
    <div className="ml-log-summary">
      {(summary.datasetManifest || summary.dealAwareSplit) && (
        <section className="ml-log-summary-section">
          <h4>Dataset</h4>
          <div className="ml-log-stat-grid">
            <div><span>Joueurs</span><strong>{formatLogNumber(summary.datasetManifest?.players)}</strong></div>
            <div><span>Donnes uniques</span><strong>{formatLogNumber(summary.dealAwareSplit?.uniqueDeals)}</strong></div>
            <div><span>Games total</span><strong>{formatLogNumber(summary.dealAwareSplit?.totalGames)}</strong></div>
          </div>
          {summary.datasetManifest?.topPlayers.length ? (
            <div className="ml-log-params-top">
              <span>Top joueurs</span>
              <strong>{summary.datasetManifest.topPlayers.map(p => p.games ? `${p.name} (${formatLogNumber(p.games)})` : p.name).join(', ')}</strong>
            </div>
          ) : null}
        </section>
      )}

      {configEntries.length > 0 && (
        <section className="ml-log-summary-section">
          <h4>Paramètres d&apos;entraînement</h4>
          <p className="ml-log-params-legend">
            <span className="ml-log-params-legend-swatch ml-log-params-legend-swatch--user" aria-hidden />
            Réglages choisis dans l&apos;assistant avant lancement ·{' '}
            <span className="ml-log-params-legend-muted">autres champs = résolus par le pipeline / SQL</span>
          </p>
          {groupMlLogConfigEntries(configEntries).map(({ title, rows }) => (
            <div key={title} className="ml-log-params-block">
              <h5>{title}</h5>
              <div className="ml-log-params-grid">
                {rows.map(([key, value]) => (
                  <div
                    key={key}
                    className={`ml-log-param-cell${ML_LOG_USER_CONFIG_KEYS.has(key) ? ' ml-log-param-cell--user' : ''}`}
                  >
                    <span>{ML_LOG_CONFIG_LABELS[key] || key}</span>
                    <strong title={String(value ?? '')}>{formatMlLogTrainingConfigValue(key, value)}</strong>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {!summary.config && summary.configRaw && (
        <section className="ml-log-summary-section">
          <h4>Config brute</h4>
          <pre className="ml-log-params-raw">{summary.configRaw}</pre>
        </section>
      )}
    </div>
  );
}

function MLImitationLogSummary({ summary }: { summary: ImitationLogSummary }) {
  const latestEpoch = summary.epochs.length > 0 ? summary.epochs[summary.epochs.length - 1] : undefined;
  const hasValTolerant = summary.epochs.some(e => e.valTolerant !== undefined && Number.isFinite(e.valTolerant));
  const latestTolerant = [...summary.epochs].reverse().find(e => e.valTolerant !== undefined && Number.isFinite(e.valTolerant))?.valTolerant;
  const epochProgress = latestEpoch
    ? {
        current: latestEpoch.epoch,
        total: latestEpoch.total,
        percent: summary.earlyStoppingEpoch ? 100 : (latestEpoch.epoch / latestEpoch.total) * 100,
        label: `epoch ${latestEpoch.epoch} / ${latestEpoch.total}`,
      }
    : undefined;
  const valAccuracy = summary.finalValAccuracy ?? latestEpoch?.val;
  const entameAccuracy = summary.finalEntameAccuracy ?? latestEpoch?.entame;
  const evalWinRate = summary.evalComplete?.winRate ?? summary.evalProgress?.winRate;
  const valTrendSecondaryValues = hasValTolerant
    ? summary.epochs.map(e => (e.valTolerant !== undefined && Number.isFinite(e.valTolerant) ? e.valTolerant : Number.NaN))
    : undefined;

  if (!summary.isImitation && summary.epochs.length === 0 && !summary.currentStep) {
    return (
      <div className="ml-log-summary ml-log-summary-empty">
        Résumé graphique disponible pour les logs d’entraînement imitation ou self-play.
      </div>
    );
  }

  return (
    <div className="ml-log-summary">
      <section className="ml-log-summary-section">
        <div className="ml-log-summary-title">
          <span>Étape en cours</span>
          <strong>{summary.currentStep ? `Step ${summary.currentStep.number}: ${summary.currentStep.title}` : 'Non détectée'}</strong>
        </div>
      </section>

      {summary.currentStep?.number === 1 && (
        <section className="ml-log-summary-section">
          <h4>Download games</h4>
          <MLLogProgressBar progress={summary.step1Progress} />
          {summary.downloadedGames !== undefined && (
            <div className="ml-log-summary-note">{formatLogNumber(summary.downloadedGames)} games téléchargés</div>
          )}
        </section>
      )}

      {(summary.currentStep?.number ?? 0) >= 2 && (
        <section className="ml-log-summary-section">
          <h4>Imitation training</h4>
          {summary.step2GamesProgress
            && summary.step2GamesProgress.total > 0
            && summary.step2GamesProgress.current < summary.step2GamesProgress.total && (
            <div className="ml-log-step2-games">
              <span className="ml-log-step2-games-title">Games traités (dataset)</span>
              <MLLogProgressBar progress={summary.step2GamesProgress} />
            </div>
          )}
          <div className="ml-log-stat-grid">
            <div><span>Feature dim</span><strong>{formatLogNumber(summary.featureDim)}</strong></div>
            <div><span>Train examples</span><strong>{formatLogNumber(summary.trainExamples)}</strong></div>
            <div><span>Val examples</span><strong>{formatLogNumber(summary.valExamples)}</strong></div>
            <div><span>Train / val games</span><strong>{formatLogNumber(summary.trainGames)} / {formatLogNumber(summary.valGames)}</strong></div>
            <div><span>Split</span><strong>{summary.splitLabel || '-'}</strong></div>
            <div><span>Model parameters</span><strong>{formatLogNumber(summary.modelParameters)}</strong></div>
          </div>
          <MLLogProgressBar progress={epochProgress} />
          {summary.earlyStoppingEpoch !== undefined && (
            <div className="ml-log-summary-note">Early stopping après {summary.earlyStoppingEpoch} epochs</div>
          )}
          <div className="ml-log-trend-grid">
            <MLLogMetricTrend label="Loss" value={latestEpoch?.loss} values={summary.epochs.map(e => e.loss)} />
            <MLLogMetricTrend
              label={hasValTolerant ? 'Val (strict · tolérant)' : 'Val'}
              value={valAccuracy}
              values={summary.epochs.map(e => e.val)}
              suffix="%"
              secondaryValues={valTrendSecondaryValues}
              secondaryValue={hasValTolerant ? latestTolerant : undefined}
            />
            <MLLogMetricTrend label="Entame" value={entameAccuracy} values={summary.epochs.map(e => e.entame)} suffix="%" />
            <MLLogMetricTrend label="Autre" value={latestEpoch?.autre} values={summary.epochs.map(e => e.autre)} suffix="%" />
          </div>
          <div className="ml-log-circle-row">
            <MLLogMetricCircle
              label={
                summary.finalValAccuracy !== undefined
                  ? (hasValTolerant ? 'Val finale (strict)' : 'Val finale')
                  : (hasValTolerant ? 'Val strict dernier epoch' : 'Val dernier epoch')
              }
              value={valAccuracy}
            />
            {hasValTolerant && (
              <MLLogMetricCircle label="Tolérant dernier epoch" value={latestTolerant} variant="accent" />
            )}
            <MLLogMetricCircle label={summary.finalEntameAccuracy !== undefined ? 'Entame finale' : 'Entame dernier epoch'} value={entameAccuracy} />
            <MLLogMetricCircle label="Autre dernier epoch" value={latestEpoch?.autre} />
          </div>
        </section>
      )}

      {(summary.currentStep?.number ?? 0) >= 3 && (
        <section className="ml-log-summary-section">
          <h4>Evaluation</h4>
          <MLLogProgressBar progress={summary.evalProgress} />
          <div className="ml-log-eval-row">
            <MLLogMetricCircle label="Win rate" value={evalWinRate} />
            <div className="ml-log-eval-result">
              <span>Evaluation complete</span>
              <strong>{summary.evalComplete ? formatLogPercent(summary.evalComplete.winRate) : formatLogPercent(summary.evalProgress?.winRate)}</strong>
              {summary.evalComplete && (
                <small>avg_score={summary.evalComplete.avgScore.toLocaleString('fr-FR')} ± {summary.evalComplete.stdScore.toLocaleString('fr-FR')}</small>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function formatLogScore(value?: number): string {
  if (value === undefined || Number.isNaN(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} pts`;
}

function MLLogSelfplayParametersView({ summary }: { summary: SelfplayLogSummary }) {
  if (!summary.config && !summary.trainRole) {
    return (
      <div className="ml-log-summary ml-log-summary-empty">
        Paramètres non détectés dans les logs.
      </div>
    );
  }

  return (
    <div className="ml-log-summary">
      <section className="ml-log-summary-section">
        <h4>Self-play (PPO)</h4>
        <div className="ml-log-stat-grid">
          <div><span>Rôle entraîné</span><strong>{summary.trainRole === 'attacker' ? 'Attaquant' : summary.trainRole === 'defender' ? 'Défenseur' : '-'}</strong></div>
          <div><span>Itérations</span><strong>{formatLogNumber(summary.config?.iterations)}</strong></div>
          <div><span>Parties / itération</span><strong>{formatLogNumber(summary.config?.gamesPerIter)}</strong></div>
          <div><span>Learning rate</span><strong>{summary.config?.lr !== undefined ? summary.config.lr.toExponential(2) : '-'}</strong></div>
          <div><span>PPO epochs</span><strong>{formatLogNumber(summary.config?.ppoEpochs)}</strong></div>
          <div><span>Batch size</span><strong>{formatLogNumber(summary.config?.batchSize)}</strong></div>
        </div>
      </section>
    </div>
  );
}

function findLatestBestSelfplayIteration(
  iterations: SelfplayIterationMetrics[],
): { index: number; iter: SelfplayIterationMetrics } | undefined {
  for (let idx = iterations.length - 1; idx >= 0; idx -= 1) {
    if (iterations[idx].isBest) return { index: idx, iter: iterations[idx] };
  }
  return undefined;
}

function formatSelfplayBestCheckpointMetric(
  iter: SelfplayIterationMetrics | undefined,
  summaryBestAvgScore?: number,
): string | undefined {
  if (summaryBestAvgScore !== undefined) return formatLogScore(summaryBestAvgScore);
  if (!iter) return undefined;
  if (iter.duplicateEvalMatchpoint !== undefined) {
    return `${iter.duplicateEvalMatchpoint.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} mp/deal`;
  }
  if (iter.evalAvgScore !== undefined) return formatLogScore(iter.evalAvgScore);
  return undefined;
}

function MLSelfplayLogSummary({ summary }: { summary: SelfplayLogSummary }) {
  const latestIter = summary.iterations.length > 0
    ? summary.iterations[summary.iterations.length - 1]
    : undefined;
  /** Dernière itération ayant des métriques (l’itération en cours peut être vide). */
  const latestMeasuredIter = [...summary.iterations].reverse().find(
    i => i.policyLoss !== undefined
      || i.evalAvgScore !== undefined
      || i.collectionDefenseWinRate !== undefined,
  );
  const trainRoleLabel = summary.trainRole === 'attacker' ? 'Attaquant' : summary.trainRole === 'defender' ? 'Défenseur' : '—';
  const trainedWinRate = (iter: SelfplayIterationMetrics) => {
    if (iter.evalWinRate !== undefined) return iter.evalWinRate;
    if (summary.trainRole === 'attacker' && iter.evalDefenseWinRate !== undefined) {
      return 100 - iter.evalDefenseWinRate;
    }
    return iter.evalDefenseWinRate;
  };

  const iterProgress = latestIter
    ? {
        current: latestIter.iteration,
        total: latestIter.total,
        percent: summary.earlyStopping
          ? 100
          : (latestIter.iteration / latestIter.total) * 100,
        label: `itération ${latestIter.iteration} / ${latestIter.total}`,
      }
    : undefined;

  const initialWinRate = summary.summary?.initialWinRate
    ?? (summary.trainRole === 'attacker'
      ? summary.initialEval?.attackWinRate
      : summary.initialEval?.defenseWinRate);
  const finalWinRate = summary.summary?.finalWinRate
    ?? (summary.trainRole === 'attacker'
      ? summary.finalEval?.attackWinRate
      : summary.finalEval?.defenseWinRate);
  const initialAvgScore = summary.summary?.initialAvgScore ?? summary.initialEval?.avgScore;
  const finalAvgScore = summary.summary?.finalAvgScore ?? summary.finalEval?.avgScore;
  const bestAvgScore = summary.summary?.bestAvgScore;
  const bestCheckpoint = findLatestBestSelfplayIteration(summary.iterations);
  const bestCheckpointIteration = summary.summary?.bestIteration ?? bestCheckpoint?.iter.iteration;
  const bestCheckpointMetric = formatSelfplayBestCheckpointMetric(bestCheckpoint?.iter, bestAvgScore);
  const bestHighlightIndex = bestCheckpoint?.index;
  const metricsIter = latestMeasuredIter ?? latestIter;
  const latestEvalWinRate = metricsIter ? trainedWinRate(metricsIter) : undefined;
  const latestEvalAvgScore = metricsIter?.evalAvgScore;
  const duplicateEvalBaseline = summary.initialEval?.duplicateEvalMatchpoint;
  const hasDuplicateEvalSeries = duplicateEvalBaseline !== undefined
    || summary.iterations.some(i => i.duplicateEvalMatchpoint !== undefined);
  const duplicateEvalValues = duplicateEvalBaseline !== undefined
    ? [duplicateEvalBaseline, ...summary.iterations.map(i => i.duplicateEvalMatchpoint ?? Number.NaN)]
    : summary.iterations.map(i => i.duplicateEvalMatchpoint ?? Number.NaN);
  const duplicateEvalHighlightIndex = bestHighlightIndex !== undefined && duplicateEvalBaseline !== undefined
    ? bestHighlightIndex + 1
    : bestHighlightIndex;
  const duplicateEvalBaselineLabel = duplicateEvalBaseline !== undefined
    ? `Baseline it. 0 : ${duplicateEvalBaseline.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} mp/deal · point gris · trait = référence`
    : undefined;

  if (!summary.isSelfplay && summary.iterations.length === 0) {
    return (
      <div className="ml-log-summary ml-log-summary-empty">
        Résumé graphique disponible pour les logs d’entraînement imitation ou self-play.
      </div>
    );
  }

  return (
    <div className="ml-log-summary">
      <section className="ml-log-summary-section">
        <div className="ml-log-summary-title">
          <span>Self-play (PPO)</span>
          <strong>{trainRoleLabel}</strong>
        </div>
      </section>

      {(summary.initialEval || summary.summary?.initialAvgScore !== undefined) && (
        <section className="ml-log-summary-section">
          <h4>Évaluation initiale</h4>
          <div className="ml-log-stat-grid">
            <div><span>Win rate ({trainRoleLabel})</span><strong>{formatLogPercent(initialWinRate)}</strong></div>
            <div><span>Avg score (preneur)</span><strong>{formatLogScore(initialAvgScore)}</strong></div>
            {summary.initialEval?.duplicateEvalMatchpoint !== undefined && (
              <div><span>Duplicate eval (baseline)</span><strong>{summary.initialEval.duplicateEvalMatchpoint.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} mp/deal</strong></div>
            )}
          </div>
        </section>
      )}

      {summary.iterations.length > 0 && (
        <section className="ml-log-summary-section">
          <h4>Entraînement</h4>
          <MLLogProgressBar progress={iterProgress} />
          {summary.earlyStopping && summary.earlyStoppingNoImproveIters !== undefined && (
            <div className="ml-log-summary-note">
              Early stopping après {summary.earlyStoppingNoImproveIters} itérations sans amélioration significative
            </div>
          )}
          <div className="ml-log-trend-grid">
            <MLLogMetricTrend
              label="Policy loss"
              value={metricsIter?.policyLoss}
              values={summary.iterations.map(i => i.policyLoss ?? Number.NaN)}
              highlightIndex={bestHighlightIndex}
            />
            <MLLogMetricTrend
              label="Entropie (H)"
              value={metricsIter?.entropy}
              values={summary.iterations.map(i => i.entropy ?? Number.NaN)}
              highlightIndex={bestHighlightIndex}
            />
            <MLLogMetricTrend
              label="Clip fraction"
              value={metricsIter?.clipFraction}
              values={summary.iterations.map(i => i.clipFraction ?? Number.NaN)}
              highlightIndex={bestHighlightIndex}
            />
            <MLLogMetricTrend
              label={`Win rate eval (${trainRoleLabel})`}
              value={latestEvalWinRate}
              values={summary.iterations.map(i => {
                const wr = trainedWinRate(i);
                return wr !== undefined ? wr : Number.NaN;
              })}
              suffix="%"
              highlightIndex={bestHighlightIndex}
            />
            <MLLogMetricTrend
              label="Avg score eval (preneur)"
              value={latestEvalAvgScore}
              values={summary.iterations.map(i => i.evalAvgScore ?? Number.NaN)}
              suffix=" pts"
              highlightIndex={bestHighlightIndex}
            />
            {summary.iterations.some(i => i.collectionDefenseWinRate !== undefined) && (
              <MLLogMetricTrend
                label="Win rate défense — collecte PPO"
                value={metricsIter?.collectionDefenseWinRate}
                values={summary.iterations.map(i => i.collectionDefenseWinRate ?? Number.NaN)}
                suffix="%"
                highlightIndex={bestHighlightIndex}
              />
            )}
            {summary.iterations.some(i => i.duplicateTrainAvgMatchpoint !== undefined) && (
              <MLLogMetricTrend
                label="Duplicate train (mp reward)"
                value={metricsIter?.duplicateTrainAvgMatchpoint}
                values={summary.iterations.map(i => i.duplicateTrainAvgMatchpoint ?? Number.NaN)}
                highlightIndex={bestHighlightIndex}
              />
            )}
            {summary.iterations.some(i => i.frontierPickRate !== undefined) && (
              <MLLogMetricTrend
                label="Frontier replay — picks"
                value={metricsIter?.frontierPickRate}
                values={summary.iterations.map(i => i.frontierPickRate ?? Number.NaN)}
                suffix="%"
                highlightIndex={bestHighlightIndex}
              />
            )}
            {summary.iterations.some(i => i.frontierNearAvg !== undefined) && (
              <MLLogMetricTrend
                label="Frontier replay — near_avg"
                value={metricsIter?.frontierNearAvg}
                values={summary.iterations.map(i => i.frontierNearAvg ?? Number.NaN)}
                highlightIndex={bestHighlightIndex}
              />
            )}
            {hasDuplicateEvalSeries && (
              <MLLogMetricTrend
                label="Duplicate eval (mp/deal)"
                value={metricsIter?.duplicateEvalMatchpoint}
                values={duplicateEvalValues}
                highlightIndex={duplicateEvalHighlightIndex}
                baselineIndex={duplicateEvalBaseline !== undefined ? 0 : undefined}
                referenceValue={duplicateEvalBaseline}
                baselineLabel={duplicateEvalBaselineLabel}
              />
            )}
          </div>
          {summary.iterations.some(i => i.evalWinRate !== undefined)
            && summary.iterations.some(i => i.collectionDefenseWinRate !== undefined) && (
            <div className="ml-log-summary-note">
              Eval ({formatLogNumber(500)} parties, quasi-greedy) vs collecte PPO ({formatLogNumber(summary.config?.gamesPerIter ?? 2000)} parties, température) — les win rates de ces deux courbes ne se complètent pas.
            </div>
          )}
          {bestCheckpointIteration !== undefined && (
            <div className="ml-log-summary-note">
              Meilleur checkpoint : itération {bestCheckpointIteration}
              {bestCheckpointMetric !== undefined ? ` (${bestCheckpointMetric})` : ''}
              {bestHighlightIndex !== undefined && (
                <span className="ml-log-summary-note-hint"> · point vert = meilleur checkpoint</span>
              )}
              {duplicateEvalBaseline !== undefined && (
                <span className="ml-log-summary-note-hint"> · baseline initiale {duplicateEvalBaseline.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} mp/deal</span>
              )}
            </div>
          )}
        </section>
      )}

      {(summary.finalEval || summary.summary?.finalAvgScore !== undefined) && (
        <section className="ml-log-summary-section">
          <h4>Résultat final</h4>
          <div className="ml-log-stat-grid">
            <div><span>Win rate ({trainRoleLabel})</span><strong>{formatLogPercent(finalWinRate)}</strong></div>
            <div><span>Avg score (preneur)</span><strong>{formatLogScore(finalAvgScore)}</strong></div>
            {summary.summary?.bestAvgScore !== undefined && (
              <div><span>Meilleur avg score</span><strong>{formatLogScore(summary.summary.bestAvgScore)}</strong></div>
            )}
            {summary.summary?.deltaAvgScore !== undefined && (
              <div><span>Delta initial → final</span><strong>{formatLogScore(summary.summary.deltaAvgScore)}</strong></div>
            )}
          </div>
          {summary.summary?.significant !== undefined && (
            <div className="ml-log-summary-note">
              {summary.summary.significant
                ? 'Amélioration statistiquement significative'
                : 'Amélioration non significative (intervalles de confiance qui se chevauchent)'}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function MLTrainingLogSummaryView({
  selfplaySummary,
  imitationSummary,
}: {
  selfplaySummary: SelfplayLogSummary;
  imitationSummary: ImitationLogSummary;
}) {
  if (selfplaySummary.isSelfplay) {
    return <MLSelfplayLogSummary summary={selfplaySummary} />;
  }
  return <MLImitationLogSummary summary={imitationSummary} />;
}

function MLTrainingLogParametersView({
  selfplaySummary,
  imitationSummary,
}: {
  selfplaySummary: SelfplayLogSummary;
  imitationSummary: ImitationLogSummary;
}) {
  if (selfplaySummary.isSelfplay) {
    return <MLLogSelfplayParametersView summary={selfplaySummary} />;
  }
  return <MLLogParametersView summary={imitationSummary} />;
}

interface MLModel {
  key: string;
  size: number;
  lastModified: string;
  source: string;
}

interface PlayerStat {
  pseudo: string;
  games: number;
}

interface DatasetPreview {
  totalGames: number;
  sampledGames: number;
  filteredEstimate: string;
  playerStats: PlayerStat[];
  uniquePlayers: number;
  eliteCount: number;
  table: string;
  dataApiError?: string;
  dataApiSource?: string;
  selectedPlayers?: Array<{ pseudo: string; rank?: number; score?: number; source: string; estimatedGames?: number }>;
  teamStats?: Array<{ id?: number; name: string; games: number; matches: number; division?: string; division_hierarchie?: number }>;
  scopeUniquePlayers?: number;
  scopeUniqueTeams?: number;
  samplePlayerStats?: PlayerStat[];
  sampleUniquePlayers?: number;
  sampleStatsGames?: number;
  totalEstimatedGames?: number;
  countIsExact?: boolean;
}

interface EvalMetrics {
  gamesPlayed: number;
  winRate: number;
  avgScore: number;
  defenseWinRate?: number;
  attackWinRate?: number;
  vsVersion?: string;
  evalDuration?: number;
  improvement?: number;
  referenceWinRate?: number;
  modelType?: 'play' | 'bidding' | 'discard';
  bidDistribution?: Record<string, number>;
  reference?: { winRate: number; avgScore: number; gamesPlayed: number };
  details?: {
    trainingType?: string;
    trainRole?: string;
    iterations?: number;
    gamesPerIteration?: number;
    featureDim?: number;
    defenderAccuracy?: string;
    attackerAccuracy?: string;
    valAccuracy?: number;
    entameAccuracy?: number;
    numGames?: number;
    numExamples?: number;
    source?: string;
    // Deductions-specific
    overall_accuracy?: number;
    macro_f1?: number;
    macro_f1_nontrivial?: number;
    macro_f1_uncertain?: number;
    macro_precision?: number;
    macro_recall?: number;
    brier_score?: number;
    brier_score_uncertain?: number;
    ece?: number;
    ece_uncertain?: number;
    uncertainty_rate?: number;
    baselines?: {
      trivial?: { f1?: number; f1_uncertain?: number; brier?: number; brier_uncertain?: number };
      belief_only?: { f1?: number; brier?: number };
      model?: { f1?: number; f1_uncertain?: number; brier?: number; brier_uncertain?: number };
    };
    num_assertions?: number;
    num_nontrivial_assertions?: number;
    train_examples?: number;
    val_examples?: number;
    mask_strategy?: string;
    mask_certain_weight?: number;
    initial_dim_weight?: number;
    consistency_weight?: number;
    im_patience?: number;
    mariage_dim_boost?: number;
    per_trick?: Record<string, {
      accuracy: number; macro_f1: number; num_examples: number;
      macro_f1_uncertain?: number; macro_f1_current?: number; macro_f1_current_uncertain?: number;
      uncertainty_rate?: number;
    }>;
    worst_assertions?: Array<{ id: string; f1: number; precision: number; recall: number; prevalence: number }>;
    best_assertions?: Array<{ id: string; f1: number; precision: number; recall: number; prevalence: number }>;
    temperature?: number;
    best_epoch?: number;
    history?: Array<{
      iteration: number;
      winRate: number;
      ciLow?: number;
      ciHigh?: number;
      gamesPlayed: number;
      defenseWins: number;
      avgScore?: number;
      avgScoreCiLow?: number;
      avgScoreCiHigh?: number;
      duplicateAvgMatchpoint?: number | null;
    }>;
    duplicateSummary?: {
      objectiveActive?: boolean;
      tot?: number | null;
      avgMatchpoint?: number | null;
      dealsPlayed?: number | null;
      dealsWithField?: number | null;
      q1Rate?: number | null;
      iteration?: number;
    };
    evalPool?: {
      opponents: Array<{
        opponent: string;
        winRate: number;
        ciLow: number;
        ciHigh: number;
        gamesPlayed: number;
        avgScore: number;
      }>;
      averageWinRate: number;
      worstWinRate: number;
      worstOpponent: string;
      bestWinRate: number;
      bestOpponent: string;
      spread: number;
    };
    datasetManifest?: {
      totalGames: number; totalPlayers: number; selectionMode: string;
      modelRole: string; diffSubtypes: string; dateFrom: string; dateTo: string;
      players: Array<{ pseudo: string; games: number }>;
    };
    /** Imitation — alignés sur le meilleur checkpoint val (v5/v7) */
    otherAccuracy?: number;
    finalTrainLoss?: number;
    imitationUniqueDeals?: number;
    imitationTrainGames?: number;
    imitationValGames?: number;
    valLoss?: number;
  };
}

function versionTagFromModelLike(m: unknown): string | undefined {
  if (!m || typeof m !== 'object') return undefined;
  const o = m as Record<string, unknown>;
  const vt = o.versionTag ?? o.version_tag;
  return typeof vt === 'string' && vt.length > 0 ? vt : undefined;
}

function parseComparisonConfig(raw: unknown): Record<string, unknown> | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw) as unknown;
      return p && typeof p === 'object' && !Array.isArray(p) ? (p as Record<string, unknown>) : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** Comparaisons ML (onglet Comparer) dont la config ou les stats agrégées référencent ce versionTag. */
function mlComparisonsReferencingVersion(
  comparisons: Array<{ id: number; config?: unknown; status: string; createdAt: string; modelStats?: unknown }>,
  versionTag: string,
): Array<{ id: number; status: string; createdAt: string; asCandidate: boolean; asOpponent: boolean; peerTags: string[] }> {
  const out: Array<{ id: number; status: string; createdAt: string; asCandidate: boolean; asOpponent: boolean; peerTags: string[] }> = [];
  for (const c of comparisons) {
    const cfg = parseComparisonConfig(c.config);
    const modelsRaw = cfg?.models;
    const models = Array.isArray(modelsRaw) ? modelsRaw : [];
    const oppRaw = cfg?.opponentModel;
    const opp = oppRaw && typeof oppRaw === 'object' ? (oppRaw as Record<string, unknown>) : undefined;

    const inModels = models.some(m => versionTagFromModelLike(m) === versionTag);
    const inOpp = versionTagFromModelLike(opp) === versionTag;

    const cr = c as Record<string, unknown>;
    const statsRaw = cr.modelStats ?? cr.model_stats;
    const stats = Array.isArray(statsRaw) ? statsRaw : [];
    const inStats = stats.some(s => versionTagFromModelLike(s) === versionTag);

    if (!inModels && !inOpp && !inStats) continue;

    const peerTags = new Set<string>();
    for (const m of models) {
      const t = versionTagFromModelLike(m);
      if (t && t !== versionTag) peerTags.add(t);
    }
    const oppTag = versionTagFromModelLike(opp);
    if (oppTag && oppTag !== versionTag) peerTags.add(oppTag);
    for (const s of stats) {
      const t = versionTagFromModelLike(s);
      if (t && t !== versionTag) peerTags.add(t);
    }

    out.push({
      id: c.id,
      status: c.status,
      createdAt: c.createdAt,
      asCandidate: inModels || inStats,
      asOpponent: inOpp,
      peerTags: Array.from(peerTags),
    });
  }
  out.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return out;
}

interface ModelVersionEntry {
  id: number;
  versionTag: string;
  modelRole: 'defender' | 'attacker' | 'deductions';
  trainingMode: string;
  trainingConfig: Record<string, unknown> | null;
  s3ModelPath: string | null;
  parentVersion: string | null;
  ovhJobId: string | null;
  ovhJobName: string | null;
  evalMetrics: EvalMetrics | null;
  status: string;
  deployedSlot: string | null;
  deployedAt: string | null;
  createdAt: string;
  notes: string | null;
}

/** Libellé affiché pour une entrée du registre (notes utilisateur, sinon tag). */
function mlRegistryDisplayName(v: Pick<ModelVersionEntry, 'notes' | 'versionTag'>): string {
  const n = v.notes?.trim();
  if (n) return n;
  return v.versionTag;
}

/** Nom convivial pour une ligne de comparatif : label API si présent, sinon registre, sinon tag. */
function mlCompareRowDisplayName(
  versionTag: string,
  modelLabel: string | undefined,
  versions: ModelVersionEntry[],
): string {
  const lbl = typeof modelLabel === 'string' ? modelLabel.trim() : '';
  if (lbl) return lbl;
  const e = versions.find(x => x.versionTag === versionTag);
  if (e) return mlRegistryDisplayName(e);
  return versionTag;
}

/** Affichage court : tout ce qui suit un « [ » est masqué (sous-titre technique). */
function mlShortModelLabel(label: string): string {
  const s = label.trim();
  const i = s.indexOf('[');
  if (i === -1) return s;
  return s.slice(0, i).trimEnd();
}

/** Libellé compact pour les menus déroulants (évite les débordements UI). */
function truncateSelectLabel(label: string, maxLen = 52): string {
  const s = mlShortModelLabel(label);
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen - 1).trimEnd()}…`;
}

/** Rang par win-rate ou score moyen (tri décroissant). */
function mlSynthRankByMetric(
  stats: Array<{ versionTag: string; winRate: number; avgScore: number }>,
  metric: 'winRate' | 'avgScore',
): Map<
  string,
  { rank: number; deltaVsBest: number; winRate: number; avgScore: number; primary: number }
> {
  const key = metric === 'avgScore' ? 'avgScore' : 'winRate';
  const rows = stats.map(ms => ({
    tag: ms.versionTag,
    primary: ms[key],
    winRate: ms.winRate,
    avgScore: ms.avgScore,
  }));
  rows.sort((a, b) => b.primary - a.primary);
  const best = rows[0]?.primary ?? 0;
  const out = new Map<string, { rank: number; deltaVsBest: number; winRate: number; avgScore: number; primary: number }>();
  let rank = 1;
  for (let i = 0; i < rows.length; i++) {
    if (i > 0 && rows[i].primary < rows[i - 1].primary) rank = i + 1;
    out.set(rows[i].tag, {
      rank,
      deltaVsBest: rows[i].primary - best,
      winRate: rows[i].winRate,
      avgScore: rows[i].avgScore,
      primary: rows[i].primary,
    });
  }
  return out;
}

/** Aligné sur l’UI « Jeu de données » / Exporter & Entraîner */
const ML_DATASET_MODE_LABELS: Record<string, string> = {
  per_donne: 'Top par donne',
  per_differe: 'Top par différé',
  ranking_general: 'Class. glissant général',
  ranking_specific: 'Class. glissant spécifique',
  ranking_live: 'Class. live',
  competition_team: 'Championnat équipes',
  competition_team_top_teams: 'Championnat équipes top',
  competition_cia: 'Championnat indiv. attaque',
  competition_cia_top_divisions: 'Championnat indiv. attaque top',
  competition_quadrette: 'Coupe Quadrette',
  freedefi_elite: 'Défis libres élite',
  freedefi_top_live: 'Défis libres top live',
  differe_indiv_top_per_donne: 'Différé indiv. top par donne',
  differe_indiv_top_general_role: 'Différé indiv. top élite (rôle)',
};

function mlVersionHiddenDim(v: ModelVersionEntry): number | null {
  const det = v.evalMetrics?.details as Record<string, unknown> | undefined;
  if (det) {
    for (const k of ['imHiddenDim', 'hidden_dim', 'im_hidden_dim', 'hiddenDim']) {
      const x = det[k];
      if (typeof x === 'number' && Number.isFinite(x)) return x;
    }
  }
  const cfg = v.trainingConfig;
  if (cfg && typeof cfg === 'object') {
    const o = cfg as Record<string, unknown>;
    for (const k of ['imHiddenDim', 'im_hidden_dim', 'hidden_dim', 'hiddenDim']) {
      const x = o[k];
      if (typeof x === 'number' && Number.isFinite(x)) return x;
    }
  }
  return null;
}

/** Source données (manifest eval ou config lancement). */
function mlVersionDatasetLine(v: ModelVersionEntry): string {
  const dm = v.evalMetrics?.details?.datasetManifest;
  if (dm?.selectionMode) {
    const mode = ML_DATASET_MODE_LABELS[dm.selectionMode] || dm.selectionMode;
    const parts: string[] = [mode];
    if (dm.diffSubtypes) parts.push(String(dm.diffSubtypes));
    return parts.join(' · ');
  }
  const tc = v.trainingConfig as Record<string, unknown> | null;
  if (tc) {
    const sm = tc.datasetSelectionMode ?? tc.selection_mode ?? tc.selectionMode;
    if (typeof sm === 'string') return ML_DATASET_MODE_LABELS[sm] || sm;
  }
  return v.trainingMode || '—';
}

function mlOpponentDisplayFromCompareConfig(cfg: Record<string, unknown> | undefined, versions: ModelVersionEntry[]): string {
  if (!cfg) return '—';
  const opp = cfg.opponentModel;
  if (!opp || typeof opp !== 'object') return '—';
  const o = opp as Record<string, unknown>;
  const label = typeof o.label === 'string' ? o.label.trim() : '';
  if (label) return label;
  const tag = versionTagFromModelLike(o);
  if (tag) return mlCompareRowDisplayName(tag, undefined, versions);
  return '—';
}

/** Tags des modèles comparés (pas l’adversaire) dans une comparaison terminée avec stats. */
function mlSynthComparedModelTags(comp: { status: string; modelStats?: unknown[] | null }): Set<string> {
  const s = new Set<string>();
  if (comp.status !== 'completed' || !comp.modelStats?.length) return s;
  for (const ms of comp.modelStats as Array<{ versionTag?: string }>) {
    if (ms?.versionTag) s.add(String(ms.versionTag));
  }
  return s;
}

function mlSynthNewComposerId(): string {
  return `ml-synth-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeMlSynthComposerBlock(raw: unknown): MLSynthComposerBlock | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' && o.id.length > 0 ? o.id : mlSynthNewComposerId();
  const role = o.role === 'defender' ? 'defender' : 'attacker';
  const selectedVersionTags = Array.isArray(o.selectedVersionTags)
    ? o.selectedVersionTags.filter((t): t is string => typeof t === 'string' && t.length > 0)
    : [];
  const metric = o.metric === 'winRate' ? 'winRate' : 'avgScore';
  const showModelChips = o.showModelChips !== false;
  return { id, role, selectedVersionTags, metric, showModelChips };
}

function mlSynthBuildCatalogForRole(
  comparisons: Array<{ id: number; status: string; config?: { compareRole?: string }; modelStats?: any[] | null }>,
  role: 'defender' | 'attacker',
  versions: ModelVersionEntry[],
): Map<string, { displayName: string; comparisonCount: number; comparisonIds: number[] }> {
  const map = new Map<string, { displayName: string; ids: Set<number> }>();
  for (const comp of comparisons) {
    if (comp.status !== 'completed' || comp.config?.compareRole !== role || !comp.modelStats?.length) continue;
    for (const ms of comp.modelStats as Array<{ versionTag?: string; modelLabel?: string }>) {
      const tag = ms?.versionTag;
      if (!tag) continue;
      let ent = map.get(tag);
      if (!ent) {
        ent = {
          displayName: mlCompareRowDisplayName(String(tag), ms.modelLabel, versions),
          ids: new Set<number>(),
        };
        map.set(tag, ent);
      }
      ent.ids.add(comp.id);
      ent.displayName = mlCompareRowDisplayName(String(tag), ms.modelLabel, versions);
    }
  }
  const out = new Map<string, { displayName: string; comparisonCount: number; comparisonIds: number[] }>();
  for (const [tag, ent] of map) {
    const comparisonIds = [...ent.ids].sort((a, b) => b - a);
    out.set(tag, {
      displayName: ent.displayName,
      comparisonCount: comparisonIds.length,
      comparisonIds,
    });
  }
  return out;
}

/** Tags affichables une fois qu’au moins un modèle est sélectionné : union des modèles présents dans une comparaison avec au moins un sélectionné. */
function mlSynthVisibleTags(
  catalogKeysSorted: string[],
  catalog: Map<string, unknown>,
  roleComparisons: Array<{ status: string; modelStats?: any[] | null }>,
  selectedTags: string[],
): string[] {
  if (selectedTags.length === 0) return catalogKeysSorted;
  const allowed = new Set(selectedTags);
  for (const comp of roleComparisons) {
    const tags = mlSynthComparedModelTags(comp);
    const touches = selectedTags.some(s => tags.has(s));
    if (!touches) continue;
    for (const t of tags) {
      if (catalog.has(t)) allowed.add(t);
    }
  }
  return catalogKeysSorted.filter(t => allowed.has(t));
}

function mlSynthTableComparisons<
  T extends { id: number; status: string; createdAt: string; modelStats?: any[] | null },
>(
  roleComparisons: T[],
  selectedTags: string[],
): T[] {
  if (selectedTags.length < 2) return [];
  const sel = new Set(selectedTags);
  return roleComparisons
    .filter(comp => {
      const tags = mlSynthComparedModelTags(comp);
      let n = 0;
      for (const s of sel) {
        if (tags.has(s)) n++;
      }
      return n >= 2;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

/** Libellé « score main » pour en-tête de colonne comparatif (remplace la date). */
function mlSynthComparisonBidScoreLabel(config: { minBidScore?: number; maxBidScore?: number } | null | undefined): string {
  const min = config?.minBidScore ?? 0;
  const max = config?.maxBidScore ?? 200;
  if (max < 200 && min === 0) return `main ≤${max}`;
  if (max < 200) return `main ${min}–${max}`;
  return `main ≥${min}`;
}

function mlSynthDiffereRankRowsForRole(
  differeRankings: { globalAttackNoSc?: unknown[]; globalDefense?: unknown[] } | null | undefined,
  role: 'defender' | 'attacker',
): Array<{ key?: string; kind?: string; rank?: number | null; eligible?: boolean }> | null {
  if (!differeRankings) return null;
  if (role === 'attacker') {
    const rows = differeRankings.globalAttackNoSc;
    return Array.isArray(rows) && rows.length > 0 ? (rows as Array<{ key?: string; kind?: string; rank?: number | null; eligible?: boolean }>) : null;
  }
  const rows = differeRankings.globalDefense;
  return Array.isArray(rows) && rows.length > 0 ? (rows as Array<{ key?: string; kind?: string; rank?: number | null; eligible?: boolean }>) : null;
}

/** Même repère que le classement différé affiché ailleurs : bot1 = Legacy RV. */
function mlDiffereRankingRowIsBot1(row: { key?: string; label?: string }): boolean {
  const key = String(row.key || '').toLowerCase();
  const label = String(row.label || '').toLowerCase();
  return key === 'bot1' || key === 'human:bot1' || label === 'bot1';
}

function mlDiffereRankingTypeLabel(row: { kind?: string; label?: string; key?: string; poolActive?: boolean }): string {
  if (mlDiffereRankingRowIsBot1(row)) return 'IA legacy';
  if (row.kind === 'ai') return row.poolActive === false ? 'IA (archivée)' : 'IA';
  return 'Humain';
}

function mlDiffereLegacyIsAttaqueSc(t: string | null | undefined): boolean {
  const s = String(t || '').toLowerCase().replace(/_/g, '');
  return s === 'attaquesc';
}

function mlDiffereFormatDebut(raw: string | null | undefined): string | null {
  if (raw == null || raw === '') return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? String(raw)
    : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function mlSynthDiffereRankForTag(
  rankings: Array<{ key?: string; kind?: string; label?: string; rank?: number | null; eligible?: boolean }> | null,
  versionTag: string,
  role: 'attacker' | 'defender',
): string {
  if (!rankings) return '—';
  if (versionTag === 'legacy-rv') {
    const row = rankings.find(r => mlDiffereRankingRowIsBot1(r));
    if (!row) return '—';
    if (row.eligible === false) return 'sous seuil';
    if (typeof row.rank === 'number' && Number.isFinite(row.rank)) return `#${row.rank}`;
    return '—';
  }
  const suffix = role === 'attacker' ? ':attacker' : ':defender';
  const expectedKey = `ai:${versionTag}${suffix}`;
  const row = rankings.find(r => r.kind === 'ai' && r.key === expectedKey);
  if (!row) return '—';
  if (row.eligible === false) return 'sous seuil';
  if (typeof row.rank === 'number' && Number.isFinite(row.rank)) return `#${row.rank}`;
  return '—';
}

const DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT = 5;

type DiffereBenchmarkRankingRowLite = {
  key?: string;
  kind?: string;
  label?: string;
  rank?: number | null;
  averagePoints?: number;
  poolActive?: boolean;
};

function parseDiffereAiRankingVersionTag(key: string, role: 'attacker' | 'defender'): string | null {
  const prefix = 'ai:';
  const suffix = `:${role}`;
  if (!key.startsWith(prefix) || !key.endsWith(suffix)) return null;
  const tag = key.slice(prefix.length, key.length - suffix.length);
  return tag || null;
}

function differeBenchmarkMetricsRankingRowsForView(
  rankings: {
    global?: unknown[];
    globalAttackNoSc?: unknown[];
    globalAttackSc?: unknown[];
    globalDefense?: unknown[];
  } | null | undefined,
  view: DiffereBenchmarkMetricsView,
): DiffereBenchmarkRankingRowLite[] {
  if (!rankings) return [];
  const pick = (arr: unknown[] | undefined) =>
    (Array.isArray(arr) ? arr : []) as DiffereBenchmarkRankingRowLite[];
  if (view === 'defense') return pick(rankings.globalDefense);
  if (view === 'attackSc') return pick(rankings.globalAttackSc);
  const attackNoSc = pick(rankings.globalAttackNoSc);
  if (attackNoSc.length > 0) return attackNoSc;
  return pick(rankings.global);
}

function topDiffereAiVersionTagsFromRankings(
  rankingRows: DiffereBenchmarkRankingRowLite[],
  role: 'attacker' | 'defender',
  limit: number,
): string[] {
  const ai = rankingRows.filter(
    r => r.kind === 'ai' && r.poolActive !== false && !mlDiffereRankingRowIsBot1(r),
  );
  ai.sort((a, b) => {
    const rankA = typeof a.rank === 'number' && Number.isFinite(a.rank) ? a.rank : Number.MAX_SAFE_INTEGER;
    const rankB = typeof b.rank === 'number' && Number.isFinite(b.rank) ? b.rank : Number.MAX_SAFE_INTEGER;
    if (rankA !== rankB) return rankA - rankB;
    return (Number(b.averagePoints) || 0) - (Number(a.averagePoints) || 0);
  });
  const tags: string[] = [];
  const seen = new Set<string>();
  for (const row of ai) {
    const tag = parseDiffereAiRankingVersionTag(String(row.key || ''), role);
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    tags.push(tag);
    if (tags.length >= limit) break;
  }
  return tags;
}

function isDiffereBenchmarkMetricsAiModelRow(row: Record<string, any>): boolean {
  const tag = String(row?.versionTag || '');
  if (!tag) return false;
  if (tag === DIFFERE_BENCHMARK_BOT1_BASELINE_TAG) return false;
  if (differeBenchmarkIsHumanTopBaselineTag(tag)) return false;
  return true;
}

/** Baselines toujours visibles avec le top N IA (mode limité). */
const DIFFERE_BENCHMARK_METRICS_LIMITED_BASELINE_TAGS = [
  'official-bot1',
  'human-top-deal-avg10',
  'human-top-differe-avg10',
] as const;

function isDiffereBenchmarkMetricsLimitedBaselineRow(row: Record<string, any>): boolean {
  const tag = String(row?.versionTag || '');
  return (DIFFERE_BENCHMARK_METRICS_LIMITED_BASELINE_TAGS as readonly string[]).includes(tag);
}

/** Limite l’affichage aux N IA les mieux classées + baselines de référence (fallback win rate si pas de classement). */
function filterDiffereBenchmarkMetricsRowsTopAi(
  rows: Record<string, any>[],
  rankedTags: string[],
  limit: number,
): Record<string, any>[] {
  const aiRows = rows.filter(isDiffereBenchmarkMetricsAiModelRow);
  const presentRanked = rankedTags.filter(t => aiRows.some(r => String(r.versionTag) === t));
  const allowedAiTags = [...presentRanked];
  if (allowedAiTags.length < limit) {
    const extras = [...aiRows]
      .filter(r => !allowedAiTags.includes(String(r.versionTag)))
      .sort(
        (a, b) =>
          (Number(b.winRate) || 0) - (Number(a.winRate) || 0)
          || String(a.modelLabel ?? '').localeCompare(String(b.modelLabel ?? ''), 'fr'),
      );
    for (const r of extras) {
      allowedAiTags.push(String(r.versionTag));
      if (allowedAiTags.length >= limit) break;
    }
  }
  const allowedAi = new Set(allowedAiTags.slice(0, limit));
  return rows.filter(r => {
    if (isDiffereBenchmarkMetricsLimitedBaselineRow(r)) return true;
    return isDiffereBenchmarkMetricsAiModelRow(r) && allowedAi.has(String(r.versionTag));
  });
}

/** Loss d’entraînement si présente dans le registre (imitation_metadata, history, etc.). */
function mlSynthExtractTrainLoss(det: Record<string, unknown>): number | null {
  for (const k of ['finalTrainLoss', 'trainLoss', 'train_loss', 'lastTrainLoss'] as const) {
    const x = det[k];
    if (typeof x === 'number' && Number.isFinite(x)) return x;
  }
  const hist = det.history;
  if (Array.isArray(hist) && hist.length > 0) {
    const last = hist[hist.length - 1];
    if (last && typeof last === 'object') {
      const o = last as Record<string, unknown>;
      const tl = o.trainLoss ?? o.loss ?? o.train_loss;
      if (typeof tl === 'number' && Number.isFinite(tl)) return tl;
    }
  }
  return null;
}

/** Résumé métriques registre (loss / val / entame / acc legacy). */
function mlSynthModelMetricsSummary(ver: ModelVersionEntry | undefined): string {
  if (!ver?.evalMetrics?.details) return '—';
  const det = ver.evalMetrics.details as Record<string, unknown>;
  const parts: string[] = [];
  const trainLoss = mlSynthExtractTrainLoss(det);
  if (trainLoss != null) {
    parts.push(`loss ${trainLoss.toFixed(4)}`);
  }
  const fmtPct = (x: unknown): string | null => {
    if (x == null || typeof x !== 'number' || Number.isNaN(x)) return null;
    const n = Number(x);
    return `${n <= 1 ? (n * 100).toFixed(1) : n.toFixed(1)}%`;
  };
  const v = fmtPct(det.valAccuracy);
  if (v) parts.push(`val ${v}`);
  const e = fmtPct(det.entameAccuracy);
  if (e) parts.push(`entame ${e}`);
  const o = fmtPct(det.otherAccuracy);
  if (o) parts.push(`autres ${o}`);
  if (parts.length === 0) {
    if (det.defenderAccuracy) parts.push(`acc ${String(det.defenderAccuracy)}`);
    else if (det.attackerAccuracy) parts.push(`acc ${String(det.attackerAccuracy)}`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

type MLSynthComposerBlock = {
  id: string;
  role: 'defender' | 'attacker';
  selectedVersionTags: string[];
  /** Métrique pour classement / Δ / affichage principal */
  metric: 'winRate' | 'avgScore';
  /** Liste des puces modèles (ajout/retrait) visible */
  showModelChips: boolean;
};

function mlJobVersionEntry(job: MLJob, versions: ModelVersionEntry[]): ModelVersionEntry | undefined {
  return versions.find(v =>
    (v.ovhJobId && v.ovhJobId === job.id) ||
    (v.ovhJobName && v.ovhJobName === job.name),
  );
}

function mlJobDisplayName(job: MLJob, versions: ModelVersionEntry[]): string {
  const version = mlJobVersionEntry(job, versions);
  return version ? mlRegistryDisplayName(version) : (job.name || job.id);
}

/** En-tête d'extrait log : nom grand public (+ tag technique si distinct). */
function formatMLJobLogExtractHeader(job: MLJob, versions: ModelVersionEntry[]): string {
  const version = mlJobVersionEntry(job, versions);
  const displayName = version ? mlRegistryDisplayName(version) : (job.name || job.id);
  const lines = [`Modèle: ${displayName}`];
  if (version?.versionTag && version.versionTag !== displayName) {
    lines.push(`Version tag: ${version.versionTag}`);
  }
  return lines.join('\n');
}

function mlDeployedVersionEntry(
  versions: ModelVersionEntry[],
  slot: 'production' | 'beta',
  role: 'defender' | 'attacker' | 'deductions',
): ModelVersionEntry | undefined {
  return versions.find(
    x => x.status === 'deployed' && x.deployedSlot === slot && x.modelRole === role,
  );
}

type MLRuntimeSlotKey = 'production' | 'beta' | 'attacker_production' | 'attacker_beta';

function mlRuntimeSlotRegistryName(slotKey: MLRuntimeSlotKey, versions: ModelVersionEntry[]): string | null {
  const role: 'defender' | 'attacker' = slotKey.startsWith('attacker') ? 'attacker' : 'defender';
  const slot: 'production' | 'beta' = slotKey.includes('beta') ? 'beta' : 'production';
  const v = mlDeployedVersionEntry(versions, slot, role);
  return v ? mlRegistryDisplayName(v) : null;
}

const ML_INFO_SLOT_ORDER: MLRuntimeSlotKey[] = [
  'production',
  'attacker_production',
  'beta',
  'attacker_beta',
];

function wilsonCIMerged(wins: number, n: number, z = 1.96): { low: number; high: number } {
  if (n === 0) return { low: 0, high: 0 };
  const p = wins / n;
  const denom = 1 + z * z / n;
  const center = p + z * z / (2 * n);
  const spread = z * Math.sqrt((p * (1 - p) + z * z / (4 * n)) / n);
  return {
    low: Math.max(0, (center - spread) / denom),
    high: Math.min(1, (center + spread) / denom),
  };
}

/** Dénominateur implicite à partir d’un taux et d’un compteur (agrégation de plusieurs ModelStats). */
function qualitativeInferDenom(count: number, rate: number): number {
  if (rate > 1e-12) return (count || 0) / rate;
  return 0;
}

function mergeWinRateBuckets(
  a: Record<string, { wins?: number; total?: number; rate?: number }> | null | undefined,
  b: Record<string, { wins?: number; total?: number; rate?: number }> | null | undefined,
): Record<string, { wins: number; total: number; rate: number }> {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  const out: Record<string, { wins: number; total: number; rate: number }> = {};
  for (const k of keys) {
    const x = a?.[k];
    const y = b?.[k];
    const tw = (x?.wins ?? 0) + (y?.wins ?? 0);
    const tt = (x?.total ?? 0) + (y?.total ?? 0);
    out[k] = { wins: tw, total: tt, rate: tt > 0 ? tw / tt : 0 };
  }
  return out;
}

function mergeQualitativeStats(a: any, b: any): any {
  const pa = a || {};
  const pb = b || {};
  const cKam = (pa.petitKamikazeCount || 0) + (pb.petitKamikazeCount || 0);
  const dKam = qualitativeInferDenom(pa.petitKamikazeCount, pa.petitKamikazeRate)
    + qualitativeInferDenom(pb.petitKamikazeCount, pb.petitKamikazeRate);
  const cHon = (pa.honneurOffertCount || 0) + (pb.honneurOffertCount || 0);
  const dHon = qualitativeInferDenom(pa.honneurOffertCount, pa.honneurOffertRate)
    + qualitativeInferDenom(pb.honneurOffertCount, pb.honneurOffertRate);
  const cPts = (pa.pointsDonnesCount || 0) + (pb.pointsDonnesCount || 0);
  const dPts = qualitativeInferDenom(pa.pointsDonnesCount, pa.pointsDonnesRate)
    + qualitativeInferDenom(pb.pointsDonnesCount, pb.pointsDonnesRate);
  const gapSum = (pa.pointsDonnesAvgGap || 0) * (pa.pointsDonnesCount || 0)
    + (pb.pointsDonnesAvgGap || 0) * (pb.pointsDonnesCount || 0);
  const qPrenOpp = (pa.petitPrenableOpportunities || 0) + (pb.petitPrenableOpportunities || 0);
  const qPrenOppDenom = (pa.petitPrenableOpportunityDenom || 0) + (pb.petitPrenableOpportunityDenom || 0);
  const qPrenReal = (pa.petitPrenableRealized || 0) + (pb.petitPrenableRealized || 0);
  const qUnc = (pa.petitChasseIncertaineCount || 0) + (pb.petitChasseIncertaineCount || 0);
  const dUnc = qualitativeInferDenom(pa.petitChasseIncertaineCount, pa.petitChasseIncertaineRate)
    + qualitativeInferDenom(pb.petitChasseIncertaineCount, pb.petitChasseIncertaineRate);

  return {
    petitKamikazeCount: cKam,
    petitKamikazeRate: dKam > 0 ? cKam / dKam : 0,
    honneurOffertCount: cHon,
    honneurOffertRate: dHon > 0 ? cHon / dHon : 0,
    pointsDonnesCount: cPts,
    pointsDonnesAvgGap: cPts > 0 ? gapSum / cPts : 0,
    pointsDonnesRate: dPts > 0 ? cPts / dPts : 0,
    petitPrenableOpportunities: qPrenOpp,
    petitPrenableOpportunityDenom: qPrenOppDenom,
    petitPrenableRealized: qPrenReal,
    petitChasseRate: qPrenOppDenom > 0 ? qPrenReal / qPrenOppDenom : 0,
    petitChasseIncertaineCount: qUnc,
    petitChasseIncertaineRate: dUnc > 0 ? qUnc / dUnc : 0,
  };
}

function mergeAvgEntropyConf(
  a: { early?: number | null; mid?: number | null; late?: number | null } | null | undefined,
  b: { early?: number | null; mid?: number | null; late?: number | null } | null | undefined,
  wa: number,
  wb: number,
): { early: number | null; mid: number | null; late: number | null } {
  const w = wa + wb;
  if (w <= 0) return { early: null, mid: null, late: null };
  const seg = (key: 'early' | 'mid' | 'late'): number | null => {
    const va = a?.[key];
    const vb = b?.[key];
    if (va == null && vb == null) return null;
    if (va == null) return vb ?? null;
    if (vb == null) return va;
    return (va * wa + vb * wb) / w;
  };
  return { early: seg('early'), mid: seg('mid'), late: seg('late') };
}

function mergeLegacyRv(a: any, b: any): any {
  if (!a && !b) return null;
  if (!a) return { ...b };
  if (!b) return { ...a };
  const ecartCalls = (a.ecartCalls || 0) + (b.ecartCalls || 0);
  const cardCalls = (a.cardCalls || 0) + (b.cardCalls || 0);
  const totalCalls = ecartCalls + cardCalls;
  const ecartEmpty = (a.ecartEmptyCardReturns || 0) + (b.ecartEmptyCardReturns || 0);
  const cardEmpty = (a.cardEmptyCardReturns || 0) + (b.cardEmptyCardReturns || 0);
  const emptyTotal = ecartEmpty + cardEmpty;
  return {
    totalCalls,
    emptyCardReturns: emptyTotal,
    emptyCardRate: totalCalls > 0 ? emptyTotal / totalCalls : 0,
    ecartCalls,
    ecartEmptyCardReturns: ecartEmpty,
    cardCalls,
    cardEmptyCardReturns: cardEmpty,
  };
}

function legacyRv99HasNonZeroCell(row: Record<string, any> | null | undefined): boolean {
  const stats = row?.legacyRvStats;
  if (!stats) return false;
  const emptyCardReturns = Number(
    stats.emptyCardReturns
      ?? ((stats.ecartEmptyCardReturns ?? 0) + (stats.cardEmptyCardReturns ?? 0)),
  );
  if (Number.isFinite(emptyCardReturns) && emptyCardReturns > 0) return true;
  const emptyCardRate = Number(stats.emptyCardRate ?? 0);
  return Number.isFinite(emptyCardRate) && emptyCardRate > 0;
}

function shouldShowLegacyRv99Column(rows: Array<Record<string, any>> | null | undefined): boolean {
  return (rows ?? []).some(legacyRv99HasNonZeroCell);
}

function mergeTwoModelStats(a: any, b: any): any {
  const na = a.totalDeals || 0;
  const nb = b.totalDeals || 0;
  const n = na + nb;
  const wins = (a.wins || 0) + (b.wins || 0);
  const winRate = n > 0 ? wins / n : 0;
  const avgScore = n > 0 ? ((a.avgScore || 0) * na + (b.avgScore || 0) * nb) / n : 0;
  let scoreStdDev = 0;
  if (n > 1) {
    const ma = a.avgScore || 0;
    const mb = b.avgScore || 0;
    const sa = a.scoreStdDev || 0;
    const sb = b.scoreStdDev || 0;
    const mean = (ma * na + mb * nb) / n;
    const pooled = (na * (sa * sa + ma * ma) + nb * (sb * sb + mb * mb)) / n - mean * mean;
    scoreStdDev = Math.sqrt(Math.max(0, pooled));
  }

  const petitA = a.petitStats || {};
  const petitB = b.petitStats || {};
  const bonusA = petitA.petitAuBoutScoringBonusCount ?? petitA.petitAuBout ?? 0;
  const bonusB = petitB.petitAuBoutScoringBonusCount ?? petitB.petitAuBout ?? 0;
  const petitStats = {
    petitLostByModel: (petitA.petitLostByModel || 0) + (petitB.petitLostByModel || 0),
    petitConservedByModel: (petitA.petitConservedByModel || 0) + (petitB.petitConservedByModel || 0),
    petitRemporteByModel: (petitA.petitRemporteByModel || 0) + (petitB.petitRemporteByModel || 0),
    petitAuBoutScoringBonusCount: bonusA + bonusB,
    petitDealsTotal: (petitA.petitDealsTotal || 0) + (petitB.petitDealsTotal || 0),
    petitStudiedNoPetitDealCount: (petitA.petitStudiedNoPetitDealCount || 0) + (petitB.petitStudiedNoPetitDealCount || 0),
    petitAuBoutEvaluatedLastTrick: (petitA.petitAuBoutEvaluatedLastTrick || 0) + (petitB.petitAuBoutEvaluatedLastTrick || 0),
    petitAuBoutEvaluatedDenom: (petitA.petitAuBoutEvaluatedDenom || 0) + (petitB.petitAuBoutEvaluatedDenom || 0),
  };

  const slamA = a.slamStats || {};
  const slamB = b.slamStats || {};
  const slamStats = {
    attempted: (slamA.attempted || 0) + (slamB.attempted || 0),
    succeeded: (slamA.succeeded || 0) + (slamB.succeeded || 0),
  };

  return {
    modelSlot: a.modelSlot || b.modelSlot,
    modelLabel: a.modelLabel || b.modelLabel,
    versionTag: a.versionTag || b.versionTag,
    totalDeals: n,
    wins,
    losses: n - wins,
    winRate,
    avgScore,
    scoreStdDev,
    winRateByStrength: mergeWinRateBuckets(a.winRateByStrength, b.winRateByStrength),
    winRateByContract: mergeWinRateBuckets(a.winRateByContract, b.winRateByContract),
    petitStats,
    slamStats,
    entameAgreement: null,
    avgConfidence: mergeAvgEntropyConf(a.avgConfidence, b.avgConfidence, na, nb),
    avgEntropy: mergeAvgEntropyConf(a.avgEntropy, b.avgEntropy, na, nb),
    cardAgreement: null,
    legacyRvStats: mergeLegacyRv(a.legacyRvStats, b.legacyRvStats),
    qualitativeStats: mergeQualitativeStats(a.qualitativeStats, b.qualitativeStats),
    winRateCI: wilsonCIMerged(wins, n),
    pValues: null,
    totalDurationMs: (a.totalDurationMs || 0) + (b.totalDurationMs || 0),
  };
}

function mergeModelStatsMany(parts: any[]): any | null {
  const v = parts.filter(Boolean);
  if (v.length === 0) return null;
  let acc = { ...v[0] };
  for (let i = 1; i < v.length; i++) acc = mergeTwoModelStats(acc, v[i]);
  return acc;
}

/** Retire les champs internes UI avant export JSON. */
function modelStatsRowForJsonExport(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    if (k.startsWith('__')) continue;
    out[k] = v;
  }
  return out;
}

type DiffereBenchmarkMetricsAggregated = {
  runsConsidered: number;
  attacker: {
    fullRun: Array<Record<string, unknown>>;
    attackNoSc: Array<Record<string, unknown>>;
    attackSc: Array<Record<string, unknown>>;
  };
  defender: {
    fullRun: Array<Record<string, unknown>>;
    defense: Array<Record<string, unknown>>;
  };
};

function filterDiffereBenchmarkRunsForMetricsAggregation(
  runs: Array<Record<string, unknown>>,
  activePoolImportIds: Set<number>,
  poolModelKeys: Set<string>,
): Array<Record<string, any>> {
  return runs.filter(r => {
    if (r.runKind !== 'benchmark' && r.runKind !== 'benchmark_baseline') return false;
    if (r.status !== 'completed') return false;
    const importId = Number(r.importId);
    if (!activePoolImportIds.has(importId)) return false;
    const rep = r.report as Record<string, unknown> | null | undefined;
    const cs = rep?.comparisonStats as { fullRun?: unknown } | undefined;
    if (!cs?.fullRun) return false;
    const repModel = rep?.model != null && typeof rep.model === 'object'
      ? rep.model as { role?: unknown; versionTag?: unknown }
      : null;
    const role = repModel?.role === 'defender' || r.modelRole === 'defender'
      ? 'defender'
      : 'attacker';
    const versionTag = String(r.modelVersionTag || repModel?.versionTag || '');
    if (r.runKind === 'benchmark_baseline') return true;
    if (versionTag === 'legacy-rv') return true;
    return poolModelKeys.has(`${role}:${versionTag}`);
  }) as Array<Record<string, any>>;
}

function aggregateDiffereBenchmarkMetricsFromRuns(
  runs: Array<Record<string, any>>,
): DiffereBenchmarkMetricsAggregated {
  type BenchmarkMetricGroup = {
    role: 'attacker' | 'defender';
    versionTag: string;
    label: string;
    drillable: boolean;
    qualitativeAvailable: boolean;
    fullRuns: any[];
    defenses: any[];
    attackNoScs: any[];
    attackScs: any[];
  };

  const groups = new Map<string, BenchmarkMetricGroup>();
  for (const run of runs) {
    const rep = run.report as Record<string, any> | undefined;
    if (!rep || rep.status === 'failed') continue;
    const cs = rep.comparisonStats as {
      fullRun?: any;
      defense?: any;
      attackNoSc?: any;
      attackSc?: any;
    } | undefined;
    if (!cs?.fullRun) continue;
    const role: 'attacker' | 'defender' =
      rep.model?.role === 'defender' || run.modelRole === 'defender' ? 'defender' : 'attacker';
    const versionTag = String(run.modelVersionTag || rep.model?.versionTag || '');
    const label = String(run.modelLabel || rep.model?.label || versionTag || '?');
    const scoreOnly = !!rep.scoreOnly;
    const drillable = !scoreOnly && !rep.drilldownDisabled;
    const qualitativeAvailable = !scoreOnly;
    const key = `${role}:${versionTag}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        role,
        versionTag,
        label,
        drillable,
        qualitativeAvailable,
        fullRuns: [],
        defenses: [],
        attackNoScs: [],
        attackScs: [],
      };
      groups.set(key, g);
    } else {
      g.drillable = g.drillable || drillable;
      g.qualitativeAvailable = g.qualitativeAvailable || qualitativeAvailable;
    }
    g.fullRuns.push(cs.fullRun);
    if (cs.defense) g.defenses.push(cs.defense);
    if (cs.attackNoSc) g.attackNoScs.push(cs.attackNoSc);
    if (cs.attackSc) g.attackScs.push(cs.attackSc);
  }

  const mergeGroupSegment = (
    g: BenchmarkMetricGroup,
    segment: 'fullRun' | 'defense' | 'attackNoSc' | 'attackSc',
  ): Record<string, any> | null => {
    const parts =
      segment === 'fullRun'
        ? g.fullRuns
        : segment === 'defense'
          ? g.defenses
          : segment === 'attackNoSc'
            ? g.attackNoScs
            : g.attackScs;
    const merged = mergeModelStatsMany(parts);
    if (!merged) return null;
    return {
      ...merged,
      modelLabel: g.label,
      versionTag: g.versionTag,
      __differeRole: g.role,
      __segment: segment,
      __runCount: parts.length,
      __drillable: g.drillable,
      __qualitativeAvailable: g.qualitativeAvailable,
    };
  };

  const sortRows = (arr: Array<Record<string, any> | null>) =>
    arr
      .filter((x): x is Record<string, any> => x != null)
      .sort(
        (a, b) =>
          (b.winRate ?? 0) - (a.winRate ?? 0)
          || String(a.modelLabel ?? '').localeCompare(String(b.modelLabel ?? ''), 'fr'),
      );

  const attackerGroups = [...groups.values()].filter(g => g.role === 'attacker');
  const defenderGroups = [...groups.values()].filter(g => g.role === 'defender');

  return {
    runsConsidered: runs.length,
    attacker: {
      fullRun: sortRows(attackerGroups.map(g => mergeGroupSegment(g, 'fullRun'))),
      attackNoSc: sortRows(attackerGroups.map(g => mergeGroupSegment(g, 'attackNoSc'))),
      attackSc: sortRows(attackerGroups.map(g => mergeGroupSegment(g, 'attackSc'))),
    },
    defender: {
      fullRun: sortRows(defenderGroups.map(g => mergeGroupSegment(g, 'fullRun'))),
      defense: sortRows(defenderGroups.map(g => mergeGroupSegment(g, 'defense'))),
    },
  };
}

type DiffereBenchmarkMetricSegment = 'fullRun' | 'defense' | 'attackNoSc' | 'attackSc';
type DiffereBenchmarkMetricsView = 'attackerFull' | 'attackNoSc' | 'attackSc' | 'defense';

const DIFFERE_BENCHMARK_METRIC_VIEW_OPTIONS: Array<{
  value: DiffereBenchmarkMetricsView;
  label: string;
  title: string;
  description: string;
  compareRole: 'attacker' | 'defender';
  segment: DiffereBenchmarkMetricSegment;
}> = [
  {
    value: 'attackerFull',
    label: 'Attaque (full)',
    title: 'Attaque — toutes les donnes (fullRun)',
    description: 'Toutes les donnes attaque du benchmark.',
    compareRole: 'attacker',
    segment: 'fullRun',
  },
  {
    value: 'attackNoSc',
    label: 'Attaque (hors s/c)',
    title: 'Attaque hors sur-contrats',
    description: 'Attaque hors sur-contrats.',
    compareRole: 'attacker',
    segment: 'attackNoSc',
  },
  {
    value: 'attackSc',
    label: 'Attaque (s/c)',
    title: 'Attaque sur-contrats',
    description: 'Attaque sur-contrats.',
    compareRole: 'attacker',
    segment: 'attackSc',
  },
  {
    value: 'defense',
    label: 'Défense',
    title: 'Défense',
    description: 'Donnes défense uniquement.',
    compareRole: 'defender',
    segment: 'defense',
  },
];

function buildDiffereBenchmarkMetricsExportPayload(
  aggregated: DiffereBenchmarkMetricsAggregated,
  scope: { mode: 'all' | 'one'; importId: number | null; sourceDiffereId: number | null },
): Record<string, unknown> {
  const segmentBlock = (
    rows: Array<Record<string, unknown>>,
    meta: { label: string; title: string; description: string; compareRole: 'attacker' | 'defender'; segment: string },
  ) => ({
    ...meta,
    models: rows.map(r => modelStatsRowForJsonExport(r)),
  });

  const opt = (value: DiffereBenchmarkMetricsView) =>
    DIFFERE_BENCHMARK_METRIC_VIEW_OPTIONS.find(o => o.value === value)!;

  return {
    kind: 'webtarot-neo-differe-benchmark-metrics',
    exportedAt: new Date().toISOString(),
    scope,
    runsConsidered: aggregated.runsConsidered,
    segments: {
      attacker: {
        fullRun: segmentBlock(aggregated.attacker.fullRun, opt('attackerFull')),
        attackNoSc: segmentBlock(aggregated.attacker.attackNoSc, opt('attackNoSc')),
        attackSc: segmentBlock(aggregated.attacker.attackSc, opt('attackSc')),
      },
      defender: {
        fullRun: segmentBlock(aggregated.defender.fullRun, {
          label: 'Défense (full)',
          title: 'Défense — toutes les donnes (fullRun)',
          description: 'Toutes les donnes défense du benchmark.',
          compareRole: 'defender',
          segment: 'fullRun',
        }),
        defense: segmentBlock(aggregated.defender.defense, opt('defense')),
      },
    },
    note:
      'Agrégats fusionnés (même logique que la modal « Métriques benchmark différé »). Pas de listes de donnes ni drill-down.',
  };
}

const DIFFERE_BENCHMARK_BOT1_BASELINE_TAG = 'official-bot1';

function differeBenchmarkIsHumanTopBaselineTag(versionTag: string): boolean {
  return versionTag.startsWith('human-top-deal-') || versionTag.startsWith('human-top-general-');
}

/** Options pour le volet droit du détail donne : autres ML du pool, et selon le joueur principal des baselines humaines / bot1 (pas humain vs humain). */
function differeBenchmarkBuildDealCompareOptions(
  primary: { runId: number; versionTag?: string },
  comparisonModels: Array<{ versionTag: string; modelLabel: string }>,
): Array<{ key: string; label: string }> {
  const out: Array<{ key: string; label: string }> = [];
  const add = (key: string, label: string) => {
    if (out.some(o => o.key === key)) return;
    out.push({ key, label });
  };
  const primaryRunId = Number(primary.runId);
  const primaryVt = String(primary.versionTag ?? '');
  const primaryIsMl = primaryRunId > 0;
  const primaryIsHumanTop = primaryRunId === 0 && differeBenchmarkIsHumanTopBaselineTag(primaryVt);
  const primaryIsBot1 = primaryRunId === 0 && primaryVt === DIFFERE_BENCHMARK_BOT1_BASELINE_TAG;

  for (const m of comparisonModels) {
    if (primaryIsMl && m.versionTag === primaryVt) continue;
    add(m.versionTag, m.modelLabel || m.versionTag);
  }

  if (primaryIsMl || primaryIsBot1) {
    for (let r = 1; r <= 3; r++) {
      add(`human-top-deal-${r}`, `Humain #${r} par donne`);
      add(`human-top-general-${r}`, `Humain #${r} par différé`);
    }
  }

  if ((primaryIsMl || primaryIsHumanTop) && primaryVt !== DIFFERE_BENCHMARK_BOT1_BASELINE_TAG) {
    add(DIFFERE_BENCHMARK_BOT1_BASELINE_TAG, 'Bot1 officiel');
  }

  return out;
}

function pickDefaultDiffereDealCompareTarget(
  options: Array<{ key: string; label: string }>,
  preferredTag: string | null | undefined,
): string | null {
  if (options.length === 0) return null;
  if (preferredTag && options.some(o => o.key === preferredTag)) return preferredTag;
  return options[0].key;
}

/** Ligne « analysée » du drill-down métrique benchmark : modèle ML pool (pas baseline bot1 / humain / moyennes). */
function differeBenchmarkIsMlPoolMetricDrillTag(versionTag: string): boolean {
  if (versionTag === DIFFERE_BENCHMARK_BOT1_BASELINE_TAG) return false;
  if (differeBenchmarkIsHumanTopBaselineTag(versionTag)) return false;
  if (versionTag === 'human-top-deal-avg10' || versionTag === 'human-top-differe-avg10') return false;
  return true;
}

const HUMAN_TOP_DEAL_AVG10_VERSION_TAG = 'human-top-deal-avg10';
const HUMAN_TOP_DIFFERE_AVG10_VERSION_TAG = 'human-top-differe-avg10';

function isHumanTop10AverageBenchmarkRow(ms: { versionTag?: string } | null | undefined): boolean {
  const t = ms?.versionTag;
  return t === HUMAN_TOP_DEAL_AVG10_VERSION_TAG || t === HUMAN_TOP_DIFFERE_AVG10_VERSION_TAG;
}

/** Valeur avant le taux entre parenthèses : lignes « Top 10 (moy) » → 1 décimale (évite 14.599999…). */
function fmtMetricCountBeforeRate(ms: { versionTag?: string } | null | undefined, n: number): string {
  if (!isHumanTop10AverageBenchmarkRow(ms)) return String(n);
  if (!Number.isFinite(n)) return '0';
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

function fmtPointsDonnesAvgGapCell(ms: { versionTag?: string } | null | undefined, gap: number): string {
  const g = Number.isFinite(gap) ? gap : 0;
  if (!isHumanTop10AverageBenchmarkRow(ms)) return g.toFixed(2);
  const r = Math.round(g * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/** Infobulles (survol) : rappel du dénominateur des taux — comparaison / benchmark ML. */
const ML_COMPARE_DENOM_TT = {
  victoires: 'Victoires du modèle / nombre total de donnes de la série. Le win rate utilise le même dénominateur (toutes les donnes).',
  winRate: 'Pourcentage de donnes gagnées du point de vue du modèle. Dénominateur : toutes les donnes de la série.',
  avgScore: 'Moyenne arithmétique des scores par donne. Dénominateur implicite : nombre de donnes.',
  scoreStdDev: 'Écart-type des scores du modèle sur les donnes (N = nombre de donnes).',
  petitLostTh:
    'Pli du Petit perdu par le camp du modèle. Dénominateur du % : donnes où le joueur évalué avait le Petit dans sa main effective avant le 1er pli (main + chien − écart si petite ou garde).',
  petitRemporteTh:
    'Camp du modèle gagnant le pli du Petit alors que le joueur évalué n’avait pas le Petit au départ. Dénominateur du % : donnes sans Petit pour le joueur évalué avant le 1er pli.',
  petitAuBoutTh:
    'Petit posé au dernier pli par le joueur évalué quand le détail est disponible. Dénominateur du % : donnes où le joueur évalué avait le Petit en main effective avant le 1er pli. Sinon : comptage des donnes avec bonus/malus petit au bout au barème.',
  legacyRv99Th: 'Part d’appels moteur RV ayant renvoyé une réponse vide (99). Dénominateur du % : nombre total d’appels RV (écart + carte).',
  petitKamikazeTh:
    'Petit « kamikaze » (preneur). Dénominateur du % : entames du preneur avec le Petit encore en main, la défense pouvant encore avoir un atout gagnant (hors seule Excuse), hors dernier pli. Neutralisé si rôle = défenseur.',
  honneurOffertTh:
    'Honneur offert à l’entame (preneur). Dénominateur du % : entames du preneur au sens de la métrique qualitative. Neutralisé si rôle = défenseur.',
  pointsDonnesTh:
    'Points donnés (joueur évalué, plis suivis). Dénominateur du % : situations où la règle 0.3 compte (hors dernier pli, etc.).',
  pointsDonnesGapTh:
    'Moyenne des écarts « points donnés » sur les seules occurrences où la métrique est comptée (même base que la colonne précédente).',
  petitPrenableTh:
    'Part des donnes avec au moins une opportunité « petit prenable » (côté preneur, rejeu qualitatif). Dénominateur du % (ratio opportunités/donnes) : donnes où le joueur évalué n’avait pas le Petit en main effective avant le 1er pli.',
  chasseRealiseeTh:
    'Chasses réussies menant au Petit posé par la défense. Dénominateur du % entre parenthèses : donnes où le joueur évalué n’avait pas le Petit avant le 1er pli (identique à « Petit prenable »).',
  chasseIncertaineTh:
    'Chasse « incertaine » (métrique 0.5). Dénominateur du % : somme sur les donnes des drapeaux internes (0 ou 1 par donne) — uniquement les donnes où la suite d’entames atout du preneur active la jauge ; ce n’est pas le même dénominateur que « Petit prenable » / chasse réalisée.',
} as const;

/** Synthèse + métriques qualitatives : une ou plusieurs lignes `ModelStats` (même rôle). */
type DiffereBenchmarkSortKey =
  | 'model'
  | 'wins'
  | 'winRate'
  | 'avgScore'
  | 'scoreStdDev'
  | 'petitLost'
  | 'petitRemporte'
  | 'petitAuBout'
  | 'legacyRv99'
  | 'duration'
  | 'petitKamikaze'
  | 'honneurOffert'
  | 'pointsDonnes'
  | 'pointsDonnesGap'
  | 'petitPrenable'
  | 'chasseRealisee'
  | 'chasseIncertaine';

type DiffereBenchmarkSortDir = 'asc' | 'desc';

/** Colonnes où une valeur plus basse est « meilleure » (défaut de tri = asc). */
const DIFFERE_BENCHMARK_SORT_LOWER_BETTER = new Set<DiffereBenchmarkSortKey>([
  'scoreStdDev',
  'petitLost',
  'legacyRv99',
  'duration',
  'petitKamikaze',
  'honneurOffert',
  'pointsDonnes',
  'pointsDonnesGap',
  'chasseIncertaine',
]);

function differeBenchmarkRowSortValue(
  row: Record<string, any>,
  key: DiffereBenchmarkSortKey,
): number | string {
  const pt = row?.petitStats;
  const q = row?.qualitativeStats;
  const heldDenom = pt?.petitAuBoutEvaluatedDenom ?? 0;
  const noPetitDenom = pt?.petitStudiedNoPetitDealCount ?? 0;
  const evN = pt?.petitAuBoutEvaluatedLastTrick ?? 0;
  switch (key) {
    case 'model':
      return String(row?.modelLabel || row?.versionTag || '');
    case 'wins':
      return Number(row?.wins ?? 0);
    case 'winRate':
      return Number(row?.winRate ?? 0);
    case 'avgScore':
      return Number(row?.avgScore ?? 0);
    case 'scoreStdDev':
      return Number(row?.scoreStdDev ?? 0);
    case 'petitLost':
      return heldDenom > 0 ? (pt?.petitLostByModel ?? 0) / heldDenom : Number.NaN;
    case 'petitRemporte':
      return noPetitDenom > 0 ? (pt?.petitRemporteByModel ?? 0) / noPetitDenom : Number.NaN;
    case 'petitAuBout':
      return heldDenom > 0
        ? evN / heldDenom
        : Number(pt?.petitAuBoutScoringBonusCount ?? pt?.petitAuBout ?? Number.NaN);
    case 'legacyRv99':
      return row?.legacyRvStats ? Number(row.legacyRvStats.emptyCardRate ?? 0) : Number.NaN;
    case 'duration':
      return Number(row?.totalDurationMs ?? 0);
    case 'petitKamikaze':
      return Number(q?.petitKamikazeRate ?? Number.NaN);
    case 'honneurOffert':
      return Number(q?.honneurOffertRate ?? Number.NaN);
    case 'pointsDonnes':
      return Number(q?.pointsDonnesRate ?? Number.NaN);
    case 'pointsDonnesGap':
      return Number(q?.pointsDonnesAvgGap ?? Number.NaN);
    case 'petitPrenable': {
      const oppDenom = q?.petitPrenableOpportunityDenom ?? 0;
      const opp = q?.petitPrenableOpportunities ?? 0;
      return oppDenom > 0 ? opp / oppDenom : Number(opp);
    }
    case 'chasseRealisee':
      return Number(q?.petitChasseRate ?? Number.NaN);
    case 'chasseIncertaine':
      return Number(q?.petitChasseIncertaineRate ?? Number.NaN);
    default:
      return Number.NaN;
  }
}

function sortDiffereBenchmarkMetricRows(
  rows: Record<string, any>[],
  key: DiffereBenchmarkSortKey,
  dir: DiffereBenchmarkSortDir,
): Record<string, any>[] {
  const mult = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = differeBenchmarkRowSortValue(a, key);
    const vb = differeBenchmarkRowSortValue(b, key);
    if (typeof va === 'string' || typeof vb === 'string') {
      const cmp = String(va).localeCompare(String(vb), 'fr', { sensitivity: 'base' });
      if (cmp !== 0) return mult * cmp;
    } else {
      const na = Number.isFinite(va) ? va : (dir === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
      const nb = Number.isFinite(vb) ? vb : (dir === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
      if (na !== nb) return mult * (na - nb);
    }
    return String(a?.modelLabel ?? '').localeCompare(String(b?.modelLabel ?? ''), 'fr');
  });
}

function differeBenchmarkColumnExtents(
  rows: Record<string, any>[],
  key: DiffereBenchmarkSortKey,
): { min: number; max: number } | null {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const row of rows) {
    const v = differeBenchmarkRowSortValue(row, key);
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
}

/** Teinte relative (0 = pire, 1 = meilleur) + largeur de mini-barre (0–1). */
function differeBenchmarkMetricVisual(
  value: number | null | undefined,
  extents: { min: number; max: number } | null,
  higherIsBetter: boolean,
): { heat: number; bar: number } | null {
  if (value == null || !Number.isFinite(value) || !extents) return null;
  const span = extents.max - extents.min;
  const rel = span <= 1e-12 ? 0.5 : (value - extents.min) / span;
  const heat = higherIsBetter ? rel : 1 - rel;
  return { heat, bar: Math.max(0.06, Math.min(1, rel)) };
}

function DiffereBenchmarkMetricValue({
  children,
  visual,
  strong,
}: {
  children: React.ReactNode;
  visual: { heat: number; bar: number } | null;
  strong?: boolean;
}) {
  const heatClass =
    visual == null
      ? ''
      : visual.heat >= 0.66
        ? ' ml-metric-heat--good'
        : visual.heat <= 0.33
          ? ' ml-metric-heat--bad'
          : ' ml-metric-heat--mid';
  return (
    <span className={`ml-metric-cell${heatClass}`}>
      {strong ? <strong>{children}</strong> : children}
      {visual ? (
        <span
          className="ml-metric-cell-bar"
          style={{
            width: `${Math.round(visual.bar * 100)}%`,
            opacity: 0.35 + visual.heat * 0.45,
          }}
          aria-hidden
        />
      ) : null}
    </span>
  );
}

function DiffereBenchmarkSortTh({
  label,
  title,
  sortKey,
  activeKey,
  dir,
  onSort,
}: {
  label: string;
  title?: string;
  sortKey: DiffereBenchmarkSortKey;
  activeKey: DiffereBenchmarkSortKey;
  dir: DiffereBenchmarkSortDir;
  onSort: (key: DiffereBenchmarkSortKey) => void;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      className={`ml-compare-th-sort${active ? ' ml-compare-th-sort--active' : ''}`}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="ml-compare-th-sort-btn"
        title={title ? `${title} — Cliquer pour trier.` : 'Cliquer pour trier.'}
        onClick={() => onSort(sortKey)}
      >
        <span>{label}</span>
        <span className="ml-compare-th-sort-ind" aria-hidden>
          {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

function DiffereBenchmarkSegmentStats({
  title,
  subtitle,
  rows,
  compareRole,
  segment,
  onOpenMetric,
}: {
  title: string;
  subtitle?: string;
  rows: Record<string, any>[];
  compareRole: 'defender' | 'attacker';
  segment: DiffereBenchmarkMetricSegment;
  onOpenMetric?: (
    metric: string,
    row: Record<string, any>,
    compareRole: 'defender' | 'attacker',
    segment: DiffereBenchmarkMetricSegment,
  ) => void;
}) {
  const def = compareRole === 'defender';
  const fmtRate = (r: number) => `${((r ?? 0) * 100).toFixed(2)}%`;
  const dash = '—';
  const showRvCol = shouldShowLegacyRv99Column(rows);

  const [synthSort, setSynthSort] = useState<{ key: DiffereBenchmarkSortKey; dir: DiffereBenchmarkSortDir }>({
    key: 'winRate',
    dir: 'desc',
  });
  const [qualSort, setQualSort] = useState<{ key: DiffereBenchmarkSortKey; dir: DiffereBenchmarkSortDir }>({
    key: 'petitKamikaze',
    dir: 'asc',
  });

  const makeToggleSort = useCallback(
    (set: React.Dispatch<React.SetStateAction<{ key: DiffereBenchmarkSortKey; dir: DiffereBenchmarkSortDir }>>) =>
      (key: DiffereBenchmarkSortKey) => {
        set(current => {
          if (current.key === key) {
            return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
          }
          return {
            key,
            dir: DIFFERE_BENCHMARK_SORT_LOWER_BETTER.has(key) || key === 'model' ? 'asc' : 'desc',
          };
        });
      },
    [],
  );
  const toggleSynthSort = useMemo(() => makeToggleSort(setSynthSort), [makeToggleSort]);
  const toggleQualSort = useMemo(() => makeToggleSort(setQualSort), [makeToggleSort]);

  const sortedSynthRows = useMemo(
    () => sortDiffereBenchmarkMetricRows(rows, synthSort.key, synthSort.dir),
    [rows, synthSort.key, synthSort.dir],
  );
  const sortedQualRows = useMemo(
    () => sortDiffereBenchmarkMetricRows(rows, qualSort.key, qualSort.dir),
    [rows, qualSort.key, qualSort.dir],
  );

  const bestWinRateKey = useMemo(() => {
    let best: string | null = null;
    let bestWr = -Infinity;
    for (const r of rows) {
      const wr = Number(r?.winRate ?? 0);
      if (wr > bestWr) {
        bestWr = wr;
        best = `${r?.__differeRole ?? ''}:${r?.versionTag ?? ''}`;
      }
    }
    return best;
  }, [rows]);

  const extents = useMemo(() => {
    const keys: DiffereBenchmarkSortKey[] = [
      'winRate',
      'avgScore',
      'scoreStdDev',
      'petitLost',
      'petitRemporte',
      'petitAuBout',
      'legacyRv99',
      'petitKamikaze',
      'honneurOffert',
      'pointsDonnes',
      'pointsDonnesGap',
      'petitPrenable',
      'chasseRealisee',
      'chasseIncertaine',
    ];
    const map = new Map<DiffereBenchmarkSortKey, { min: number; max: number } | null>();
    for (const k of keys) map.set(k, differeBenchmarkColumnExtents(rows, k));
    return map;
  }, [rows]);

  const visualFor = (row: Record<string, any>, key: DiffereBenchmarkSortKey) =>
    differeBenchmarkMetricVisual(
      (() => {
        const v = differeBenchmarkRowSortValue(row, key);
        return typeof v === 'number' ? v : null;
      })(),
      extents.get(key) ?? null,
      !DIFFERE_BENCHMARK_SORT_LOWER_BETTER.has(key),
    );

  const drillCell = (
    metric: string,
    row: Record<string, any>,
    content: React.ReactNode,
    titleText: string,
    cellCls?: string,
  ) => {
    const cls = ['ml-metric-td', cellCls].filter(Boolean).join(' ') || undefined;
    if (!onOpenMetric || row.__drillable === false) return <td className={cls}>{content}</td>;
    return (
      <td
        role="button"
        tabIndex={0}
        className={cls}
        style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
        title={titleText}
        onClick={e => {
          e.stopPropagation();
          onOpenMetric(metric, row, compareRole, segment);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenMetric(metric, row, compareRole, segment);
          }
        }}
      >
        {content}
      </td>
    );
  };

  const rowIsBest = (ms: Record<string, any>) =>
    bestWinRateKey != null && `${ms?.__differeRole ?? ''}:${ms?.versionTag ?? ''}` === bestWinRateKey;

  return (
    <div className="ml-compare-section ml-compare-qualitative" style={{ marginBottom: 20 }}>
      <h5 style={{ marginTop: 0 }}>{title}</h5>
      {subtitle ? (
        <p style={{ fontSize: '0.82rem', opacity: 0.88, marginTop: 0, marginBottom: 8 }}>{subtitle}</p>
      ) : null}
      <p className="ml-compare-hint" style={{ marginTop: 0, marginBottom: 8, fontSize: '0.78rem' }}>
        Cliquez sur un en-tête pour trier. Barres / teintes = position relative parmi les modèles affichés
        (vert = meilleur, rouge = moins bon pour la métrique).
      </p>
      <div className="ml-compare-table-wrap">
        <table className="ml-compare-table ml-compare-table--compact">
          <thead>
            <tr>
              <DiffereBenchmarkSortTh
                label="Modèle"
                sortKey="model"
                activeKey={synthSort.key}
                dir={synthSort.dir}
                onSort={toggleSynthSort}
              />
              <DiffereBenchmarkSortTh
                label="Victoires"
                title={ML_COMPARE_DENOM_TT.victoires}
                sortKey="wins"
                activeKey={synthSort.key}
                dir={synthSort.dir}
                onSort={toggleSynthSort}
              />
              <DiffereBenchmarkSortTh
                label="Win rate"
                title={ML_COMPARE_DENOM_TT.winRate}
                sortKey="winRate"
                activeKey={synthSort.key}
                dir={synthSort.dir}
                onSort={toggleSynthSort}
              />
              <DiffereBenchmarkSortTh
                label="Score moy."
                title={ML_COMPARE_DENOM_TT.avgScore}
                sortKey="avgScore"
                activeKey={synthSort.key}
                dir={synthSort.dir}
                onSort={toggleSynthSort}
              />
              <DiffereBenchmarkSortTh
                label="Écart-type"
                title={ML_COMPARE_DENOM_TT.scoreStdDev}
                sortKey="scoreStdDev"
                activeKey={synthSort.key}
                dir={synthSort.dir}
                onSort={toggleSynthSort}
              />
              <DiffereBenchmarkSortTh
                label="Petit perdu"
                title={ML_COMPARE_DENOM_TT.petitLostTh}
                sortKey="petitLost"
                activeKey={synthSort.key}
                dir={synthSort.dir}
                onSort={toggleSynthSort}
              />
              <DiffereBenchmarkSortTh
                label="Petit remporté"
                title={ML_COMPARE_DENOM_TT.petitRemporteTh}
                sortKey="petitRemporte"
                activeKey={synthSort.key}
                dir={synthSort.dir}
                onSort={toggleSynthSort}
              />
              <DiffereBenchmarkSortTh
                label="Petit au bout"
                title={ML_COMPARE_DENOM_TT.petitAuBoutTh}
                sortKey="petitAuBout"
                activeKey={synthSort.key}
                dir={synthSort.dir}
                onSort={toggleSynthSort}
              />
              {showRvCol ? (
                <DiffereBenchmarkSortTh
                  label="RV 99"
                  title={ML_COMPARE_DENOM_TT.legacyRv99Th}
                  sortKey="legacyRv99"
                  activeKey={synthSort.key}
                  dir={synthSort.dir}
                  onSort={toggleSynthSort}
                />
              ) : null}
              <DiffereBenchmarkSortTh
                label="Durée"
                sortKey="duration"
                activeKey={synthSort.key}
                dir={synthSort.dir}
                onSort={toggleSynthSort}
              />
            </tr>
          </thead>
          <tbody>
            {sortedSynthRows.map((ms, idx) => {
              const pt = ms?.petitStats;
              const remporte = pt?.petitRemporteByModel ?? 0;
              const fftBonus = pt?.petitAuBoutScoringBonusCount ?? pt?.petitAuBout ?? 0;
              const evN = pt?.petitAuBoutEvaluatedLastTrick ?? 0;
              const heldDenom = pt?.petitAuBoutEvaluatedDenom ?? 0;
              const noPetitDenom = pt?.petitStudiedNoPetitDealCount ?? 0;
              const evPct = heldDenom > 0 ? ((evN / heldDenom) * 100).toFixed(1) : null;
              return (
                <tr
                  key={`${ms?.versionTag ?? idx}-${idx}`}
                  className={rowIsBest(ms) ? 'ml-compare-best' : undefined}
                >
                  <td className="ml-compare-model-name">
                    <strong>{ms?.modelLabel || ms?.versionTag || '—'}</strong>
                  </td>
                  <td>{fmtMetricCountBeforeRate(ms, ms?.wins ?? 0)}/{fmtMetricCountBeforeRate(ms, ms?.totalDeals ?? 0)}</td>
                  <td className="ml-compare-winrate ml-metric-td">
                    <DiffereBenchmarkMetricValue visual={visualFor(ms, 'winRate')} strong>
                      {((ms?.winRate ?? 0) * 100).toFixed(1)}%
                    </DiffereBenchmarkMetricValue>
                  </td>
                  <td className="ml-metric-td">
                    <DiffereBenchmarkMetricValue visual={visualFor(ms, 'avgScore')}>
                      {(ms?.avgScore ?? 0).toFixed(1)}
                    </DiffereBenchmarkMetricValue>
                  </td>
                  <td className="ml-metric-td">
                    <DiffereBenchmarkMetricValue visual={visualFor(ms, 'scoreStdDev')}>
                      {(ms?.scoreStdDev ?? 0).toFixed(1)}
                    </DiffereBenchmarkMetricValue>
                  </td>
                  {drillCell(
                    'petitLost',
                    ms,
                    heldDenom > 0 ? (
                      <DiffereBenchmarkMetricValue visual={visualFor(ms, 'petitLost')}>
                        {`${fmtMetricCountBeforeRate(ms, pt?.petitLostByModel ?? 0)} (${(((pt?.petitLostByModel ?? 0) / heldDenom) * 100).toFixed(0)}%)`}
                      </DiffereBenchmarkMetricValue>
                    ) : '—',
                    `${ML_COMPARE_DENOM_TT.petitLostTh} — Lister les donnes avec Petit perdu.`,
                  )}
                  {drillCell(
                    'petitRemporte',
                    ms,
                    noPetitDenom > 0 ? (
                      <DiffereBenchmarkMetricValue visual={visualFor(ms, 'petitRemporte')}>
                        {`${fmtMetricCountBeforeRate(ms, remporte)} (${((remporte / noPetitDenom) * 100).toFixed(0)}%)`}
                      </DiffereBenchmarkMetricValue>
                    ) : '—',
                    `${ML_COMPARE_DENOM_TT.petitRemporteTh} — Lister les donnes avec Petit remporté.`,
                  )}
                  {drillCell(
                    'petitAuBout',
                    ms,
                    heldDenom > 0 ? (
                      <DiffereBenchmarkMetricValue visual={visualFor(ms, 'petitAuBout')}>
                        {`${fmtMetricCountBeforeRate(ms, evN)} (${evPct}%)`}
                      </DiffereBenchmarkMetricValue>
                    ) : (fftBonus > 0 ? String(fmtMetricCountBeforeRate(ms, fftBonus)) : '—'),
                    heldDenom > 0
                      ? `${ML_COMPARE_DENOM_TT.petitAuBoutTh} — Lister les donnes : Petit au dernier pli (${evN}/${heldDenom}).`
                      : `Lister les donnes avec bonus/malus petit au bout (barème FFT) : ${fftBonus}.`,
                  )}
                  {showRvCol ? (
                    ms.legacyRvStats
                      ? drillCell(
                        'legacyRv99',
                        ms,
                        (
                          <DiffereBenchmarkMetricValue visual={visualFor(ms, 'legacyRv99')}>
                            {`${(ms.legacyRvStats.emptyCardRate * 100).toFixed(1)}%`}
                          </DiffereBenchmarkMetricValue>
                        ),
                        `${ML_COMPARE_DENOM_TT.legacyRv99Th} — ${ms.legacyRvStats.emptyCardReturns}/${ms.legacyRvStats.totalCalls} appels RV.`,
                      )
                      : <td>—</td>
                  ) : null}
                  <td>{Math.round((ms?.totalDurationMs ?? 0) / 1000)}s</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.some(ms => ms?.qualitativeStats) ? (
        <>
          <h6 style={{ marginTop: 14, marginBottom: 8 }}>Métriques qualitatives</h6>
          <p className="ml-compare-hint" style={{ marginTop: 0, marginBottom: 8, fontSize: '0.82rem' }}>
            Indicateurs comportementaux (la plupart des taux « preneur » sont neutralisés si rôle = défense).
          </p>
          <div className="ml-compare-table-wrap">
            <table className="ml-compare-table ml-compare-table--compact">
              <thead>
                <tr>
                  <DiffereBenchmarkSortTh
                    label="Modèle"
                    sortKey="model"
                    activeKey={qualSort.key}
                    dir={qualSort.dir}
                    onSort={toggleQualSort}
                  />
                  <DiffereBenchmarkSortTh
                    label="Petit kamikaze"
                    title={ML_COMPARE_DENOM_TT.petitKamikazeTh}
                    sortKey="petitKamikaze"
                    activeKey={qualSort.key}
                    dir={qualSort.dir}
                    onSort={toggleQualSort}
                  />
                  <DiffereBenchmarkSortTh
                    label="Honneur offert"
                    title={ML_COMPARE_DENOM_TT.honneurOffertTh}
                    sortKey="honneurOffert"
                    activeKey={qualSort.key}
                    dir={qualSort.dir}
                    onSort={toggleQualSort}
                  />
                  <DiffereBenchmarkSortTh
                    label="Points donnés"
                    title={ML_COMPARE_DENOM_TT.pointsDonnesTh}
                    sortKey="pointsDonnes"
                    activeKey={qualSort.key}
                    dir={qualSort.dir}
                    onSort={toggleQualSort}
                  />
                  <DiffereBenchmarkSortTh
                    label="Écart pts (moy.)"
                    title={ML_COMPARE_DENOM_TT.pointsDonnesGapTh}
                    sortKey="pointsDonnesGap"
                    activeKey={qualSort.key}
                    dir={qualSort.dir}
                    onSort={toggleQualSort}
                  />
                  <DiffereBenchmarkSortTh
                    label="Petit prenable"
                    title={ML_COMPARE_DENOM_TT.petitPrenableTh}
                    sortKey="petitPrenable"
                    activeKey={qualSort.key}
                    dir={qualSort.dir}
                    onSort={toggleQualSort}
                  />
                  <DiffereBenchmarkSortTh
                    label="Chasse réalisée"
                    title={ML_COMPARE_DENOM_TT.chasseRealiseeTh}
                    sortKey="chasseRealisee"
                    activeKey={qualSort.key}
                    dir={qualSort.dir}
                    onSort={toggleQualSort}
                  />
                  <DiffereBenchmarkSortTh
                    label="Chasse incertaine"
                    title={ML_COMPARE_DENOM_TT.chasseIncertaineTh}
                    sortKey="chasseIncertaine"
                    activeKey={qualSort.key}
                    dir={qualSort.dir}
                    onSort={toggleQualSort}
                  />
                </tr>
              </thead>
              <tbody>
                {sortedQualRows.map((ms, idx) => {
                  const q = ms?.qualitativeStats;
                  const kRate = q?.petitKamikazeRate ?? 0;
                  const hRate = q?.honneurOffertRate ?? 0;
                  const kWarn = kRate > 0.005;
                  const hWarn = hRate > 0.02;
                  const opp = q?.petitPrenableOpportunities ?? 0;
                  const oppDenom = q?.petitPrenableOpportunityDenom ?? 0;
                  const oppPct = !def && oppDenom > 0 ? ((opp / oppDenom) * 100).toFixed(1) : null;
                  const realized = q?.petitPrenableRealized ?? 0;
                  const chasseRate = q?.petitChasseRate ?? 0;
                  if (ms?.__drillable === false && ms?.__qualitativeAvailable === false) {
                    return (
                      <tr key={`${ms?.versionTag ?? idx}-q-${idx}`}>
                        <td className="ml-compare-model-name">
                          <strong>{ms?.modelLabel || ms?.versionTag || '—'}</strong>
                        </td>
                        <td colSpan={7} style={{ opacity: 0.7 }}>Score-only : plis non importés pour ces joueurs.</td>
                      </tr>
                    );
                  }
                  return (
                    <tr
                      key={`${ms?.versionTag ?? idx}-q-${idx}`}
                      className={rowIsBest(ms) ? 'ml-compare-best' : undefined}
                    >
                      <td className="ml-compare-model-name">
                        <strong>{ms?.modelLabel || ms?.versionTag || '—'}</strong>
                      </td>
                      {def ? (
                        <td className={kWarn ? 'ml-compare-qualitative-warn' : undefined}>{dash}</td>
                      ) : drillCell(
                        'petitKamikaze',
                        ms,
                        (
                          <DiffereBenchmarkMetricValue visual={visualFor(ms, 'petitKamikaze')}>
                            {`${fmtMetricCountBeforeRate(ms, q?.petitKamikazeCount ?? 0)} (${fmtRate(kRate)})`}
                          </DiffereBenchmarkMetricValue>
                        ),
                        `${ML_COMPARE_DENOM_TT.petitKamikazeTh} — Lister les donnes avec Petit kamikaze.`,
                        kWarn ? 'ml-compare-qualitative-warn' : undefined,
                      )}
                      {def ? (
                        <td className={hWarn ? 'ml-compare-qualitative-warn' : undefined}>{dash}</td>
                      ) : drillCell(
                        'honneurOffert',
                        ms,
                        (
                          <DiffereBenchmarkMetricValue visual={visualFor(ms, 'honneurOffert')}>
                            {`${fmtMetricCountBeforeRate(ms, q?.honneurOffertCount ?? 0)} (${fmtRate(hRate)})`}
                          </DiffereBenchmarkMetricValue>
                        ),
                        `${ML_COMPARE_DENOM_TT.honneurOffertTh} — Lister les donnes avec honneur offert.`,
                        hWarn ? 'ml-compare-qualitative-warn' : undefined,
                      )}
                      {drillCell(
                        'pointsDonnes',
                        ms,
                        (
                          <DiffereBenchmarkMetricValue visual={visualFor(ms, 'pointsDonnes')}>
                            {`${fmtMetricCountBeforeRate(ms, q?.pointsDonnesCount ?? 0)} (${fmtRate(q?.pointsDonnesRate ?? 0)})`}
                          </DiffereBenchmarkMetricValue>
                        ),
                        `${ML_COMPARE_DENOM_TT.pointsDonnesTh} — Lister les donnes avec points donnés.`,
                      )}
                      <td className="ml-metric-td" title={ML_COMPARE_DENOM_TT.pointsDonnesGapTh}>
                        <DiffereBenchmarkMetricValue visual={visualFor(ms, 'pointsDonnesGap')}>
                          {fmtPointsDonnesAvgGapCell(ms, q?.pointsDonnesAvgGap ?? 0)}
                        </DiffereBenchmarkMetricValue>
                      </td>
                      {def ? <td>{dash}</td> : drillCell(
                        'petitPrenableOpportunity',
                        ms,
                        (
                          <DiffereBenchmarkMetricValue visual={visualFor(ms, 'petitPrenable')}>
                            {oppPct != null ? `${fmtMetricCountBeforeRate(ms, opp)} (${oppPct}%)` : fmtMetricCountBeforeRate(ms, opp)}
                          </DiffereBenchmarkMetricValue>
                        ),
                        oppDenom > 0
                          ? `${ML_COMPARE_DENOM_TT.petitPrenableTh} — Lister les donnes : ${opp}/${oppDenom} donne(s) avec opportunité.`
                          : `${ML_COMPARE_DENOM_TT.petitPrenableTh} — Lister les donnes avec opportunité petit prenable.`,
                      )}
                      {def ? <td>{dash}</td> : drillCell(
                        'petitPrenableRealized',
                        ms,
                        (
                          <DiffereBenchmarkMetricValue visual={visualFor(ms, 'chasseRealisee')}>
                            {`${fmtMetricCountBeforeRate(ms, realized)} (${fmtRate(chasseRate)})`}
                          </DiffereBenchmarkMetricValue>
                        ),
                        `${ML_COMPARE_DENOM_TT.chasseRealiseeTh} — Lister les donnes où la chasse a mené au Petit posé.`,
                      )}
                      {def ? <td>{dash}</td> : drillCell(
                        'petitChasseIncertaine',
                        ms,
                        (
                          <DiffereBenchmarkMetricValue visual={visualFor(ms, 'chasseIncertaine')}>
                            {`${fmtMetricCountBeforeRate(ms, q?.petitChasseIncertaineCount ?? 0)} (${fmtRate(q?.petitChasseIncertaineRate ?? 0)})`}
                          </DiffereBenchmarkMetricValue>
                        ),
                        `${ML_COMPARE_DENOM_TT.chasseIncertaineTh} — Lister les donnes avec chasse incertaine.`,
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {rows.map((ms, idx) => {
        const slam = ms?.slamStats;
        if (!slam || (slam.attempted <= 0 && slam.succeeded <= 0)) return null;
        return (
          <p key={`slam-${idx}`} style={{ marginTop: 10, fontSize: '0.88rem', opacity: 0.9 }}>
            <strong>{ms?.modelLabel || ms?.versionTag}</strong>
            {' — chelems : '}
            {fmtMetricCountBeforeRate(ms, slam.succeeded)} réussi(s) / {fmtMetricCountBeforeRate(ms, slam.attempted)} tentative(s)
          </p>
        );
      })}
    </div>
  );
}

function MLTrainingTab({
  session,
  section,
  onSectionChange,
}: {
  session: Session;
  section: MLSection;
  onSectionChange: (s: MLSection) => void;
}) {
  const { startTraining } = useApp();
  const [status, setStatus] = useState<MLStatus | null>(null);
  const [jobs, setJobs] = useState<MLJob[]>([]);
  const [models, setModels] = useState<MLModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [selectedJob, setSelectedJob] = useState<MLJob | null>(null);
  const [logViewMode, setLogViewMode] = useState<MLLogViewMode>('raw');
  const [hiddenJobIds, setHiddenJobIds] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(ML_TRAINING_HIDDEN_JOBS_KEY);
      const p = raw ? JSON.parse(raw) : [];
      return Array.isArray(p) ? p.filter((x: unknown) => typeof x === 'string') : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(ML_TRAINING_HIDDEN_JOBS_KEY, JSON.stringify(hiddenJobIds));
    } catch { /* ignore */ }
  }, [hiddenJobIds]);

  const visibleJobs = useMemo(
    () => jobs.filter(j => !hiddenJobIds.includes(j.id)),
    [jobs, hiddenJobIds],
  );
  const selectedJobLogText = selectedJob?.logs || selectedJob?.info || '';
  const selectedJobCleanLogText = useMemo(() => cleanMLJobLogs(selectedJobLogText), [selectedJobLogText]);
  const selectedJobImitationSummary = useMemo(() => parseImitationLogSummary(selectedJobLogText), [selectedJobLogText]);
  const selectedJobSelfplaySummary = useMemo(() => parseSelfplayLogSummary(selectedJobLogText), [selectedJobLogText]);

  const [datasetPreview, setDatasetPreview] = useState<DatasetPreview | null>(null);
  const [magnusCandidates, setMagnusCandidates] = useState<MagnusCandidate[]>([]);
  const [magnusCandidatesLoading, setMagnusCandidatesLoading] = useState(false);
  const [magnusCacheMeta, setMagnusCacheMeta] = useState<{
    stale?: boolean;
    refreshing?: boolean;
    computedAt?: string | null;
  }>({});
  const [magnusQuery, setMagnusQuery] = useState('');
  const [magnusDropdownOpen, setMagnusDropdownOpen] = useState(false);
  const [magnusSearchHits, setMagnusSearchHits] = useState<Array<{
    pseudo: string;
    label: string;
    volumes?: MagnusVolumeBreakdown | null;
    volumesLoading?: boolean;
  }>>([]);
  const [magnusExtraLabels, setMagnusExtraLabels] = useState<Record<string, string>>({});
  const [magnusPlayerVolumes, setMagnusPlayerVolumes] = useState<Record<string, MagnusVolumeBreakdown>>({});
  const magnusIgnoreBlurRef = useRef(false);
  const magnusCacheFetchedRef = useRef(false);
  const magnusVolumeFetchInFlightRef = useRef(new Set<string>());
  const [modelVersions, setModelVersions] = useState<ModelVersionEntry[]>([]);
  const [versionsExpandedTags, setVersionsExpandedTags] = useState<Set<string>>(() => new Set());
  const [versionsFilterName, setVersionsFilterName] = useState('');
  const [versionsFilterMode, setVersionsFilterMode] = useState('');
  const [versionsFilterStatus, setVersionsFilterStatus] = useState('');
  const [versionsFilterRole, setVersionsFilterRole] = useState<'all' | 'attacker' | 'defender' | 'deductions'>('all');
  const [teamChampPhases, setTeamChampPhases] = useState<Array<{ nom: string; debut?: string; fin?: string }>>([]);

  const trainingModesInRegistry = useMemo(() => {
    const s = new Set<string>();
    for (const v of modelVersions) s.add(v.trainingMode);
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [modelVersions]);

  const filteredModelVersions = useMemo(() => {
    const q = versionsFilterName.trim().toLowerCase();
    return modelVersions.filter(v => {
      if (versionsFilterRole !== 'all' && v.modelRole !== versionsFilterRole) return false;
      if (versionsFilterStatus && v.status !== versionsFilterStatus) return false;
      if (versionsFilterMode && v.trainingMode !== versionsFilterMode) return false;
      if (q) {
        const name = (v.notes || '').toLowerCase();
        const tag = v.versionTag.toLowerCase();
        const short = v.versionTag.replace(/^(def|att)-(sp|im|bid|dis|full|rl)-/, '').toLowerCase();
        if (!name.includes(q) && !tag.includes(q) && !short.includes(q)) return false;
      }
      return true;
    });
  }, [modelVersions, versionsFilterName, versionsFilterMode, versionsFilterStatus, versionsFilterRole]);

  const toggleVersionExpanded = useCallback((versionTag: string) => {
    setVersionsExpandedTags(prev => {
      const n = new Set(prev);
      if (n.has(versionTag)) n.delete(versionTag);
      else n.add(versionTag);
      return n;
    });
  }, []);
  const selectedJobDisplayName = selectedJob ? mlJobDisplayName(selectedJob, modelVersions) : '';

  const deployedSortedVersions = useMemo(() => {
    const all = modelVersions.filter(v => v.status === 'deployed');
    const orderSlot = (s: string | null) => (s === 'production' ? 0 : 1);
    const orderRole = (r: string) => (r === 'defender' ? 0 : r === 'attacker' ? 1 : 2);
    return [...all].sort(
      (a, b) =>
        orderSlot(a.deployedSlot) - orderSlot(b.deployedSlot) ||
        orderRole(a.modelRole) - orderRole(b.modelRole),
    );
  }, [modelVersions]);

  type TrainMode = 'full' | 'reward_only' | 'rlhf_only' | 'imitation' | 'deductions' | 'bidding' | 'discard' | 'selfplay';

  // Refonte 2026-04 : sélection par compétition (idcompetition) + freedefi élite
  // Refonte 2026-05 : top divisions CIA + sources différé individuel.
  type PlayerSelectionMode =
    | 'competition_team'
    | 'competition_team_top_teams'
    | 'competition_cia'
    | 'competition_cia_top_divisions'
    | 'competition_quadrette'
    | 'freedefi_elite'
    | 'freedefi_top_live'
    | 'differe_indiv_top_per_donne'
    | 'differe_indiv_top_general_role'
    | 'bot1_distill'
    | 'bot1_soft_distill'
    | 'magnus_attacker';
  type CompetitionEtat = 'fini' | 'abandon' | 'encours' | 'annule';
  type EliteSource = 'live' | 'differe_general' | 'differe_general_attack' | 'differe_general_defense';
  type TeamDuplicateStrategy = 'random' | 'attack_best' | 'defense_best';
  type DiffSubtype = 'mixte' | 'maxi' | 'attaque' | 'attaqueSC' | 'maxiAtt' | 'defense' | 'maxiDef';

  const ATTACKER_ONLY_MODES: PlayerSelectionMode[] = [
    'competition_cia', 'competition_cia_top_divisions', 'magnus_attacker',
  ];
  const isAttackerOnly = (mode: PlayerSelectionMode) => ATTACKER_ONLY_MODES.includes(mode);
  const isCompetitionMode = (mode: PlayerSelectionMode) =>
    mode === 'competition_team'
    || mode === 'competition_team_top_teams'
    || mode === 'competition_cia'
    || mode === 'competition_cia_top_divisions'
    || mode === 'competition_quadrette';
  const isTeamTopTeamsMode = (mode: PlayerSelectionMode) =>
    mode === 'competition_team_top_teams' || mode === 'competition_cia_top_divisions';
  const isFreedefiMode = (mode: PlayerSelectionMode) =>
    mode === 'freedefi_elite' || mode === 'freedefi_top_live';
  const isDiffereMode = (mode: PlayerSelectionMode) =>
    mode === 'differe_indiv_top_per_donne' || mode === 'differe_indiv_top_general_role';
  const isBot1DistillMode = (mode: PlayerSelectionMode) => mode === 'bot1_distill';
  const isBot1SoftDistillMode = (mode: PlayerSelectionMode) => mode === 'bot1_soft_distill';
  const isMagnusMode = (mode: PlayerSelectionMode) => mode === 'magnus_attacker';
  const usesBot1Distillation = (mode: PlayerSelectionMode) =>
    mode === 'bot1_distill' || mode === 'bot1_soft_distill';

  const DIFF_SUBTYPES_ATTACKER: DiffSubtype[] = ['mixte', 'maxi', 'attaque', 'attaqueSC', 'maxiAtt'];
  const DIFF_SUBTYPES_DEFENDER: DiffSubtype[] = ['mixte', 'maxi', 'defense', 'maxiDef'];
  const allowedDiffSubtypes = (role: 'attacker' | 'defender'): DiffSubtype[] =>
    role === 'attacker' ? DIFF_SUBTYPES_ATTACKER : DIFF_SUBTYPES_DEFENDER;

  const [trainConfig, setTrainConfig] = useState({
    mode: 'full' as TrainMode,
    timesteps: 200000,
    blend: 0.5,
    envs: 12,
    rewardEpochs: 30,
    dataTable: 'matchs',
    maxGames: 5000,
    featureVersion: 'v5',
    imEpochs: 50,
    imBatchSize: 128,
    imLr: 0.001,
    imEntameWeight: 3.0,
    // Suit symmetry augmentation: on by default since the comparative
    // analysis (Châtaigne / Loir / Nénuphar runs) showed it consistently
    // helps without downsides. v5 attacker keeps it too — safe.
    suitAugmentation: true,
    // Multi-regime position gate (Aile de Nénuphar plateau analysis):
    // adds ~33k params conditioned on (is_entame, is_before_taker,
    // taker_played_this_trick). v7+ only — silently ignored for v5.
    // On by default for the same empirical reason as suitAugmentation.
    regimeGate: true,
    // Phase 1 — soft targets via carte-equivalence classes (attacker only).
    cardEquivalence: false,
    lowRankLimit: 5 as 5 | 6 | 7,
    equivEnableTypeB: true,
    valetCavalierEquiv: false,
    // BeliefPolicy backbone width. 512 = standard (~1.8M params). 1024 =
    // capacity-plateau test (~7M params). Inference auto-detects from the
    // checkpoint, so choosing anything other than 512 is transparent in
    // production. v7+ only.
    imHiddenDim: 512 as 256 | 384 | 512 | 768 | 1024,
    // ── Bot1 distillation (mode playerSelectionMode='bot1_distill') ──
    botDistillNGames: 5000,
    botDistillSeed: 42,
    botDistillWorkers: 16,
    botDistillMinContract: 0,
    // ── Bot1 SOFT distillation (mode bot1_soft_distill) ──
    // Mix Bot1 with humans expert games at the example level. Solves the
    // pure-Bot1 plateau (~46% val acc) by keeping the human-style backbone
    // and injecting Bot1 corrections on the hard regimes.
    botSoftDistillAlpha: 0.5,
    botSoftDistillHumansMode: 'differe_indiv_top_general_role' as
      'differe_indiv_top_general_role' | 'competition_team_top_teams',
    modelRole: 'defender' as 'defender' | 'attacker',
    biddingDeals: 5000,
    biddingSimsPerBid: 8,
    discardDeals: 3000,
    discardCandidates: 20,
    baseVersion: '' as string,
    selfplayIterations: 30,
    selfplayGamesPerIter: 2000,
    opponentVersion: '' as string,
    selfplayPreset: 'standard' as 'conservative' | 'standard' | 'aggressive' | 'advanced',
    selfplayLr: 3e-5,
    selfplayPpoEpochs: 3,
    selfplayBatchSize: 256,
    selfplayClipEps: 0.2,
    selfplayEntropyCoef: 0.03,
    selfplayTargetEntropy: 0.5,
    selfplayTemperatureStart: 1.0,
    selfplayTemperatureEnd: 0.5,
    selfplayEarlyStopPatience: 5,
    selfplayEvalGames: 500,
    selfplayEvalPool: [] as string[],
    /** Min fraction Garde+ during self-play collection (0 = natural Chavey). */
    selfplayMinGardeFraction: 0.75,
    /**
     * Coordinated-cheater defender curriculum (design_self_play_piste_a_2026_06_29.md
     * §5.3): only meaningful when modelRole === 'attacker' — hardens the attacker
     * against a frozen defender whose 3 seats know each other's exact hands.
     */
    coordinatedCheaterDefense: false,
    /** Optional DiscardPolicy path for self-play collection (Petite/Garde). Empty = smart_discard. */
    selfplayDiscardModel: '',
    // Refonte 2026-04 : nouveaux champs de sélection
    playerSelectionMode: 'competition_team_top_teams' as PlayerSelectionMode,
    competitionEtats: ['fini'] as CompetitionEtat[],
    elitePoolSize: 200,
    eliteSource: 'differe_general' as EliteSource,
    teamDivisionHierarchyMax: 2,
    teamPhaseMinLabel: '',
    teamPhaseMaxLabel: '',
    teamRequireBoth: true,
    teamMaxGamesPerMatch: 8,
    teamOneTablePerDeal: false,
    teamDuplicateStrategy: 'random' as TeamDuplicateStrategy,
    sampleSeed: 'webtarot-team-top',
    // ─── Différé individuel (Refonte 2026-05) ────────────────────────
    differeTopPerDonne: 3,
    differeMinParticipantsPerDonne: 0,
    differeSubtypes: [] as DiffSubtype[],
    differeStrictRoleFilter: true,
    magnusPseudo: '',
    magnusPseudos: [] as string[],
    magnusSourceDiffereAttaque: true,
    magnusSourceDiffereMixteMaxi: true,
    magnusSourceCia: true,
    magnusSourceFreedefi: true,
    dateMonths: 0,
    trainDateFrom: '',
    trainDateTo: '',
    timeoutHours: 0,
    timeoutMinutes: 0,
    // Deductions-specific tuning
    maskStrategy: 'soft' as 'none' | 'soft' | 'hard',
    maskCertainWeight: 0.1,
    initialDimWeight: 1.0 / 18.0,
    consistencyWeight: 0.1,
    /** Époques sans amélioration de la val loss avant early stop (imitation + déductions). */
    imPatience: 10,
    /** Multiplicateur BCE sur les assertions has_mariage_* (≥ 1, défaut 1.5). */
    mariageDimBoost: 1.5,
    /** Objectif duplicate expérimental (désactivé par défaut). */
    duplicateObjective: false,
    duplicateImportIds: '',
    duplicateEvalDeals: 40,
    duplicateTrainFraction: 0.25,
    duplicateNormalizeFieldSize: false,
    duplicateFrontierReplay: false,
    duplicateFrontierFraction: 0.2,
    duplicateFrontierDeltaRaw: 30,
  });

  const [timeoutEstimate, setTimeoutEstimate] = useState<{ timeoutSeconds: number; estimatedMinutes: number } | null>(null);

  // Self-play presets — values reflect the analysis of the run "Croc de Lune"
  // (entropy too high, late-iteration regression, single-opponent over-fit).
  type SelfplayPresetKey = 'conservative' | 'standard' | 'aggressive';
  const SELFPLAY_PRESETS: Record<SelfplayPresetKey, {
    label: string;
    desc: string;
    iterations: number;
    gamesPerIter: number;
    lr: number;
    ppoEpochs: number;
    batchSize: number;
    clipEps: number;
    entropyCoef: number;
    targetEntropy: number;
    temperatureStart: number;
    temperatureEnd: number;
    earlyStopPatience: number;
    evalGames: number;
  }> = {
    conservative: {
      label: 'Conservateur',
      desc: 'Petits pas, early-stop strict, éval large. Idéal pour modèles déjà bons.',
      iterations: 20,
      gamesPerIter: 2000,
      lr: 1e-5,
      ppoEpochs: 3,
      batchSize: 256,
      clipEps: 0.15,
      entropyCoef: 0.01,
      targetEntropy: 0.4,
      temperatureStart: 1.0,
      temperatureEnd: 0.3,
      earlyStopPatience: 3,
      evalGames: 1000,
    },
    standard: {
      label: 'Standard',
      desc: 'Réglages corrigés (entropie 0.5 au lieu de 0.91 auto). Bon point de départ.',
      iterations: 25,
      gamesPerIter: 2000,
      lr: 2e-5,
      ppoEpochs: 3,
      batchSize: 256,
      clipEps: 0.2,
      entropyCoef: 0.02,
      targetEntropy: 0.5,
      temperatureStart: 1.0,
      temperatureEnd: 0.4,
      earlyStopPatience: 5,
      evalGames: 500,
    },
    aggressive: {
      label: 'Agressif',
      desc: 'Apprentissage rapide, plus de risque de divergence. Pour modèles loin de l\'optimum.',
      iterations: 30,
      gamesPerIter: 2500,
      lr: 5e-5,
      ppoEpochs: 4,
      batchSize: 256,
      clipEps: 0.25,
      entropyCoef: 0.05,
      targetEntropy: 0.6,
      temperatureStart: 1.2,
      temperatureEnd: 0.4,
      earlyStopPatience: 7,
      evalGames: 500,
    },
  };

  const applySelfplayPreset = useCallback((preset: SelfplayPresetKey | 'advanced') => {
    if (preset === 'advanced') {
      setTrainConfig(c => ({ ...c, selfplayPreset: 'advanced' }));
      return;
    }
    const p = SELFPLAY_PRESETS[preset];
    setTrainConfig(c => ({
      ...c,
      selfplayPreset: preset,
      selfplayIterations: p.iterations,
      selfplayGamesPerIter: p.gamesPerIter,
      selfplayLr: p.lr,
      selfplayPpoEpochs: p.ppoEpochs,
      selfplayBatchSize: p.batchSize,
      selfplayClipEps: p.clipEps,
      selfplayEntropyCoef: p.entropyCoef,
      selfplayTargetEntropy: p.targetEntropy,
      selfplayTemperatureStart: p.temperatureStart,
      selfplayTemperatureEnd: p.temperatureEnd,
      selfplayEarlyStopPatience: p.earlyStopPatience,
      selfplayEvalGames: p.evalGames,
    }));
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    adminFetch(`${BACKEND_URL}/api/competition/team-champ`, session.pseudo, { signal: ctrl.signal })
      .then(r => r.json())
      .then(data => {
        const phases = data?.data?.phases;
        if (Array.isArray(phases)) {
          setTeamChampPhases(phases.map((p: any) => ({ nom: p.nom, debut: p.debut, fin: p.fin })));
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [session.pseudo]);

  // Estimate timeout whenever key params change
  useEffect(() => {
    if (trainConfig.mode !== 'imitation' && trainConfig.mode !== 'deductions') return;
    const ctrl = new AbortController();
    adminFetch(`${BACKEND_URL}/api/admin/ml/estimate-timeout`, session.pseudo, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ maxGames: trainConfig.maxGames, imEpochs: trainConfig.imEpochs }),
      signal: ctrl.signal,
    })
      .then(r => r.json())
      .then(data => setTimeoutEstimate(data))
      .catch(() => {});
    return () => ctrl.abort();
  }, [trainConfig.mode, trainConfig.maxGames, trainConfig.imEpochs]);

  // Auto-fix model_role when switching to attacker-only mode
  useEffect(() => {
    if (isAttackerOnly(trainConfig.playerSelectionMode) && trainConfig.modelRole !== 'attacker') {
      setTrainConfig(c => ({ ...c, modelRole: 'attacker' }));
    }
    if (isMagnusMode(trainConfig.playerSelectionMode)) {
      setTrainConfig(c => ({
        ...c,
        modelRole: 'attacker',
        maxGames: c.maxGames < 8000 ? 10000 : c.maxGames,
        featureVersion: 'v7',
        cardEquivalence: false,
        suitAugmentation: true,
        regimeGate: true,
      }));
    }
  }, [trainConfig.playerSelectionMode]);

  // Sync magnusPseudo CSV ↔ magnusPseudos array
  useEffect(() => {
    if (!isMagnusMode(trainConfig.playerSelectionMode)) return;
    const csv = (trainConfig.magnusPseudos || []).join(',');
    if (csv !== (trainConfig.magnusPseudo || '')) {
      setTrainConfig(c => ({ ...c, magnusPseudo: csv }));
    }
  }, [trainConfig.playerSelectionMode, trainConfig.magnusPseudos]);

  const magnusSourcesState = useMemo(() => ({
    differeAttaque: trainConfig.magnusSourceDiffereAttaque,
    differeMixteMaxi: trainConfig.magnusSourceDiffereMixteMaxi,
    cia: trainConfig.magnusSourceCia,
    freedefiAttacker: trainConfig.magnusSourceFreedefi,
  }), [
    trainConfig.magnusSourceDiffereAttaque,
    trainConfig.magnusSourceDiffereMixteMaxi,
    trainConfig.magnusSourceCia,
    trainConfig.magnusSourceFreedefi,
  ]);

  const loadMagnusCandidates = useCallback((opts?: { forceRefresh?: boolean }) => {
    if (!isMagnusMode(trainConfig.playerSelectionMode)) return;
    const ctrl = new AbortController();
    const showSpinner = magnusCandidates.length === 0;
    if (showSpinner) setMagnusCandidatesLoading(true);
    const params = new URLSearchParams({
      limit: '20',
      minParticipationRatio: '0.8',
    });
    if (opts?.forceRefresh) params.set('refresh', '1');
    adminFetch(
      `${BACKEND_URL}/api/admin/ml/magnus-candidates?${params}`,
      session.pseudo,
      { signal: ctrl.signal },
    )
      .then(r => r.json())
      .then(data => {
        if (ctrl.signal.aborted) return;
        const list: MagnusCandidate[] = Array.isArray(data.candidates) ? data.candidates : [];
        setMagnusCandidates(list);
        setMagnusCacheMeta({
          stale: !!data.stale,
          refreshing: !!data.refreshing,
          computedAt: data.computedAt ?? null,
        });
        // Seed per-player volume map from cache
        setMagnusPlayerVolumes(prev => {
          const next = { ...prev };
          for (const c of list) {
            if (c.volumes) next[c.pseudo.toLowerCase()] = c.volumes;
          }
          return next;
        });
        // Poll while refreshing / empty first compute (background, can take 1–2 min)
        if (data.refreshing || data.stale || list.length === 0) {
          let attempts = 0;
          const poll = () => {
            if (ctrl.signal.aborted) return;
            attempts += 1;
            if (attempts > 24) return; // ~3 min
            adminFetch(
              `${BACKEND_URL}/api/admin/ml/magnus-candidates?limit=20&minParticipationRatio=0.8`,
              session.pseudo,
            )
              .then(r => r.json())
              .then(d2 => {
                if (ctrl.signal.aborted) return;
                const list2: MagnusCandidate[] = Array.isArray(d2.candidates) ? d2.candidates : [];
                if (list2.length > 0) {
                  setMagnusCandidates(list2);
                  setMagnusCandidatesLoading(false);
                  setMagnusPlayerVolumes(prev => {
                    const next = { ...prev };
                    for (const c of list2) {
                      if (c.volumes) next[c.pseudo.toLowerCase()] = c.volumes;
                    }
                    return next;
                  });
                }
                setMagnusCacheMeta({
                  stale: !!d2.stale,
                  refreshing: !!d2.refreshing,
                  computedAt: d2.computedAt ?? null,
                });
                if (d2.refreshing || list2.length === 0) {
                  window.setTimeout(poll, 8000);
                }
              })
              .catch(() => {
                window.setTimeout(poll, 8000);
              });
          };
          window.setTimeout(poll, 5000);
        }
      })
      .catch(() => {
        if (!ctrl.signal.aborted && magnusCandidates.length === 0) setMagnusCandidates([]);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setMagnusCandidatesLoading(false);
      });
    return () => ctrl.abort();
  }, [trainConfig.playerSelectionMode, session.pseudo, magnusCandidates.length]);

  // Prefetch cache when entering Magnus mode; refresh on autocomplete open if stale (>7j côté serveur)
  useEffect(() => {
    if (!isMagnusMode(trainConfig.playerSelectionMode)) {
      magnusCacheFetchedRef.current = false;
      return;
    }
    if (magnusCacheFetchedRef.current) return;
    magnusCacheFetchedRef.current = true;
    return loadMagnusCandidates();
  }, [trainConfig.playerSelectionMode, loadMagnusCandidates]);

  const ensureMagnusPlayerVolumes = useCallback((pseudo: string, label?: string) => {
    const key = pseudo.trim().toLowerCase();
    if (!key) return;
    if (magnusPlayerVolumes[key]) return;
    if (magnusVolumeFetchInFlightRef.current.has(key)) return;
    magnusVolumeFetchInFlightRef.current.add(key);
    const params = new URLSearchParams({ pseudo });
    if (label) params.set('label', label);
    adminFetch(
      `${BACKEND_URL}/api/admin/ml/magnus-player-volumes?${params}`,
      session.pseudo,
    )
      .then(r => r.json())
      .then(data => {
        if (!data?.volumes) return;
        setMagnusPlayerVolumes(prev => ({ ...prev, [key]: data.volumes }));
        setMagnusSearchHits(prev => prev.map(h => (
          h.pseudo.toLowerCase() === key
            ? { ...h, volumes: data.volumes, volumesLoading: false }
            : h
        )));
      })
      .catch(() => {
        setMagnusSearchHits(prev => prev.map(h => (
          h.pseudo.toLowerCase() === key ? { ...h, volumesLoading: false } : h
        )));
      })
      .finally(() => {
        magnusVolumeFetchInFlightRef.current.delete(key);
      });
  }, [magnusPlayerVolumes, session.pseudo]);

  // Free-text player search fallback (outside top 20)
  useEffect(() => {
    if (!isMagnusMode(trainConfig.playerSelectionMode)) return;
    const q = magnusQuery.trim();
    if (q.length < 2) {
      setMagnusSearchHits([]);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      fetch(
        `${BACKEND_URL}/api/players/search?q=${encodeURIComponent(q)}&limit=8&compet=true`,
        { signal: ctrl.signal },
      )
        .then(r => (r.ok ? r.json() : { players: [] }))
        .then(data => {
          const players = data.players || data.results || data || [];
          const list = (Array.isArray(players) ? players : []).map((p: any) => ({
            pseudo: String(p.pseudo || p.pseudo_encoded || ''),
            label: String(p.pseudoDisplay || p.pseudo_display || p.pseudo_clean || p.pseudo || ''),
            volumes: null as MagnusVolumeBreakdown | null,
            volumesLoading: false,
          })).filter((p: { pseudo: string }) => p.pseudo);
          setMagnusSearchHits(list);
        })
        .catch(() => setMagnusSearchHits([]));
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [magnusQuery, trainConfig.playerSelectionMode]);

  // Quand la recherche ne laisse qu’un seul hit hors top20 → volumes à la volée
  useEffect(() => {
    if (!isMagnusMode(trainConfig.playerSelectionMode)) return;
    if (!magnusDropdownOpen) return;
    const selected = new Set((trainConfig.magnusPseudos || []).map(p => p.toLowerCase()));
    const q = magnusQuery.trim().toLowerCase();
    const fromTop = magnusCandidates.filter(c => {
      if (!c.pseudo || selected.has(c.pseudo.toLowerCase())) return false;
      if (!q) return true;
      return c.label.toLowerCase().includes(q) || c.pseudo.toLowerCase().includes(q);
    });
    const fromSearch = magnusSearchHits.filter(h => {
      if (!h.pseudo || selected.has(h.pseudo.toLowerCase())) return false;
      return !fromTop.some(c => c.pseudo.toLowerCase() === h.pseudo.toLowerCase());
    });
    if (fromSearch.length === 1 && fromTop.length === 0) {
      const only = fromSearch[0];
      if (!magnusPlayerVolumes[only.pseudo.toLowerCase()] && !only.volumesLoading) {
        setMagnusSearchHits(prev => prev.map(h => (
          h.pseudo === only.pseudo ? { ...h, volumesLoading: true } : h
        )));
        ensureMagnusPlayerVolumes(only.pseudo, only.label);
      }
    }
  }, [
    magnusQuery,
    magnusSearchHits,
    magnusCandidates,
    magnusDropdownOpen,
    trainConfig.magnusPseudos,
    trainConfig.playerSelectionMode,
    magnusPlayerVolumes,
    ensureMagnusPlayerVolumes,
  ]);

  // Volumes pour joueurs sélectionnés hors cache
  useEffect(() => {
    if (!isMagnusMode(trainConfig.playerSelectionMode)) return;
    for (const pseudo of trainConfig.magnusPseudos || []) {
      const key = pseudo.toLowerCase();
      if (!magnusPlayerVolumes[key]) {
        const label = magnusExtraLabels[key] || pseudo.replace(/¶/g, ' ');
        ensureMagnusPlayerVolumes(pseudo, label);
      }
    }
  }, [
    trainConfig.magnusPseudos,
    trainConfig.playerSelectionMode,
    magnusPlayerVolumes,
    magnusExtraLabels,
    ensureMagnusPlayerVolumes,
  ]);

  const magnusLabelByPseudo = useMemo(() => {
    const m = new Map<string, string>();
    for (const [k, v] of Object.entries(magnusExtraLabels)) m.set(k, v);
    for (const c of magnusCandidates) m.set(c.pseudo.toLowerCase(), c.label);
    for (const h of magnusSearchHits) m.set(h.pseudo.toLowerCase(), h.label);
    return m;
  }, [magnusCandidates, magnusSearchHits, magnusExtraLabels]);

  const volumesForPseudo = useCallback((pseudo: string): MagnusVolumeBreakdown | null => {
    const key = pseudo.toLowerCase();
    if (magnusPlayerVolumes[key]) return magnusPlayerVolumes[key];
    const cand = magnusCandidates.find(c => c.pseudo.toLowerCase() === key);
    return cand?.volumes ?? null;
  }, [magnusPlayerVolumes, magnusCandidates]);

  const addMagnusPseudo = (pseudo: string, label?: string) => {
    const p = pseudo.trim();
    if (!p) return;
    magnusIgnoreBlurRef.current = false;
    if (label) {
      setMagnusExtraLabels(prev => ({ ...prev, [p.toLowerCase()]: label }));
    }
    ensureMagnusPlayerVolumes(p, label);
    setTrainConfig(c => {
      const cur = c.magnusPseudos || [];
      if (cur.some(x => x.toLowerCase() === p.toLowerCase())) return c;
      if (cur.length >= MAGNUS_MAX_PSEUDOS) return c;
      const next = [...cur, p];
      return { ...c, magnusPseudos: next, magnusPseudo: next.join(',') };
    });
    setMagnusQuery('');
    setMagnusDropdownOpen(false);
  };

  const pickMagnusCandidate = (pseudo: string, label?: string) => {
    magnusIgnoreBlurRef.current = true;
    addMagnusPseudo(pseudo, label);
  };

  const removeMagnusPseudo = (pseudo: string) => {
    setTrainConfig(c => {
      const next = (c.magnusPseudos || []).filter(x => x.toLowerCase() !== pseudo.toLowerCase());
      return { ...c, magnusPseudos: next, magnusPseudo: next.join(',') };
    });
  };

  const openMagnusDropdown = () => {
    setMagnusDropdownOpen(true);
    // Au clic/focus autocomplete : si cache stale, relance refresh fond
    if (magnusCacheMeta.stale || magnusCandidates.length === 0) {
      loadMagnusCandidates({ forceRefresh: magnusCacheMeta.stale });
    }
  };

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      adminFetch(`${BACKEND_URL}/api/admin/ml/status`, session.pseudo).then(r => r.json()),
      adminFetch(`${BACKEND_URL}/api/admin/ml/jobs`, session.pseudo).then(r => r.json()),
      adminFetch(`${BACKEND_URL}/api/admin/ml/models`, session.pseudo).then(r => r.json()),
      adminFetch(`${BACKEND_URL}/api/admin/ml/versions`, session.pseudo).then(r => r.json()).catch(() => ({ versions: [] })),
    ])
      .then(([s, j, m, mv]) => {
        setStatus(s);
        setJobs(j.jobs || []);
        setModels(m.models || []);
        setModelVersions(mv.versions || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [session.pseudo]);

  useEffect(() => { refresh(); }, [refresh]);

  // Silent auto-refresh every 30s for jobs, status, and versions
  useEffect(() => {
    if (!['jobs', 'overview', 'versions', 'history', 'compare'].includes(section)) return;
    const interval = setInterval(() => {
      Promise.all([
        adminFetch(`${BACKEND_URL}/api/admin/ml/status`, session.pseudo).then(r => r.json()).catch(() => null),
        adminFetch(`${BACKEND_URL}/api/admin/ml/jobs`, session.pseudo).then(r => r.json()).catch(() => null),
        adminFetch(`${BACKEND_URL}/api/admin/ml/versions`, session.pseudo).then(r => r.json()).catch(() => null),
        adminFetch(`${BACKEND_URL}/api/admin/ml/sync-jobs`, session.pseudo, { method: 'POST' }).catch(() => null),
      ]).then(([s, j, mv]) => {
        if (s) setStatus(s);
        if (j?.jobs) setJobs(j.jobs);
        if (mv?.versions) setModelVersions(mv.versions);
      });
    }, 30_000);
    return () => clearInterval(interval);
  }, [section, session.pseudo]);

  const computeDateFrom = () =>
    trainConfig.dateMonths > 0
      ? new Date(Date.now() - trainConfig.dateMonths * 30 * 86400000).toISOString().slice(0, 10)
      : '';

  const ML_DIFFERE_DATE_FLOOR = '2016-01-01';
  const ML_MAX_TRAIN_DATE_SPAN_MS = 5 * 366 * 24 * 3600 * 1000;

  const requiresDiffereTopPerDonneExplicitDates =
    trainConfig.playerSelectionMode === 'differe_indiv_top_per_donne'
    && (trainConfig.mode === 'imitation' || trainConfig.mode === 'deductions');

  const validateDiffereTopPerDonneDates = (): { ok: true; needsWideAck: boolean } | { ok: false; message: string } => {
    if (!requiresDiffereTopPerDonneExplicitDates) return { ok: true, needsWideAck: false };
    const df = trainConfig.trainDateFrom.trim();
    const dt = trainConfig.trainDateTo.trim();
    if (!df && !dt) {
      return {
        ok: false,
        message: `Indiquez au moins une borne (début de tournoi min. ou max., filtre API sur liste.debut). Données disponibles depuis ${ML_DIFFERE_DATE_FLOOR}.`,
      };
    }
    if (df && df < ML_DIFFERE_DATE_FLOOR) {
      return { ok: false, message: `La date minimale ne peut pas être avant ${ML_DIFFERE_DATE_FLOOR}.` };
    }
    if (dt && dt < ML_DIFFERE_DATE_FLOOR) {
      return { ok: false, message: `La date maximale ne peut pas être avant ${ML_DIFFERE_DATE_FLOOR}.` };
    }
    if (df && dt) {
      const t0 = Date.parse(`${df}T00:00:00`);
      const t1 = Date.parse(`${dt}T23:59:59`);
      if (Number.isNaN(t0) || Number.isNaN(t1)) {
        return { ok: false, message: 'Format de date invalide (AAAA-MM-JJ).' };
      }
      if (t1 < t0) {
        return { ok: false, message: 'La date maximale doit être postérieure ou égale à la date minimale.' };
      }
      if (t1 - t0 > ML_MAX_TRAIN_DATE_SPAN_MS) return { ok: true, needsWideAck: true };
    }
    return { ok: true, needsWideAck: false };
  };

  const resolveTrainApiDates = () => {
    if (requiresDiffereTopPerDonneExplicitDates) {
      return {
        dateFrom: trainConfig.trainDateFrom.trim(),
        dateTo: trainConfig.trainDateTo.trim(),
      };
    }
    return { dateFrom: computeDateFrom(), dateTo: '' };
  };

  const phaseOptions = teamChampPhases.length > 0
    ? teamChampPhases
    : Array.from({ length: 70 }, (_, i) => ({ nom: `Phase ${i + 1}` }));
  const formatPhaseOption = (phase: { nom: string; debut?: string; fin?: string }) => {
    const debut = phase.debut ? String(phase.debut).slice(0, 10) : '';
    const fin = phase.fin ? String(phase.fin).slice(0, 10) : '';
    return debut || fin ? `${phase.nom} (${debut || '?'} - ${fin || '?'})` : phase.nom;
  };

  const handlePreviewDataset = async (opts: { computeExactCount?: boolean } = {}) => {
    const computeExactCount = opts.computeExactCount === true;
    const v = validateDiffereTopPerDonneDates();
    if (!v.ok) {
      flash('err', v.message);
      return;
    }
    let wideDateRangeAck = false;
    if (v.needsWideAck) {
      if (!confirm(
        'La plage entre la date minimale et la date maximale dépasse 5 ans. '
        + 'Les requêtes SQL peuvent être très lourdes. Continuer l’aperçu ?',
      )) return;
      wideDateRangeAck = true;
    }
    setBusy(computeExactCount ? 'preview-count' : 'preview');
    try {
      const { dateFrom, dateTo } = resolveTrainApiDates();
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/preview-dataset`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({
          playerSelectionMode: trainConfig.playerSelectionMode,
          maxGames: trainConfig.maxGames,
          competitionEtats: trainConfig.competitionEtats,
          elitePoolSize: trainConfig.elitePoolSize,
          eliteSource: trainConfig.eliteSource,
          teamDivisionHierarchyMax: trainConfig.teamDivisionHierarchyMax,
          teamPhaseMinLabel: trainConfig.teamPhaseMinLabel,
          teamPhaseMaxLabel: trainConfig.teamPhaseMaxLabel,
          teamRequireBoth: trainConfig.teamRequireBoth,
          teamMaxGamesPerMatch: trainConfig.teamMaxGamesPerMatch,
          teamOneTablePerDeal: trainConfig.teamOneTablePerDeal,
          teamDuplicateStrategy: trainConfig.teamDuplicateStrategy,
          sampleSeed: trainConfig.sampleSeed,
          differeTopPerDonne: trainConfig.differeTopPerDonne,
          differeMinParticipantsPerDonne: trainConfig.differeMinParticipantsPerDonne,
          differeSubtypes: trainConfig.differeSubtypes,
          dateFrom,
          dateTo,
          ...(wideDateRangeAck ? { wideDateRangeAck: true } : {}),
          modelRole: trainConfig.modelRole,
          computeExactCount,
          magnusPseudos: trainConfig.magnusPseudos || [],
          magnusPseudo: (trainConfig.magnusPseudos || []).join(',') || trainConfig.magnusPseudo.trim(),
          magnusSources: {
            differeAttaque: trainConfig.magnusSourceDiffereAttaque,
            differeMixteMaxi: trainConfig.magnusSourceDiffereMixteMaxi,
            cia: trainConfig.magnusSourceCia,
            freedefiAttacker: trainConfig.magnusSourceFreedefi,
          },
        }),
      });
      const data = await res.json();
      setDatasetPreview(data);
    } catch (err: any) {
      flash('err', `Erreur aperçu : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const flash = (type: 'ok' | 'err', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const formatModelVersionLabel = (v: ModelVersionEntry) => {
    const slot = v.deployedSlot ? ` — ${v.deployedSlot}` : '';
    const role = v.modelRole === 'attacker' ? 'ATT' : v.modelRole === 'defender' ? 'DEF' : 'DED';
    return `${v.notes || v.versionTag} (${role}, ${v.trainingMode}${slot})`;
  };

  const isPlayModelPath = (path: string) => path.endsWith('.pt') || path.endsWith('.pth');

  const isEligiblePlayBaseVersion = (v: ModelVersionEntry) => {
    if (v.deployedSlot === 'production' || v.deployedSlot === 'beta') return true;
    // Trained/deployed rows may lack s3ModelPath (legacy seed, sync lag) — backend resolves it.
    if (v.status === 'trained' || v.status === 'evaluated' || v.status === 'deployed') return true;
    return !!v.s3ModelPath && isPlayModelPath(v.s3ModelPath);
  };

  const isEligiblePpoBaseVersion = (v: ModelVersionEntry) => {
    if (v.deployedSlot === 'production' || v.deployedSlot === 'beta') return true;
    return !!v.s3ModelPath && v.s3ModelPath.endsWith('.zip');
  };

  const sortBaseVersionOptions = (a: ModelVersionEntry, b: ModelVersionEntry) => {
    const aDeployed = a.deployedSlot === 'production' ? 0 : a.deployedSlot === 'beta' ? 1 : 2;
    const bDeployed = b.deployedSlot === 'production' ? 0 : b.deployedSlot === 'beta' ? 1 : 2;
    if (aDeployed !== bDeployed) return aDeployed - bDeployed;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  };

  const getBaseVersionOptions = (mode: TrainMode = trainConfig.mode, role = trainConfig.modelRole) => {
    if (mode === 'deductions' || mode === 'reward_only') return [];
    const expectedRole = mode === 'bidding' || mode === 'discard' ? 'defender' : role;
    const needsPpoPolicy = mode === 'full' || mode === 'rlhf_only';
    const eligible = (v: ModelVersionEntry) =>
      needsPpoPolicy ? isEligiblePpoBaseVersion(v) : isEligiblePlayBaseVersion(v);

    const options = modelVersions.filter(v => v.modelRole === expectedRole && eligible(v));

    // Belt-and-suspenders: always surface slot-deployed models for play roles.
    if (expectedRole === 'defender' || expectedRole === 'attacker') {
      for (const slot of ['production', 'beta'] as const) {
        const deployed = mlDeployedVersionEntry(modelVersions, slot, expectedRole);
        if (deployed && !options.some(v => v.versionTag === deployed.versionTag)) {
          options.push(deployed);
        }
      }
    }

    return options.sort(sortBaseVersionOptions);
  };

  const getOpponentVersionOptions = () => {
    if (trainConfig.mode !== 'selfplay') return [];
    const opponentRole = trainConfig.modelRole === 'attacker' ? 'defender' : 'attacker';
    const options = modelVersions.filter(v => v.modelRole === opponentRole && isEligiblePlayBaseVersion(v));

    for (const slot of ['production', 'beta'] as const) {
      const deployed = mlDeployedVersionEntry(modelVersions, slot, opponentRole);
      if (deployed && !options.some(v => v.versionTag === deployed.versionTag)) {
        options.push(deployed);
      }
    }

    return options.sort(sortBaseVersionOptions);
  };

  const handleCollectElite = async () => {
    setBusy('collect');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/collect-elite`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({
          topN: trainConfig.elitePoolSize,
          source: trainConfig.eliteSource,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `${data.count} joueurs élite collectés et uploadés sur S3`);
    } catch (err: any) {
      flash('err', `Erreur collecte : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleLaunch = async () => {
    const isDataFromStock = trainConfig.mode === 'imitation' || trainConfig.mode === 'deductions';
    const msg = trainConfig.mode === 'deductions'
      ? 'Lancer l\'entraînement DeductionMLP ? (téléchargement des parties + GPU)'
      : isDataFromStock
        ? 'Lancer un entraînement par imitation ? (téléchargement données + GPU)'
        : 'Lancer un entraînement ? (facturation GPU en cours)';
    if (!confirm(msg)) return;
    const v = validateDiffereTopPerDonneDates();
    if (!v.ok) {
      flash('err', v.message);
      return;
    }
    let wideDateRangeAck = false;
    if (v.needsWideAck) {
      if (!confirm(
        'La plage entre la date minimale et la date maximale dépasse 5 ans. '
        + 'Confirmer le lancement (requêtes SQL lourdes) ?',
      )) return;
      wideDateRangeAck = true;
    }
    setBusy('launch');
    try {
      const timeoutSeconds = (trainConfig.timeoutHours * 60 + trainConfig.timeoutMinutes) * 60;
      const baseVersionOptions = getBaseVersionOptions();
      const opponentVersionOptions = getOpponentVersionOptions();
      const { dateFrom, dateTo } = resolveTrainApiDates();
      const payload = {
        ...trainConfig,
        baseVersion: baseVersionOptions.some(v => v.versionTag === trainConfig.baseVersion) ? trainConfig.baseVersion : '',
        // 'legacy-rv' is a virtual opponent (Bot1, the C binary) — accept it
        // alongside real model versions. The backend dispatches on the
        // string directly without S3 copy.
        opponentVersion:
          trainConfig.opponentVersion === 'legacy-rv'
            ? 'legacy-rv'
            : (opponentVersionOptions.some(v => v.versionTag === trainConfig.opponentVersion) ? trainConfig.opponentVersion : ''),
        dateFrom,
        dateTo,
        ...(wideDateRangeAck ? { wideDateRangeAck: true } : {}),
        timeoutSeconds: timeoutSeconds > 0 ? timeoutSeconds : undefined,
        magnusPseudos: trainConfig.magnusPseudos || [],
        magnusPseudo: (trainConfig.magnusPseudos || []).join(',') || trainConfig.magnusPseudo.trim(),
        magnusSources: {
          differeAttaque: trainConfig.magnusSourceDiffereAttaque,
          differeMixteMaxi: trainConfig.magnusSourceDiffereMixteMaxi,
          cia: trainConfig.magnusSourceCia,
          freedefiAttacker: trainConfig.magnusSourceFreedefi,
        },
        duplicateObjective: trainConfig.duplicateObjective || undefined,
        duplicateImportIds: trainConfig.duplicateImportIds.trim()
          ? trainConfig.duplicateImportIds
            .split(/[,\s]+/)
            .map(s => parseInt(s.trim(), 10))
            .filter(n => Number.isFinite(n) && n > 0)
          : undefined,
        duplicateEvalDeals: trainConfig.duplicateEvalDeals,
        duplicateTrainFraction: trainConfig.duplicateTrainFraction,
        duplicateNormalizeFieldSize: trainConfig.duplicateNormalizeFieldSize || undefined,
        duplicateFrontierReplay: trainConfig.duplicateFrontierReplay || undefined,
        duplicateFrontierFraction: trainConfig.duplicateFrontierFraction,
        duplicateFrontierDeltaRaw: trainConfig.duplicateFrontierDeltaRaw,
      };
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/launch`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `Job lancé : ${data.name} — version ${data.versionTag || data.jobId}`);
      refresh();
    } catch (err: any) {
      flash('err', `Erreur lancement : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleStop = async (jobId: string) => {
    if (!confirm('Arrêter ce job ?')) return;
    try {
      await adminFetch(`${BACKEND_URL}/api/admin/ml/jobs/${jobId}/stop`, session.pseudo, { method: 'POST' });
      flash('ok', 'Job arrêté');
      refresh();
    } catch (err: any) {
      flash('err', err.message);
    }
  };

  const handleCopyJobLogs = async () => {
    if (!selectedJob) return;
    const raw = selectedJob.logs || selectedJob.info || '';
    if (!raw) {
      flash('err', 'Aucun contenu à copier');
      return;
    }
    const { text: logBody, removedProgressLines } = extractMLJobLogsForCopy(raw);
    if (!logBody) {
      flash('err', 'Aucun contenu utile après filtrage');
      return;
    }
    const text = `${formatMLJobLogExtractHeader(selectedJob, modelVersions)}\n\n${logBody}`;
    try {
      await navigator.clipboard.writeText(text);
      flash('ok', removedProgressLines > 0
        ? `Extrait copié (${removedProgressLines.toLocaleString('fr-FR')} ligne${removedProgressLines > 1 ? 's' : ''} de progression filtrée${removedProgressLines > 1 ? 's' : ''})`
        : 'Extrait copié dans le presse-papiers');
    } catch {
      flash('err', 'Copie impossible (permissions navigateur)');
    }
  };

  const handleHideJobFromList = (jobId: string) => {
    setHiddenJobIds(prev => (prev.includes(jobId) ? prev : [...prev, jobId]));
  };

  const handleHideAllVisibleJobs = () => {
    const visible = jobs.filter(j => !hiddenJobIds.includes(j.id));
    if (visible.length === 0) return;
    if (!confirm(`Masquer les ${visible.length} job(s) affiché(s) ? Les jobs ne sont pas supprimés côté OVH ; vous pourrez tout réafficher.`)) return;
    setHiddenJobIds(prev => [...new Set([...prev, ...visible.map(j => j.id)])]);
  };

  const handleResetHiddenJobs = () => {
    if (hiddenJobIds.length === 0) return;
    if (!confirm('Réafficher tous les jobs masqués dans cette liste ?')) return;
    setHiddenJobIds([]);
  };

  const viewingJobIdRef = useRef<string | null>(null);

  const handleViewLogs = useCallback(async (jobId: string) => {
    viewingJobIdRef.current = jobId;
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/jobs/${jobId}`, session.pseudo);
      const data = await res.json();
      if (viewingJobIdRef.current !== jobId) return;
      if (!res.ok || data.error) {
        flash('err', typeof data.error === 'string' ? data.error : 'Impossible de charger les logs du job');
        return;
      }
      if (!data.id) {
        flash('err', 'Réponse invalide du serveur (job introuvable)');
        return;
      }
      setSelectedJob(data);
    } catch {
      if (viewingJobIdRef.current === jobId) {
        flash('err', 'Impossible de charger les logs du job');
      }
    }
  }, [session.pseudo]);

  // Quand la liste auto-refresh passe en « terminé » avant la modale : mettre à jour l’état et recharger les logs
  useEffect(() => {
    if (!selectedJob) return;
    const row = jobs.find(j => j.id === selectedJob.id);
    if (!row) return;

    const modalActive = isMlJobActiveState(selectedJob.state);
    const rowActive = isMlJobActiveState(row.state);

    if (modalActive && !rowActive) {
      setSelectedJob(prev =>
        prev && prev.id === row.id
          ? { ...prev, state: row.state, duration: row.duration, info: row.info ?? prev.info, stoppedAt: row.stoppedAt }
          : prev,
      );
      void handleViewLogs(row.id);
      return;
    }

    if (!modalActive && rowActive) return;

    if (
      row.state !== selectedJob.state
      || row.duration !== selectedJob.duration
      || (row.info ?? '') !== (selectedJob.info ?? '')
    ) {
      setSelectedJob(prev =>
        prev && prev.id === row.id
          ? {
              ...prev,
              state: row.state,
              duration: row.duration,
              info: row.info ?? prev.info,
              stoppedAt: row.stoppedAt,
            }
          : prev,
      );
    }
  }, [jobs, selectedJob?.id, selectedJob?.state, selectedJob?.duration, selectedJob?.info, handleViewLogs]);

  // Logs en direct tant que le job est actif
  useEffect(() => {
    if (!selectedJob) {
      viewingJobIdRef.current = null;
      return;
    }
    if (!isMlJobActiveState(selectedJob.state)) return;
    const jobId = selectedJob.id;
    const interval = setInterval(() => {
      if (viewingJobIdRef.current !== jobId) return;
      void handleViewLogs(jobId);
    }, 15_000);
    return () => clearInterval(interval);
  }, [selectedJob?.id, selectedJob?.state, handleViewLogs]);

  // Après fin de job, l’API OVH peut agréger les dernières lignes avec un léger retard
  useEffect(() => {
    if (!selectedJob || isMlJobActiveState(selectedJob.state)) return;
    const jobId = selectedJob.id;
    const t1 = setTimeout(() => {
      if (viewingJobIdRef.current === jobId) void handleViewLogs(jobId);
    }, 500);
    const t2 = setTimeout(() => {
      if (viewingJobIdRef.current === jobId) void handleViewLogs(jobId);
    }, 2500);
    const t3 = setTimeout(() => {
      if (viewingJobIdRef.current === jobId) void handleViewLogs(jobId);
    }, 8000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [selectedJob?.id, selectedJob?.state, handleViewLogs]);

  const [deploySlot, setDeploySlot] = useState<'production' | 'beta'>('production');
  const [showS3Advanced, setShowS3Advanced] = useState(false);
  const [trainingExportTag, setTrainingExportTag] = useState<string | 'all' | null>(null);

  const handleExportTrainingJson = async (versionTag: string) => {
    setTrainingExportTag(versionTag);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/ml/versions/${encodeURIComponent(versionTag)}`,
        session.pseudo,
      );
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const payload = {
        kind: 'webtarot-neo-ml-training-export',
        scope: 'single' as const,
        exportedAt: new Date().toISOString(),
        version: data,
      };
      const safeName = versionTag.replace(/[^\w.-]+/g, '_').slice(0, 80);
      downloadJsonFile(`ml-training-${safeName}.json`, payload);
      flash('ok', `Export entraînement — ${versionTag}`);
    } catch (err: any) {
      flash('err', err?.message || 'Export JSON impossible');
    } finally {
      setTrainingExportTag(null);
    }
  };

  const handleExportAllTrainingJson = async () => {
    setTrainingExportTag('all');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/versions`, session.pseudo);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const versions = data.versions || [];
      const payload = {
        kind: 'webtarot-neo-ml-training-registry',
        scope: 'registry' as const,
        exportedAt: new Date().toISOString(),
        count: versions.length,
        versions,
      };
      downloadJsonFile(`ml-training-registry-${new Date().toISOString().slice(0, 10)}.json`, payload);
      flash('ok', `Export de ${versions.length} version(s)`);
    } catch (err: any) {
      flash('err', err?.message || 'Export JSON impossible');
    } finally {
      setTrainingExportTag(null);
    }
  };

  const handleVersionDeploy = async (versionTag: string, slot: 'production' | 'beta') => {
    const slotLabel = slot === 'beta' ? 'BETA' : 'PRODUCTION';
    if (!confirm(`Déployer la version ${versionTag} en ${slotLabel} ?`)) return;
    setBusy('deploy');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/versions/${encodeURIComponent(versionTag)}/deploy`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({ slot }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      flash('ok', data.message);
      refresh();
    } catch (err: any) {
      flash('err', `Erreur déploiement : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleDeleteVersion = async (versionTag: string) => {
    if (!confirm(`Supprimer la version ${versionTag} de l'historique ?`)) return;
    setBusy('delete');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/versions/${encodeURIComponent(versionTag)}`, session.pseudo, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `Version ${versionTag} supprimée`);
      refresh();
    } catch (err: any) {
      flash('err', `Erreur suppression : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleRenameVersion = async (versionTag: string, currentName: string) => {
    const newName = prompt('Nouveau nom :', currentName);
    if (!newName || newName === currentName) return;
    setBusy('rename');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/versions/${encodeURIComponent(versionTag)}`, session.pseudo, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: newName }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `Renommé en "${newName}"`);
      refresh();
    } catch (err: any) {
      flash('err', `Erreur renommage : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleSyncJobs = async () => {
    setBusy('sync');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/sync-jobs`, session.pseudo, { method: 'POST' });
      const data = await res.json();
      flash('ok', `${data.synced} version(s) synchronisée(s)`);
      refresh();
    } catch (err: any) {
      flash('err', err.message);
    } finally {
      setBusy('');
    }
  };

  const handleImportEval = async (versionTag: string) => {
    setBusy('eval');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/versions/${encodeURIComponent(versionTag)}/eval`, session.pseudo, { method: 'POST' });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `Métriques importées : win_rate=${(data.metrics.winRate * 100).toFixed(1)}%`);
      refresh();
    } catch (err: any) {
      flash('err', `Pas de résultats d'évaluation : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleDeploy = async (modelKey: string) => {
    const slotLabel = deploySlot === 'beta' ? 'BETA' : 'PRODUCTION';
    if (!confirm(`Déployer ${modelKey} en ${slotLabel} ?`)) return;
    setBusy('deploy');
    try {
      const matchingVersion = modelVersions.find(v => v.s3ModelPath === modelKey);
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/deploy`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({ modelKey, slot: deploySlot, versionTag: matchingVersion?.versionTag }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      flash('ok', data.message);
      refresh();
    } catch (err: any) {
      flash('err', `Erreur déploiement : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  // ── Compare state ──────────────────────────────────────────────────
  interface ComparisonRunningSummary {
    versionTag: string;
    label: string;
    wins: number;
    dealsPlayed: number;
    winRate: number;
  }

  interface ComparisonListItem {
    id: number;
    config: any;
    status: string;
    progress: number;
    totalSteps: number;
    modelStats: any[] | null;
    runningSummaries?: ComparisonRunningSummary[] | null;
    createdAt: string;
    completedAt: string | null;
    error: string | null;
  }

  const [comparisons, setComparisons] = useState<ComparisonListItem[]>([]);
  const [compareHistoryFilter, setCompareHistoryFilter] = useState('');
  const [compareHistoryRoleFilter, setCompareHistoryRoleFilter] = useState<'all' | 'defender' | 'attacker'>('all');
  const [newCompareModalOpen, setNewCompareModalOpen] = useState(false);
  const comparisonsByVersionTag = useMemo(() => {
    const m = new Map<string, ReturnType<typeof mlComparisonsReferencingVersion>>();
    for (const ver of modelVersions) {
      m.set(ver.versionTag, mlComparisonsReferencingVersion(comparisons, ver.versionTag));
    }
    return m;
  }, [comparisons, modelVersions]);

  const comparisonsFiltered = useMemo(() => {
    let list = comparisons;
    if (compareHistoryRoleFilter !== 'all') {
      list = list.filter(c => c.config?.compareRole === compareHistoryRoleFilter);
    }
    const needle = compareHistoryFilter.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(comp => {
      const models = comp.config?.models as Array<{ label?: string; versionTag?: string }> | undefined;
      if (Array.isArray(models)) {
        for (const m of models) {
          const tag = String(m.versionTag ?? '');
          const lbl = typeof m.label === 'string' ? m.label : '';
          const display = mlCompareRowDisplayName(tag, lbl || undefined, modelVersions);
          if (`${display} ${tag} ${lbl}`.toLowerCase().includes(needle)) return true;
        }
      }
      const opp = comp.config?.opponentModel as { label?: string; versionTag?: string } | undefined;
      if (opp && typeof opp === 'object') {
        const tag = String(opp.versionTag ?? '');
        const lbl = typeof opp.label === 'string' ? opp.label : '';
        const display = mlCompareRowDisplayName(tag, lbl || undefined, modelVersions);
        if (`${display} ${tag} ${lbl}`.toLowerCase().includes(needle)) return true;
      }
      return false;
    });
  }, [comparisons, compareHistoryFilter, compareHistoryRoleFilter, modelVersions]);

  const runningMlComparison = useMemo(
    () => comparisons.find(c => c.status === 'running'),
    [comparisons],
  );

  const [selectedComparison, setSelectedComparison] = useState<ComparisonListItem | null>(null);
  const [compareMetricDrilldown, setCompareMetricDrilldown] = useState<{
    metric: string;
    versionTag: string;
    modelLabel: string;
  } | null>(null);
  const [compareMetricDealsPayload, setCompareMetricDealsPayload] = useState<{
    deals: Array<{
      dealIndex: number;
      bidScore: number;
      contractLabel: string;
      metricPresenceForOtherModels?: Record<string, boolean>;
      evaluatedSidePointsStudied?: number;
      evaluatedSidePointsByOtherTag?: Record<string, number | null>;
    }>;
    metric: string;
    compareRole?: string;
    comparisonModels?: Array<{ versionTag: string; modelLabel: string; avgScore: number }>;
    defaultComparisonVersionTag?: string | null;
  } | null>(null);
  /** Modèle « colonne 2 » : même métrique sur cette donne jouée par un autre modèle */
  const [compareMetricOtherVersionTag, setCompareMetricOtherVersionTag] = useState<string | null>(null);
  const [compareMetricDealsLoading, setCompareMetricDealsLoading] = useState(false);
  const [compareBatchTrainingLoading, setCompareBatchTrainingLoading] = useState(false);
  const [compareDealDetailPanel, setCompareDealDetailPanel] = useState<Record<string, unknown> | null>(null);
  const [compareDealDetailComparePanel, setCompareDealDetailComparePanel] = useState<Record<string, unknown> | null>(
    null,
  );
  const [compareDealDetailLoading, setCompareDealDetailLoading] = useState(false);
  const [compareDealDetailCompareLoading, setCompareDealDetailCompareLoading] = useState(false);
  const [compareRv99DiagnosticLoading, setCompareRv99DiagnosticLoading] = useState(false);
  const [compareRv99Diagnostic, setCompareRv99Diagnostic] = useState<Record<string, unknown> | null>(null);
  /** Pli sélectionné dans la grille : mains affichées au début de ce pli (cartes des plis précédents masquées). */
  const [compareDealDetailTrickFocus, setCompareDealDetailTrickFocus] = useState<number | null>(null);
  /** Évite la fermeture instantanée du drill-down : le clic qui ouvre peut se terminer sur le backdrop nouvellement monté (mouseup ≠ mousedown). */
  const [metricDrillUiSafe, setMetricDrillUiSafe] = useState(false);

  const selectedComparisonShowRv99Column = useMemo(
    () => shouldShowLegacyRv99Column(selectedComparison?.modelStats as Array<Record<string, any>> | undefined),
    [selectedComparison?.modelStats],
  );

  useEffect(() => {
    if (!compareMetricDrilldown) {
      setMetricDrillUiSafe(false);
      return;
    }
    setMetricDrillUiSafe(false);
    const t = window.setTimeout(() => setMetricDrillUiSafe(true), 450);
    return () => window.clearTimeout(t);
  }, [compareMetricDrilldown]);

  const closeCompareMetricDrilldown = useCallback(() => {
    setCompareMetricDrilldown(null);
    setCompareMetricDealsPayload(null);
    setCompareMetricOtherVersionTag(null);
    setCompareMetricDealsLoading(false);
    setCompareDealDetailPanel(null);
    setCompareDealDetailComparePanel(null);
    setCompareDealDetailLoading(false);
    setCompareDealDetailCompareLoading(false);
    setCompareRv99Diagnostic(null);
    setCompareRv99DiagnosticLoading(false);
    setCompareDealDetailTrickFocus(null);
  }, []);

  /** Charge la liste de donnes pour le modèle « analysé » ; `comparisonTagOverride` fixe le modèle colonne oui/non (ex. inversion). */
  const loadCompareMetricDrilldownDeals = useCallback(
    async (
      metric: string,
      versionTag: string,
      modelLabel: string,
      opts?: { comparisonTagOverride?: string | null },
    ) => {
      if (!selectedComparison?.id) return;
      setCompareMetricDrilldown({ metric, versionTag, modelLabel });
      setCompareMetricDealsLoading(true);
      setCompareMetricDealsPayload(null);
      setCompareMetricOtherVersionTag(null);
      setCompareDealDetailPanel(null);
      setCompareRv99Diagnostic(null);
      setCompareDealDetailTrickFocus(null);
      try {
        const params = new URLSearchParams({ versionTag, metric });
        const res = await adminFetch(
          `${BACKEND_URL}/api/admin/ml/compare/${selectedComparison.id}/metric-deals?${params}`,
          session.pseudo,
        );
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        const cmList = Array.isArray(data.comparisonModels) ? data.comparisonModels : [];
        setCompareMetricDealsPayload({
          deals: data.deals ?? [],
          metric: data.metric ?? metric,
          compareRole: data.compareRole,
          comparisonModels: cmList.length > 0 ? cmList : undefined,
          defaultComparisonVersionTag:
            typeof data.defaultComparisonVersionTag === 'string'
              ? data.defaultComparisonVersionTag
              : data.defaultComparisonVersionTag === null
                ? null
                : undefined,
        });
        const hasOverride = opts !== undefined && Object.prototype.hasOwnProperty.call(opts, 'comparisonTagOverride');
        const comparisonTag = hasOverride
          ? opts!.comparisonTagOverride ?? null
          : typeof data.defaultComparisonVersionTag === 'string'
            ? data.defaultComparisonVersionTag
            : cmList[0]?.versionTag ?? null;
        setCompareMetricOtherVersionTag(comparisonTag);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Chargement impossible';
        flash('err', msg);
        closeCompareMetricDrilldown();
      } finally {
        setCompareMetricDealsLoading(false);
      }
    },
    [selectedComparison?.id, session.pseudo, flash, closeCompareMetricDrilldown],
  );

  const openCompareMetricDrilldown = useCallback(
    async (metric: string, versionTag: string, modelLabel: string) => {
      await loadCompareMetricDrilldownDeals(metric, versionTag, modelLabel);
    },
    [loadCompareMetricDrilldownDeals],
  );

  /** Modèles disponibles pour la colonne « même métrique (autre joueur ML) » — API ou repli sur les stats du détail */
  const compareDrillOtherModelOptions = useMemo(() => {
    const p = compareMetricDealsPayload?.comparisonModels;
    if (p?.length) return p;
    if (!compareMetricDrilldown?.versionTag || !selectedComparison?.modelStats?.length) return [];
    return [...selectedComparison.modelStats]
      .filter((ms: { versionTag: string }) => ms.versionTag !== compareMetricDrilldown.versionTag)
      .sort((a: { avgScore: number }, b: { avgScore: number }) => b.avgScore - a.avgScore)
      .map((ms: { versionTag: string; modelLabel: string; avgScore: number }) => ({
        versionTag: ms.versionTag,
        modelLabel: ms.modelLabel,
        avgScore: ms.avgScore,
      }));
  }, [compareMetricDealsPayload?.comparisonModels, compareMetricDrilldown?.versionTag, selectedComparison?.modelStats]);

  useEffect(() => {
    if (!compareMetricDealsPayload) return;
    if (compareMetricOtherVersionTag != null) return;
    const first = compareDrillOtherModelOptions[0];
    if (first) setCompareMetricOtherVersionTag(first.versionTag);
  }, [compareMetricDealsPayload, compareMetricOtherVersionTag, compareDrillOtherModelOptions]);

  /** Le modèle comparaison devient analysé ; l’ancien analysé passe en colonne oui/non. */
  const invertCompareMetricDrillModels = useCallback(async () => {
    if (!compareMetricDrilldown || !compareMetricOtherVersionTag || !selectedComparison?.id) return;
    const newDrilledTag = compareMetricOtherVersionTag;
    const newCompareTag = compareMetricDrilldown.versionTag;
    const newDrilledLabel =
      selectedComparison.modelStats?.find((ms: { versionTag: string }) => ms.versionTag === newDrilledTag)
        ?.modelLabel ?? newDrilledTag;
    await loadCompareMetricDrilldownDeals(compareMetricDrilldown.metric, newDrilledTag, newDrilledLabel, {
      comparisonTagOverride: newCompareTag,
    });
  }, [
    compareMetricDrilldown,
    compareMetricOtherVersionTag,
    selectedComparison?.id,
    selectedComparison?.modelStats,
    loadCompareMetricDrilldownDeals,
  ]);

  /** Colonnes d’entête (répétition Sud–Ouest) où siègent les joueurs pilotés par le modèle étudié pour ce rôle / preneur. */
  const dealDetailStudiedSeatCols = useMemo(() => {
    if (!compareDealDetailPanel) return [] as number[];
    const taker = Number((compareDealDetailPanel as { takerPosition?: number }).takerPosition ?? 0);
    const roleRaw = (compareDealDetailPanel as { compareRole?: string }).compareRole;
    const role: 'defender' | 'attacker' = roleRaw === 'defender' ? 'defender' : 'attacker';
    return mlCompareStudiedSeatColumnIndexes(taker, role);
  }, [compareDealDetailPanel]);

  const dealDetailStudiedSeats = useMemo(
    () => [...new Set(dealDetailStudiedSeatCols.map(c => c % 4))],
    [dealDetailStudiedSeatCols],
  );

  /** Préfixe de plis consécutifs depuis le 1 où les 4 cartes sont strictement les mêmes (ordre du pli) entre gauche et droite. */
  const dealDetailSyncTrickNums = useMemo(() => {
    if (!compareDealDetailPanel || !compareDealDetailComparePanel) return new Set<number>();
    return mlCompareIdenticalCardPrefixTrickNumbers(
      (compareDealDetailPanel.tricks as Array<Record<string, unknown>>) ?? [],
      (compareDealDetailComparePanel.tricks as Array<Record<string, unknown>>) ?? [],
    );
  }, [compareDealDetailPanel, compareDealDetailComparePanel]);

  /** Cartes présentes dans l’écart des deux modèles (surlignées sur la ligne Écart). */
  const dealDetailEcartCommonBothModels = useMemo(() => {
    if (!compareDealDetailPanel || !compareDealDetailComparePanel) return new Set<number>();
    const ea = (compareDealDetailPanel.ecart as number[]) ?? [];
    const eb = (compareDealDetailComparePanel.ecart as number[]) ?? [];
    const sb = new Set(eb);
    return new Set(ea.filter(c => sb.has(c)));
  }, [compareDealDetailPanel, compareDealDetailComparePanel]);

  const toggleCompareDealTrickFocus = useCallback((trickNum1: number) => {
    setCompareDealDetailTrickFocus(prev => (prev === trickNum1 ? null : trickNum1));
  }, []);

  /** Position dans `compareMetricDealsPayload.deals` (métrique filtrée) pour navigation précédent / suivant. */
  const compareDealDetailMetricNav = useMemo(() => {
    const deals = compareMetricDealsPayload?.deals ?? [];
    if (!compareDealDetailPanel || deals.length === 0) {
      return {
        listIndex: -1,
        listLen: 0,
        prevDealIndex: null as number | null,
        nextDealIndex: null as number | null,
      };
    }
    const cur = Number(compareDealDetailPanel.dealIndex);
    const listIndex = deals.findIndex(d => d.dealIndex === cur);
    const listLen = deals.length;
    const prevDealIndex = listIndex > 0 ? deals[listIndex - 1].dealIndex : null;
    const nextDealIndex =
      listIndex >= 0 && listIndex < listLen - 1 ? deals[listIndex + 1].dealIndex : null;
    return { listIndex, listLen, prevDealIndex, nextDealIndex };
  }, [compareMetricDealsPayload?.deals, compareDealDetailPanel]);

  const openCompareDealDetail = useCallback(
    async (versionTag: string, dealIndex: number, opts?: { metric?: string }) => {
      if (!selectedComparison?.id) return;
      setCompareDealDetailLoading(true);
      setCompareDealDetailPanel(null);
      setCompareDealDetailComparePanel(null);
      setCompareRv99Diagnostic(null);
      setCompareDealDetailTrickFocus(null);
      try {
        const params = new URLSearchParams({ versionTag, dealIndex: String(dealIndex) });
        const metricParam = opts?.metric ?? compareMetricDrilldown?.metric;
        if (metricParam) params.set('metric', metricParam);
        const res = await adminFetch(
          `${BACKEND_URL}/api/admin/ml/compare/${selectedComparison.id}/deal-detail?${params}`,
          session.pseudo,
        );
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        setCompareDealDetailPanel(data);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Détail impossible';
        flash('err', msg);
      } finally {
        setCompareDealDetailLoading(false);
      }
    },
    [selectedComparison?.id, session.pseudo, flash, compareMetricDrilldown?.metric],
  );

  const switchCompareDealDrillMetric = useCallback(
    async (metric: string) => {
      if (!compareMetricDrilldown || !compareDealDetailPanel || !selectedComparison?.id) return;
      if (metric === compareMetricDrilldown.metric) return;
      const dealIndex = Number(compareDealDetailPanel.dealIndex);
      if (!Number.isFinite(dealIndex)) return;
      setCompareMetricDrilldown(prev => (prev ? { ...prev, metric } : null));
      await openCompareDealDetail(compareMetricDrilldown.versionTag, dealIndex, { metric });
    },
    [compareMetricDrilldown, compareDealDetailPanel, selectedComparison?.id, openCompareDealDetail],
  );

  const runCompareRv99Diagnostic = useCallback(async () => {
    if (!selectedComparison?.id || !compareMetricDrilldown || !compareDealDetailPanel) return;
    const dealIndex = Number(compareDealDetailPanel.dealIndex);
    if (!Number.isFinite(dealIndex)) return;
    setCompareRv99DiagnosticLoading(true);
    try {
      const params = new URLSearchParams({
        versionTag: compareMetricDrilldown.versionTag,
        dealIndex: String(dealIndex),
      });
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/ml/compare/${selectedComparison.id}/rv99-diagnostic?${params}`,
        session.pseudo,
      );
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setCompareRv99Diagnostic(data);
      if (Array.isArray(data.metricHighlights) && data.metricHighlights.length > 0) {
        setCompareDealDetailPanel(prev => prev ? { ...prev, metricHighlights: data.metricHighlights } : prev);
      }
    } catch (e: unknown) {
      flash('err', e instanceof Error ? e.message : 'Diagnostic RV99 impossible');
    } finally {
      setCompareRv99DiagnosticLoading(false);
    }
  }, [selectedComparison?.id, compareMetricDrilldown, compareDealDetailPanel, session.pseudo, flash]);

  /** Donne jouée par le modèle de comparaison (moitié droite), même index — pour comparaison côte à côte. */
  useEffect(() => {
    if (!compareDealDetailPanel || !selectedComparison?.id || !compareMetricDrilldown) {
      setCompareDealDetailComparePanel(null);
      setCompareDealDetailCompareLoading(false);
      return;
    }
    const dealIndex = Number(compareDealDetailPanel.dealIndex);
    if (!Number.isFinite(dealIndex)) return;
    const primaryTag = compareMetricDrilldown.versionTag;
    const otherTag = compareMetricOtherVersionTag;
    const metric = compareMetricDrilldown.metric;
    if (!otherTag || otherTag === primaryTag) {
      setCompareDealDetailComparePanel(null);
      setCompareDealDetailCompareLoading(false);
      return;
    }
    let cancelled = false;
    setCompareDealDetailCompareLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ versionTag: otherTag, dealIndex: String(dealIndex) });
        if (metric) params.set('metric', metric);
        const res = await adminFetch(
          `${BACKEND_URL}/api/admin/ml/compare/${selectedComparison.id}/deal-detail?${params}`,
          session.pseudo,
        );
        const data = await res.json();
        if (cancelled) return;
        if (data.error) {
          setCompareDealDetailComparePanel(null);
          return;
        }
        setCompareDealDetailComparePanel(data);
      } catch {
        if (!cancelled) setCompareDealDetailComparePanel(null);
      } finally {
        if (!cancelled) setCompareDealDetailCompareLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setCompareDealDetailCompareLoading(false);
    };
  }, [
    compareDealDetailPanel,
    compareMetricOtherVersionTag,
    compareMetricDrilldown,
    selectedComparison?.id,
    session.pseudo,
  ]);

  /**
   * Charge jusqu’à ML_COMPARE_TRAINING_MAX_DEALS donnes et ouvre Entraînement.
   * @param preferredListIndex — index 0-based dans la liste du drill-down pour afficher cette donne en premier (détail).
   */
  const openCompareDealsInTraining = useCallback(async (preferredListIndex?: number) => {
    if (!session.compet) {
      flash('err', 'Le mode compétition doit être activé pour ouvrir l’onglet Entraînement.');
      return;
    }
    if (!compareMetricDealsPayload?.deals?.length || !compareMetricDrilldown || !selectedComparison?.id) return;

    const fullList = compareMetricDealsPayload.deals;
    const cappedList = fullList.slice(0, ML_COMPARE_TRAINING_MAX_DEALS);

    let selectedIndex = 0;
    if (preferredListIndex !== undefined) {
      if (preferredListIndex < 0 || preferredListIndex >= fullList.length) {
        flash('err', 'Donne introuvable dans la liste.');
        return;
      }
      if (preferredListIndex >= ML_COMPARE_TRAINING_MAX_DEALS) {
        flash(
          'ok',
          `Seules les ${ML_COMPARE_TRAINING_MAX_DEALS} premières donnes sont envoyées. Ouverture sur la première donne.`,
        );
        selectedIndex = 0;
      } else {
        selectedIndex = preferredListIndex;
      }
    }

    const cfg = selectedComparison?.config as
      | { compareRole?: string; opponentModel?: { label?: string; versionTag?: string } }
      | undefined;
    const compareRole: 'defender' | 'attacker' =
      cfg?.compareRole === 'defender' ? 'defender' : 'attacker';
    const evaluatedLabel = mlComparePublicModelLabel({
      label: compareMetricDrilldown.modelLabel,
      versionTag: compareMetricDrilldown.versionTag,
    });
    const opponentLabel = mlComparePublicModelLabel(cfg?.opponentModel ?? null);

    setCompareBatchTrainingLoading(true);
    try {
      const dealsOut = await Promise.all(
        cappedList.map(async d => {
          const params = new URLSearchParams({
            versionTag: compareMetricDrilldown.versionTag,
            dealIndex: String(d.dealIndex),
            metric: compareMetricDrilldown.metric,
          });
          const res = await adminFetch(
            `${BACKEND_URL}/api/admin/ml/compare/${selectedComparison.id}/deal-detail?${params}`,
            session.pseudo,
          );
          const data = await res.json();
          if (data.error) throw new Error(data.error);
          return mlCompareDetailToReplayDeal(
            data as Record<string, unknown>,
            { dealIndex: d.dealIndex, contractLabel: d.contractLabel, bidScore: d.bidScore },
            compareRole,
            evaluatedLabel,
            opponentLabel,
            compareMetricDrilldown.metric,
          );
        }),
      );
      const title = `${ML_COMPARE_METRIC_TITLES[compareMetricDrilldown.metric] ?? compareMetricDrilldown.metric} — ${compareMetricDrilldown.modelLabel}`;
      sessionStorage.setItem(
        'library_load_request',
        JSON.stringify({
          compareReplayBatch: {
            title,
            deals: dealsOut.map(({ dealIndex, label, gameData, replaySeatLabels, metricDrill }) => ({
              dealIndex,
              label,
              gameData,
              replaySeatLabels,
              metricDrill,
            })),
            selectedIndex,
          },
          mode: 'replay',
          position: 0,
          replayAllBots: true,
          historyContext: {
            kind: 'compare',
            compare: {
              metric: compareMetricDrilldown.metric,
              metricLabel: ML_COMPARE_METRIC_TITLES[compareMetricDrilldown.metric]
                ?? compareMetricDrilldown.metric,
              modelLabel: compareMetricDrilldown.modelLabel,
              dealCount: dealsOut.length,
              selectedDealIndex: selectedIndex,
            },
          },
        }),
      );
      startTraining({
        sessionId: '',
        displayMode: 'cards_on_table',
        showTimeline: true,
        botConfig: { disabledBots: new Set() },
      });
    } catch (e: unknown) {
      flash('err', e instanceof Error ? e.message : 'Chargement des donnes impossible');
    } finally {
      setCompareBatchTrainingLoading(false);
    }
  }, [
    session.compet,
    session.pseudo,
    compareMetricDealsPayload,
    compareMetricDrilldown,
    selectedComparison,
    flash,
    startTraining,
  ]);

  const openCompareDealInTraining = useCallback(async () => {
    const d = compareDealDetailPanel;
    if (!d || compareMetricDrilldown == null || !compareMetricDealsPayload?.deals?.length) return;
    const raw = d as Record<string, unknown>;
    const dealIndex = Number(raw.dealIndex);
    if (!Number.isFinite(dealIndex)) {
      flash('err', 'Donne invalide.');
      return;
    }
    const preferredListIndex = compareMetricDealsPayload.deals.findIndex(x => x.dealIndex === dealIndex);
    if (preferredListIndex < 0) {
      flash('err', 'Donne introuvable dans la liste.');
      return;
    }
    await openCompareDealsInTraining(preferredListIndex);
  }, [
    compareDealDetailPanel,
    compareMetricDrilldown,
    compareMetricDealsPayload,
    openCompareDealsInTraining,
    flash,
  ]);

  const compareDetailScrollYRef = useRef(0);
  const compareDetailOverlayRef = useRef<HTMLDivElement | null>(null);

  const closeComparisonDetail = useCallback(() => {
    closeCompareMetricDrilldown();
    const y = compareDetailScrollYRef.current;
    setSelectedComparison(null);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, y);
      });
    });
  }, [closeCompareMetricDrilldown]);

  useEffect(() => {
    if (!selectedComparison) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [selectedComparison]);

  useEffect(() => {
    if (!selectedComparison) return;
    compareDetailOverlayRef.current?.scrollTo(0, 0);
  }, [selectedComparison?.id]);

  useEffect(() => {
    if (!selectedComparison) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (compareMetricDrilldown) {
        closeCompareMetricDrilldown();
        return;
      }
      closeComparisonDetail();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedComparison, closeComparisonDetail, compareMetricDrilldown, closeCompareMetricDrilldown]);

  const [compareConfig, setCompareConfig] = useState({
    compareRole: 'defender' as 'defender' | 'attacker',
    dealCount: 200,
    minBidScore: 0,
    maxBidScore: 200,
    allowedContracts: [2, 3, 4] as number[],
    crossModelAnalysis: false,
    crossModelSampleSize: 50,
  });
  const [compareModels, setCompareModels] = useState<Array<{ slot: string; label: string; versionTag: string }>>([
    { slot: 'production', label: '', versionTag: '' },
    { slot: 'beta', label: '', versionTag: '' },
  ]);
  const [compareOpponent, setCompareOpponent] = useState({ slot: 'production', label: '', versionTag: '' });
  const legacyRvCompareModel = { slot: 'legacy-rv', label: 'Legacy RV', versionTag: 'legacy-rv' };
  const pimcCompareModel = { slot: 'pimc', label: 'PIMC en ligne (recherche)', versionTag: 'pimc' };
  const [differeImportId, setDiffereImportId] = useState('');
  const [differeImports, setDiffereImports] = useState<any[]>([]);
  const [bot1Report, setBot1Report] = useState<any | null>(null);
  const [differePool, setDifferePool] = useState<any[]>([]);
  const [differeModelPool, setDiffereModelPool] = useState<any[]>([]);
  const [differeRuns, setDiffereRuns] = useState<any[]>([]);
  const [differeBenchmarkRunsStored, setDiffereBenchmarkRunsStored] = useState(0);
  const [differeRankings, setDiffereRankings] = useState<any | null>(null);
  const [differeModelRole, setDiffereModelRole] = useState<'attacker' | 'defender'>('attacker');
  const [differeModelVersionTag, setDiffereModelVersionTag] = useState('');
  const [differeHybridLeadTag, setDiffereHybridLeadTag] = useState('');
  const [differeHybridFollowTag, setDiffereHybridFollowTag] = useState('');
  const [differeHybridDepth, setDiffereHybridDepth] = useState(7);
  const [differeHybridId, setDiffereHybridId] = useState('g-v1');
  const [differeRankingRole, setDiffereRankingRole] = useState<'attacker' | 'defender'>('attacker');
  /** Synthèse pool benchmark : quelle vue de classement afficher sur la page. */
  const [differePoolBenchmarkView, setDifferePoolBenchmarkView] = useState<
    'global' | 'attackNoSc' | 'attackSc' | 'defense' | 'perDiffere'
  >('global');
  /** Mode « par différé » : sous-catégorie pour filtrer la liste des différés. */
  const [differePerDiffCategory, setDifferePerDiffCategory] = useState<'attacker' | 'defender' | 'attackSc'>('attacker');
  const [differePerDiffSelectedImportId, setDifferePerDiffSelectedImportId] = useState<number | null>(null);
  const [differeToolsModalOpen, setDiffereToolsModalOpen] = useState(false);
  const [differeToolsModalTab, setDiffereToolsModalTab] = useState<
    'validation' | 'poolCriteria' | 'poolList' | 'modelPool' | 'runsRankings'
  >('validation');
  const [differeBenchmarkMetricsModalOpen, setDiffereBenchmarkMetricsModalOpen] = useState(false);
  /** Tous les différés du pool (agrégation) ou un import précis. */
  const [differeBenchmarkMetricsScope, setDiffereBenchmarkMetricsScope] = useState<'all' | 'one'>('all');
  const [differeBenchmarkMetricsView, setDiffereBenchmarkMetricsView] =
    useState<DiffereBenchmarkMetricsView>('attackerFull');
  /** false = top 5 IA + baselines réf. ; true = tous les modèles. */
  const [differeBenchmarkMetricsShowAllModels, setDiffereBenchmarkMetricsShowAllModels] = useState(false);
  const [differeBenchmarkMetricsImportId, setDiffereBenchmarkMetricsImportId] = useState<number | null>(null);
  const [differeMetricsExportBusy, setDiffereMetricsExportBusy] = useState(false);
  /** Runs benchmark terminés dédiés à la modal métriques (évite la troncature ~30 de la liste admin). */
  const [differeBenchmarkMetricsRunList, setDiffereBenchmarkMetricsRunList] = useState<Array<Record<string, unknown>>>([]);
  const [differeBenchmarkMetricsRunsLoading, setDiffereBenchmarkMetricsRunsLoading] = useState(false);
  const [differeMetricDrilldown, setDiffereMetricDrilldown] = useState<{
    metric: string;
    versionTag: string;
    modelLabel: string;
    compareRole: 'defender' | 'attacker';
    segment: DiffereBenchmarkMetricSegment;
  } | null>(null);
  const [differeMetricDealsPayload, setDiffereMetricDealsPayload] = useState<Record<string, any> | null>(null);
  const [differeMetricDealsLoading, setDiffereMetricDealsLoading] = useState(false);
  /** Modèle ML du pool pour la colonne « même métrique sur cette donne » (drill-down benchmark différé). */
  const [differeBenchmarkDrillCompareTag, setDiffereBenchmarkDrillCompareTag] = useState<string | null>(null);
  const [differeDealDetailPanel, setDiffereDealDetailPanel] = useState<Record<string, unknown> | null>(null);
  const [differeDealDetailLoading, setDiffereDealDetailLoading] = useState(false);
  const [differeDealDetailComparePanel, setDiffereDealDetailComparePanel] = useState<Record<string, unknown> | null>(null);
  const [differeDealDetailCompareLoading, setDiffereDealDetailCompareLoading] = useState(false);
  /** Clé du 2e volet (versionTag ML ou baselineKey bot1 / human-top-…). */
  const [differeDealDetailCompareTarget, setDiffereDealDetailCompareTarget] = useState<string | null>(null);
  const [differeDealDetailTrickFocus, setDiffereDealDetailTrickFocus] = useState<number | null>(null);
  /** Filtre liste différés (modal onglet pool) : tous / attaque / défense. */
  const [differePoolModalRoleFilter, setDifferePoolModalRoleFilter] = useState<'all' | 'attacker' | 'defender'>('all');
  /** Affichage liste modèles dans le modal : segment attaque vs défense. */
  const [differeModalModelPoolViewRole, setDiffereModalModelPoolViewRole] = useState<'attacker' | 'defender'>('attacker');
  /** Import par critères : contourner la validation bot1 (équivalent API validateBot1BeforePool: false). */
  const [poolCriteriaBypassBot1, setPoolCriteriaBypassBot1] = useState(false);
  const [criteriaImportWizardStep, setCriteriaImportWizardStep] = useState<'idle' | 'preview'>('idle');
  const [criteriaPreviewPayload, setCriteriaPreviewPayload] = useState<any>(null);
  /** Progression étape 2 import critères (un appel API par différé pour barre de progression). */
  const [criteriaExecuteProgress, setCriteriaExecuteProgress] = useState<{ current: number; total: number } | null>(null);
  const [bot1BatchProgress, setBot1BatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [bot1BatchNotOkIds, setBot1BatchNotOkIds] = useState<number[]>([]);
  const [differePoolReimportProgress, setDifferePoolReimportProgress] = useState<{
    current: number;
    total: number;
    failedIds: number[];
  } | null>(null);
  const [differeMinParticipation, setDiffereMinParticipation] = useState(0.8);
  const [differeFallbackPolicy, setDiffereFallbackPolicy] = useState<'none' | 'non_card'>('non_card');
  /** Progression cumulée pendant « Lancer les runs manquants » (tous les différés du batch). */
  const [differeSyncBatchProgress, setDiffereSyncBatchProgress] = useState<{
    totalPlannedDeals: number;
    plannedRuns: number;
    doneDeals: number;
    completedRuns: number;
    runningRuns: number;
    percent: number;
  } | null>(null);
  /** Estimation avant sync : couples modèle×différé sans run terminé (aligné sur le backend). */
  const [differeSyncPreview, setDiffereSyncPreview] = useState<{
    missingRuns: number;
    plannedDeals: number;
    differesWithMissingRuns: number;
    poolTotalDiffereCount?: number;
    poolActiveDiffereCount?: number;
    poolInactiveDiffereCount?: number;
    activeMlModelsCount?: number;
    importedDiffereCount?: number;
  } | null>(null);
  const [differeWorkerStatus, setDiffereWorkerStatus] = useState<{
    ovhConfigured?: boolean;
    ovhDelegationEnabled?: boolean;
    health?: { ok?: boolean; latencyMs?: number; error?: string; url?: string };
  } | null>(null);
  const [differePoolCriteria, setDifferePoolCriteria] = useState({
    dateFrom: '',
    dateTo: '',
    legacyType: '',
    count: '',
    /** Exclure les # déjà présents dans le pool actif (recommandé). */
    excludeAlreadyInPool: true,
    /** Mélanger puis prendre N ; défaut = tri par id croissant (reproductible, souvent anciens en premier). */
    randomSample: false,
  });
  const [compareExportId, setCompareExportId] = useState<number | null>(null);
  const [compareAppendModel, setCompareAppendModel] = useState({ slot: '', label: '', versionTag: '' });

  const [mlSynthComposers, setMlSynthComposers] = useState<MLSynthComposerBlock[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(ML_SYNTH_COMPOSERS_KEY);
      if (!raw) return [];
      const p = JSON.parse(raw) as unknown;
      if (!Array.isArray(p)) return [];
      return p.map(normalizeMlSynthComposerBlock).filter((x): x is MLSynthComposerBlock => x != null);
    } catch {
      return [];
    }
  });
  /** Si false : chips avec libellés courts uniquement (masque le texte après « [ » et allège). */
  const [mlSynthShowChipFullLabels, setMlSynthShowChipFullLabels] = useState(true);

  useEffect(() => {
    try {
      localStorage.setItem(ML_SYNTH_COMPOSERS_KEY, JSON.stringify(mlSynthComposers));
    } catch { /* ignore */ }
  }, [mlSynthComposers]);

  const mlSynthCompletedByRole = useMemo(() => {
    const completed = (c: ComparisonListItem) =>
      c.status === 'completed' && (c.modelStats?.length ?? 0) > 0;
    return {
      defender: comparisons.filter(c => completed(c) && c.config?.compareRole === 'defender'),
      attacker: comparisons.filter(c => completed(c) && c.config?.compareRole === 'attacker'),
    };
  }, [comparisons]);

  const refreshComparisons = useCallback(async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare?limit=200`, session.pseudo);
      const data = await res.json();
      const raw = (data.comparisons || []) as Array<Record<string, unknown>>;
      setComparisons(
        raw.map(c => ({
          ...c,
          id: Number(c.id),
          status: String(c.status ?? ''),
          config: typeof c.config === 'string' ? parseComparisonConfig(c.config) : c.config,
          modelStats: (c.modelStats ?? c.model_stats) as ComparisonListItem['modelStats'],
          createdAt: String(c.createdAt ?? c.created_at ?? ''),
        })) as ComparisonListItem[],
      );
    } catch { /* ignore */ }
  }, [session.pseudo]);

  const refreshDiffereImports = useCallback(async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/imports`, session.pseudo);
      const data = await res.json();
      setDiffereImports(data.imports || []);
    } catch { /* ignore */ }
  }, [session.pseudo]);

  const refreshDiffereBenchmark = useCallback(async () => {
    try {
      /** Sans `role` : pool complet → classements globaux attaque/défense et liste « par différé » dans une seule réponse. */
      const [poolRes, modelsRes, runsRes, rankingsRes, syncPrevRes, workerRes] = await Promise.all([
        adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/pool`, session.pseudo),
        adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/models`, session.pseudo),
        adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/runs?limit=120`, session.pseudo),
        adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/rankings?minParticipationRatio=${differeMinParticipation}`, session.pseudo),
        adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/sync-preview`, session.pseudo),
        adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/worker-status`, session.pseudo),
      ]);
      const [poolData, modelsData, runsData, rankingsData, syncPrevData, workerData] = await Promise.all([
        poolRes.json(),
        modelsRes.json(),
        runsRes.json(),
        rankingsRes.json(),
        syncPrevRes.json(),
        workerRes.json(),
      ]);
      setDifferePool(poolData.pool || []);
      setDiffereModelPool(modelsData.models || []);
      setDiffereRuns(runsData.runs || []);
      setDiffereBenchmarkRunsStored(Number(runsData.benchmarkRunsStored ?? 0));
      setDiffereRankings(rankingsData.rankings || null);
      if (!syncPrevRes.ok || syncPrevData?.error) {
        setDiffereSyncPreview(null);
      } else {
        const num = (v: unknown) => (v != null && v !== '' ? Number(v) : undefined);
        setDiffereSyncPreview({
          missingRuns: Number(syncPrevData.missingRuns ?? 0),
          plannedDeals: Number(syncPrevData.plannedDeals ?? 0),
          differesWithMissingRuns: Number(syncPrevData.differesWithMissingRuns ?? 0),
          poolTotalDiffereCount: num(syncPrevData.poolTotalDiffereCount),
          poolActiveDiffereCount: num(syncPrevData.poolActiveDiffereCount),
          poolInactiveDiffereCount: num(syncPrevData.poolInactiveDiffereCount),
          activeMlModelsCount: num(syncPrevData.activeMlModelsCount),
          importedDiffereCount: num(syncPrevData.importedDiffereCount),
        });
      }
      if (workerRes.ok && !workerData?.error) {
        setDiffereWorkerStatus({
          ovhConfigured: workerData.ovhConfigured,
          ovhDelegationEnabled: workerData.ovhDelegationEnabled,
          health: workerData.health,
        });
      } else {
        setDiffereWorkerStatus(null);
      }
    } catch { /* ignore */ }
  }, [differeMinParticipation, session.pseudo]);

  useEffect(() => {
    if (section === 'compare') {
      refreshComparisons();
      refreshDiffereImports();
      refreshDiffereBenchmark();
      const interval = setInterval(() => {
        refreshComparisons();
        refreshDiffereBenchmark();
      }, 5_000);
      return () => clearInterval(interval);
    }
    if (section === 'versions') {
      void refreshComparisons();
    }
  }, [section, refreshComparisons, refreshDiffereBenchmark, refreshDiffereImports]);

  const poolImportIdSet = useMemo(
    () => new Set(
      (differePool as Array<{ importId?: number }>)
        .map(p => Number(p.importId))
        .filter(n => n > 0),
    ),
    [differePool],
  );

  const activeDifferePoolImportIdSet = useMemo(
    () => new Set(
      (differePool as Array<{ importId?: number; active?: boolean }>)
        .filter(p => p.active !== false)
        .map(p => Number(p.importId))
        .filter(n => n > 0),
    ),
    [differePool],
  );

  /** Modèles actifs du pool (+ legacy-rv) — métriques benchmark (archivés exclus). */
  const differeBenchmarkPoolModelKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const m of differeModelPool as Array<{ role?: string; versionTag?: string; active?: boolean }>) {
      if (m.active === false) continue;
      if (!m.role || !m.versionTag) continue;
      keys.add(`${m.role}:${m.versionTag}`);
    }
    keys.add('attacker:legacy-rv');
    keys.add('defender:legacy-rv');
    return keys;
  }, [differeModelPool]);

  const differeImportsNotInPool = useMemo(
    () => (differeImports as any[]).filter((imp: { id: number }) => !poolImportIdSet.has(imp.id)),
    [differeImports, poolImportIdSet],
  );

  /** Ordre : différés du pool d’abord, puis les autres importés ; même ensemble que « tout valider ». */
  const bot1ValidationQueue = useMemo(() => {
    type Row = { importId: number; sourceDiffereId: number; inPool: boolean };
    const byImp = new Map<number, Row>();
    for (const p of differePool as Array<{ importId: number; sourceDiffereId: number }>) {
      const prev = byImp.get(p.importId);
      if (!prev) {
        byImp.set(p.importId, {
          importId: p.importId,
          sourceDiffereId: p.sourceDiffereId,
          inPool: true,
        });
      } else {
        prev.inPool = true;
      }
    }
    for (const imp of differeImports as Array<{ id: number; sourceDiffereId: number }>) {
      if (!byImp.has(imp.id)) {
        byImp.set(imp.id, {
          importId: imp.id,
          sourceDiffereId: imp.sourceDiffereId,
          inPool: false,
        });
      }
    }
    const rows = [...byImp.values()];
    rows.sort((a, b) => (a.inPool === b.inPool
      ? a.sourceDiffereId - b.sourceDiffereId
      : a.inPool ? -1 : 1));
    return rows;
  }, [differePool, differeImports]);

  const differePoolStatsByRole = useMemo(() => {
    const pool = differePool as Array<{ role?: string; dealCount?: number }>;
    const attackers = pool.filter(p => p.role === 'attacker');
    const defenders = pool.filter(p => p.role === 'defender');
    const dealSum = (rows: typeof pool) => rows.reduce((acc, r) => acc + (Number(r.dealCount) || 0), 0);
    const totalDealsAll = dealSum(pool);
    return {
      attacker: { count: attackers.length, deals: dealSum(attackers) },
      defender: { count: defenders.length, deals: dealSum(defenders) },
      totalCount: pool.length,
      totalDealsAll,
    };
  }, [differePool]);

  const differePoolModalFiltered = useMemo(() => {
    const pool = differePool as any[];
    if (differePoolModalRoleFilter === 'all') return pool;
    return pool.filter((p: { role?: string }) => p.role === differePoolModalRoleFilter);
  }, [differePool, differePoolModalRoleFilter]);

  const differeModelPoolActiveForView = useMemo(
    () => (differeModelPool as any[]).filter(
      m => m.active !== false && m.role === differeModalModelPoolViewRole,
    ),
    [differeModelPool, differeModalModelPoolViewRole],
  );

  const differeModelPoolArchivedForView = useMemo(
    () => (differeModelPool as any[]).filter(
      m => m.active === false && m.role === differeModalModelPoolViewRole,
    ),
    [differeModelPool, differeModalModelPoolViewRole],
  );

  const differePerDiffPickerLists = useMemo(() => {
    const all = (differeRankings?.differes ?? []) as Array<{
      importId: number;
      sourceDiffereId: number;
      role: string;
      participants?: Array<{ attSc?: number }>;
    }>;
    const attacker = all.filter(d => d.role === 'attacker');
    const defender = all.filter(d => d.role === 'defender');
    const attackSc = attacker.filter(d =>
      (d.participants ?? []).some(p => typeof p.attSc === 'number' && p.attSc !== 0),
    );
    return {
      attacker,
      defender,
      attackSc: attackSc.length > 0 ? attackSc : attacker,
    };
  }, [differeRankings]);

  const differePerDiffResolvedImportId = useMemo(() => {
    const list = differePerDiffPickerLists[differePerDiffCategory];
    if (!list.length) return null;
    if (
      differePerDiffSelectedImportId != null
      && list.some(d => d.importId === differePerDiffSelectedImportId)
    ) {
      return differePerDiffSelectedImportId;
    }
    return list[0].importId;
  }, [differePerDiffPickerLists, differePerDiffCategory, differePerDiffSelectedImportId]);

  useEffect(() => {
    if (differePoolBenchmarkView !== 'perDiffere') return;
    const list = differePerDiffPickerLists[differePerDiffCategory];
    if (!list.length) {
      setDifferePerDiffSelectedImportId(null);
      return;
    }
    setDifferePerDiffSelectedImportId(prev =>
      prev != null && list.some(d => d.importId === prev) ? prev : list[0].importId,
    );
  }, [differePoolBenchmarkView, differePerDiffCategory, differePerDiffPickerLists]);

  const formatDiffereDebutCell = (raw: unknown) => {
    if (raw == null || raw === '') return '—';
    const d = new Date(String(raw));
    return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  useEffect(() => {
    if (!differeToolsModalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [differeToolsModalOpen]);

  useEffect(() => {
    if (!differeToolsModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDiffereToolsModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [differeToolsModalOpen]);

  useEffect(() => {
    if (!differeToolsModalOpen) {
      setCriteriaImportWizardStep('idle');
      setCriteriaPreviewPayload(null);
    }
  }, [differeToolsModalOpen]);

  useEffect(() => {
    if (!newCompareModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNewCompareModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [newCompareModalOpen]);

  useEffect(() => {
    if (differeToolsModalOpen && differeToolsModalTab === 'modelPool') {
      setDiffereModelRole(differeModalModelPoolViewRole);
    }
  }, [differeToolsModalOpen, differeToolsModalTab, differeModalModelPoolViewRole]);

  const handleStartComparison = async () => {
    const validModels = compareModels.filter(m => m.versionTag);
    if (validModels.length < 2) {
      flash('err', 'Sélectionnez au moins 2 modèles à comparer');
      return;
    }
    if (!compareOpponent.versionTag) {
      flash('err', 'Sélectionnez un modèle adversaire');
      return;
    }
    if (!confirm(`Lancer une comparaison de ${validModels.length} modèles sur ${compareConfig.dealCount} donnes ?`)) return;

    setBusy('compare');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({
          ...compareConfig,
          models: validModels,
          opponentModel: compareOpponent,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `Comparaison lancée (id: ${data.id})`);
      setNewCompareModalOpen(false);
      refreshComparisons();
    } catch (err: any) {
      flash('err', `Erreur : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleStopComparison = async (id: number) => {
    if (!confirm(
      'Arrêter après la donne en cours ? La comparaison sera enregistrée comme terminée, avec des statistiques '
      + 'sur le nombre de donnes déjà jouées (tronquées au même préfixe pour chaque modèle afin de rester comparable). '
      + 'L’analyse croisée entre modèles est ignorée si l’arrêt est anticipé.',
    )) return;
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/${id}/stop`, session.pseudo, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!data.stopped) {
        flash('err', 'Impossible d’arrêter (comparaison inactive ou déjà terminée).');
        refreshComparisons();
        return;
      }
      flash('ok', 'Arrêt demandé : la comparaison se terminera après la donne en cours.');
      refreshComparisons();
    } catch (err: any) {
      flash('err', err?.message || 'Arrêt impossible');
    }
  };

  const handleDeleteComparison = async (id: number) => {
    if (!confirm('Supprimer cette comparaison ?')) return;
    try {
      await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/${id}`, session.pseudo, { method: 'DELETE' });
      flash('ok', 'Comparaison supprimée');
      if (selectedComparison?.id === id) closeComparisonDetail();
      refreshComparisons();
    } catch { /* ignore */ }
  };

  const handleAppendModelToComparison = async () => {
    if (!selectedComparison?.id) return;
    if (!compareAppendModel.versionTag) {
      flash('err', 'Choisissez un modèle à ajouter');
      return;
    }
    const existingTags = new Set(
      (selectedComparison.config?.models ?? []).map((m: { versionTag?: string }) => m.versionTag),
    );
    if (existingTags.has(compareAppendModel.versionTag)) {
      flash('err', 'Ce modèle figure déjà dans cette comparaison');
      return;
    }
    if (!confirm(
      `Ajouter « ${compareAppendModel.label || compareAppendModel.versionTag} » sur les mêmes donnes que la comparaison #${selectedComparison.id} ? `
      + 'La comparaison repassera en cours d’exécution puis les résultats seront mis à jour.',
    )) return;

    setBusy('compare');
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/ml/compare/${selectedComparison.id}/append-model`,
        session.pseudo,
        {
          method: 'POST',
          body: JSON.stringify({ model: compareAppendModel }),
        },
      );
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `Comparaison #${selectedComparison.id} — ajout du modèle lancé`);
      setCompareAppendModel({ slot: '', label: '', versionTag: '' });
      closeComparisonDetail();
      refreshComparisons();
    } catch (err: any) {
      flash('err', err?.message || 'Erreur');
    } finally {
      setBusy('');
    }
  };

  const handleViewComparison = async (id: number) => {
    compareDetailScrollYRef.current = window.scrollY;
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/${id}`, session.pseudo);
      const data = await res.json();
      setSelectedComparison(data);
    } catch { /* ignore */ }
  };

  const handleExportComparisonJson = async (id: number) => {
    setCompareExportId(id);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/ml/compare/${id}?details=true`,
        session.pseudo,
      );
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      downloadJsonFile(`ml-compare-${id}.json`, data);
      flash('ok', `Export JSON — comparaison #${id} (donnes détaillées incluses)`);
    } catch (err: any) {
      flash('err', err?.message || 'Export JSON impossible');
    } finally {
      setCompareExportId(null);
    }
  };

  const handleExportDiffereBenchmarkSummary = () => {
    try {
      const runs = differeRuns as Array<{
        runKind?: string;
        status?: string;
      }>;
      const benchmarkRuns = runs.filter(r => r.runKind === 'benchmark');
      const pool = differePool as Array<{ active?: boolean; role?: string }>;
      const modelPool = differeModelPool as Array<{
        active?: boolean;
        label?: string;
        role?: string;
        versionTag?: string;
        slot?: string;
      }>;
      const payload = {
        kind: 'webtarot-neo-differe-benchmark-summary',
        exportedAt: new Date().toISOString(),
        filters: {
          rankingRole: differeRankingRole,
          minParticipationRatio: differeMinParticipation,
        },
        aggregate: differeRankings
          ? {
              global: differeRankings.global,
              globalAttackNoSc: differeRankings.globalAttackNoSc,
              globalAttackSc: differeRankings.globalAttackSc,
              globalDefense: differeRankings.globalDefense,
              totalDifferes: differeRankings.totalDifferes,
              minParticipationRatio: differeRankings.minParticipationRatio,
            }
          : null,
        stats: {
          pool: {
            total: pool.length,
            active: pool.filter(p => p.active).length,
            activeByRole: {
              attacker: pool.filter(p => p.active && p.role === 'attacker').length,
              defender: pool.filter(p => p.active && p.role === 'defender').length,
            },
          },
          modelPool: modelPool
            .filter(m => m.active)
            .map(m => ({
              label: m.label,
              role: m.role,
              versionTag: m.versionTag,
              slot: m.slot,
            })),
          runs: {
            totalListed: runs.length,
            benchmarkRunsStoredInDb: differeBenchmarkRunsStored,
            benchmark: {
              total: benchmarkRuns.length,
              completed: benchmarkRuns.filter(r => r.status === 'completed').length,
              running: benchmarkRuns.filter(r => r.status === 'running').length,
              failed: benchmarkRuns.filter(r => r.status === 'failed').length,
            },
          },
        },
      };
      downloadJsonFile(
        `ml-differe-benchmark-summary-${new Date().toISOString().slice(0, 10)}.json`,
        payload,
      );
      flash('ok', 'Résumé benchmark différés exporté (classement global + stats, sans détail par différé)');
    } catch (err: any) {
      flash('err', err?.message || 'Export impossible');
    }
  };

  const handleImportDiffere = async () => {
    const differeId = parseInt(differeImportId, 10);
    if (!differeId) {
      flash('err', 'Indiquez un id de différé individuel');
      return;
    }
    setBusy('differe-import');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/import`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({ differeId }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `Différé #${differeId} importé (${data.import.dealCount} donnes, ${data.import.participantCount} participants)`);
      setBot1Report(null);
      await refreshDiffereImports();
    } catch (err: any) {
      flash('err', `Import impossible : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleValidateBot1 = async (importId: number) => {
    setBusy(`bot1-${importId}`);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/${importId}/validate-bot1`, session.pseudo, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setBot1Report(data.report);
      flash('ok', `Validation bot1 : import ${data.report.replay.importValidationOk ? 'OK' : 'KO'} · scores ${data.report.replay.rawScoreMatches}/${data.report.replay.comparedDeals} · plis ${data.report.replay.trickMatches ?? '?'}/${data.report.replay.tricksCompared ?? '?'}`);
    } catch (err: any) {
      flash('err', `Validation bot1 échouée : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleValidateAllBot1 = async () => {
    const queue = bot1ValidationQueue;
    if (queue.length === 0) {
      flash('err', 'Aucun différé importé à valider');
      return;
    }
    if (!confirm(
      `Lancer la validation bot1 sur ${queue.length} différé(s) — ceux du pool et ceux importés mais pas encore au pool (vérif préalable) ? `
      + 'Cela peut prendre plusieurs minutes.',
    )) return;
    setBusy('bot1-batch');
    setBot1BatchNotOkIds([]);
    setBot1BatchProgress({ current: 0, total: queue.length });
    const notOk: number[] = [];
    try {
      for (let i = 0; i < queue.length; i++) {
        const { importId, sourceDiffereId } = queue[i];
        try {
          const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/${importId}/validate-bot1`, session.pseudo, {
            method: 'POST',
          });
          const data = await res.json();
          if (data.error) throw new Error(data.error);
          if (!isBot1ReportOk(data.report)) {
            notOk.push(sourceDiffereId);
          }
        } catch {
          notOk.push(sourceDiffereId);
        }
        setBot1BatchProgress({ current: i + 1, total: queue.length });
      }
      setBot1BatchNotOkIds(notOk);
      flash(
        'ok',
        `Validation bot1 terminée : ${queue.length - notOk.length} OK, ${notOk.length} non conforme(s)${
          notOk.length ? ` — #${notOk.join(', #')}` : ''
        }.`,
      );
      await refreshDiffereBenchmark();
    } finally {
      setBusy('');
      setBot1BatchProgress(null);
    }
  };

  const handleAddDiffereToPool = async (importId: number) => {
    setBusy(`differe-pool-${importId}`);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/pool`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({ importId }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `Différé #${data.entry.sourceDiffereId} ajouté au pool`);
      await refreshDiffereBenchmark();
    } catch (err: any) {
      flash('err', `Ajout au pool impossible : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const validateCriteriaImportForm = (): { ok: false } | { ok: true; count: number | null } => {
    const count = differePoolCriteria.count.trim() ? parseInt(differePoolCriteria.count, 10) : null;
    const hasDateCriterion = !!differePoolCriteria.dateFrom || !!differePoolCriteria.dateTo;
    if (!hasDateCriterion && count === null) {
      flash('err', 'Indiquez au moins une date début/fin ou un nombre de différés à charger');
      return { ok: false };
    }
    if (count !== null && (!Number.isFinite(count) || count <= 0)) {
      flash('err', 'Le nombre de différés doit être positif');
      return { ok: false };
    }
    return { ok: true, count };
  };

  const handleCriteriaImportPreview = async () => {
    const v = validateCriteriaImportForm();
    if (!v.ok) return;
    const { count } = v;
    setBusy('differe-criteria-preview');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/pool/import-criteria/preview`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({
          dateFrom: differePoolCriteria.dateFrom || null,
          dateTo: differePoolCriteria.dateTo || null,
          legacyType: differePoolCriteria.legacyType || null,
          count,
          excludeAlreadyInPool: differePoolCriteria.excludeAlreadyInPool,
          randomSample: differePoolCriteria.randomSample,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setCriteriaPreviewPayload(data.preview);
      setCriteriaImportWizardStep('preview');
      flash('ok', `Aperçu : ${data.preview?.selectedCount ?? 0} différé(s) retenu(s) pour les critères.`);
    } catch (err: any) {
      flash('err', `Aperçu impossible : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleCriteriaImportExecute = async () => {
    const rows = criteriaPreviewPayload?.rows as Array<{ sourceDiffereId: number; previewError?: string }> | undefined;
    if (!rows?.length) {
      flash('err', 'Préparez d’abord la sélection (étape 1).');
      return;
    }
    const ids = rows.filter(r => !r.previewError).map(r => r.sourceDiffereId);
    if (ids.length === 0) {
      flash('err', 'Aucun différé exploitable : toutes les lignes d’aperçu sont en erreur.');
      return;
    }
    setBusy('differe-criteria-execute');
    setCriteriaExecuteProgress({ current: 0, total: ids.length });
    const importedAccum: unknown[] = [];
    const skippedAccum: Array<{ sourceDiffereId: number; reason?: string }> = [];
    let rejectedBot1Accum = 0;
    try {
      for (let i = 0; i < ids.length; i++) {
        const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/pool/import-criteria/execute`, session.pseudo, {
          method: 'POST',
          body: JSON.stringify({
            sourceDiffereIds: [ids[i]],
            validateBot1BeforePool: !poolCriteriaBypassBot1,
          }),
        });
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        importedAccum.push(...(data.imported ?? []));
        skippedAccum.push(...(data.skipped ?? []));
        rejectedBot1Accum += Number(data.rejectedByBot1Validation ?? 0);
        setCriteriaExecuteProgress({ current: i + 1, total: ids.length });
      }
      const added = importedAccum.length;
      const skipCount = skippedAccum.length;
      const idsFailed = skippedAccum.map(s => s.sourceDiffereId);
      flash(
        'ok',
        poolCriteriaBypassBot1
          ? `${added} différé(s) au pool (sans bot1). ${skipCount} ignoré(s).`
          : `Étape import : ${added} ajouté(s) au pool ; ${rejectedBot1Accum} rejeté(s) après validation bot1 ; ${skipCount} ligne(s) d’ignorés.${
            idsFailed.length ? ` Échecs #${idsFailed.join(', #')}.` : ''
          }`,
      );
      setCriteriaImportWizardStep('idle');
      setCriteriaPreviewPayload(null);
      setDiffereToolsModalTab('runsRankings');
      await refreshDiffereImports();
      await refreshDiffereBenchmark();
    } catch (err: any) {
      flash('err', `Import impossible : ${err.message}`);
    } finally {
      setBusy('');
      setCriteriaExecuteProgress(null);
    }
  };

  const handleRemoveDiffereFromPool = async (poolId: number) => {
    if (!confirm('Retirer ce différé du pool benchmark ?')) return;
    setBusy(`differe-pool-remove-${poolId}`);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/pool/${poolId}`, session.pseudo, { method: 'DELETE' });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', 'Différé retiré du pool');
      await refreshDiffereBenchmark();
    } catch (err: any) {
      flash('err', `Retrait impossible : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleReimportDifferePool = async () => {
    const ids = [
      ...new Set(
        (differePool as Array<{ sourceDiffereId?: unknown }>)
          .map(entry => Number(entry.sourceDiffereId))
          .filter(id => Number.isFinite(id) && id > 0),
      ),
    ].sort((a, b) => a - b);
    if (ids.length === 0) {
      flash('err', 'Aucun différé dans le pool à ré-importer.');
      return;
    }
    if (!confirm(
      `Ré-importer les snapshots de ${ids.length} différé(s) du pool ?\n`
      + 'Cela met à jour les données source (dont les plis des Top humains) sans relancer les modèles.',
    )) return;

    const failedIds: number[] = [];
    setBusy('differe-pool-reimport');
    setDifferePoolReimportProgress({ current: 0, total: ids.length, failedIds: [] });
    try {
      for (let idx = 0; idx < ids.length; idx++) {
        const differeId = ids[idx];
        setDifferePoolReimportProgress({ current: idx, total: ids.length, failedIds: [...failedIds] });
        try {
          const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/import`, session.pseudo, {
            method: 'POST',
            body: JSON.stringify({ differeId, preserveRuns: true }),
          });
          const data = await res.json();
          if (data.error) throw new Error(data.error);
        } catch {
          failedIds.push(differeId);
        }
        setDifferePoolReimportProgress({ current: idx + 1, total: ids.length, failedIds: [...failedIds] });
      }
      await refreshDiffereImports();
      await refreshDiffereBenchmark();
      if (failedIds.length > 0) {
        flash('err', `Ré-import terminé avec ${failedIds.length} échec(s) : #${failedIds.join(', #')}`);
      } else {
        flash('ok', `${ids.length} différé(s) du pool ré-importé(s), sans relance des modèles.`);
      }
    } finally {
      setBusy('');
      setDifferePoolReimportProgress(null);
    }
  };

  const handleAddDiffereModel = async () => {
    if (!differeModelVersionTag) {
      flash('err', 'Sélectionnez un modèle à ajouter au pool');
      return;
    }
    let model: Record<string, unknown> | null = null;
    if (differeModelVersionTag === 'legacy-rv') {
      model = legacyRvCompareModel;
    } else if (differeModelVersionTag === 'pimc') {
      model = pimcCompareModel;
    } else if (differeModelVersionTag === 'hybrid') {
      if (differeModelRole !== 'attacker') {
        flash('err', 'Le méta-slot hybrid est réservé au rôle attaque');
        return;
      }
      if (!differeHybridLeadTag || !differeHybridFollowTag) {
        flash('err', 'Hybride : choisir le modèle entame (Loir) et le modèle réponses (consensus)');
        return;
      }
      const id = (differeHybridId || 'g-v1').trim().replace(/[^a-zA-Z0-9._-]+/g, '-');
      model = {
        slot: 'hybrid',
        versionTag: `hybrid:${id}`,
        label: `Hybrid Loir-lead+cons-follow→IARV@${differeHybridDepth}`,
        leadVersionTag: differeHybridLeadTag,
        followVersionTag: differeHybridFollowTag,
        openingsDepth: differeHybridDepth,
      };
    } else {
      const ver = modelVersions.find(v => v.versionTag === differeModelVersionTag);
      if (!ver?.s3ModelPath?.endsWith('.pt')) {
        model = null;
      } else {
        model = {
          slot: ver.versionTag,
          label: ver?.notes || ver?.versionTag || differeModelVersionTag,
          versionTag: differeModelVersionTag,
        };
      }
    }
    if (!model) {
      flash('err', 'Ce modèle doit disposer d’un artefact .pt S3 avant de pouvoir jouer les différés benchmark');
      return;
    }
    setBusy('differe-model-pool');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/models`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({ ...model, role: differeModelRole }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `${data.entry.label} ajouté au pool ${data.entry.role === 'attacker' ? 'attaque' : 'défense'}`);
      setDiffereModelVersionTag('');
      setDiffereHybridLeadTag('');
      setDiffereHybridFollowTag('');
      await refreshDiffereBenchmark();
    } catch (err: any) {
      flash('err', `Ajout modèle impossible : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleArchiveDiffereModel = async (modelPoolId: number, label: string) => {
    if (!confirm(
      `Archiver « ${label} » ?\n`
      + 'Le modèle ne sera plus inclus dans les sync (runs manquants) mais restera visible au classement avec ses résultats déjà joués.',
    )) return;
    setBusy(`differe-model-archive-${modelPoolId}`);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/models/${modelPoolId}`, session.pseudo, { method: 'DELETE' });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', 'Modèle archivé — historique conservé au classement');
      await refreshDiffereBenchmark();
    } catch (err: any) {
      flash('err', `Archivage impossible : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleReactivateDiffereModel = async (modelPoolId: number, label: string) => {
    if (!confirm(`Réactiver « ${label} » dans le pool benchmark ?`)) return;
    setBusy(`differe-model-reactivate-${modelPoolId}`);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/models/${modelPoolId}`, session.pseudo, {
        method: 'PATCH',
        body: JSON.stringify({ active: true }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', 'Modèle réactivé dans le pool');
      await refreshDiffereBenchmark();
    } catch (err: any) {
      flash('err', `Réactivation impossible : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleRerunDiffereModel = async (modelPoolId: number, label: string) => {
    const fallbackLabel = differeFallbackPolicy === 'none' ? 'aucun fallback' : 'fallback autorisé sauf pour les cartes jouées';
    if (!confirm(
      `Rejouer le benchmark différé pour « ${label} » sur les différés actifs compatibles ?\n`
      + `Les anciens runs benchmark terminés/en erreur de ce modèle seront supprimés puis recréés.\n`
      + `Mode : ${fallbackLabel}.`,
    )) return;
    setBusy(`differe-model-rerun-${modelPoolId}`);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/models/${modelPoolId}/rerun`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({ fallbackPolicy: differeFallbackPolicy }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const firstError = Array.isArray(data.errors) && data.errors.length > 0 ? data.errors[0] : null;
      if ((data.failed ?? 0) > 0) {
        flash('err', `Rejeu terminé avec ${data.failed} échec(s) : ${firstError?.message ?? 'voir historique des runs'}`);
      } else {
        flash('ok', `Rejeu terminé : ${data.started} run(s), ${data.deleted ?? 0} ancien(s) run(s) supprimé(s)`);
      }
      await refreshDiffereBenchmark();
    } catch (err: any) {
      flash('err', `Rejeu impossible : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const runDiffereBenchmarkSync = async (confirmLocalFallback = false, target: 'auto' | 'local' = 'auto') => {
    setBusy('differe-sync');
    let syncAcceptedAsync = false;
    if (differeSyncPreview?.plannedDeals) {
      setDiffereSyncBatchProgress({
        totalPlannedDeals: differeSyncPreview.plannedDeals,
        plannedRuns: differeSyncPreview.missingRuns,
        doneDeals: 0,
        completedRuns: 0,
        runningRuns: 0,
        percent: 0,
      });
    }
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/sync`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({
          fallbackPolicy: differeFallbackPolicy,
          target,
          confirmLocalFallback,
        }),
      });
      const data = await res.json();
      if (res.status === 202 && data.accepted) {
        syncAcceptedAsync = true;
        flash('ok', data.message || 'Sync benchmark lancé — suivi via la barre de progression');
        return;
      }
      if (res.status === 409 && data.needsFallbackConfirmation) {
        const ovhErr = data.routing?.ovhError || data.message || 'Worker OVH indisponible';
        if (!confirm(
          `Connexion au worker OVH interrompue ou échec :\n${ovhErr}\n\n`
          + 'Le worker OVH peut encore tourner (vérifiez les runs « running » et la barre batch).\n'
          + 'Annulez sauf si vous voulez relancer sur Neo prod (risque de doublons).',
        )) {
          flash('err', `Benchmark non lancé — OVH : ${ovhErr}`);
          return;
        }
        await runDiffereBenchmarkSync(true, 'local');
        return;
      }
      if (data.error) throw new Error(data.error);
      const firstError = Array.isArray(data.errors) && data.errors.length > 0 ? data.errors[0] : null;
      const targetLabel = data.executionTarget === 'ovh'
        ? 'serveur OVH CPU'
        : 'serveur Neo prod';
      const fallbackNote = data.routing?.fallbackUsed ? ' (repli prod après échec OVH)' : '';
      if ((data.failed ?? 0) > 0) {
        flash('err', `Sync ${targetLabel}${fallbackNote} : ${data.failed} échec(s) — ${firstError?.message ?? 'voir historique des runs'}`);
      } else {
        flash('ok', `Sync ${targetLabel}${fallbackNote} : ${data.started} run(s), ${data.skipped} déjà présents`);
      }
      await refreshDiffereBenchmark();
    } catch (err: any) {
      flash('err', `Sync impossible : ${err.message}`);
    } finally {
      if (!syncAcceptedAsync) setBusy('');
    }
  };

  const handleSyncDifferePool = async () => {
    const fallbackLabel = differeFallbackPolicy === 'none' ? 'aucun fallback' : 'fallback autorisé sauf pour les cartes jouées';
    const pv = differeSyncPreview;
    const est = pv && pv.missingRuns > 0
      ? `\nEstimation : ${pv.missingRuns} run(s), ${pv.plannedDeals} donne(s), ${pv.differesWithMissingRuns} différé(s) concerné(s).`
      : '';
    const ovhHint = differeWorkerStatus?.ovhDelegationEnabled
      ? '\nCible prioritaire : serveur OVH CPU (repli prod si indisponible).'
      : differeWorkerStatus?.ovhConfigured
        ? '\nWorker OVH configuré mais injoignable — vous pourrez choisir le repli prod.'
        : '\nExécution sur le serveur Neo prod (worker OVH non activé).';
    if (!confirm(`Lancer les runs manquants pour tous les couples modèle × différé compatibles ?\nMode : ${fallbackLabel}.${est}${ovhHint}`)) return;
    await runDiffereBenchmarkSync(false, 'auto');
  };

  const handleClearDiffereBenchmarkResults = async () => {
    if (!confirm(
      'Effacer les résultats des benchmarks différés (scores par donne et classements IA) ? '
      + 'Les runs benchmark terminés ou en erreur seront supprimés de la base ; les exécutions en cours sont conservées. '
      + 'Les validations bot1 (runs validation) ne sont pas concernées.',
    )) return;
    setBusy('differe-clear-benchmark-results');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/benchmark-runs`, session.pseudo, { method: 'DELETE' });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `${data.deleted ?? 0} run(s) benchmark effacé(s) — classements recalculés sans ces IA`);
      await refreshDiffereBenchmark();
    } catch (err: any) {
      flash('err', `Purge impossible : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleHideDiffereRunsFromAdminList = async () => {
    if (!confirm(
      'Masquer dans cette page tous les runs différés terminés ou en erreur (benchmarks et validations) ? '
      + 'Les scores et classements restent en base ; seul le tableau d’historique admin sera vidé.',
    )) return;
    setBusy('differe-hide-run-list');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/runs/hide-from-admin-list`, session.pseudo, { method: 'POST' });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('ok', `${data.updated ?? 0} run(s) masqué(s) de la liste — classements inchangés`);
      await refreshDiffereBenchmark();
    } catch (err: any) {
      flash('err', `Action impossible : ${err.message}`);
    } finally {
      setBusy('');
    }
  };

  const differeBenchmarkRunAgg = useMemo(() => {
    const rows = (differeRuns as Array<{ runKind?: string; status?: string; progress?: number; totalDeals?: number }>)
      .filter(r => r.runKind === 'benchmark' && r.status === 'running');
    if (rows.length === 0) return null;
    let prog = 0;
    let tot = 0;
    for (const r of rows) {
      prog += Math.max(0, Number(r.progress) || 0);
      tot += Math.max(0, Number(r.totalDeals) || 0);
    }
    const pct = tot > 0 ? Math.min(100, Math.round((prog / tot) * 100)) : null;
    return { prog, tot, pct };
  }, [differeRuns]);

  const differeProgressForBar = useMemo(() => {
    if (
      differeSyncBatchProgress !== null
      && differeSyncBatchProgress.totalPlannedDeals > 0
    ) {
      return {
        prog: differeSyncBatchProgress.doneDeals,
        tot: differeSyncBatchProgress.totalPlannedDeals,
        pct: differeSyncBatchProgress.percent,
        batch: true as const,
        completedRuns: differeSyncBatchProgress.completedRuns,
        plannedRuns: differeSyncBatchProgress.plannedRuns,
      };
    }
    if (differeBenchmarkRunAgg) {
      return { ...differeBenchmarkRunAgg, batch: false as const };
    }
    return null;
  }, [differeSyncBatchProgress, differeBenchmarkRunAgg]);

  const isDiffereBenchmarkBatchBusy = busy === 'differe-sync' || busy.startsWith('differe-model-rerun-');
  const differeBenchmarkProgressLabel = useMemo(
    () => formatDiffereBenchmarkProgressLabel(
      differeProgressForBar,
      isDiffereBenchmarkBatchBusy ? 'Runs benchmark en cours...' : 'En cours...',
    ),
    [differeProgressForBar, isDiffereBenchmarkBatchBusy],
  );

  const hasRunningDiffereBenchmark = differeBenchmarkRunAgg !== null;
  const hasDiffereSyncBatchProgress = differeSyncBatchProgress !== null;

  const differeBenchmarkRunsWithComparisonStats = useMemo(() => {
    let source: Array<Record<string, unknown>>;
    if (!differeBenchmarkMetricsModalOpen) {
      source = differeRuns as Array<Record<string, unknown>>;
    } else if (differeBenchmarkMetricsRunsLoading) {
      source = [];
    } else {
      source = differeBenchmarkMetricsRunList;
    }
    return source.filter(r => {
      if (r.runKind !== 'benchmark' && r.runKind !== 'benchmark_baseline') return false;
      if (r.status !== 'completed') return false;
      const importId = Number(r.importId);
      if (!activeDifferePoolImportIdSet.has(importId)) return false;
      const rep = r.report as Record<string, unknown> | null | undefined;
      const cs = rep?.comparisonStats as { fullRun?: unknown } | undefined;
      if (!cs?.fullRun) return false;
      const repModel = rep?.model != null && typeof rep.model === 'object'
        ? rep.model as { role?: unknown; versionTag?: unknown }
        : null;
      const role = repModel?.role === 'defender' || r.modelRole === 'defender'
        ? 'defender'
        : 'attacker';
      const versionTag = String(r.modelVersionTag || repModel?.versionTag || '');
      if (r.runKind === 'benchmark_baseline') return true;
      if (versionTag === 'legacy-rv') return true;
      return differeBenchmarkPoolModelKeys.has(`${role}:${versionTag}`);
    });
  }, [
    differeRuns,
    differeBenchmarkMetricsModalOpen,
    differeBenchmarkMetricsRunsLoading,
    differeBenchmarkMetricsRunList,
    activeDifferePoolImportIdSet,
    differeBenchmarkPoolModelKeys,
  ]);

  const differeBenchmarkMetricsImportOptions = useMemo(() => {
    const m = new Map<number, { importId: number; sourceDiffereId: number }>();
    for (const r of differePool as Array<Record<string, unknown>>) {
      const id = Number(r.importId);
      if (!Number.isFinite(id)) continue;
      if (!m.has(id)) {
        m.set(id, { importId: id, sourceDiffereId: Number(r.sourceDiffereId) || id });
      }
    }
    return [...m.values()].sort((a, b) => a.sourceDiffereId - b.sourceDiffereId);
  }, [differePool]);

  const closeDiffereMetricDrilldown = useCallback(() => {
    setDiffereMetricDrilldown(null);
    setDiffereMetricDealsPayload(null);
    setDiffereMetricDealsLoading(false);
    setDiffereBenchmarkDrillCompareTag(null);
    setDiffereDealDetailPanel(null);
    setDiffereDealDetailComparePanel(null);
    setDiffereDealDetailCompareLoading(false);
    setDiffereDealDetailCompareTarget(null);
    setDiffereDealDetailLoading(false);
    setDiffereDealDetailTrickFocus(null);
  }, []);

  useEffect(() => {
    if (!differeBenchmarkMetricsModalOpen || differeBenchmarkMetricsScope !== 'one') return;
    const opts = differeBenchmarkMetricsImportOptions;
    if (opts.length === 0) return;
    const ok = opts.some(o => o.importId === differeBenchmarkMetricsImportId);
    if (!ok) setDiffereBenchmarkMetricsImportId(opts[0].importId);
  }, [
    differeBenchmarkMetricsModalOpen,
    differeBenchmarkMetricsScope,
    differeBenchmarkMetricsImportId,
    differeBenchmarkMetricsImportOptions,
  ]);

  useEffect(() => {
    if (!differeBenchmarkMetricsModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDiffereBenchmarkMetricsModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [differeBenchmarkMetricsModalOpen]);

  useEffect(() => {
    if (differeBenchmarkMetricsModalOpen) {
      setDiffereBenchmarkMetricsShowAllModels(false);
    }
  }, [differeBenchmarkMetricsModalOpen]);

  useEffect(() => {
    if (!differeBenchmarkMetricsModalOpen) {
      setDiffereBenchmarkMetricsRunList([]);
      setDiffereBenchmarkMetricsRunsLoading(false);
      closeDiffereMetricDrilldown();
      return;
    }
    setDiffereBenchmarkMetricsRunsLoading(true);
    let cancelled = false;
    void (async () => {
      try {
        const selectedOption = DIFFERE_BENCHMARK_METRIC_VIEW_OPTIONS.find(o => o.value === differeBenchmarkMetricsView)
          ?? DIFFERE_BENCHMARK_METRIC_VIEW_OPTIONS[0];
        const params = new URLSearchParams({
          limit: '5000',
          role: selectedOption.compareRole,
          segment: selectedOption.segment,
        });
        if (differeBenchmarkMetricsScope === 'one' && differeBenchmarkMetricsImportId != null) {
          params.set('importId', String(differeBenchmarkMetricsImportId));
        }
        const res = await adminFetch(
          `${BACKEND_URL}/api/admin/ml/compare/differes/benchmark-completed-runs?${params}`,
          session.pseudo,
        );
        const data = await res.json();
        if (!cancelled) {
          if (data?.error) {
            setDiffereBenchmarkMetricsRunList([]);
          } else {
            setDiffereBenchmarkMetricsRunList([
              ...(((data?.runs as Array<Record<string, unknown>> | undefined) ?? [])),
              ...(((data?.baselines as Array<Record<string, unknown>> | undefined) ?? [])),
            ]);
          }
        }
      } catch {
        if (!cancelled) setDiffereBenchmarkMetricsRunList([]);
      } finally {
        if (!cancelled) setDiffereBenchmarkMetricsRunsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    differeBenchmarkMetricsModalOpen,
    differeBenchmarkMetricsScope,
    differeBenchmarkMetricsImportId,
    differeBenchmarkMetricsView,
    session.pseudo,
    closeDiffereMetricDrilldown,
  ]);

  const differeBenchmarkMetricsAggregated = useMemo(() => {
    let runs = differeBenchmarkRunsWithComparisonStats as Array<Record<string, any>>;
    if (differeBenchmarkMetricsScope === 'one' && differeBenchmarkMetricsImportId != null) {
      runs = runs.filter(r => Number(r.importId) === differeBenchmarkMetricsImportId);
    }
    return aggregateDiffereBenchmarkMetricsFromRuns(runs);
  }, [
    differeBenchmarkRunsWithComparisonStats,
    differeBenchmarkMetricsScope,
    differeBenchmarkMetricsImportId,
  ]);

  const differeBenchmarkMetricsSelectedView = useMemo(() => {
    const option = DIFFERE_BENCHMARK_METRIC_VIEW_OPTIONS.find(o => o.value === differeBenchmarkMetricsView)
      ?? DIFFERE_BENCHMARK_METRIC_VIEW_OPTIONS[0];
    const allRows =
      option.value === 'attackerFull'
        ? differeBenchmarkMetricsAggregated.attacker.fullRun
        : option.value === 'attackNoSc'
          ? differeBenchmarkMetricsAggregated.attacker.attackNoSc
          : option.value === 'attackSc'
            ? differeBenchmarkMetricsAggregated.attacker.attackSc
            : differeBenchmarkMetricsAggregated.defender.defense;
    const aiCount = allRows.filter(isDiffereBenchmarkMetricsAiModelRow).length;
    const rankedTags = topDiffereAiVersionTagsFromRankings(
      differeBenchmarkMetricsRankingRowsForView(differeRankings, option.value),
      option.compareRole,
      DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT,
    );
    const rows = differeBenchmarkMetricsShowAllModels || aiCount <= DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT
      ? allRows
      : filterDiffereBenchmarkMetricsRowsTopAi(allRows, rankedTags, DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT);
    return {
      ...option,
      rows,
      allRows,
      aiCount,
      limitedToTopAi: !differeBenchmarkMetricsShowAllModels && aiCount > DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT,
      rankingBasedLimit: rankedTags.length > 0,
    };
  }, [
    differeBenchmarkMetricsAggregated,
    differeBenchmarkMetricsView,
    differeBenchmarkMetricsShowAllModels,
    differeRankings,
  ]);

  const handleExportDiffereBenchmarkMetrics = useCallback(async () => {
    setDiffereMetricsExportBusy(true);
    try {
      const scopeMode = differeBenchmarkMetricsScope;
      const importId = differeBenchmarkMetricsImportId;
      const params = new URLSearchParams({ limit: '5000' });
      if (scopeMode === 'one' && importId != null) {
        params.set('importId', String(importId));
      }
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/ml/compare/differes/benchmark-completed-runs?${params}`,
        session.pseudo,
      );
      const data = await res.json();
      if (data?.error) throw new Error(data.error);
      const rawRuns = [
        ...(((data?.runs as Array<Record<string, unknown>> | undefined) ?? [])),
        ...(((data?.baselines as Array<Record<string, unknown>> | undefined) ?? [])),
      ];
      const runs = filterDiffereBenchmarkRunsForMetricsAggregation(
        rawRuns,
        activeDifferePoolImportIdSet,
        differeBenchmarkPoolModelKeys,
      );
      const aggregated = aggregateDiffereBenchmarkMetricsFromRuns(runs);
      const selectedImport = scopeMode === 'one' && importId != null
        ? differeBenchmarkMetricsImportOptions.find(o => o.importId === importId)
        : null;
      const payload = buildDiffereBenchmarkMetricsExportPayload(aggregated, {
        mode: scopeMode,
        importId: scopeMode === 'one' ? importId : null,
        sourceDiffereId: selectedImport?.sourceDiffereId ?? null,
      });
      const date = new Date().toISOString().slice(0, 10);
      const scopeSuffix = scopeMode === 'one' && selectedImport
        ? `-differe-${selectedImport.sourceDiffereId}`
        : '-all';
      downloadJsonFile(`ml-differe-benchmark-metrics${scopeSuffix}-${date}.json`, payload);
      flash(
        'ok',
        `Métriques benchmark exportées (${aggregated.runsConsidered} run(s), tous segments, sans drill-down)`,
      );
    } catch (err: any) {
      flash('err', err?.message || 'Export métriques benchmark impossible');
    } finally {
      setDiffereMetricsExportBusy(false);
    }
  }, [
    differeBenchmarkMetricsScope,
    differeBenchmarkMetricsImportId,
    differeBenchmarkMetricsImportOptions,
    activeDifferePoolImportIdSet,
    differeBenchmarkPoolModelKeys,
    session.pseudo,
    flash,
  ]);

  const loadDiffereBenchmarkDrillDeals = useCallback(
    async (
      drill: {
        metric: string;
        versionTag: string;
        modelLabel: string;
        compareRole: 'defender' | 'attacker';
        segment: DiffereBenchmarkMetricSegment;
      },
      compareVersionTag: string | undefined,
    ) => {
      setDiffereMetricDealsLoading(true);
      setDiffereDealDetailPanel(null);
      setDiffereDealDetailTrickFocus(null);
      try {
        const params = new URLSearchParams({
          versionTag: drill.versionTag,
          role: drill.compareRole,
          metric: drill.metric,
          segment: drill.segment,
        });
        if (differeBenchmarkMetricsScope === 'one' && differeBenchmarkMetricsImportId != null) {
          params.set('importId', String(differeBenchmarkMetricsImportId));
        }
        if (compareVersionTag != null && compareVersionTag !== '') {
          params.set('compareVersionTag', compareVersionTag);
        }
        const res = await adminFetch(
          `${BACKEND_URL}/api/admin/ml/compare/differes/metric-deals?${params}`,
          session.pseudo,
        );
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        setDiffereMetricDealsPayload(data);
        setDiffereBenchmarkDrillCompareTag(
          typeof data.compareVersionTagUsed === 'string' ? data.compareVersionTagUsed : compareVersionTag ?? null,
        );
      } catch (e: unknown) {
        flash('err', e instanceof Error ? e.message : 'Chargement des donnes impossible');
        closeDiffereMetricDrilldown();
      } finally {
        setDiffereMetricDealsLoading(false);
      }
    },
    [
      session.pseudo,
      differeBenchmarkMetricsScope,
      differeBenchmarkMetricsImportId,
      flash,
      closeDiffereMetricDrilldown,
    ],
  );

  const openDiffereBenchmarkMetricDrilldown = useCallback(
    async (
      metric: string,
      row: Record<string, any>,
      compareRole: 'defender' | 'attacker',
      segment: DiffereBenchmarkMetricSegment,
    ) => {
      const versionTag = String(row.versionTag || '');
      if (!versionTag) {
        flash('err', 'Version modèle introuvable pour ce benchmark.');
        return;
      }
      const modelLabel = String(row.modelLabel || row.versionTag || versionTag);
      const drill = { metric, versionTag, modelLabel, compareRole, segment };
      setDiffereBenchmarkDrillCompareTag(null);
      setDiffereMetricDrilldown(drill);
      await loadDiffereBenchmarkDrillDeals(drill, undefined);
    },
    [flash, loadDiffereBenchmarkDrillDeals],
  );

  const invertDiffereBenchmarkMetricDrill = useCallback(async () => {
    if (!differeMetricDrilldown || !differeBenchmarkDrillCompareTag) return;
    const oldPrimaryVt = differeMetricDrilldown.versionTag;
    const newPrimaryVt = differeBenchmarkDrillCompareTag;
    if (!differeBenchmarkIsMlPoolMetricDrillTag(oldPrimaryVt) || !differeBenchmarkIsMlPoolMetricDrillTag(newPrimaryVt)) {
      return;
    }
    const cm =
      (differeMetricDealsPayload?.comparisonModels as Array<{ versionTag: string; modelLabel: string }> | undefined)
      ?? [];
    const newPrimaryLabel = cm.find(m => m.versionTag === newPrimaryVt)?.modelLabel ?? newPrimaryVt;
    const drill = {
      ...differeMetricDrilldown,
      versionTag: newPrimaryVt,
      modelLabel: newPrimaryLabel,
    };
    setDiffereMetricDrilldown(drill);
    await loadDiffereBenchmarkDrillDeals(drill, oldPrimaryVt);
  }, [
    differeMetricDrilldown,
    differeBenchmarkDrillCompareTag,
    differeMetricDealsPayload?.comparisonModels,
    loadDiffereBenchmarkDrillDeals,
  ]);

  const openDiffereBenchmarkDealDetail = useCallback(
    async (
      runId: number,
      dealIndex: number,
      metricOverride?: string,
      baselineKey?: string | null,
      importId?: number | null,
    ) => {
      const metric = metricOverride ?? differeMetricDrilldown?.metric;
      setDiffereDealDetailLoading(true);
      setDiffereDealDetailPanel(null);
      setDiffereDealDetailComparePanel(null);
      setDiffereDealDetailCompareTarget(null);
      setDiffereDealDetailTrickFocus(null);
      try {
        const params = new URLSearchParams({ dealIndex: String(dealIndex) });
        if (metric) params.set('metric', metric);
        let url = `${BACKEND_URL}/api/admin/ml/compare/differes/runs/${runId}/deal-detail?${params}`;
        if (baselineKey) {
          if (importId == null) throw new Error('Import introuvable pour cette baseline.');
          params.set('baselineKey', baselineKey);
          params.set('importId', String(importId));
          url = `${BACKEND_URL}/api/admin/ml/compare/differes/baselines/deal-detail?${params}`;
        }
        const res = await adminFetch(
          url,
          session.pseudo,
        );
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        setDiffereDealDetailPanel(data);
        const cm =
          (differeMetricDealsPayload?.comparisonModels as Array<{ versionTag: string; modelLabel: string }> | undefined)
          ?? [];
        const opts = differeBenchmarkBuildDealCompareOptions(
          { runId: Number(data.runId), versionTag: String(data.versionTag ?? '') },
          cm,
        );
        setDiffereDealDetailCompareTarget(pickDefaultDiffereDealCompareTarget(opts, differeBenchmarkDrillCompareTag));
      } catch (e: unknown) {
        flash('err', e instanceof Error ? e.message : 'Détail impossible');
      } finally {
        setDiffereDealDetailLoading(false);
      }
    },
    [
      differeMetricDrilldown?.metric,
      differeMetricDealsPayload?.comparisonModels,
      differeBenchmarkDrillCompareTag,
      session.pseudo,
      flash,
    ],
  );

  const differeDealDetailStudiedSeatCols = useMemo(() => {
    if (!differeDealDetailPanel) return [] as number[];
    const taker = Number((differeDealDetailPanel as { takerPosition?: number }).takerPosition ?? 0);
    const roleRaw = (differeDealDetailPanel as { compareRole?: string }).compareRole;
    const role: 'defender' | 'attacker' = roleRaw === 'defender' ? 'defender' : 'attacker';
    return mlCompareStudiedSeatColumnIndexes(taker, role);
  }, [differeDealDetailPanel]);

  const differeDealDetailStudiedSeats = useMemo(
    () => [...new Set(differeDealDetailStudiedSeatCols.map(c => c % 4))],
    [differeDealDetailStudiedSeatCols],
  );

  const differeDealDetailMetricNav = useMemo(() => {
    const deals = (differeMetricDealsPayload?.deals as Array<{
      runId: number;
      dealIndex: number;
      baselineKey?: string | null;
      importId?: number | null;
    }> | undefined) ?? [];
    if (!differeDealDetailPanel || deals.length === 0) {
      return {
        listIndex: -1,
        listLen: 0,
        prev: null as { runId: number; dealIndex: number; baselineKey?: string | null; importId?: number | null } | null,
        next: null as { runId: number; dealIndex: number; baselineKey?: string | null; importId?: number | null } | null,
      };
    }
    const curRunId = Number(differeDealDetailPanel.runId);
    const curDealIndex = Number(differeDealDetailPanel.dealIndex);
    const curImportId = Number(differeDealDetailPanel.importId);
    const curBaselineKey = String((differeDealDetailPanel as { versionTag?: unknown }).versionTag ?? '');
    const listIndex = deals.findIndex(d =>
      Number(d.runId) === curRunId
      && Number(d.dealIndex) === curDealIndex
      && (d.baselineKey ? (d.baselineKey === curBaselineKey && Number(d.importId) === curImportId) : true));
    return {
      listIndex,
      listLen: deals.length,
      prev: listIndex > 0 ? deals[listIndex - 1] : null,
      next: listIndex >= 0 && listIndex < deals.length - 1 ? deals[listIndex + 1] : null,
    };
  }, [differeMetricDealsPayload?.deals, differeDealDetailPanel]);

  const differeDealDetailCompareOptions = useMemo(() => {
    if (!differeDealDetailPanel) return [] as Array<{ key: string; label: string }>;
    const cm =
      (differeMetricDealsPayload?.comparisonModels as Array<{ versionTag: string; modelLabel: string }> | undefined)
      ?? [];
    return differeBenchmarkBuildDealCompareOptions(
      {
        runId: Number(differeDealDetailPanel.runId),
        versionTag: String((differeDealDetailPanel as { versionTag?: unknown }).versionTag ?? ''),
      },
      cm,
    );
  }, [differeDealDetailPanel, differeMetricDealsPayload?.comparisonModels]);

  const differeDealDetailSyncTrickNums = useMemo(() => {
    if (!differeDealDetailPanel || !differeDealDetailComparePanel) return new Set<number>();
    return mlCompareIdenticalCardPrefixTrickNumbers(
      (differeDealDetailPanel.tricks as Array<Record<string, unknown>>) ?? [],
      (differeDealDetailComparePanel.tricks as Array<Record<string, unknown>>) ?? [],
    );
  }, [differeDealDetailPanel, differeDealDetailComparePanel]);

  const differeDealDetailEcartCommonBoth = useMemo(() => {
    if (!differeDealDetailPanel || !differeDealDetailComparePanel) return new Set<number>();
    const ea = (differeDealDetailPanel.ecart as number[]) ?? [];
    const eb = (differeDealDetailComparePanel.ecart as number[]) ?? [];
    const sb = new Set(eb);
    return new Set(ea.filter(c => sb.has(c)));
  }, [differeDealDetailPanel, differeDealDetailComparePanel]);

  useEffect(() => {
    if (!differeDealDetailPanel || !differeMetricDrilldown || !differeDealDetailCompareTarget) {
      setDiffereDealDetailComparePanel(null);
      setDiffereDealDetailCompareLoading(false);
      return;
    }
    const primaryVt = String((differeDealDetailPanel as { versionTag?: unknown }).versionTag ?? '');
    const primaryRunId = Number(differeDealDetailPanel.runId);
    if (differeDealDetailCompareTarget === primaryVt && primaryRunId > 0) {
      setDiffereDealDetailComparePanel(null);
      setDiffereDealDetailCompareLoading(false);
      return;
    }
    const importId = Number(differeDealDetailPanel.importId);
    const dealIndex = Number(differeDealDetailPanel.dealIndex);
    const metric = differeMetricDrilldown.metric;
    const role = differeMetricDrilldown.compareRole;
    if (!Number.isFinite(importId) || !Number.isFinite(dealIndex)) {
      setDiffereDealDetailComparePanel(null);
      setDiffereDealDetailCompareLoading(false);
      return;
    }
    const target = differeDealDetailCompareTarget;
    const isBaseline =
      target === DIFFERE_BENCHMARK_BOT1_BASELINE_TAG || differeBenchmarkIsHumanTopBaselineTag(target);
    let cancelled = false;
    setDiffereDealDetailCompareLoading(true);
    void (async () => {
      try {
        const params = new URLSearchParams({ dealIndex: String(dealIndex) });
        if (metric) params.set('metric', metric);
        let url: string;
        if (isBaseline) {
          params.set('baselineKey', target);
          params.set('importId', String(importId));
          url = `${BACKEND_URL}/api/admin/ml/compare/differes/baselines/deal-detail?${params}`;
        } else {
          const rRun = await adminFetch(
            `${BACKEND_URL}/api/admin/ml/compare/differes/benchmark-run-id?${new URLSearchParams({
              importId: String(importId),
              versionTag: target,
              role,
            })}`,
            session.pseudo,
          );
          const meta = await rRun.json();
          if (cancelled) return;
          const otherRunId = meta?.runId != null ? Number(meta.runId) : NaN;
          if (!Number.isFinite(otherRunId)) {
            setDiffereDealDetailComparePanel(null);
            return;
          }
          url = `${BACKEND_URL}/api/admin/ml/compare/differes/runs/${otherRunId}/deal-detail?${params}`;
        }
        const res = await adminFetch(url, session.pseudo);
        const data = await res.json();
        if (cancelled) return;
        if (data.error) {
          setDiffereDealDetailComparePanel(null);
          return;
        }
        setDiffereDealDetailComparePanel(data);
      } catch {
        if (!cancelled) setDiffereDealDetailComparePanel(null);
      } finally {
        if (!cancelled) setDiffereDealDetailCompareLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setDiffereDealDetailCompareLoading(false);
    };
  }, [differeDealDetailPanel, differeDealDetailCompareTarget, differeMetricDrilldown, session.pseudo]);

  const toggleDiffereDealDetailTrickFocus = useCallback((trickNum1: number) => {
    setDiffereDealDetailTrickFocus(prev => (prev === trickNum1 ? null : trickNum1));
  }, []);

  useEffect(() => {
    if (!isDiffereBenchmarkBatchBusy && !hasRunningDiffereBenchmark && !hasDiffereSyncBatchProgress) {
      setDiffereSyncBatchProgress(null);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/active-sync-progress`, session.pseudo);
        const data = await res.json();
        if (cancelled || data?.error) return;
        if (data.active && typeof data.totalPlannedDeals === 'number') {
          setDiffereSyncBatchProgress({
            totalPlannedDeals: data.totalPlannedDeals,
            plannedRuns: typeof data.plannedRuns === 'number' ? data.plannedRuns : 0,
            doneDeals: typeof data.doneDeals === 'number' ? data.doneDeals : 0,
            completedRuns: typeof data.completedRuns === 'number' ? data.completedRuns : 0,
            runningRuns: typeof data.runningRuns === 'number' ? data.runningRuns : 0,
            percent: typeof data.percent === 'number' ? data.percent : 0,
          });
        } else if (busy === 'differe-sync' && !data.active && !hasRunningDiffereBenchmark) {
          setDiffereSyncBatchProgress(null);
          setBusy('');
        } else if (!isDiffereBenchmarkBatchBusy) {
          setDiffereSyncBatchProgress(null);
        }
      } catch { /* ignore */ }
    };
    void poll();
    const id = setInterval(poll, 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [busy, hasDiffereSyncBatchProgress, hasRunningDiffereBenchmark, isDiffereBenchmarkBatchBusy, session.pseudo]);

  const showDiffereBenchmarkProgressBar = isDiffereBenchmarkBatchBusy
    || hasRunningDiffereBenchmark
    || hasDiffereSyncBatchProgress;

  if (loading) return <div className="admin-loading">Chargement...</div>;

  const stateLabel = (s: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      RUNNING:      { label: 'En cours',       cls: 'running' },
      QUEUED:       { label: 'En attente',     cls: 'pending' },
      PENDING:      { label: 'Démarrage',      cls: 'pending' },
      INITIALIZING: { label: 'Initialisation', cls: 'pending' },
      DONE:         { label: 'Terminé',        cls: 'done' },
      FAILED:       { label: 'Échoué',         cls: 'failed' },
      ERROR:        { label: 'Erreur',         cls: 'failed' },
      STOPPED:      { label: 'Arrêté',         cls: 'stopped' },
      INTERRUPTED:  { label: 'Interrompu',     cls: 'stopped' },
    };
    const info = map[s] || { label: s, cls: '' };
    return <span className={`ml-job-badge ml-job-badge--${info.cls}`}>{info.label}</span>;
  };

  const formatDuration = (sec?: number) => {
    if (!sec) return '—';
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}min ${sec % 60}s`;
    return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}min`;
  };

  const selectBenchmarkRankingRows = (rows: any[]) => {
    const sortedRows = [...rows].sort((a, b) => {
      const rankA = typeof a.rank === 'number' ? a.rank : Number.MAX_SAFE_INTEGER;
      const rankB = typeof b.rank === 'number' ? b.rank : Number.MAX_SAFE_INTEGER;
      if (rankA !== rankB) return rankA - rankB;
      const totA = typeof a.tot === 'number' ? a.tot : typeof a.averagePoints === 'number' ? a.averagePoints : 0;
      const totB = typeof b.tot === 'number' ? b.tot : typeof b.averagePoints === 'number' ? b.averagePoints : 0;
      return totB - totA;
    });
    const selected = new Set<number>();
    const addIndex = (idx: number) => {
      if (idx >= 0 && idx < sortedRows.length) selected.add(idx);
    };
    for (let i = 0; i < Math.min(3, sortedRows.length); i++) addIndex(i);
    sortedRows.forEach((row, idx) => {
      if (row.kind === 'ai') {
        const r = typeof row.rank === 'number' && Number.isFinite(row.rank) ? row.rank : null;
        const showNeighbors = r !== null && r >= 1 && r <= 50;
        addIndex(idx);
        if (showNeighbors) {
          addIndex(idx - 1);
          addIndex(idx + 1);
        }
      } else if (mlDiffereRankingRowIsBot1(row)) {
        addIndex(idx);
      }
    });

    return Array.from(selected)
      .sort((a, b) => a - b)
      .map(idx => sortedRows[idx]);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const baseVersionOptions = getBaseVersionOptions();
  const opponentVersionOptions = getOpponentVersionOptions();
  const selectedBaseVersion = baseVersionOptions.some(v => v.versionTag === trainConfig.baseVersion)
    ? trainConfig.baseVersion
    : '';
  const selectedOpponentVersion = trainConfig.opponentVersion === 'legacy-rv'
    || opponentVersionOptions.some(v => v.versionTag === trainConfig.opponentVersion)
    ? trainConfig.opponentVersion
    : '';
  const canChooseBaseVersion = trainConfig.mode !== 'deductions' && trainConfig.mode !== 'reward_only';
  const canChooseTrainingRole = trainConfig.mode === 'selfplay' || trainConfig.mode === 'full' || trainConfig.mode === 'rlhf_only';
  const needsPpoBase = trainConfig.mode === 'full' || trainConfig.mode === 'rlhf_only';

  return (
    <div className="admin-ml">
      {message && (
        <div className={`ml-flash ml-flash-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="ml-sections-nav">
        {ML_SECTION_ORDER.map(s => (
          <button
            key={s}
            className={`ml-section-btn ${section === s ? 'active' : ''}`}
            onClick={() => onSectionChange(s)}
          >
            {ML_SECTION_LABELS[s]}
          </button>
        ))}
      </div>

      {!status?.configured && (
        <div className="ml-warning">
          Credentials OVHcloud non configurés. Définissez OVH_AI_TOKEN, OVH_S3_ACCESS_KEY et OVH_S3_SECRET_KEY dans les variables d'environnement du backend.
        </div>
      )}

      {/* ── Overview ─────────────────────────────────────── */}
      {section === 'overview' && status && (
        <div className="ml-overview">
          {modelVersions.length > 0 && (
            <div className="ml-deployment-overview">
              <h3 className="ml-deployment-overview-title">Modèles par environnement</h3>
              <p className="ml-deployment-overview-hint">
                Noms issus du registre (notes). À droite : ce que le serveur jouerieML a réellement chargé est rappelé dans l’onglet Déployer.
              </p>
              <div className="ml-deployment-overview-grid">
                {(['production', 'beta'] as const).map(env => {
                  const def = mlDeployedVersionEntry(modelVersions, env, 'defender');
                  const att = mlDeployedVersionEntry(modelVersions, env, 'attacker');
                  const ded = mlDeployedVersionEntry(modelVersions, env, 'deductions');
                  const envTitle = env === 'production' ? 'Production' : 'Bêta';
                  return (
                    <div key={env} className={`ml-deployment-env ml-deployment-env--${env}`}>
                      <div className="ml-deployment-env-header">{envTitle}</div>
                      <dl className="ml-deployment-env-list">
                        <div className="ml-deployment-env-row">
                          <dt>Défense</dt>
                          <dd>{def ? mlRegistryDisplayName(def) : <span className="ml-deployment-missing">—</span>}</dd>
                        </div>
                        <div className="ml-deployment-env-row">
                          <dt>Attaque</dt>
                          <dd>{att ? mlRegistryDisplayName(att) : <span className="ml-deployment-missing">—</span>}</dd>
                        </div>
                        <div className="ml-deployment-env-row">
                          <dt>Déductions</dt>
                          <dd>
                            {ded
                              ? mlRegistryDisplayName(ded)
                              : env === 'production' && status.deductionModelLoaded
                                ? 'Modèle chargé (jouerieML)'
                                : <span className="ml-deployment-missing">—</span>}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="admin-stats-grid">
            <StatCard label="Version BDD (legacy)" value={status.modelVersion} accent="blue" />
            <StatCard label="Feedback prêt" value={status.feedbackReady} accent="green" />
            <StatCard label="Feedback consommé" value={status.feedbackConsumed} />
            <StatCard
              label="Jobs en cours"
              value={status.runningJobs}
              accent={status.runningJobs > 0 ? 'orange' : undefined}
            />
            <StatCard
              label="Modèle déductions"
              value={status.deductionModelLoaded ? 'Chargé' : 'Non chargé'}
              accent={status.deductionModelLoaded ? 'green' : undefined}
            />
          </div>
          <div className="ml-status-indicator">
            {status.runningJobs > 0 ? (
              <span className="ml-badge ml-badge-running">Entraînement en cours</span>
            ) : status.feedbackReady >= 20 ? (
              <span className="ml-badge ml-badge-ready">Prêt à entraîner ({status.feedbackReady} feedbacks)</span>
            ) : (
              <span className="ml-badge ml-badge-wait">⏳ Pas assez de données ({status.feedbackReady}/20 min.)</span>
            )}
          </div>

          {modelVersions.length > 0 && (
            <div className="admin-section" style={{ marginTop: '16px' }}>
              <h3>Détail des versions déployées (registre)</h3>
              <div className="ml-deployed-versions ml-deployed-versions--grid">
                {deployedSortedVersions.map(v => {
                  const displayName = mlRegistryDisplayName(v);
                  const accuracy = v.evalMetrics?.details?.defenderAccuracy || v.evalMetrics?.details?.attackerAccuracy;
                  const roleLabel = v.modelRole === 'deductions' ? 'Déductions' : v.modelRole === 'defender' ? 'Défense' : 'Attaque';
                  return (
                    <div key={v.id} className={`ml-deployed-card ml-deployed-card--${v.deployedSlot || 'none'}`}>
                      <div className="ml-deployed-card-top">
                        <span className={`ml-deployed-slot ml-deployed-slot--${v.deployedSlot}`}>
                          {(v.deployedSlot === 'beta' ? 'BÊTA' : 'PRODUCTION')}
                        </span>
                        <span className="ml-deployed-role">{roleLabel}</span>
                      </div>
                      <div className="ml-deployed-name">{displayName}</div>
                      {displayName !== v.versionTag ? (
                        <div className="ml-deployed-tagline">{v.versionTag}</div>
                      ) : null}
                      <div className="ml-deployed-meta">
                        {v.trainingMode}
                        {v.modelRole === 'deductions' && v.evalMetrics?.details?.macro_f1_nontrivial
                          ? ` — F1: ${((v.evalMetrics.details.macro_f1_nontrivial as number) * 100).toFixed(1)}%`
                          : accuracy ? ` — accuracy: ${accuracy}` : ''}
                        {v.evalMetrics?.details?.trainingType === 'selfplay' ? ` — win rate: ${(v.evalMetrics.winRate * 100).toFixed(1)}%` : ''}
                      </div>
                    </div>
                  );
                })}
                {deployedSortedVersions.length === 0 && (
                  <span className="ml-deployed-none">Aucune version du registre déployée</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Export & Train ────────────────────────────────── */}
      {section === 'train' && (
        <div className="ml-train">
          <div className="admin-section" style={{ background: 'rgba(100,149,237,0.08)', borderLeft: '3px solid #6495ed', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' }}>
            <h3 style={{ marginTop: 0 }}>Guide rapide</h3>
            <div style={{ fontSize: '0.9em', lineHeight: '1.7' }}>
              <p style={{ margin: '0 0 6px' }}><strong>Parcours typique pour entraîner une IA :</strong></p>
              <ol style={{ margin: '0 0 8px 20px', padding: 0 }}>
                <li><strong>Imitation learning</strong> : modèle supervisé depuis des donnes filtrées en SQL.</li>
                <li><strong>Déductions</strong> : DeductionMLP entraîné sur les mêmes sources propres.</li>
                <li><strong>RLHF / self-play</strong> : amélioration d'un modèle déjà déployé.</li>
              </ol>
              <p style={{ margin: 0, color: '#666' }}>
                La sélection ML utilise maintenant <code>idcompetition</code>, <code>etats</code> et <code>pseudos</code> côté API, sans post-filtrage Python.
              </p>
            </div>
          </div>

          <div className="admin-section">
            <h3>Mode d'entraînement</h3>
            <div className="ml-mode-cards">
              {([
                { value: 'imitation' as TrainMode, icon: <GraduationIcon size={22} />, label: 'Imitation', desc: 'Apprendre depuis les donnes filtrées SQL' },
                { value: 'deductions' as TrainMode, icon: <CommentIcon size={22} />, label: 'Déductions', desc: 'MLP assertions / mains adverses' },
                { value: 'selfplay' as TrainMode, icon: <CrossedSwordsTableIcon size={22} />, label: 'Self-play', desc: 'L\'IA joue contre elle-même' },
                { value: 'full' as TrainMode, icon: <CycleIcon size={22} />, label: 'Cycle complet', desc: 'Reward + PPO (feedback requis)' },
                { value: 'reward_only' as TrainMode, icon: <ScaleIcon size={22} />, label: 'Reward seul', desc: 'Vérifier le feedback' },
                { value: 'rlhf_only' as TrainMode, icon: <CrosshairIcon size={22} />, label: 'RLHF seul', desc: 'Renforcement sans recalcul reward' },
                { value: 'bidding' as TrainMode, icon: <CardsIcon size={22} />, label: 'Enchères', desc: 'Self-play enchères' },
                { value: 'discard' as TrainMode, icon: <PackageIcon size={22} />, label: 'Écart', desc: 'Self-play écart' },
              ]).map(m => (
                <button
                  key={m.value}
                  className={`ml-mode-card${trainConfig.mode === m.value ? ' ml-mode-card--active' : ''}`}
                  onClick={() => {
                    const updates: Partial<typeof trainConfig> = { mode: m.value };
                    if (m.value === 'deductions') {
                      updates.playerSelectionMode = 'competition_team_top_teams';
                      updates.modelRole = 'defender';
                      updates.maxGames = 10000;
                      updates.featureVersion = 'v7';
                      updates.imBatchSize = 256;
                    } else if (m.value === 'imitation') {
                      updates.playerSelectionMode = 'competition_team_top_teams';
                      updates.modelRole = 'defender';
                      updates.maxGames = 5000;
                      updates.featureVersion = 'v5';
                      updates.imBatchSize = 128;
                    }
                    setTrainConfig(c => ({ ...c, ...updates }));
                  }}
                  type="button"
                >
                  <span className="ml-mode-card-icon">{m.icon}</span>
                  <span className="ml-mode-card-label">{m.label}</span>
                  <span className="ml-mode-card-desc">{m.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {canChooseBaseVersion && (
            <div className="admin-section">
              <h3>Modèle de base à améliorer</h3>
              <div className="ml-form">
                {canChooseTrainingRole && (
                  <label className="ml-field">
                    <span>Rôle entraîné</span>
                    <div className="ml-role-toggle">
                      <button
                        type="button"
                        className={`ml-role-btn${trainConfig.modelRole === 'defender' ? ' ml-role-btn--active ml-role-btn--def' : ''}`}
                        onClick={() => setTrainConfig(c => ({ ...c, modelRole: 'defender', baseVersion: '', opponentVersion: '' }))}
                      >
                        Défenseur
                      </button>
                      <button
                        type="button"
                        className={`ml-role-btn${trainConfig.modelRole === 'attacker' ? ' ml-role-btn--active ml-role-btn--att' : ''}`}
                        onClick={() => setTrainConfig(c => ({ ...c, modelRole: 'attacker', baseVersion: '', opponentVersion: '' }))}
                      >
                        Attaquant
                      </button>
                    </div>
                  </label>
                )}

                <label className="ml-field">
                  <span>{needsPpoBase ? 'Politique PPO de départ' : 'Version de base'}</span>
                  <select
                    value={selectedBaseVersion}
                    onChange={e => setTrainConfig(c => ({ ...c, baseVersion: e.target.value }))}
                  >
                    <option value="">
                      {needsPpoBase
                        ? 'Automatique / politique PPO existante'
                        : trainConfig.mode === 'imitation'
                          ? 'Aucune (entraînement from scratch — poids initialisés aléatoirement)'
                          : 'Production actuelle (par défaut)'}
                    </option>
                    {baseVersionOptions.map(v => (
                      <option key={v.versionTag} value={v.versionTag}>
                        {formatModelVersionLabel(v)}
                      </option>
                    ))}
                  </select>
                  <span className="ml-field-help">
                    {needsPpoBase
                      ? 'RLHF ne peut reprendre qu\'une politique PPO .zip ; les modèles supervisés .pt ne sont pas proposés ici.'
                      : trainConfig.mode === 'imitation'
                        ? 'Optionnel : initialise l\'imitation (warm-start) avec les poids de cette version au lieu de partir d\'une init aléatoire. Si vide → vrai from scratch (LR plein, aucun biais d\'un modèle précédent).'
                        : 'Optionnel : cette version est copiée comme modèle de départ avant le job.'}
                  </span>
                  {baseVersionOptions.length === 0 && (
                    <span className="ml-field-help">
                      Aucune version compatible disponible pour ce mode et ce rôle.
                    </span>
                  )}
                </label>

                {trainConfig.mode === 'selfplay' && (
                  <label className="ml-field">
                    <span>Adversaire figé</span>
                    <select
                      value={selectedOpponentVersion}
                      onChange={e => setTrainConfig(c => ({ ...c, opponentVersion: e.target.value }))}
                    >
                      <option value="">Adversaire par défaut</option>
                      <option value="legacy-rv">
                        ★ Bot1 (legacy RV — C binary, ~50-100 ms / coup)
                      </option>
                      {opponentVersionOptions.map(v => {
                        const fullLabel = formatModelVersionLabel(v);
                        return (
                          <option key={v.versionTag} value={v.versionTag} title={fullLabel}>
                            {truncateSelectLabel(fullLabel)}
                          </option>
                        );
                      })}
                    </select>
                    <span className="ml-field-help">
                      En self-play, le modèle de base est entraîné contre cet adversaire non modifié.
                      <br />
                      <strong>Bot1</strong> : opposant rule-based plus fort que tous les modèles ML actuels.
                      Recommandé pour casser le plateau d'imitation. Coût : ~2× plus lent que vs un ML
                      (un appel subprocess par décision). L'image d'entraînement doit avoir été buildée avec les binaires (cf. <code>scripts/setup_rv_binaries.sh</code>).
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}

          {(trainConfig.mode === 'imitation' || trainConfig.mode === 'deductions') && (
            <div className="admin-section">
              <h3>1. Source des donnes & sélection des joueurs</h3>
              <div className="ml-form">
                <label className="ml-field">
                  <span>Source</span>
                  <select
                    value={trainConfig.playerSelectionMode}
                    onChange={e => setTrainConfig(c => ({ ...c, playerSelectionMode: e.target.value as PlayerSelectionMode }))}
                  >
                    {Object.entries(ML_PLAYER_SELECTION_MODE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </label>

                <label className="ml-field">
                  <span>Rôle du modèle</span>
                  <div className="ml-role-toggle">
                    <button
                      type="button"
                      className={`ml-role-btn${trainConfig.modelRole === 'defender' ? ' ml-role-btn--active ml-role-btn--def' : ''}`}
                      disabled={isAttackerOnly(trainConfig.playerSelectionMode)}
                      onClick={() => setTrainConfig(c => ({ ...c, modelRole: 'defender' }))}
                    >
                      Défenseur
                    </button>
                    <button
                      type="button"
                      className={`ml-role-btn${trainConfig.modelRole === 'attacker' ? ' ml-role-btn--active ml-role-btn--att' : ''}`}
                      onClick={() => setTrainConfig(c => ({ ...c, modelRole: 'attacker' }))}
                    >
                      Attaquant
                    </button>
                  </div>
                  {isAttackerOnly(trainConfig.playerSelectionMode) && (
                    <span className="ml-field-help">
                      {isMagnusMode(trainConfig.playerSelectionMode)
                        ? 'Mode Magnus : rôle attaquant uniquement.'
                        : 'Le CIA est verrouillé sur le rôle attaquant.'}
                    </span>
                  )}
                </label>

                {isMagnusMode(trainConfig.playerSelectionMode) && (
                  <>
                    {/* div (not label): buttons inside <label> re-activate the input and break selection */}
                    <div className="ml-field">
                      <span>Sources de donnes</span>
                      <div className="ml-magnus-source-toggles" role="group" aria-label="Sources de donnes">
                        {([
                          { key: 'magnusSourceDiffereAttaque' as const, label: 'Diff. att.', title: 'Différé attaque / attaqueSC / maxiAtt' },
                          { key: 'magnusSourceDiffereMixteMaxi' as const, label: 'Diff. mixte', title: 'Différé mixte / maxi (preneur)' },
                          { key: 'magnusSourceCia' as const, label: 'CIA', title: 'Championnat individuel attaque' },
                          { key: 'magnusSourceFreedefi' as const, label: 'Défis libres', title: 'Défis libres (preneur)' },
                        ]).map(src => (
                          <button
                            key={src.key}
                            type="button"
                            title={src.title}
                            className={`ml-magnus-source-toggle${trainConfig[src.key] ? ' is-on' : ''}`}
                            aria-pressed={trainConfig[src.key]}
                            onClick={() => setTrainConfig(c => ({ ...c, [src.key]: !c[src.key] }))}
                          >
                            {src.label}
                          </button>
                        ))}
                      </div>
                      <span className="ml-field-help">
                        Clique pour activer/désactiver. Différé depuis 2016-01-01. Volumes ci-dessous suivent ces sources.
                      </span>
                    </div>
                    <div className="ml-field">
                      <span>Joueurs Magnus (1–{MAGNUS_MAX_PSEUDOS})</span>
                      <div className="ml-magnus-picker">
                        <div className="ml-magnus-chips">
                          {(trainConfig.magnusPseudos || []).map(pseudo => {
                            const label = magnusLabelByPseudo.get(pseudo.toLowerCase())
                              || pseudo.replace(/¶/g, ' ');
                            const vols = volumesForPseudo(pseudo);
                            const expected = vols
                              ? magnusExpectedDonnes(vols, magnusSourcesState)
                              : null;
                            return (
                              <span key={pseudo} className="ml-magnus-chip">
                                <span className="ml-magnus-chip-label">{label}</span>
                                <span className="ml-magnus-chip-vol">
                                  {expected != null ? `${formatMagnusDonnes(expected)} donnes` : '…'}
                                </span>
                                <button
                                  type="button"
                                  className="ml-magnus-chip-remove"
                                  onClick={() => removeMagnusPseudo(pseudo)}
                                  aria-label={`Retirer ${label}`}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                        <div className="ml-magnus-autocomplete">
                          <input
                            type="text"
                            value={magnusQuery}
                            onChange={e => {
                              setMagnusQuery(e.target.value);
                              openMagnusDropdown();
                            }}
                            onFocus={() => openMagnusDropdown()}
                            onBlur={() => {
                              if (magnusIgnoreBlurRef.current) {
                                magnusIgnoreBlurRef.current = false;
                                return;
                              }
                              window.setTimeout(() => setMagnusDropdownOpen(false), 150);
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const q = magnusQuery.trim();
                                if (q) addMagnusPseudo(q, q);
                              } else if (e.key === 'Escape') {
                                setMagnusDropdownOpen(false);
                              }
                            }}
                            placeholder={
                              (trainConfig.magnusPseudos || []).length >= MAGNUS_MAX_PSEUDOS
                                ? `Maximum ${MAGNUS_MAX_PSEUDOS} joueurs`
                                : 'Top 20 benchmark ou rechercher…'
                            }
                            disabled={(trainConfig.magnusPseudos || []).length >= MAGNUS_MAX_PSEUDOS}
                            autoComplete="off"
                          />
                          {magnusDropdownOpen
                            && (trainConfig.magnusPseudos || []).length < MAGNUS_MAX_PSEUDOS && (
                            <div
                              className="ml-magnus-dropdown"
                              onMouseDown={e => {
                                e.preventDefault();
                              }}
                            >
                              {magnusCandidatesLoading && magnusCandidates.length === 0 && (
                                <div className="ml-magnus-dropdown-msg">
                                  Chargement du top 20 (1er calcul peut prendre 1–2 min)…
                                </div>
                              )}
                              {(magnusCacheMeta.refreshing || magnusCacheMeta.stale) && magnusCandidates.length > 0 && (
                                <div className="ml-magnus-dropdown-msg">
                                  Actualisation des volumes en arrière-plan…
                                </div>
                              )}
                              {!(magnusCandidatesLoading && magnusCandidates.length === 0) && (() => {
                                const selected = new Set(
                                  (trainConfig.magnusPseudos || []).map(p => p.toLowerCase()),
                                );
                                const q = magnusQuery.trim().toLowerCase();
                                const fromTop = magnusCandidates.filter(c => {
                                  if (!c.pseudo || selected.has(c.pseudo.toLowerCase())) return false;
                                  if (!q) return true;
                                  return c.label.toLowerCase().includes(q)
                                    || c.pseudo.toLowerCase().includes(q);
                                });
                                const fromSearch = magnusSearchHits.filter(h => {
                                  if (!h.pseudo || selected.has(h.pseudo.toLowerCase())) return false;
                                  return !fromTop.some(
                                    c => c.pseudo.toLowerCase() === h.pseudo.toLowerCase(),
                                  );
                                });
                                if (fromTop.length === 0 && fromSearch.length === 0) {
                                  return (
                                    <div className="ml-magnus-dropdown-msg">
                                      {q.length >= 2
                                        ? 'Aucun joueur — Entrée pour ajouter le texte saisi'
                                        : 'Aucun candidat'}
                                    </div>
                                  );
                                }
                                return (
                                  <>
                                    {fromTop.length > 0 && (
                                      <div className="ml-magnus-dropdown-section">
                                        Top benchmark attaque (hors s/c)
                                      </div>
                                    )}
                                    {fromTop.map(c => {
                                      const expected = magnusExpectedDonnes(
                                        volumesForPseudo(c.pseudo) || c.volumes,
                                        magnusSourcesState,
                                      );
                                      return (
                                        <button
                                          key={c.pseudo}
                                          type="button"
                                          className="ml-magnus-option"
                                          onMouseDown={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            pickMagnusCandidate(c.pseudo, c.label);
                                          }}
                                        >
                                          <span className="ml-magnus-option-rank">#{c.rank}</span>
                                          <span className="ml-magnus-option-name">{c.label}</span>
                                          <span className="ml-magnus-option-vol">
                                            {formatMagnusDonnes(expected)}
                                          </span>
                                        </button>
                                      );
                                    })}
                                    {fromSearch.length > 0 && (
                                      <div className="ml-magnus-dropdown-section">Recherche</div>
                                    )}
                                    {fromSearch.map(h => {
                                      const vols = h.volumes || volumesForPseudo(h.pseudo);
                                      const expected = vols
                                        ? magnusExpectedDonnes(vols, magnusSourcesState)
                                        : null;
                                      return (
                                        <button
                                          key={h.pseudo}
                                          type="button"
                                          className="ml-magnus-option"
                                          onMouseDown={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            pickMagnusCandidate(h.pseudo, h.label);
                                          }}
                                        >
                                          <span className="ml-magnus-option-rank">·</span>
                                          <span className="ml-magnus-option-name">{h.label}</span>
                                          <span className="ml-magnus-option-vol">
                                            {h.volumesLoading ? '…' : formatMagnusDonnes(expected)}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                      <span className="ml-field-help">
                        Volumes ventilés par source (cache 7 j). Mix = OR SQL, pas de rééquilibrage.
                        {magnusCacheMeta.computedAt
                          ? ` Cache : ${new Date(magnusCacheMeta.computedAt).toLocaleString('fr-FR')}.`
                          : ''}
                      </span>
                    </div>
                  </>
                )}

                {isCompetitionMode(trainConfig.playerSelectionMode) && (
                  <label className="ml-field">
                    <span>États retenus</span>
                    <div className="ml-checkbox-row">
                      {(['fini', 'abandon', 'encours', 'annule'] as CompetitionEtat[]).map(etat => (
                        <label key={etat}>
                          <input
                            type="checkbox"
                            checked={trainConfig.competitionEtats.includes(etat)}
                            onChange={e => setTrainConfig(c => ({
                              ...c,
                              competitionEtats: e.target.checked
                                ? Array.from(new Set([...c.competitionEtats, etat]))
                                : c.competitionEtats.filter(v => v !== etat),
                            }))}
                          />
                          {etat}
                        </label>
                      ))}
                    </div>
                    <span className="ml-field-help">Par défaut : uniquement les matchs terminés.</span>
                  </label>
                )}

                {isFreedefiMode(trainConfig.playerSelectionMode) && (
                  <>
                    <label className="ml-field">
                      <span>Source élite</span>
                      <select
                        value={trainConfig.eliteSource}
                        onChange={e => setTrainConfig(c => ({ ...c, eliteSource: e.target.value as EliteSource }))}
                      >
                        <option value="differe_general">Classement glissant général</option>
                        <option value="live">Top live 4 joueurs</option>
                      </select>
                    </label>
                    <label className="ml-field">
                      <span>Taille du pool élite</span>
                      <input
                        type="number"
                        min={1}
                        max={1000}
                        value={trainConfig.elitePoolSize}
                        onChange={e => setTrainConfig(c => ({ ...c, elitePoolSize: Math.max(1, Math.min(1000, Number(e.target.value) || 1)) }))}
                      />
                      <span className="ml-field-help">Ces pseudos sont injectés dans le filtre SQL <code>pseudos=</code>.</span>
                    </label>
                  </>
                )}

                {isTeamTopTeamsMode(trainConfig.playerSelectionMode) && (
                  <>
                    <label className="ml-field">
                      <span>Divisions retenues</span>
                      <select
                        value={trainConfig.teamDivisionHierarchyMax}
                        onChange={e => setTrainConfig(c => ({ ...c, teamDivisionHierarchyMax: Number(e.target.value) }))}
                      >
                        <option value={0}>Toutes les divisions</option>
                        <option value={1}>D1 seulement (élite)</option>
                        <option value={2}>D1 à D2</option>
                        <option value={3}>D1 à D3</option>
                        <option value={4}>D1 à D4</option>
                        <option value={5}>D1 à D5</option>
                        <option value={6}>D1 à D6</option>
                        <option value={7}>D1 à D7</option>
                      </select>
                    </label>
                    <label className="ml-field">
                      <span>Phase minimum</span>
                      <select
                        value={trainConfig.teamPhaseMinLabel}
                        onChange={e => setTrainConfig(c => ({ ...c, teamPhaseMinLabel: e.target.value }))}
                      >
                        <option value="">Aucune</option>
                        {phaseOptions.map(phase => (
                          <option key={phase.nom} value={phase.nom}>{formatPhaseOption(phase)}</option>
                        ))}
                      </select>
                    </label>
                    <label className="ml-field">
                      <span>Phase maximum</span>
                      <select
                        value={trainConfig.teamPhaseMaxLabel}
                        onChange={e => setTrainConfig(c => ({ ...c, teamPhaseMaxLabel: e.target.value }))}
                      >
                        <option value="">Aucune</option>
                        {phaseOptions.map(phase => (
                          <option key={phase.nom} value={phase.nom}>{formatPhaseOption(phase)}</option>
                        ))}
                      </select>
                    </label>
                    <label className="ml-field">
                      <span>Éligibilité du match</span>
                      <select
                        value={trainConfig.teamRequireBoth ? 'both' : 'one'}
                        onChange={e => setTrainConfig(c => ({ ...c, teamRequireBoth: e.target.value === 'both' }))}
                      >
                        <option value="both">Les deux équipes dans le scope</option>
                        <option value="one">Au moins une équipe dans le scope</option>
                      </select>
                    </label>
                    <label className="ml-field">
                      <span>Max donnes par match</span>
                      <input
                        type="number"
                        min={1}
                        max={16}
                        value={trainConfig.teamMaxGamesPerMatch}
                        onChange={e => setTrainConfig(c => ({ ...c, teamMaxGamesPerMatch: Math.max(1, Math.min(16, Number(e.target.value) || 1)) }))}
                      />
                      <span className="ml-field-help">Nombre de donnes distinctes par match ; avec les deux tables, cela peut représenter jusqu'à 2 lignes par donne.</span>
                    </label>
                    <label className="ml-field">
                      <span>Duplicate</span>
                      <select
                        value={trainConfig.teamOneTablePerDeal ? 'one' : 'both'}
                        onChange={e => setTrainConfig(c => ({ ...c, teamOneTablePerDeal: e.target.value === 'one' }))}
                      >
                        <option value="both">Conserver les deux tables d'une même donne</option>
                        <option value="one">Une seule table par donne duplicate</option>
                      </select>
                      <span className="ml-field-help">Permet de réduire la corrélation entre deux tables jouant exactement la même donne.</span>
                    </label>
                    {trainConfig.teamOneTablePerDeal && (
                      <label className="ml-field">
                        <span>Choix de la table duplicate</span>
                        <select
                          value={trainConfig.teamDuplicateStrategy}
                          onChange={e => setTrainConfig(c => ({ ...c, teamDuplicateStrategy: e.target.value as TeamDuplicateStrategy }))}
                        >
                          <option value="random">Aléatoire reproductible</option>
                          <option value="attack_best">Meilleure réussite de l'attaque</option>
                          <option value="defense_best">Meilleure réussite de la défense</option>
                        </select>
                        <span className="ml-field-help">Pour l'imitation, “aléatoire” évite de biaiser vers les coups gagnants ; les options best sont utiles pour des datasets plus sélectifs.</span>
                      </label>
                    )}
                    <label className="ml-field">
                      <span>Seed random</span>
                      <input
                        type="text"
                        value={trainConfig.sampleSeed}
                        onChange={e => setTrainConfig(c => ({ ...c, sampleSeed: e.target.value }))}
                      />
                      <span className="ml-field-help">Même seed = même échantillon, utile pour reproduire un training.</span>
                    </label>
                  </>
                )}

                {isDiffereMode(trainConfig.playerSelectionMode) && (
                  <>
                    {trainConfig.playerSelectionMode === 'differe_indiv_top_per_donne' && (
                      <>
                        <label className="ml-field">
                          <span>Top X meilleurs résultats par donne</span>
                          <input
                            type="number"
                            min={1}
                            max={20}
                            value={trainConfig.differeTopPerDonne}
                            onChange={e => setTrainConfig(c => ({ ...c, differeTopPerDonne: Math.max(1, Math.min(20, Number(e.target.value) || 1)) }))}
                          />
                          <span className="ml-field-help">Garde uniquement les X meilleurs résultats (par <code>scoreduplicate</code>) sur chaque donne. L'identité des joueurs est secondaire.</span>
                        </label>
                        <label className="ml-field">
                          <span>Min participants par donne</span>
                          <input
                            type="number"
                            min={0}
                            max={1000}
                            value={trainConfig.differeMinParticipantsPerDonne}
                            onChange={e => setTrainConfig(c => ({ ...c, differeMinParticipantsPerDonne: Math.max(0, Math.min(1000, Number(e.target.value) || 0)) }))}
                          />
                          <span className="ml-field-help">Ignore les donnes jouées par moins de N joueurs (0 = pas de filtre). Utile pour ne garder que les donnes statistiquement intéressantes.</span>
                        </label>
                        {(trainConfig.mode === 'imitation' || trainConfig.mode === 'deductions') && (
                          <>
                            <label className="ml-field">
                              <span>Début tournoi — min (API liste.debut)</span>
                              <input
                                type="date"
                                min="2016-01-01"
                                value={trainConfig.trainDateFrom}
                                onChange={e => setTrainConfig(c => ({ ...c, trainDateFrom: e.target.value }))}
                              />
                              <span className="ml-field-help">
                                Pour imitation / déductions : au moins cette date ou la date max. ci-dessous (plancher 2016-01-01). Si les deux sont remplies et l’écart dépasse 5 ans, une confirmation sera demandée.
                              </span>
                            </label>
                            <label className="ml-field">
                              <span>Début tournoi — max (API liste.debut)</span>
                              <input
                                type="date"
                                min="2016-01-01"
                                value={trainConfig.trainDateTo}
                                onChange={e => setTrainConfig(c => ({ ...c, trainDateTo: e.target.value }))}
                              />
                            </label>
                          </>
                        )}
                      </>
                    )}
                    {trainConfig.playerSelectionMode === 'differe_indiv_top_general_role' && (
                      <>
                        <label className="ml-field">
                          <span>Source élite (rôle)</span>
                          <select
                            value={trainConfig.eliteSource}
                            onChange={e => setTrainConfig(c => ({ ...c, eliteSource: e.target.value as EliteSource }))}
                          >
                            <option value="differe_general_attack">Top attaque (classement_att)</option>
                            <option value="differe_general_defense">Top défense (classement_def)</option>
                            <option value="differe_general">Top total (classement_tot)</option>
                          </select>
                          <span className="ml-field-help">Pour un modèle attaquant, choisir “Top attaque” ; pour un modèle défenseur, “Top défense”.</span>
                        </label>
                        <label className="ml-field">
                          <span>Taille du pool élite (top N)</span>
                          <input
                            type="number"
                            min={1}
                            max={1000}
                            value={trainConfig.elitePoolSize}
                            onChange={e => setTrainConfig(c => ({ ...c, elitePoolSize: Math.max(1, Math.min(1000, Number(e.target.value) || 1)) }))}
                          />
                          <span className="ml-field-help">Les pseudos top N du classement glissant général sont injectés dans le filtre <code>pseudos=</code>.</span>
                        </label>
                      </>
                    )}
                    <label className="ml-field">
                      <span>Sous-types de différé</span>
                      <div className="ml-checkbox-row">
                        {allowedDiffSubtypes(trainConfig.modelRole).map(sub => (
                          <label key={sub}>
                            <input
                              type="checkbox"
                              checked={trainConfig.differeSubtypes.includes(sub)}
                              onChange={e => setTrainConfig(c => ({
                                ...c,
                                differeSubtypes: e.target.checked
                                  ? Array.from(new Set([...c.differeSubtypes, sub]))
                                  : c.differeSubtypes.filter(v => v !== sub),
                              }))}
                            />
                            {sub}
                          </label>
                        ))}
                      </div>
                      <span className="ml-field-help">
                        Vide = auto-sélection ({allowedDiffSubtypes(trainConfig.modelRole).join(', ')}). Sous-types restreints au rôle pour éviter les donnes incohérentes.
                      </span>
                    </label>
                    <label className="ml-field">
                      <span>Filtrage rôle strict (Python)</span>
                      <select
                        value={trainConfig.differeStrictRoleFilter ? 'strict' : 'all'}
                        onChange={e => setTrainConfig(c => ({ ...c, differeStrictRoleFilter: e.target.value === 'strict' }))}
                      >
                        <option value="strict">Strict — drop les donnes où l'humain n'a pas le bon rôle</option>
                        <option value="all">Toutes — pas de filtrage côté pipeline</option>
                      </select>
                      <span className="ml-field-help">En strict, le pipeline Python ne garde que les donnes où l'humain a pris (modèle attaquant) ou défendu (modèle défenseur).</span>
                    </label>
                    <label className="ml-field">
                      <span>Seed random</span>
                      <input
                        type="text"
                        value={trainConfig.sampleSeed}
                        onChange={e => setTrainConfig(c => ({ ...c, sampleSeed: e.target.value }))}
                      />
                      <span className="ml-field-help">Même seed = même échantillon, utile pour reproduire un training.</span>
                    </label>
                  </>
                )}

                {usesBot1Distillation(trainConfig.playerSelectionMode) && (
                  <>
                    {isBot1DistillMode(trainConfig.playerSelectionMode) ? (
                      <div style={{ gridColumn: '1 / -1', padding: '8px 12px', background: '#1a1f2e', border: '1px solid #2a3142', borderRadius: 6, fontSize: '0.92em' }}>
                        <strong>Distillation Bot1 pure</strong> — le dataset est généré à la volée dans le job OVH par
                        Bot1 jouant les 4 sièges. Aucun appel API, aucun pseudo élite.
                        Coût : ~30 min pour 5 000 donnes (16 workers), ~2 h pour 20 000.
                        <br/><em>⚠ Plafond observé (Griffe de Nénuphar, compare-22)</em> : val accuracy
                        plafonne à ~46 % et le winrate vs Bot1 chute à ~17 %. Préférer le mode
                        <strong> Bot1 + humains (mix soft) </strong> pour conserver le backbone humain.
                      </div>
                    ) : (
                      <div style={{ gridColumn: '1 / -1', padding: '8px 12px', background: '#1a2e1a', border: '1px solid #2a4232', borderRadius: 6, fontSize: '0.92em' }}>
                        <strong>Distillation Bot1 + humains (mix soft) ★</strong> — le dataset combine Bot1×4
                        (corrections sur les Gardes difficiles) avec des donnes humains expert (backbone stylistique).
                        Le ratio α contrôle la proportion de Bot1 dans les batchs. Cible recommandée pour la défense :
                        α=0.5 + source humains <code>differe_indiv_top_general_role</code>. Coût : distillation Bot1
                        + téléchargement humans + entraînement.
                      </div>
                    )}
                    <label className="ml-field">
                      <span>Nombre de donnes Bot1</span>
                      <input
                        type="number"
                        min={100}
                        max={50000}
                        step={500}
                        value={trainConfig.botDistillNGames}
                        onChange={e => setTrainConfig(c => ({ ...c, botDistillNGames: Math.max(100, Math.min(50000, Number(e.target.value) || 5000)) }))}
                      />
                      <span className="ml-field-help">5 000 = bon point de départ ; 20 000 = saturation des gains.</span>
                    </label>
                    <label className="ml-field">
                      <span>Workers parallèles</span>
                      <input
                        type="number"
                        min={1}
                        max={64}
                        value={trainConfig.botDistillWorkers}
                        onChange={e => setTrainConfig(c => ({ ...c, botDistillWorkers: Math.max(1, Math.min(64, Number(e.target.value) || 16)) }))}
                      />
                      <span className="ml-field-help">Bot1 est mono-thread ; le speed-up est ~linéaire jusqu'au nombre de vCPU disponibles.</span>
                    </label>
                    <label className="ml-field">
                      <span>Filtre min. contrat</span>
                      <select
                        value={trainConfig.botDistillMinContract}
                        onChange={e => setTrainConfig(c => ({ ...c, botDistillMinContract: Number(e.target.value) }))}
                      >
                        <option value={0}>Tous (incl. Petites)</option>
                        <option value={2}>Garde et plus (skip Petites) ★ recommandé défense</option>
                        <option value={3}>Garde Sans et plus (très restrictif)</option>
                        <option value={4}>Garde Contre uniquement</option>
                      </select>
                      <span className="ml-field-help">
                        Skip les Petites concentre l'apprentissage sur les Gardes — c'est là que
                        l'écart avec Bot1 se creuse (compare-21 : ML 51 % vs Bot1 sur mains ≥35 pts).
                      </span>
                    </label>
                    <label className="ml-field">
                      <span>Seed</span>
                      <input
                        type="number"
                        value={trainConfig.botDistillSeed}
                        onChange={e => setTrainConfig(c => ({ ...c, botDistillSeed: Number(e.target.value) || 42 }))}
                      />
                    </label>

                    {isBot1SoftDistillMode(trainConfig.playerSelectionMode) && (
                      <>
                        <label className="ml-field">
                          <span>Source humans (mix)</span>
                          <select
                            value={trainConfig.botSoftDistillHumansMode}
                            onChange={e => setTrainConfig(c => ({
                              ...c,
                              botSoftDistillHumansMode: e.target.value as
                                'differe_indiv_top_general_role' | 'competition_team_top_teams',
                            }))}
                          >
                            <option value="differe_indiv_top_general_role">
                              Différé top N par rôle ★ recommandé défense (Croc/Aile)
                            </option>
                            <option value="competition_team_top_teams">
                              Championnat équipes top divisions ★ recommandé attaque
                            </option>
                          </select>
                          <span className="ml-field-help">
                            Source des donnes humaines pour la moitié backbone du mix. Le rôle
                            modèle (attaque/défense) détermine automatiquement les sous-types
                            de différé et les filtres SQL. Les paramètres Élite / Différé /
                            Championnat ci-dessus s'appliquent à cette source.
                          </span>
                        </label>
                        <label className="ml-field">
                          <span>Ratio α (Bot1 / total)</span>
                          <input
                            type="number"
                            min={0.05}
                            max={0.95}
                            step={0.05}
                            value={trainConfig.botSoftDistillAlpha}
                            onChange={e => setTrainConfig(c => ({
                              ...c,
                              botSoftDistillAlpha: Math.max(0.05, Math.min(0.95, Number(e.target.value) || 0.5)),
                            }))}
                          />
                          <span className="ml-field-help">
                            Fraction d'exemples Bot1 dans le train set après resampling.
                            <br/>0.30-0.40 = humains-dominant (préserve le style humain, correction Bot1 légère).
                            <br/>0.50 = équilibré ★ recommandé en première itération.
                            <br/>0.60-0.70 = Bot1-dominant (correction agressive, risque de plateau).
                          </span>
                        </label>
                      </>
                    )}
                  </>
                )}

                {!requiresDiffereTopPerDonneExplicitDates && (
                <label className="ml-field">
                  <span>Fenêtre temporelle</span>
                  <select
                    value={trainConfig.dateMonths}
                    onChange={e => setTrainConfig(c => ({ ...c, dateMonths: Number(e.target.value) }))}
                  >
                    <option value={0}>Toutes les dates</option>
                    <option value={6}>6 derniers mois</option>
                    <option value={12}>12 derniers mois</option>
                    <option value={24}>24 derniers mois</option>
                  </select>
                </label>
                )}

                <label className="ml-field">
                  <span>Nombre max de donnes</span>
                  <input
                    type="number"
                    min={100}
                    step={100}
                    value={trainConfig.maxGames}
                    onChange={e => setTrainConfig(c => ({ ...c, maxGames: Number(e.target.value) || 100 }))}
                  />
                </label>
              </div>

              <div className="ml-actions" style={{ marginTop: 12 }}>
                <button className="admin-button" type="button" disabled={busy === 'preview' || busy === 'preview-count'} onClick={() => handlePreviewDataset()}>
                  {busy === 'preview' ? 'Aperçu...' : 'Prévisualiser le dataset'}
                </button>
                {isFreedefiMode(trainConfig.playerSelectionMode) && (
                  <button className="admin-button secondary" type="button" disabled={busy === 'collect'} onClick={handleCollectElite}>
                    {busy === 'collect' ? 'Collecte...' : 'Collecter les élites'}
                  </button>
                )}
              </div>

              {datasetPreview && (
                <div className="ml-dataset-preview" style={{ marginTop: 12 }}>
                  <strong>Aperçu :</strong> {datasetPreview.filteredEstimate || `${datasetPreview.totalGames} parties disponibles`}
                  {datasetPreview.countIsExact === false && !datasetPreview.dataApiError && (
                    <div style={{ marginTop: 6 }}>
                      <button
                        className="admin-button secondary"
                        type="button"
                        disabled={busy === 'preview' || busy === 'preview-count'}
                        onClick={() => handlePreviewDataset({ computeExactCount: true })}
                        title="Lance un COUNT(*) SQL exact (peut prendre jusqu'à 90 s)"
                      >
                        {busy === 'preview-count' ? 'Calcul du COUNT exact...' : 'Compter exactement'}
                      </button>
                    </div>
                  )}
                  {datasetPreview.dataApiError && <div className="ml-error">{datasetPreview.dataApiError}</div>}
                  {datasetPreview.selectedPlayers && datasetPreview.selectedPlayers.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '0.9em' }}>
                      <strong>Pseudos sélectionnés :</strong> {datasetPreview.selectedPlayers.slice(0, 12).map(p => p.pseudo).join(', ')}
                      {datasetPreview.selectedPlayers.length > 12 ? '...' : ''}
                    </div>
                  )}
                  {(datasetPreview.sampleUniquePlayers || datasetPreview.scopeUniquePlayers || datasetPreview.scopeUniqueTeams) && (
                    <div style={{ marginTop: 8, fontSize: '0.9em' }}>
                      <strong>Couverture :</strong>{' '}
                      {datasetPreview.sampleUniquePlayers ? `${datasetPreview.sampleUniquePlayers} joueurs dans l'échantillon${datasetPreview.sampleStatsGames ? ` (${datasetPreview.sampleStatsGames} parties analysées)` : ''}` : ''}
                      {datasetPreview.scopeUniquePlayers ? `${datasetPreview.sampleUniquePlayers ? ' — ' : ''}${datasetPreview.scopeUniquePlayers} joueurs dans le scope` : ''}
                      {datasetPreview.scopeUniqueTeams ? ` — ${datasetPreview.scopeUniqueTeams} équipes dans le scope` : ''}
                    </div>
                  )}
                  {datasetPreview.teamStats && datasetPreview.teamStats.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '0.9em' }}>
                      <strong>Top équipes scope :</strong>{' '}
                      {datasetPreview.teamStats.slice(0, 20).map(t => `${t.name} (${t.games})`).join(', ')}
                    </div>
                  )}
                  {datasetPreview.samplePlayerStats && datasetPreview.samplePlayerStats.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '0.9em' }}>
                      <strong>Top joueurs échantillon :</strong>{' '}
                      {datasetPreview.samplePlayerStats.slice(0, 20).map(p => `${p.pseudo} (${p.games})`).join(', ')}
                      {datasetPreview.sampleUniquePlayers ? ` — ${datasetPreview.sampleUniquePlayers} joueurs` : ''}
                    </div>
                  )}
                  {datasetPreview.playerStats && datasetPreview.playerStats.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '0.9em' }}>
                      <strong>Top joueurs humains (scope) :</strong>{' '}
                      {datasetPreview.playerStats.slice(0, 20).map(p => `${p.pseudo} (${p.games})`).join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {(trainConfig.mode === 'imitation' || trainConfig.mode === 'deductions') && (
            <div className="admin-section">
              <h3>2. Hyperparamètres</h3>
              <div className="ml-form">
                <label className="ml-field">
                  <span>Feature version</span>
                  <select value={trainConfig.featureVersion} onChange={e => setTrainConfig(c => ({ ...c, featureVersion: e.target.value }))}>
                    <option value="v5">v5 — entame</option>
                    <option value="v7">v7 — beliefs</option>
                  </select>
                </label>
                <label className="ml-field">
                  <span>Epochs</span>
                  <input type="number" min={1} value={trainConfig.imEpochs} onChange={e => setTrainConfig(c => ({ ...c, imEpochs: Number(e.target.value) || 1 }))} />
                </label>
                <label className="ml-field">
                  <span>Batch size</span>
                  <input type="number" min={16} value={trainConfig.imBatchSize} onChange={e => setTrainConfig(c => ({ ...c, imBatchSize: Number(e.target.value) || 16 }))} />
                </label>
                <label className="ml-field">
                  <span>Learning rate</span>
                  <input type="number" step="0.0001" value={trainConfig.imLr} onChange={e => setTrainConfig(c => ({ ...c, imLr: Number(e.target.value) || 0.001 }))} />
                </label>
                <label className="ml-field">
                  <span>Patience early stopping</span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={trainConfig.imPatience}
                    title="Nombre d’époques sans baisse de val loss avant arrêt"
                    onChange={e => setTrainConfig(c => ({ ...c, imPatience: Math.min(50, Math.max(1, Number(e.target.value) || 10)) }))}
                  />
                </label>
                {trainConfig.mode === 'imitation' && (
                  <label className="ml-field">
                    <span>Augmentation symétrie couleurs</span>
                    <label
                      className="ml-checkbox-row"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}
                    >
                      <input
                        type="checkbox"
                        checked={trainConfig.suitAugmentation}
                        onChange={e => setTrainConfig(c => ({ ...c, suitAugmentation: e.target.checked }))}
                      />
                      Permuter aléatoirement les 4 couleurs (♥♠♦♣) à chaque epoch
                    </label>
                    <span className="ml-field-help">
                      Le tarot 4 joueurs est invariant par permutation des couleurs ordinaires (atouts et Excuse exclus).
                      Cette augmentation force le réseau à apprendre cette symétrie au lieu d'avoir à la (mal) déduire de l'encodage one-hot par carte.
                      Multiplie la diversité effective du dataset jusqu'à 24× sans coût en mémoire. Le set de validation n'est jamais permuté.
                    </span>
                  </label>
                )}
                {trainConfig.mode === 'imitation' && trainConfig.featureVersion === 'v7' && trainConfig.modelRole === 'attacker' && (
                  <label className="ml-field">
                    <span>Regroupement cartes équivalentes (Phase 1)</span>
                    <label
                      className="ml-checkbox-row"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}
                    >
                      <input
                        type="checkbox"
                        checked={trainConfig.cardEquivalence}
                        onChange={e => setTrainConfig(c => ({ ...c, cardEquivalence: e.target.checked }))}
                      />
                      Soft targets : distribution uniforme sur les cartes interchangeables du preneur
                    </label>
                    <span className="ml-field-help">
                      Expérimental — Phase 0.5 : peut dégrader fortement la gestion du Petit
                      (jusqu'à 3–5× plus de Petits perdus sur le benchmark). Le Type B fusionne
                      les atouts bas consécutifs (2–20), ce qui aplatit l'apprentissage de la chasse.
                      Soft targets : distribution uniforme sur la classe (renormalisée aux coups
                      légaux). Attaque uniquement. Ne pas activer en production sans validation.
                    </span>
                    {trainConfig.cardEquivalence && (
                      <div style={{ marginLeft: 16, marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <label className="ml-field" style={{ marginBottom: 0 }}>
                          <span>Limite "petites cartes" (Type A)</span>
                          <select
                            value={trainConfig.lowRankLimit}
                            onChange={e => setTrainConfig(c => ({ ...c, lowRankLimit: parseInt(e.target.value) as 5 | 6 | 7 }))}
                          >
                            <option value={5}>rangs 1→5 (défaut)</option>
                            <option value={6}>rangs 1→6</option>
                            <option value={7}>rangs 1→7 (7♥ ≡ A♥)</option>
                          </select>
                          <span className="ml-field-help">
                            Rangs 1 à N d'une même couleur ordinaire regroupés, même si non consécutifs en main.
                          </span>
                        </label>
                        <label className="ml-field" style={{ marginBottom: 0 }}>
                          <label
                            className="ml-checkbox-row"
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                          >
                            <input
                              type="checkbox"
                              checked={trainConfig.equivEnableTypeB}
                              onChange={e => setTrainConfig(c => ({ ...c, equivEnableTypeB: e.target.checked }))}
                            />
                            Type B — séquences consécutives (atouts 2–20 inclus)
                          </label>
                          <span className="ml-field-help">
                            Fusionne les cartes consécutives de même valeur en points (ex. 7♥–8♥,
                            Atout 5–6–7). Phase 0.5 : principal facteur de régression sur le Petit
                            si activé. Décocher = Type A seul (ablation).
                          </span>
                        </label>
                        <label className="ml-field" style={{ marginBottom: 0 }}>
                          <label
                            className="ml-checkbox-row"
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                          >
                            <input
                              type="checkbox"
                              checked={trainConfig.valetCavalierEquiv}
                              onChange={e => setTrainConfig(c => ({ ...c, valetCavalierEquiv: e.target.checked }))}
                            />
                            Type C — Valet ≡ Cavalier (même couleur) — expérimental
                          </label>
                          <span className="ml-field-help">
                            Regroupe Valet et Cavalier d'une couleur ordinaire uniquement (pas l'atout).
                            Valeurs différentes (1,5 vs 2,5 pts).
                          </span>
                        </label>
                      </div>
                    )}
                  </label>
                )}
                {trainConfig.mode === 'imitation' && trainConfig.featureVersion === 'v7' && (
                  <label className="ml-field">
                    <span>Tête multi-régime (gate position)</span>
                    <label
                      className="ml-checkbox-row"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}
                    >
                      <input
                        type="checkbox"
                        checked={trainConfig.regimeGate}
                        onChange={e => setTrainConfig(c => ({ ...c, regimeGate: e.target.checked }))}
                      />
                      Ajouter un gate conditionné sur (entame / avant-preneur / preneur-joué)
                    </label>
                    <span className="ml-field-help">
                      Spécialise dynamiquement les neurones selon la position relative au preneur dans le pli courant.
                      Particulièrement utile pour le <strong>défenseur</strong> (sa stratégie varie sharp&shy;ement
                      selon qu'il entame, joue avant ou après le preneur). +33k paramètres (~2 % du modèle).
                      L'inférence détecte automatiquement le gate dans le checkpoint — backward compatible.
                    </span>
                  </label>
                )}
                {trainConfig.mode === 'imitation' && trainConfig.featureVersion === 'v7' && (
                  <label className="ml-field">
                    <span>Hidden dim (capacité du modèle)</span>
                    <select
                      value={trainConfig.imHiddenDim}
                      onChange={e => setTrainConfig(c => ({
                        ...c,
                        imHiddenDim: Number(e.target.value) as 256 | 384 | 512 | 768 | 1024,
                      }))}
                    >
                      <option value={256}>256 (~0.5M params, léger)</option>
                      <option value={384}>384 (~1.0M params)</option>
                      <option value={512}>512 (~1.8M params, défaut)</option>
                      <option value={768}>768 (~4.0M params)</option>
                      <option value={1024}>1024 (~7.0M params, capacité ×4)</option>
                    </select>
                    <span className="ml-field-help">
                      Largeur du backbone <code>BeliefPolicy</code>. Tous les modèles ML actuels
                      (Châtaigne, Cyprès, Loir, Nénuphar…) plafonnent à ~+50 avgScore vs Bot1 à +86.
                      <strong>1024 quadruple les paramètres</strong> et permet de tester si ce plafond vient
                      d'une capacité insuffisante. L'inférence détecte automatiquement le hidden_dim depuis
                      le checkpoint — n'importe quelle valeur se charge transparente en production.
                    </span>
                  </label>
                )}
                {trainConfig.mode === 'deductions' && (
                  <>
                    <label className="ml-field">
                      <span>Masquage</span>
                      <select value={trainConfig.maskStrategy} onChange={e => setTrainConfig(c => ({ ...c, maskStrategy: e.target.value as 'none' | 'soft' | 'hard' }))}>
                        <option value="soft">Soft</option>
                        <option value="hard">Hard</option>
                        <option value="none">Aucun</option>
                      </select>
                    </label>
                    <label className="ml-field">
                      <span>Poids cas certains (soft)</span>
                      <input
                        type="number"
                        step={0.05}
                        min={0.01}
                        max={1}
                        value={trainConfig.maskCertainWeight}
                        title="Poids des assertions logiquement certaines quand masquage = soft"
                        onChange={e => setTrainConfig(c => ({ ...c, maskCertainWeight: Math.min(1, Math.max(0.01, Number(e.target.value) || 0.1)) }))}
                      />
                    </label>
                    <label className="ml-field">
                      <span>Cohérence exclusive</span>
                      <input
                        type="number"
                        step={0.05}
                        min={0}
                        max={2}
                        value={trainConfig.consistencyWeight}
                        title="Pénalité si Σ p(adversaires) &gt; 1 pour Roi, Petit, 21, Excuse, mariages…"
                        onChange={e => setTrainConfig(c => ({ ...c, consistencyWeight: Math.min(2, Math.max(0, Number(e.target.value) || 0.1)) }))}
                      />
                    </label>
                    <label className="ml-field">
                      <span>Boost loss mariages</span>
                      <input
                        type="number"
                        step={0.1}
                        min={1}
                        max={8}
                        value={trainConfig.mariageDimBoost}
                        title="Multiplicateur de la loss sur has_mariage_* (1 = désactivé)"
                        onChange={e => setTrainConfig(c => ({ ...c, mariageDimBoost: Math.min(8, Math.max(1, Number(e.target.value) || 1.5)) }))}
                      />
                    </label>
                  </>
                )}
              </div>
            </div>
          )}

          {(trainConfig.mode === 'full' || trainConfig.mode === 'reward_only' || trainConfig.mode === 'rlhf_only') && (
            <div className="admin-section">
              <h3>Paramètres RLHF</h3>
              <div className="ml-form">
                <label className="ml-field"><span>Timesteps PPO</span><input type="number" value={trainConfig.timesteps} onChange={e => setTrainConfig(c => ({ ...c, timesteps: Number(e.target.value) || 0 }))} /></label>
                <label className="ml-field"><span>Blend feedback</span><input type="number" step="0.05" min="0" max="1" value={trainConfig.blend} onChange={e => setTrainConfig(c => ({ ...c, blend: Number(e.target.value) }))} /></label>
                <label className="ml-field"><span>Envs</span><input type="number" min="1" value={trainConfig.envs} onChange={e => setTrainConfig(c => ({ ...c, envs: Number(e.target.value) || 1 }))} /></label>
                <label className="ml-field"><span>Reward epochs</span><input type="number" min="1" value={trainConfig.rewardEpochs} onChange={e => setTrainConfig(c => ({ ...c, rewardEpochs: Number(e.target.value) || 1 }))} /></label>
              </div>
            </div>
          )}

          {trainConfig.mode === 'selfplay' && (
            <div className="admin-section">
              <h3>Paramètres self-play</h3>

              {/* Preset selector */}
              <div className="ml-preset-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
                {(['conservative', 'standard', 'aggressive'] as const).map(key => {
                  const p = SELFPLAY_PRESETS[key];
                  const active = trainConfig.selfplayPreset === key;
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => applySelfplayPreset(key)}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: active ? '2px solid var(--admin-accent, #6c5ce7)' : '1px solid var(--admin-border, #444)',
                        background: active ? 'rgba(108,92,231,0.12)' : 'transparent',
                        color: 'inherit',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{p.label}</div>
                      <div style={{ fontSize: '0.78em', opacity: 0.75, lineHeight: 1.3 }}>{p.desc}</div>
                      <div style={{ fontSize: '0.72em', opacity: 0.6, marginTop: 6 }}>
                        {p.iterations} iter × {p.gamesPerIter} • lr={p.lr} • H={p.targetEntropy}
                      </div>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => applySelfplayPreset('advanced')}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: trainConfig.selfplayPreset === 'advanced' ? '2px solid var(--admin-accent, #6c5ce7)' : '1px solid var(--admin-border, #444)',
                    background: trainConfig.selfplayPreset === 'advanced' ? 'rgba(108,92,231,0.12)' : 'transparent',
                    color: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Avancé</div>
                  <div style={{ fontSize: '0.78em', opacity: 0.75, lineHeight: 1.3 }}>Tous les hyperparamètres modifiables individuellement.</div>
                </button>
              </div>

              {/* Always-visible basics */}
              <div className="ml-form">
                <label className="ml-field"><span>Itérations</span><input type="number" min="1" value={trainConfig.selfplayIterations} onChange={e => setTrainConfig(c => ({ ...c, selfplayIterations: Number(e.target.value) || 1, selfplayPreset: 'advanced' }))} /></label>
                <label className="ml-field"><span>Parties / itération</span><input type="number" min="1" value={trainConfig.selfplayGamesPerIter} onChange={e => setTrainConfig(c => ({ ...c, selfplayGamesPerIter: Number(e.target.value) || 1, selfplayPreset: 'advanced' }))} /></label>
                <label className="ml-field" title="Part minimale de Gardes jouables (barème Chavey) pendant la collecte PPO. 0 = mix naturel (~88 % Petites). Les évals restent en mix naturel.">
                  <span>Min. Garde+ (collecte)</span>
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    value={trainConfig.selfplayMinGardeFraction}
                    onChange={e => setTrainConfig(c => ({
                      ...c,
                      selfplayMinGardeFraction: Math.max(0, Math.min(1, Number(e.target.value) || 0)),
                      selfplayPreset: 'advanced',
                    }))}
                  />
                  <span className="ml-field-help" style={{ display: 'block', marginTop: 4, fontSize: '0.85em', opacity: 0.85 }}>
                    Défaut 0,75 (75&nbsp;% Gardes en entraînement). Mettre 0 pour retrouver le mix Chavey classique.
                  </span>
                </label>
                <label
                  className="ml-field"
                  title="Sans modèle → smart_discard. Avec chemin .pt → DiscardPolicy pour le preneur ML en collecte. La porte d'éval play garde smart_discard / écart Bot1 figé."
                >
                  <span>Modèle d'écart (path)</span>
                  <input
                    type="text"
                    placeholder="production/discard_model.pt"
                    value={trainConfig.selfplayDiscardModel}
                    onChange={e => setTrainConfig(c => ({
                      ...c,
                      selfplayDiscardModel: e.target.value,
                      selfplayPreset: 'advanced',
                    }))}
                  />
                  <span className="ml-field-help" style={{ display: 'block', marginTop: 4, fontSize: '0.85em', opacity: 0.85 }}>
                    Optionnel — chemin conteneur vers DiscardPolicy. Vide = heuristique smart_discard.
                  </span>
                </label>
                {trainConfig.modelRole === 'attacker' && (
                  <label
                    className="ml-field"
                    title="Les 3 sièges défenseurs (figés) connaissent leurs mains entre eux (jamais celle du preneur/écart) — durcit l'entraînement de l'attaquant. L'évaluation/porte de sélection reste toujours sur une défense réaliste, non trichée. N'a d'effet que si le défenseur figé utilise les features de croyance (checkpoints v7/v8 ; sans effet sur un checkpoint v5 ou Bot1)."
                  >
                    <span>
                      <input
                        type="checkbox"
                        checked={trainConfig.coordinatedCheaterDefense}
                        onChange={e => setTrainConfig(c => ({
                          ...c,
                          coordinatedCheaterDefense: e.target.checked,
                          selfplayPreset: 'advanced',
                        }))}
                        style={{ marginRight: 6 }}
                      />
                      Défenseur coordonné-tricheur (durcit l'attaquant)
                    </span>
                    <span className="ml-field-help" style={{ display: 'block', marginTop: 4, fontSize: '0.85em', opacity: 0.85 }}>
                      Expérimental — lancer d'abord <code>scripts/bench_coordinated_cheater_defense.py</code> pour
                      vérifier que le gain est significatif avant un run complet.
                    </span>
                  </label>
                )}
              </div>

              {/* Advanced mode: full hyperparam grid */}
              {trainConfig.selfplayPreset === 'advanced' && (
                <>
                  <h4 style={{ marginTop: 16, marginBottom: 8, fontSize: '0.95em', opacity: 0.85 }}>Optimisation PPO</h4>
                  <div className="ml-form">
                    <label className="ml-field" title="Plus petit = updates plus stables. Défaut script: 3e-5">
                      <span>Learning rate</span>
                      <input type="number" step="0.000005" min="0" value={trainConfig.selfplayLr} onChange={e => setTrainConfig(c => ({ ...c, selfplayLr: Number(e.target.value) || 0 }))} />
                    </label>
                    <label className="ml-field" title="Nombre de passes PPO sur chaque batch d'expériences. Défaut: 3">
                      <span>PPO epochs</span>
                      <input type="number" min="1" max="10" value={trainConfig.selfplayPpoEpochs} onChange={e => setTrainConfig(c => ({ ...c, selfplayPpoEpochs: Number(e.target.value) || 1 }))} />
                    </label>
                    <label className="ml-field" title="Taille du mini-batch PPO. Défaut: 256">
                      <span>Batch size</span>
                      <input type="number" min="32" step="32" value={trainConfig.selfplayBatchSize} onChange={e => setTrainConfig(c => ({ ...c, selfplayBatchSize: Number(e.target.value) || 32 }))} />
                    </label>
                    <label className="ml-field" title="ε de PPO. Plus petit = updates plus prudents. Défaut: 0.2">
                      <span>Clip ε</span>
                      <input type="number" step="0.01" min="0.05" max="0.4" value={trainConfig.selfplayClipEps} onChange={e => setTrainConfig(c => ({ ...c, selfplayClipEps: Number(e.target.value) || 0.2 }))} />
                    </label>
                  </div>

                  <h4 style={{ marginTop: 16, marginBottom: 8, fontSize: '0.95em', opacity: 0.85 }}>Exploration / entropie</h4>
                  <div className="ml-form">
                    <label className="ml-field" title="Bonus d'entropie pour favoriser l'exploration. Défaut: 0.03">
                      <span>Entropy coef</span>
                      <input type="number" step="0.005" min="0" max="0.2" value={trainConfig.selfplayEntropyCoef} onChange={e => setTrainConfig(c => ({ ...c, selfplayEntropyCoef: Number(e.target.value) || 0 }))} />
                    </label>
                    <label className="ml-field" title="Entropie cible. ≤0 = mode AUTO legacy (souvent trop élevé). 0.5 = bon point de départ.">
                      <span>Target entropy</span>
                      <input type="number" step="0.05" min="0" max="2" value={trainConfig.selfplayTargetEntropy} onChange={e => setTrainConfig(c => ({ ...c, selfplayTargetEntropy: Number(e.target.value) }))} />
                    </label>
                    <label className="ml-field" title="Température softmax au début. Plus haute = plus exploratoire.">
                      <span>Température start</span>
                      <input type="number" step="0.1" min="0.1" max="2" value={trainConfig.selfplayTemperatureStart} onChange={e => setTrainConfig(c => ({ ...c, selfplayTemperatureStart: Number(e.target.value) || 1 }))} />
                    </label>
                    <label className="ml-field" title="Température softmax à la fin. Plus basse = plus exploitative.">
                      <span>Température end</span>
                      <input type="number" step="0.1" min="0.1" max="2" value={trainConfig.selfplayTemperatureEnd} onChange={e => setTrainConfig(c => ({ ...c, selfplayTemperatureEnd: Number(e.target.value) || 0.5 }))} />
                    </label>
                  </div>

                  <h4 style={{ marginTop: 16, marginBottom: 8, fontSize: '0.95em', opacity: 0.85 }}>Évaluation et arrêt</h4>
                  <div className="ml-form">
                    <label className="ml-field" title="Itérations sans amélioration significative avant d'arrêter. Défaut: 10">
                      <span>Early-stop patience</span>
                      <input type="number" min="1" max="30" value={trainConfig.selfplayEarlyStopPatience} onChange={e => setTrainConfig(c => ({ ...c, selfplayEarlyStopPatience: Number(e.target.value) || 1 }))} />
                    </label>
                    <label className="ml-field" title="Parties par évaluation. Plus = CI plus serré (mais plus lent).">
                      <span>Parties d'éval</span>
                      <input type="number" min="100" step="100" value={trainConfig.selfplayEvalGames} onChange={e => setTrainConfig(c => ({ ...c, selfplayEvalGames: Number(e.target.value) || 500 }))} />
                    </label>
                  </div>
                </>
              )}

              {/* Eval pool: multi-select opponents for the final-eval-only pass */}
              {(() => {
                const poolCandidates = getOpponentVersionOptions();
                const oppRoleLabel = trainConfig.modelRole === 'attacker' ? 'défenseur' : 'attaquant';
                return (
                  <>
                    <h4 style={{ marginTop: 16, marginBottom: 4, fontSize: '0.95em', opacity: 0.85 }}>Pool d'évaluation finale (anti-sur-spécialisation)</h4>
                    <p style={{ marginTop: 0, marginBottom: 8, fontSize: '0.82em', opacity: 0.75 }}>
                      L'entraînement utilise un seul adversaire (sélectionné plus haut). À la fin, on ré-évalue le best contre chaque {oppRoleLabel} coché ci-dessous, pour détecter une sur-spécialisation.
                    </p>
                    {poolCandidates.length === 0 ? (
                      <div style={{ fontSize: '0.85em', opacity: 0.7, padding: 8 }}>
                        Aucun {oppRoleLabel} disponible avec un .pt en S3.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 220, overflowY: 'auto', padding: 6, border: '1px solid var(--admin-border, #333)', borderRadius: 6 }}>
                        {poolCandidates.map(v => {
                          const checked = trainConfig.selfplayEvalPool.includes(v.versionTag);
                          const labelText = v.notes && /[A-ZÀ-Ü]/.test(v.notes.charAt(0)) && v.notes.includes(' d') ? v.notes : v.versionTag;
                          return (
                            <label key={v.versionTag} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', border: checked ? '1px solid var(--admin-accent, #6c5ce7)' : '1px solid transparent', borderRadius: 4, fontSize: '0.85em', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={e => setTrainConfig(c => ({
                                  ...c,
                                  selfplayEvalPool: e.target.checked
                                    ? [...c.selfplayEvalPool, v.versionTag]
                                    : c.selfplayEvalPool.filter(t => t !== v.versionTag),
                                }))}
                              />
                              <span title={v.versionTag}>{labelText}</span>
                              {v.deployedSlot && <span style={{ fontSize: '0.7em', opacity: 0.6 }}>({v.deployedSlot})</span>}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {trainConfig.selfplayEvalPool.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: '0.8em', opacity: 0.8 }}>
                        {trainConfig.selfplayEvalPool.length} adversaire(s) sélectionné(s).
                        <button type="button" onClick={() => setTrainConfig(c => ({ ...c, selfplayEvalPool: [] }))} style={{ marginLeft: 8, fontSize: '0.85em', padding: '2px 8px', cursor: 'pointer' }}>Tout désélectionner</button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {(trainConfig.mode === 'bidding' || trainConfig.mode === 'discard') && (
            <div className="admin-section">
              <h3>Paramètres spécialisés</h3>
              <div className="ml-form">
                {trainConfig.mode === 'bidding' ? (
                  <>
                    <label className="ml-field"><span>Donnes enchères</span><input type="number" min="1" value={trainConfig.biddingDeals} onChange={e => setTrainConfig(c => ({ ...c, biddingDeals: Number(e.target.value) || 1 }))} /></label>
                    <label className="ml-field"><span>Simulations / enchère</span><input type="number" min="1" value={trainConfig.biddingSimsPerBid} onChange={e => setTrainConfig(c => ({ ...c, biddingSimsPerBid: Number(e.target.value) || 1 }))} /></label>
                  </>
                ) : (
                  <>
                    <label className="ml-field"><span>Donnes écart</span><input type="number" min="1" value={trainConfig.discardDeals} onChange={e => setTrainConfig(c => ({ ...c, discardDeals: Number(e.target.value) || 1 }))} /></label>
                    <label className="ml-field"><span>Candidats</span><input type="number" min="1" value={trainConfig.discardCandidates} onChange={e => setTrainConfig(c => ({ ...c, discardCandidates: Number(e.target.value) || 1 }))} /></label>
                  </>
                )}
              </div>
            </div>
          )}

          {(trainConfig.mode === 'imitation' || trainConfig.mode === 'selfplay') && (
            <div className="admin-section">
              <h3>Objectif duplicate (expérimental)</h3>
              <div className="ml-form">
                <label className="ml-field">
                  <label className="ml-checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={trainConfig.duplicateObjective}
                      onChange={e => setTrainConfig(c => ({ ...c, duplicateObjective: e.target.checked }))}
                    />
                    Activer l&apos;objectif duplicate (matchpoint)
                  </label>
                  <span className="ml-field-help">
                    Désactivé par défaut — le comportement historique (val accuracy / avg_score) est conservé.
                    {trainConfig.mode === 'selfplay'
                      ? ' En self-play : reward PPO matchpoint sur un mix de donnes différées (phase 2) + sélection du checkpoint sur mp/deal.'
                      : ' En imitation : rapport duplicate post-entraînement uniquement (le checkpoint reste choisi par val accuracy).'}
                  </span>
                </label>
                {trainConfig.duplicateObjective && (
                  <>
                    <label className="ml-field">
                      <span>Import IDs différé (optionnel)</span>
                      <input
                        type="text"
                        placeholder="ex. 12, 34 — vide = ~10 train + 6 eval auto (hors s/c)"
                        value={trainConfig.duplicateImportIds}
                        onChange={e => setTrainConfig(c => ({ ...c, duplicateImportIds: e.target.value }))}
                      />
                      <span className="ml-field-help">
                        Vide : pioche automatique dans le pool actif (rôle attaquant/défenseur),
                        exclusion des différés sur-contrats (attaqueSC / maxiDef), split train/eval hold-out.
                      </span>
                    </label>
                    <label className="ml-field">
                      <span>Donnes par éval duplicate</span>
                      <input
                        type="number"
                        min={5}
                        max={200}
                        value={trainConfig.duplicateEvalDeals}
                        onChange={e => setTrainConfig(c => ({ ...c, duplicateEvalDeals: Number(e.target.value) || 40 }))}
                      />
                    </label>
                    {trainConfig.mode === 'selfplay' && (
                      <label className="ml-field">
                        <span>Fraction collecte duplicate (reward PPO)</span>
                        <input
                          type="number"
                          min={0}
                          max={1}
                          step={0.05}
                          value={trainConfig.duplicateTrainFraction}
                          onChange={e => setTrainConfig(c => ({
                            ...c,
                            duplicateTrainFraction: Math.max(0, Math.min(1, Number(e.target.value) || 0)),
                          }))}
                        />
                        <span className="ml-field-help">
                          Part des parties de collecte jouées sur des donnes différées fixes
                          avec reward matchpoint (défaut 0.25). 0 = sélection/eval duplicate seulement.
                        </span>
                      </label>
                    )}
                    {trainConfig.mode === 'selfplay' && (
                      <label className="ml-checkbox-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={trainConfig.duplicateNormalizeFieldSize}
                          onChange={e => setTrainConfig(c => ({
                            ...c,
                            duplicateNormalizeFieldSize: e.target.checked,
                          }))}
                        />
                        <span>
                          Normaliser la sélection duplicate par taille du champ
                          <span className="ml-field-help" style={{ display: 'block', marginTop: 4 }}>
                            Désactivé par défaut (mp/deal brut). Si coché : sélection du best sur
                            mp_rate = mp ÷ (2×N joueurs), indépendant de la taille du différé.
                            Le reward PPO reste toujours normalisé ; seule la sélection change.
                          </span>
                        </span>
                      </label>
                    )}
                    {trainConfig.mode === 'selfplay' && (
                      <>
                        <label className="ml-checkbox-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                          <input
                            type="checkbox"
                            checked={trainConfig.duplicateFrontierReplay}
                            onChange={e => setTrainConfig(c => ({
                              ...c,
                              duplicateFrontierReplay: e.target.checked,
                            }))}
                          />
                          <span>
                            Frontier replay (donnes “au coude-à-coude”)
                            <span className="ml-field-help" style={{ display: 'block', marginTop: 4 }}>
                              Désactivé par défaut. Quand activé, une fraction des donnes duplicate de collecte
                              est tirée d’un buffer de donnes où beaucoup d’humains sont juste devant le modèle
                              (gain marginal en mp élevé).
                            </span>
                          </span>
                        </label>
                        {trainConfig.duplicateFrontierReplay && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 6 }}>
                            <label className="ml-field">
                              <span>Fraction frontier</span>
                              <input
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={trainConfig.duplicateFrontierFraction}
                                onChange={e => setTrainConfig(c => ({
                                  ...c,
                                  duplicateFrontierFraction: Math.max(0, Math.min(1, Number(e.target.value) || 0)),
                                }))}
                              />
                            </label>
                            <label className="ml-field">
                              <span>Δ frontier (pts raw)</span>
                              <input
                                type="number"
                                min={0}
                                step={5}
                                value={trainConfig.duplicateFrontierDeltaRaw}
                                onChange={e => setTrainConfig(c => ({
                                  ...c,
                                  duplicateFrontierDeltaRaw: Math.max(0, Number(e.target.value) || 0),
                                }))}
                              />
                              <span className="ml-field-help">
                                Bande [score, score+Δ] en points raw (API). 30 ≈ 10 pts harness.
                              </span>
                            </label>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <div className="admin-section">
            <h3>Lancement</h3>
            {timeoutEstimate && (trainConfig.mode === 'imitation' || trainConfig.mode === 'deductions') && (
              <p style={{ color: '#666' }}>Durée estimée : ~{timeoutEstimate.estimatedMinutes} min (timeout {Math.round(timeoutEstimate.timeoutSeconds / 60)} min).</p>
            )}
            <button className="admin-button primary" type="button" disabled={busy === 'launch'} onClick={handleLaunch}>
              {busy === 'launch' ? 'Lancement...' : 'Lancer l\'entraînement'}
            </button>
          </div>
        </div>
      )}

      {section === 'jobs' && (
        <div className="ml-jobs">
          <div className="ml-jobs-header">
            <h3>Historique des jobs</h3>
            <div className="ml-jobs-header-actions">
              <button type="button" className="admin-btn-sm" onClick={refresh}>Rafraîchir</button>
              {jobs.length > 0 && visibleJobs.length > 0 && (
                <button type="button" className="admin-btn-sm" onClick={handleHideAllVisibleJobs}>
                  Masquer la liste affichée
                </button>
              )}
              {hiddenJobIds.length > 0 && (
                <button type="button" className="admin-btn-sm admin-btn-secondary" onClick={handleResetHiddenJobs}>
                  Réafficher les jobs masqués ({hiddenJobIds.length})
                </button>
              )}
            </div>
          </div>
          <p className="ml-jobs-hint">
            Masquer retire uniquement l’affichage dans ce navigateur ; les jobs OVH ne sont pas supprimés.
          </p>

          {jobs.length === 0 ? (
            <div className="admin-empty">Aucun job d'entraînement</div>
          ) : visibleJobs.length === 0 ? (
            <div className="admin-empty">
              Tous les jobs sont masqués ({jobs.length} au total).
              {' '}
              <button type="button" className="admin-btn-sm" onClick={handleResetHiddenJobs}>Réafficher</button>
            </div>
          ) : (
            <div className="ml-jobs-list">
              {visibleJobs.map(j => {
                const isActive = isMlJobActiveState(j.state);
                const isFailed = ['FAILED', 'ERROR'].includes(j.state);
                const d = new Date(j.createdAt);
                const dateStr = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                const timeStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                const nameParts = (j.name || j.id.slice(0, 8)).match(/^tarot-(\w+?)-(att|def)-(.+)$/);
                const modeLabel = nameParts?.[1] || (j.name || j.id.slice(0, 8));
                const roleLabel = nameParts?.[2] === 'att' ? 'ATT' : nameParts?.[2] === 'def' ? 'DEF' : null;
                const displayName = mlJobDisplayName(j, modelVersions);
                const showDisplayName = displayName && displayName !== j.name && displayName !== j.id;

                return (
                  <div key={j.id} className={`ml-job-card ${isActive ? 'ml-job-card--active' : ''} ${isFailed ? 'ml-job-card--failed' : ''}`}>
                    <div className="ml-job-card-main">
                      <div className="ml-job-card-info">
                        <div className="ml-job-card-title">
                          {showDisplayName && <span className="ml-job-card-model-name" title={j.name}>{displayName}</span>}
                          <span className="ml-job-card-mode">{modeLabel}</span>
                          {roleLabel && <span className="ml-job-card-role">{roleLabel}</span>}
                          {stateLabel(j.state)}
                        </div>
                        <div className="ml-job-card-meta">
                          <span>{dateStr} {timeStr}</span>
                          <span className="ml-job-card-sep">·</span>
                          <span>{formatDuration(j.duration)}</span>
                          <span className="ml-job-card-sep">·</span>
                          <span>{j.gpu || 'CPU'}</span>
                        </div>
                        {isFailed && j.info && (
                          <div className="ml-job-card-error">{j.info}</div>
                        )}
                      </div>
                      <div className="ml-job-card-actions">
                        {isActive && (
                          <button type="button" className="ml-job-btn ml-job-btn--danger" onClick={() => handleStop(j.id)}>Stop</button>
                        )}
                        <button type="button" className="ml-job-btn" onClick={() => handleViewLogs(j.id)}>Logs</button>
                        <button
                          type="button"
                          className="ml-job-btn ml-job-btn--ghost"
                          title="Masquer cette ligne"
                          onClick={() => handleHideJobFromList(j.id)}
                        >
                          Masquer
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Versions ──────────────────────────────────────── */}
      {section === 'versions' && (
        <div className="ml-versions">
          <div className="ml-versions-header">
            <h3>Historique des versions</h3>
            <div className="ml-versions-actions">
              <button className="admin-btn-sm" onClick={handleSyncJobs} disabled={!!busy}>
                {busy === 'sync' ? 'Sync...' : 'Synchroniser les jobs'}
              </button>
              <button
                type="button"
                className="admin-btn-sm"
                onClick={handleExportAllTrainingJson}
                disabled={!!busy || trainingExportTag !== null || modelVersions.length === 0}
                title="Toutes les entrées du registre (config, métriques, jobs S3) — pour analyse"
              >
                {trainingExportTag === 'all' ? 'Export...' : 'Exporter tout (JSON)'}
              </button>
              <button className="admin-btn-sm" onClick={refresh}>Rafraichir</button>
            </div>
          </div>

          <div className="ml-versions-filters" role="search" aria-label="Filtrer les versions">
            <label className="ml-versions-filter-field">
              <span>Nom</span>
              <input
                type="search"
                value={versionsFilterName}
                onChange={e => setVersionsFilterName(e.target.value)}
                placeholder="Notes ou tag…"
                autoComplete="off"
              />
            </label>
            <label className="ml-versions-filter-field">
              <span>Mode</span>
              <select value={versionsFilterMode} onChange={e => setVersionsFilterMode(e.target.value)}>
                <option value="">Tous</option>
                {trainingModesInRegistry.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </label>
            <label className="ml-versions-filter-field">
              <span>Statut</span>
              <select value={versionsFilterStatus} onChange={e => setVersionsFilterStatus(e.target.value)}>
                <option value="">Tous</option>
                <option value="training">Entraînement</option>
                <option value="trained">Entraîné</option>
                <option value="evaluating">Évaluation</option>
                <option value="evaluated">Évalué</option>
                <option value="deployed">Déployé</option>
                <option value="retired">Retiré</option>
              </select>
            </label>
            <label className="ml-versions-filter-field">
              <span>Rôle</span>
              <select
                value={versionsFilterRole}
                onChange={e => setVersionsFilterRole(e.target.value as 'all' | 'attacker' | 'defender' | 'deductions')}
              >
                <option value="all">Tous</option>
                <option value="attacker">Attaque</option>
                <option value="defender">Défense</option>
                <option value="deductions">Déductions</option>
              </select>
            </label>
          </div>

          {modelVersions.length === 0 ? (
            <div className="admin-empty">Aucune version enregistree. Lancez un entrainement pour creer une nouvelle version.</div>
          ) : filteredModelVersions.length === 0 ? (
            <div className="admin-empty">Aucune version ne correspond aux filtres.</div>
          ) : (
            <div className="ml-version-list">
              {filteredModelVersions.map(v => {
                const history = v.evalMetrics?.details?.history;
                const isSelfplay = v.evalMetrics?.details?.trainingType === 'selfplay';
                const cfg = v.trainingConfig as Record<string, unknown> | null;
                const expanded = versionsExpandedTags.has(v.versionTag);
                return (
                <div
                  key={v.id}
                  className={`ml-version-card ml-version-card--compact ml-version-card--${v.status}${expanded ? ' ml-version-card--expanded' : ''}`}
                >
                  <div
                    className="ml-version-summary-row"
                    role="button"
                    tabIndex={0}
                    aria-expanded={expanded}
                    title={expanded ? 'Réduire le détail' : 'Afficher le détail'}
                    onClick={() => toggleVersionExpanded(v.versionTag)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleVersionExpanded(v.versionTag);
                      }
                    }}
                  >
                    <div className="ml-version-header-row ml-version-header-row--summary">
                      <div className="ml-version-tag">
                        <span className={`ml-role-icon ml-role-icon--${v.trainingMode === 'deductions' ? 'deductions' : v.modelRole}`} title={v.trainingMode === 'deductions' ? 'Déductions' : v.modelRole === 'defender' ? 'Défense' : 'Attaque'}>
                          {v.trainingMode === 'deductions' ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                          ) : v.modelRole === 'defender' ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/><line x1="5" y1="5" x2="9" y2="5"/><line x1="5" y1="5" x2="5" y2="9"/><line x1="19" y1="5" x2="15" y2="5"/><line x1="19" y1="5" x2="19" y2="9"/></svg>
                          )}
                        </span>
                        {(() => {
                          const displayName = v.notes || '';
                          const hasClanName = displayName && /[A-ZÀ-Ü]/.test(displayName.charAt(0)) && displayName.includes(' d');
                          return hasClanName ? (
                            <>
                              <span className="ml-version-clan-name" onClick={e => { e.stopPropagation(); handleRenameVersion(v.versionTag, displayName); }} style={{ cursor: 'pointer' }} title="Cliquer pour renommer">{displayName}</span>
                              <span className="ml-version-tag-text ml-version-tag-sub" title={v.versionTag}>{v.versionTag.replace(/^(def|att)-(sp|im|bid|dis|full|rl)-/, '')}</span>
                            </>
                          ) : (
                            <span className="ml-version-tag-text" onClick={e => { e.stopPropagation(); handleRenameVersion(v.versionTag, displayName || v.versionTag); }} style={{ cursor: 'pointer' }} title="Cliquer pour renommer">{displayName || v.versionTag}</span>
                          );
                        })()}
                        <span className="ml-version-mode-badge">{v.trainingMode}</span>
                      </div>
                      <div className="ml-version-summary-right">
                        <span className="ml-version-summary-date">{new Date(v.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        <span className={`ml-version-status ml-version-status--${v.status}`}>
                          {v.status === 'training' && 'Entrainement...'}
                          {v.status === 'trained' && 'Entraine'}
                          {v.status === 'evaluating' && 'Evaluation...'}
                          {v.status === 'evaluated' && 'Evalue'}
                          {v.status === 'deployed' && `Deploye (${v.deployedSlot})`}
                          {v.status === 'retired' && 'Retire'}
                        </span>
                        <span className="ml-version-chevron" aria-hidden>{expanded ? '▼' : '▶'}</span>
                      </div>
                    </div>
                  </div>

                  {expanded && (
                  <div className="ml-version-detail">
                  {(() => {
                    const det = v.evalMetrics?.details;
                    const pct = (x: number | undefined) =>
                      x == null || Number.isNaN(x) ? '—' : `${(Number(x) <= 1 ? x * 100 : x).toFixed(1)}${Number(x) <= 1 ? '%' : ''}`;
                    const lossVal = det?.finalTrainLoss ?? det?.valLoss;
                    const hasLoss = typeof lossVal === 'number' && !Number.isNaN(lossVal);
                    const hasAcc = det && (
                      det.valAccuracy != null
                      || det.entameAccuracy != null
                      || det.otherAccuracy != null
                      || det.defenderAccuracy
                      || det.attackerAccuracy
                    );
                    const dm = det?.datasetManifest;
                    const modeLabels: Record<string, string> = {
                      per_donne: 'Top par donne', per_differe: 'Top par différé',
                      ranking_general: 'Class. glissant général', ranking_specific: 'Class. glissant spécifique',
                      ranking_live: 'Class. live',
                      competition_team: 'Championnat équipes',
                      competition_team_top_teams: 'Championnat équipes top',
                      competition_cia: 'Championnat individuel attaque',
                      competition_cia_top_divisions: 'Championnat indiv. attaque top',
                      competition_quadrette: 'Coupe Quadrette',
                      freedefi_elite: 'Défis libres élite',
                      freedefi_top_live: 'Défis libres top live',
                      differe_indiv_top_per_donne: 'Différé indiv. top par donne',
                      differe_indiv_top_general_role: 'Différé indiv. top élite (rôle)',
                    };
                    const showImitationData = v.trainingMode === 'imitation' && (
                      det?.imitationUniqueDeals != null
                      || det?.imitationTrainGames != null
                      || det?.imitationValGames != null
                      || dm
                    );
                    if (!hasLoss && !hasAcc && !showImitationData) return null;
                    return (
                      <div className="ml-version-detail-summary">
                        <div className="ml-version-detail-summary-title">Synthèse</div>
                        {(hasLoss || hasAcc) && (
                          <div className="ml-version-detail-summary-grid">
                            {hasLoss && (
                              <div className="ml-version-detail-kv">
                                <span className="ml-version-detail-k">Loss (dernier epoch)</span>
                                <span className="ml-version-detail-v">{(lossVal as number).toFixed(5)}</span>
                              </div>
                            )}
                            {det?.valAccuracy != null && (
                              <div className="ml-version-detail-kv">
                                <span className="ml-version-detail-k">Accuracy val</span>
                                <span className="ml-version-detail-v">{pct(det.valAccuracy)}</span>
                              </div>
                            )}
                            {det?.entameAccuracy != null && (
                              <div className="ml-version-detail-kv">
                                <span className="ml-version-detail-k">Accuracy entame</span>
                                <span className="ml-version-detail-v">{pct(det.entameAccuracy)}</span>
                              </div>
                            )}
                            {det?.otherAccuracy != null && (
                              <div className="ml-version-detail-kv">
                                <span className="ml-version-detail-k">Accuracy autres coups</span>
                                <span className="ml-version-detail-v">{pct(det.otherAccuracy)}</span>
                              </div>
                            )}
                            {(det?.defenderAccuracy || det?.attackerAccuracy) && !det?.valAccuracy && (
                              <div className="ml-version-detail-kv">
                                <span className="ml-version-detail-k">Accuracy (legacy)</span>
                                <span className="ml-version-detail-v">{String(det.defenderAccuracy || det.attackerAccuracy)}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {showImitationData && (
                          <div className="ml-version-imitation-stats">
                            <div className="ml-version-detail-summary-sub">Jeu de données (imitation)</div>
                            <div className="ml-version-detail-summary-grid">
                              {det?.imitationUniqueDeals != null && (
                                <div className="ml-version-detail-kv">
                                  <span className="ml-version-detail-k">Donnes uniques</span>
                                  <span className="ml-version-detail-v">{(det.imitationUniqueDeals as number).toLocaleString('fr-FR')}</span>
                                </div>
                              )}
                              {(det?.imitationTrainGames != null || det?.imitationValGames != null) && (
                                <div className="ml-version-detail-kv">
                                  <span className="ml-version-detail-k">Parties train / val</span>
                                  <span className="ml-version-detail-v">
                                    {det?.imitationTrainGames != null ? (det.imitationTrainGames as number).toLocaleString('fr-FR') : '—'}
                                    {' / '}
                                    {det?.imitationValGames != null ? (det.imitationValGames as number).toLocaleString('fr-FR') : '—'}
                                  </span>
                                </div>
                              )}
                              {dm && (
                                <>
                                  <div className="ml-version-detail-kv">
                                    <span className="ml-version-detail-k">Parties (manifest)</span>
                                    <span className="ml-version-detail-v">{dm.totalGames.toLocaleString('fr-FR')}</span>
                                  </div>
                                  <div className="ml-version-detail-kv">
                                    <span className="ml-version-detail-k">Joueurs</span>
                                    <span className="ml-version-detail-v">{dm.totalPlayers.toLocaleString('fr-FR')}</span>
                                  </div>
                                  <div className="ml-version-detail-kv">
                                    <span className="ml-version-detail-k">Source</span>
                                    <span className="ml-version-detail-v">{modeLabels[dm.selectionMode] || dm.selectionMode}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {(() => {
                    const compRows = comparisonsByVersionTag.get(v.versionTag) ?? [];
                    return (
                      <div
                        className="ml-version-comparisons-ref"
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                      >
                        <div className="ml-version-detail-summary-sub">Comparaisons ML (onglet Comparer)</div>
                        {compRows.length === 0 ? (
                          <p className="ml-version-comparisons-empty">
                            Aucune entrée dans l&apos;historique récent (200 dernières comparaisons) ne référence ce tag
                            dans la config ou les statistiques agrégées. Si vous venez d&apos;en lancer une, rafraîchissez
                            la page ou ouvrez l&apos;onglet Comparer puis revenez ici.
                          </p>
                        ) : (
                          <div className="ml-version-comparison-minis-wrap">
                            {compRows.map(row => {
                              const full = comparisons.find(c => c.id === row.id);
                              const cfg = (full?.config ?? {}) as Record<string, unknown>;
                              const dealCount = Number(cfg.effectiveDealCount ?? cfg.dealCount) || 0;
                              const minBid = cfg.minBidScore != null ? Number(cfg.minBidScore) : null;
                              const maxBid = cfg.maxBidScore != null ? Number(cfg.maxBidScore) : null;
                              const bidRange = minBid != null && maxBid != null && !Number.isNaN(minBid) && !Number.isNaN(maxBid)
                                ? `${minBid}–${maxBid}`
                                : '—';
                              const roleFr = cfg.compareRole === 'defender' ? 'défense' : 'attaque';
                              const oppName = mlOpponentDisplayFromCompareConfig(cfg, modelVersions);
                              const stats = Array.isArray(full?.modelStats) ? full!.modelStats! as Array<Record<string, unknown>> : [];
                              const sorted = [...stats].sort(
                                (a, b) => (Number(b.winRate) || 0) - (Number(a.winRate) || 0),
                              );
                              const openCompare = () => {
                                void handleViewComparison(row.id);
                                onSectionChange('compare');
                              };
                              return (
                                <div
                                  key={row.id}
                                  className="ml-version-comparison-mini"
                                  role="button"
                                  tabIndex={0}
                                  title="Ouvrir cette comparaison dans l’onglet Comparer"
                                  onClick={e => {
                                    e.stopPropagation();
                                    openCompare();
                                  }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      openCompare();
                                    }
                                  }}
                                >
                                  <div className="ml-version-comparison-mini-head">
                                    <span className="ml-version-comparison-mini-id">#{row.id}</span>
                                    <span className={`ml-version-comparison-mini-status ml-version-comparison-mini-status--${String(row.status).replace(/[^a-z]/gi, '')}`}>
                                      {row.status}
                                    </span>
                                  </div>
                                  <div className="ml-version-comparison-mini-meta">
                                    {dealCount > 0 ? `${dealCount} donnes` : '— donnes'}
                                    {' · '}
                                    vs <strong title={oppName}>{mlShortModelLabel(oppName)}</strong>
                                    {' · '}
                                    main preneur {bidRange} pts
                                    {' · '}
                                    {roleFr}
                                  </div>
                                  <div className="ml-version-comparison-mini-date">
                                    {new Date(row.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                                  </div>
                                  {sorted.length > 0 ? (
                                    <table className="ml-version-comparison-mini-table">
                                      <thead>
                                        <tr>
                                          <th>Modèle</th>
                                          <th>WR</th>
                                          <th>Score</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {sorted.map(ms => {
                                          const tag = String(ms.versionTag ?? '');
                                          const isSelf = tag === v.versionTag;
                                          const name = mlCompareRowDisplayName(
                                            tag,
                                            typeof ms.modelLabel === 'string' ? ms.modelLabel : undefined,
                                            modelVersions,
                                          );
                                          const wr = Number(ms.winRate);
                                          const sc = Number(ms.avgScore);
                                          return (
                                            <tr
                                              key={tag || name}
                                              className={isSelf ? 'ml-version-comparison-mini-row--self' : undefined}
                                            >
                                              <td className="ml-version-comparison-mini-name" title={name}>{mlShortModelLabel(name)}</td>
                                              <td className="ml-version-comparison-mini-num">
                                                {(Number.isFinite(wr) ? wr * 100 : 0).toFixed(1)}%
                                              </td>
                                              <td className="ml-version-comparison-mini-num">
                                                {Number.isFinite(sc) ? sc.toFixed(1) : '—'}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  ) : (
                                    <p className="ml-version-comparison-mini-pending">
                                      {row.status === 'completed' ? 'Pas de stats agrégées' : 'Stats en cours ou indisponibles'}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div className="ml-version-meta">
                    {v.parentVersion && (
                      <span>Parent : <strong>{(() => {
                        const parent = modelVersions.find(p => p.versionTag === v.parentVersion);
                        const parentClan = parent?.notes && /[A-ZÀ-Ü]/.test(parent.notes.charAt(0)) && parent.notes.includes(' d');
                        return parentClan ? parent.notes : v.parentVersion;
                      })()}</strong></span>
                    )}
                    {isSelfplay && v.evalMetrics?.details?.iterations && (
                      <span>{v.evalMetrics.details.iterations} iterations x {v.evalMetrics.details.gamesPerIteration} parties</span>
                    )}
                    {isSelfplay && v.evalMetrics?.details?.trainRole && (
                      <span>Role entraine : {v.evalMetrics.details.trainRole === 'defender' ? 'Defense' : 'Attaque'}</span>
                    )}
                    {!isSelfplay && cfg && (
                      <>
                        {cfg.selfplayIterations && <span>{String(cfg.selfplayIterations)} iter x {String(cfg.selfplayGamesPerIter || 500)} parties</span>}
                        {cfg.epochs && <span>{String(cfg.epochs)} epochs</span>}
                      </>
                    )}
                    {v.s3ModelPath && <span className="ml-version-s3path">{v.s3ModelPath}</span>}
                  </div>

                  {v.evalMetrics && (
                    <div className="ml-version-metrics-row">
                      <div className="ml-version-metrics">
                        {v.trainingMode === 'deductions' ? (<>
                          {!!v.evalMetrics.details?.macro_f1_nontrivial && (
                            <div className="ml-version-metric ml-metric-accuracy">
                              <span className="ml-version-metric-value">
                                {((v.evalMetrics.details.macro_f1_nontrivial as number) * 100).toFixed(1)}%
                              </span>
                              <span className="ml-version-metric-label">Macro F1 (non-trivial)</span>
                            </div>
                          )}
                          {!!v.evalMetrics.details?.overall_accuracy && (
                            <div className="ml-version-metric">
                              <span className="ml-version-metric-value">
                                {((v.evalMetrics.details.overall_accuracy as number) * 100).toFixed(1)}%
                              </span>
                              <span className="ml-version-metric-label">Accuracy</span>
                            </div>
                          )}
                          {!!v.evalMetrics.details?.macro_precision && (
                            <div className="ml-version-metric">
                              <span className="ml-version-metric-value">
                                {((v.evalMetrics.details.macro_precision as number) * 100).toFixed(1)}%
                              </span>
                              <span className="ml-version-metric-label">Précision</span>
                            </div>
                          )}
                          {!!v.evalMetrics.details?.macro_recall && (
                            <div className="ml-version-metric">
                              <span className="ml-version-metric-value">
                                {((v.evalMetrics.details.macro_recall as number) * 100).toFixed(1)}%
                              </span>
                              <span className="ml-version-metric-label">Rappel</span>
                            </div>
                          )}
                          {!!v.evalMetrics.details?.num_assertions && (
                            <div className="ml-version-metric">
                              <span className="ml-version-metric-value">
                                {v.evalMetrics.details.num_assertions as number}
                                {!!v.evalMetrics.details?.num_nontrivial_assertions && (
                                  <span style={{ fontSize: '0.75em', opacity: 0.7 }}> ({v.evalMetrics.details.num_nontrivial_assertions as number} actives)</span>
                                )}
                              </span>
                              <span className="ml-version-metric-label">Assertions</span>
                            </div>
                          )}
                          {!!v.evalMetrics.details?.train_examples && (
                            <div className="ml-version-metric">
                              <span className="ml-version-metric-value">{(v.evalMetrics.details.train_examples as number).toLocaleString()}</span>
                              <span className="ml-version-metric-label">Exemples train</span>
                            </div>
                          )}
                          {!!v.evalMetrics.details?.val_examples && (
                            <div className="ml-version-metric">
                              <span className="ml-version-metric-value">{(v.evalMetrics.details.val_examples as number).toLocaleString()}</span>
                              <span className="ml-version-metric-label">Exemples val</span>
                            </div>
                          )}
                          {!!v.evalMetrics.details?.temperature && (
                            <div className="ml-version-metric">
                              <span className="ml-version-metric-value">{(v.evalMetrics.details.temperature as number).toFixed(3)}</span>
                              <span className="ml-version-metric-label">Température</span>
                            </div>
                          )}
                          {!!v.evalMetrics.details?.best_epoch && (
                            <div className="ml-version-metric">
                              <span className="ml-version-metric-value">{v.evalMetrics.details.best_epoch as number}</span>
                              <span className="ml-version-metric-label">Meilleure epoch</span>
                            </div>
                          )}
                        </>) : (<>
                        {/* Val accuracy (imitation learning — from eval_results.json) */}
                        {!!v.evalMetrics.details?.valAccuracy && (
                          <div className="ml-version-metric ml-metric-accuracy">
                            <span className="ml-version-metric-value">
                              {((v.evalMetrics.details.valAccuracy as number) * 100).toFixed(1)}%
                            </span>
                            <span className="ml-version-metric-label">Val accuracy</span>
                          </div>
                        )}
                        {!!v.evalMetrics.details?.entameAccuracy && (
                          <div className="ml-version-metric">
                            <span className="ml-version-metric-value">
                              {((v.evalMetrics.details.entameAccuracy as number) * 100).toFixed(1)}%
                            </span>
                            <span className="ml-version-metric-label">Entame accuracy</span>
                          </div>
                        )}
                        {/* Legacy accuracy field */}
                        {!v.evalMetrics.details?.valAccuracy && (v.evalMetrics.details?.defenderAccuracy || v.evalMetrics.details?.attackerAccuracy) && (
                          <div className="ml-version-metric ml-metric-accuracy">
                            <span className="ml-version-metric-value">
                              {v.evalMetrics.details?.defenderAccuracy || v.evalMetrics.details?.attackerAccuracy}
                            </span>
                            <span className="ml-version-metric-label">Accuracy</span>
                          </div>
                        )}
                        {/* Win rate (self-play) */}
                        {isSelfplay && (
                          <div className="ml-version-metric ml-metric-main">
                            <span className="ml-version-metric-value">{(v.evalMetrics.winRate * 100).toFixed(1)}%</span>
                            <span className="ml-version-metric-label">Win rate (self-play)</span>
                          </div>
                        )}
                        {/* For legacy models without selfplay, show the main metric */}
                        {!isSelfplay && !(v.evalMetrics.details?.defenderAccuracy || v.evalMetrics.details?.attackerAccuracy) && (
                          <div className="ml-version-metric ml-metric-main">
                            <span className="ml-version-metric-value">{(v.evalMetrics.winRate * 100).toFixed(1)}%</span>
                            <span className="ml-version-metric-label">Score</span>
                          </div>
                        )}
                        {v.evalMetrics.improvement !== undefined && (
                          <div className={`ml-version-metric ${v.evalMetrics.improvement >= 0 ? 'ml-metric-positive' : 'ml-metric-negative'}`}>
                            <span className="ml-version-metric-value">
                              {v.evalMetrics.improvement >= 0 ? '+' : ''}{(v.evalMetrics.improvement * 100).toFixed(1)}%
                            </span>
                            <span className="ml-version-metric-label">Progression</span>
                          </div>
                        )}
                        {v.evalMetrics.referenceWinRate !== undefined && (
                          <div className="ml-version-metric ml-metric-ref">
                            <span className="ml-version-metric-value">{(v.evalMetrics.referenceWinRate * 100).toFixed(1)}%</span>
                            <span className="ml-version-metric-label">Depart</span>
                          </div>
                        )}
                        {v.evalMetrics.gamesPlayed > 0 && (
                          <div className="ml-version-metric">
                            <span className="ml-version-metric-value">{v.evalMetrics.gamesPlayed.toLocaleString()}</span>
                            <span className="ml-version-metric-label">Parties</span>
                          </div>
                        )}
                        {!!v.evalMetrics.details?.featureDim && (
                          <div className="ml-version-metric">
                            <span className="ml-version-metric-value">{v.evalMetrics.details.featureDim as number}d</span>
                            <span className="ml-version-metric-label">Features</span>
                          </div>
                        )}
                        </>)}
                      </div>

                      {/* Duplicate eval summary (self-play + duplicate objective) */}
                      {isSelfplay && v.evalMetrics.details?.duplicateSummary && (() => {
                        const dup = v.evalMetrics.details.duplicateSummary as {
                          objectiveActive?: boolean;
                          tot?: number | null;
                          avgMatchpoint?: number | null;
                          dealsPlayed?: number | null;
                          dealsWithField?: number | null;
                          q1Rate?: number | null;
                          iteration?: number;
                        };
                        if (!dup.objectiveActive) return null;
                        const played = dup.dealsPlayed ?? 0;
                        const withField = dup.dealsWithField ?? 0;
                        const fieldWarn = played > 0 && withField < played;
                        return (
                          <details style={{ marginTop: 8, width: '100%' }}>
                            <summary style={{ cursor: 'pointer', fontSize: '0.85em', fontWeight: 600, color: 'var(--admin-text-secondary, #888)' }}>
                              Duplicate (matchpoint) — iter. {dup.iteration ?? '?'}
                              {fieldWarn && (
                                <span style={{ marginLeft: 8, color: '#e53935' }}>
                                  ⚠ field={withField}/{played}
                                </span>
                              )}
                            </summary>
                            <div className="ml-version-metrics" style={{ marginTop: 6 }}>
                              <div className="ml-version-metric">
                                <span className="ml-version-metric-value">{dup.tot ?? '—'}</span>
                                <span className="ml-version-metric-label">Tot MP</span>
                              </div>
                              <div className="ml-version-metric">
                                <span className="ml-version-metric-value">
                                  {dup.avgMatchpoint != null ? dup.avgMatchpoint.toFixed(1) : '—'}
                                </span>
                                <span className="ml-version-metric-label">MP / donne</span>
                              </div>
                              <div className="ml-version-metric">
                                <span className="ml-version-metric-value">
                                  {played}{withField != null ? ` / ${withField}` : ''}
                                </span>
                                <span className="ml-version-metric-label">Donnes (jouées / field)</span>
                              </div>
                              {dup.q1Rate != null && (
                                <div className="ml-version-metric">
                                  <span className="ml-version-metric-value">{(dup.q1Rate * 100).toFixed(1)}%</span>
                                  <span className="ml-version-metric-label">Q1 disasters</span>
                                </div>
                              )}
                            </div>
                          </details>
                        );
                      })()}

                      {/* Eval pool: per-opponent breakdown for selfplay models */}
                      {isSelfplay && v.evalMetrics.details?.evalPool && (
                        <details style={{ marginTop: 8, width: '100%' }}>
                          <summary style={{ cursor: 'pointer', fontSize: '0.85em', fontWeight: 600, color: 'var(--admin-text-secondary, #888)' }}>
                            Pool d'évaluation : {v.evalMetrics.details.evalPool.opponents.length} adversaire(s) — moy. {(v.evalMetrics.details.evalPool.averageWinRate * 100).toFixed(1)}%
                            {v.evalMetrics.details.evalPool.spread > 0.10 && (
                              <span style={{ marginLeft: 8, color: '#e53935', fontWeight: 700 }}>
                                ⚠ écart {(v.evalMetrics.details.evalPool.spread * 100).toFixed(1)}% (sur-spécialisation)
                              </span>
                            )}
                          </summary>
                          <table style={{ width: '100%', fontSize: '0.82em', marginTop: 6, borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid var(--admin-border, #333)' }}>
                                <th style={{ textAlign: 'left', padding: '3px 8px' }}>Adversaire</th>
                                <th style={{ textAlign: 'right', padding: '3px 8px' }}>Win rate</th>
                                <th style={{ textAlign: 'right', padding: '3px 8px' }}>Avg score</th>
                                <th style={{ textAlign: 'right', padding: '3px 8px' }}>Parties</th>
                              </tr>
                            </thead>
                            <tbody>
                              {v.evalMetrics.details.evalPool.opponents.map(op => {
                                const wr = op.winRate;
                                const color = wr >= 0.5 ? '#4caf50' : wr >= 0.45 ? '#ff9800' : '#e53935';
                                return (
                                  <tr key={op.opponent}>
                                    <td style={{ padding: '2px 8px', fontFamily: 'monospace' }}>{op.opponent}</td>
                                    <td style={{ textAlign: 'right', padding: '2px 8px', fontWeight: 600, color }}>
                                      {(wr * 100).toFixed(1)}%
                                    </td>
                                    <td style={{ textAlign: 'right', padding: '2px 8px' }}>{op.avgScore.toFixed(1)}</td>
                                    <td style={{ textAlign: 'right', padding: '2px 8px' }}>{op.gamesPlayed.toLocaleString()}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </details>
                      )}

                      {/* Per-trick breakdown for deductions models */}
                      {v.trainingMode === 'deductions' && v.evalMetrics?.details?.per_trick && (
                        <details style={{ marginTop: 8, width: '100%' }}>
                          <summary style={{ cursor: 'pointer', fontSize: '0.85em', fontWeight: 600, color: 'var(--admin-text-secondary, #888)' }}>
                            Breakdown par pli
                          </summary>
                          <table style={{ width: '100%', fontSize: '0.82em', marginTop: 6, borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid var(--admin-border, #333)' }}>
                                <th style={{ textAlign: 'left', padding: '3px 8px' }}>Plis</th>
                                <th style={{ textAlign: 'right', padding: '3px 8px' }}>Macro F1</th>
                                <th style={{ textAlign: 'right', padding: '3px 8px' }}>Accuracy</th>
                                <th style={{ textAlign: 'right', padding: '3px 8px' }}>Exemples</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(v.evalMetrics.details.per_trick as Record<string, { accuracy: number; macro_f1: number; num_examples: number }>).map(([group, m]) => (
                                <tr key={group}>
                                  <td style={{ padding: '2px 8px' }}>{group}</td>
                                  <td style={{ textAlign: 'right', padding: '2px 8px', fontWeight: 600, color: m.macro_f1 < 0.9 ? '#e53935' : m.macro_f1 < 0.98 ? '#ff9800' : '#4caf50' }}>
                                    {(m.macro_f1 * 100).toFixed(1)}%
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '2px 8px' }}>{(m.accuracy * 100).toFixed(1)}%</td>
                                  <td style={{ textAlign: 'right', padding: '2px 8px' }}>{m.num_examples.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </details>
                      )}
                      {/* Worst / best assertions for deductions models */}
                      {v.trainingMode === 'deductions' && v.evalMetrics?.details?.worst_assertions && (
                        <details style={{ marginTop: 4, width: '100%' }}>
                          <summary style={{ cursor: 'pointer', fontSize: '0.85em', fontWeight: 600, color: 'var(--admin-text-secondary, #888)' }}>
                            Pires / meilleures assertions
                          </summary>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 6, fontSize: '0.8em' }}>
                            <div>
                              <div style={{ fontWeight: 600, marginBottom: 4, color: '#e53935' }}>Pires (F1 le plus bas)</div>
                              {(v.evalMetrics.details.worst_assertions as Array<{ id: string; f1: number; precision: number; recall: number; prevalence: number }>).slice(0, 7).map(a => (
                                <div key={a.id} style={{ padding: '1px 0', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={a.id}>{a.id}</span>
                                  <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>F1={(a.f1 * 100).toFixed(1)}%</span>
                                </div>
                              ))}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, marginBottom: 4, color: '#4caf50' }}>Meilleures (F1 le plus haut)</div>
                              {(v.evalMetrics.details.best_assertions as Array<{ id: string; f1: number; precision: number; recall: number; prevalence: number }>)?.slice(0, 7).map(a => (
                                <div key={a.id} style={{ padding: '1px 0', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={a.id}>{a.id}</span>
                                  <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>F1={(a.f1 * 100).toFixed(1)}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </details>
                      )}

                      {/* Sparkline for self-play training history */}
                      {history && history.length > 1 ? (
                        <div className="ml-sparkline-wrap">
                          <svg viewBox={`0 0 ${Math.max(history.length - 1, 1) * 10} 40`} className="ml-sparkline" preserveAspectRatio="none">
                            {(() => {
                              const dupActive = Boolean(v.evalMetrics?.details?.duplicateSummary?.objectiveActive);
                              const values = history.map(h => (
                                dupActive && h.duplicateAvgMatchpoint != null
                                  ? h.duplicateAvgMatchpoint
                                  : (h.avgScore ?? h.winRate * 100)
                              ));
                              const min = Math.min(...values);
                              const max = Math.max(...values);
                              const range = max - min || 0.01;
                              const points = values.map((r, i) => `${i * 10},${40 - ((r - min) / range) * 36}`).join(' ');
                              return <polyline points={points} fill="none" stroke="var(--ml-sparkline-color, #818cf8)" strokeWidth="2" strokeLinejoin="round" />;
                            })()}
                          </svg>
                          <span className="ml-sparkline-label">
                            {Number(v.evalMetrics?.details?.iterations) || history.length} iter.
                            {v.evalMetrics?.details?.duplicateSummary?.objectiveActive ? ' (MP)' : ''}
                          </span>
                        </div>
                      ) : null}

                      {/* Dataset manifest from imitation learning */}
                      {v.evalMetrics?.details?.datasetManifest && (() => {
                        const dm = v.evalMetrics!.details!.datasetManifest;
                        const modeLabels: Record<string, string> = {
                          per_donne: 'Top par donne', per_differe: 'Top par différé',
                          ranking_general: 'Class. glissant général', ranking_specific: 'Class. glissant spécifique',
                          ranking_live: 'Class. live',
                          competition_team: 'Championnat équipes',
                          competition_team_top_teams: 'Championnat équipes top',
                          competition_cia: 'Championnat individuel attaque',
                          competition_cia_top_divisions: 'Championnat indiv. attaque top',
                          competition_quadrette: 'Coupe Quadrette',
                          freedefi_elite: 'Défis libres élite',
                          freedefi_top_live: 'Défis libres top live',
                          differe_indiv_top_per_donne: 'Différé indiv. top par donne',
                          differe_indiv_top_general_role: 'Différé indiv. top élite (rôle)',
                        };
                        return (
                          <div className="ml-dataset-manifest">
                            <div className="ml-dataset-manifest-header">
                              <span className="ml-dataset-manifest-title">Jeu de données réel</span>
                              <span className="ml-dataset-manifest-summary">
                                {dm.totalGames.toLocaleString()} parties — {dm.totalPlayers} joueurs
                              </span>
                            </div>
                            <div className="ml-dataset-manifest-tags">
                              <span className="ml-manifest-tag">{modeLabels[dm.selectionMode] || dm.selectionMode}</span>
                              {dm.diffSubtypes && <span className="ml-manifest-tag">{dm.diffSubtypes}</span>}
                              {dm.dateFrom && <span className="ml-manifest-tag">depuis {dm.dateFrom.slice(0, 10)}</span>}
                            </div>
                            {dm.players && dm.players.length > 0 && (
                              <details className="ml-dataset-manifest-players">
                                <summary>
                                  {dm.totalPlayers} joueurs ({dm.players.slice(0, 3).map(p => p.pseudo).join(', ')}
                                  {dm.totalPlayers > 3 ? '...' : ''})
                                </summary>
                                <div className="ml-manifest-player-list">
                                  {dm.players.map((p, i) => (
                                    <div key={p.pseudo} className="ml-manifest-player-row">
                                      <span className="ml-manifest-player-rank">#{i + 1}</span>
                                      <span className="ml-manifest-player-name">{p.pseudo}</span>
                                      <span className="ml-manifest-player-games">{p.games.toLocaleString()} parties</span>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <div className="ml-version-actions">
                    {v.ovhJobId && (
                      <button
                        type="button"
                        className="ml-job-btn"
                        onClick={() => void handleViewLogs(v.ovhJobId!)}
                      >
                        Logs d&apos;entraînement
                      </button>
                    )}
                    <button
                      type="button"
                      className="admin-btn-sm"
                      onClick={() => handleExportTrainingJson(v.versionTag)}
                      disabled={!!busy || trainingExportTag !== null}
                      title="Config et métriques d’entraînement (API registre) — pour prompt Cursor"
                    >
                      {trainingExportTag === v.versionTag ? 'Export...' : 'Exporter JSON'}
                    </button>
                    {['trained', 'evaluated', 'deployed'].includes(v.status) && (
                      <button className="admin-btn-sm" onClick={() => handleImportEval(v.versionTag)} disabled={!!busy}>
                        {busy === 'eval' ? '...' : 'Evaluer'}
                      </button>
                    )}
                    {v.s3ModelPath && v.status !== 'deployed' && (
                      <>
                        <button className="admin-btn-sm admin-btn-primary" onClick={() => handleVersionDeploy(v.versionTag, 'beta')} disabled={!!busy}>
                          Deployer Beta
                        </button>
                        <button className="admin-btn-sm admin-btn-success" onClick={() => handleVersionDeploy(v.versionTag, 'production')} disabled={!!busy}>
                          Deployer Prod
                        </button>
                      </>
                    )}
                    {v.status === 'deployed' && v.deployedSlot === 'beta' && v.s3ModelPath && (
                      <button className="admin-btn-sm admin-btn-success" onClick={() => handleVersionDeploy(v.versionTag, 'production')} disabled={!!busy}>
                        Promouvoir Prod
                      </button>
                    )}
                    {v.status !== 'deployed' && (
                      <button className="ml-job-btn ml-job-btn--danger" onClick={() => handleDeleteVersion(v.versionTag)} disabled={!!busy}>
                        Supprimer
                      </button>
                    )}
                  </div>
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Deploy ───────────────────────────────────────── */}
      {section === 'deploy' && (
        <div className="ml-deploy">
          <h3>Deploiement</h3>

          <div className="ml-deploy-slot-picker">
            <span className="ml-deploy-slot-label">Cible :</span>
            <button
              className={`ml-deploy-slot-btn${deploySlot === 'production' ? ' active' : ''}`}
              onClick={() => setDeploySlot('production')}
            >
              Production
            </button>
            <button
              className={`ml-deploy-slot-btn ml-deploy-slot-btn--beta${deploySlot === 'beta' ? ' active' : ''}`}
              onClick={() => setDeploySlot('beta')}
            >
              Beta
            </button>
          </div>

          {(status?.slots || status?.deductionModelLoaded !== undefined) && (
            <div className="ml-slots-status-wrap">
              <h4 className="ml-slots-status-title">Serveur jouerieML — fichiers chargés</h4>
              <p className="ml-slots-status-sub">
                Correspondance avec le registre : le nom affiché est celui de la version déployée sur le même slot (prod / bêta) et le même rôle.
              </p>
              <div className="ml-slots-status-grid">
                {status?.slots && ML_INFO_SLOT_ORDER.map(slotKey => {
                  const info = status.slots![slotKey] as { loaded?: boolean; version?: string; feature_dim?: number } | undefined;
                  if (!info) return null;
                  const env = slotKey.includes('beta') ? 'beta' : 'production';
                  const roleShort = slotKey.startsWith('attacker') ? 'Attaque' : 'Défense';
                  const envLong = env === 'production' ? 'Production' : 'Bêta';
                  const regName = mlRuntimeSlotRegistryName(slotKey, modelVersions);
                  const techParts: string[] = [];
                  if (info.version) techParts.push(String(info.version));
                  if (info.feature_dim != null) techParts.push(`${info.feature_dim} dims`);
                  const techLine = techParts.join(' · ');
                  return (
                    <div key={slotKey} className={`ml-slot-panel ml-slot-panel--${env}`}>
                      <div className="ml-slot-panel-head">
                        <div className="ml-slot-panel-titles">
                          <span className="ml-slot-panel-role">{roleShort}</span>
                          <span className="ml-slot-panel-sep">·</span>
                          <span className="ml-slot-panel-env">{envLong}</span>
                        </div>
                        <span className={`ml-slot-dot${info.loaded ? ' loaded' : ''}`} title={info.loaded ? 'Chargé' : 'Vide'} />
                      </div>
                      <div className="ml-slot-panel-body">
                        {info.loaded ? (
                          <>
                            <div className="ml-slot-panel-primary">{regName ?? info.version ?? '—'}</div>
                            {techLine ? <div className="ml-slot-panel-tech">{techLine}</div> : null}
                          </>
                        ) : (
                          <div className="ml-slot-panel-empty">Non chargé</div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="ml-slot-panel ml-slot-panel--deductions">
                  <div className="ml-slot-panel-head">
                    <div className="ml-slot-panel-titles">
                      <span className="ml-slot-panel-role">Déductions</span>
                      <span className="ml-slot-panel-sep">·</span>
                      <span className="ml-slot-panel-env">Global</span>
                    </div>
                    <span className={`ml-slot-dot${status?.deductionModelLoaded ? ' loaded' : ''}`} />
                  </div>
                  <div className="ml-slot-panel-body">
                    {status?.deductionModelLoaded ? (
                      <>
                        <div className="ml-slot-panel-primary">
                          {(() => {
                            const ded =
                              mlDeployedVersionEntry(modelVersions, 'production', 'deductions') ??
                              mlDeployedVersionEntry(modelVersions, 'beta', 'deductions');
                            return ded ? mlRegistryDisplayName(ded) : 'Modèle présent sur le serveur';
                          })()}
                        </div>
                        <div className="ml-slot-panel-tech">Fichier deduction_model.pt</div>
                      </>
                    ) : (
                      <div className="ml-slot-panel-empty">Non chargé</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Deployable versions from registry */}
          {(() => {
            const deployable = modelVersions.filter(v =>
              ['trained', 'evaluated'].includes(v.status) && v.s3ModelPath
            );
            return deployable.length > 0 ? (
              <div className="ml-deploy-versions">
                <h4>Versions disponibles</h4>
                <div className="ml-model-list">
                  {deployable.map(v => (
                    <div key={v.id} className="ml-model-card ml-model-card--version">
                      <div className="ml-model-info">
                        <span className="ml-model-name">
                          <span className={`ml-role-icon ml-role-icon--${v.trainingMode === 'deductions' ? 'deductions' : v.modelRole}`}>
                            {v.trainingMode === 'deductions' ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            ) : v.modelRole === 'defender' ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/><line x1="5" y1="5" x2="9" y2="5"/><line x1="5" y1="5" x2="5" y2="9"/><line x1="19" y1="5" x2="15" y2="5"/><line x1="19" y1="5" x2="19" y2="9"/></svg>
                            )}
                          </span>
                          {(() => {
                            const hasClan = v.notes && /[A-ZÀ-Ü]/.test(v.notes.charAt(0)) && v.notes.includes(' d');
                            return hasClan ? v.notes : v.versionTag;
                          })()}
                        </span>
                        <span className="ml-model-meta">
                          {v.trainingMode}
                          {v.trainingMode === 'deductions' && v.evalMetrics?.details?.macro_f1_nontrivial
                            ? ` — F1: ${((v.evalMetrics.details.macro_f1_nontrivial as number) * 100).toFixed(1)}%`
                            : v.evalMetrics ? ` — Win rate: ${(v.evalMetrics.winRate * 100).toFixed(1)}%` : ''}
                          {v.trainingMode !== 'deductions' && v.evalMetrics?.improvement !== undefined
                            ? ` (${v.evalMetrics.improvement >= 0 ? '+' : ''}${(v.evalMetrics.improvement * 100).toFixed(1)}%)`
                            : ''}
                          {v.trainingMode !== 'deductions' && v.evalMetrics?.gamesPlayed ? ` — ${v.evalMetrics.gamesPlayed.toLocaleString()} parties` : ''}
                          {v.trainingMode === 'deductions' && v.evalMetrics?.details?.num_assertions
                            ? ` — ${v.evalMetrics.details.num_assertions} assertions`
                            : ''}
                        </span>
                      </div>
                      <button
                        className="admin-btn admin-btn-primary"
                        onClick={() => handleVersionDeploy(v.versionTag, deploySlot)}
                        disabled={!!busy}
                      >
                        {busy === 'deploy-version'
                          ? 'Deploiement...'
                          : `Deployer ${deploySlot === 'beta' ? 'BETA' : 'PROD'}`}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* S3 raw models — collapsible */}
          <div className="ml-deploy-s3-section">
            <button className="ml-deploy-s3-toggle" onClick={() => setShowS3Advanced(!showS3Advanced)}>
              {showS3Advanced ? 'Masquer' : 'Afficher'} les fichiers S3 bruts ({models.length})
            </button>
            {showS3Advanced && (
              models.length === 0 ? (
                <div className="admin-empty">Aucun modele dans Object Storage</div>
              ) : (
                <div className="ml-model-list">
                  {models.map(m => (
                    <div key={m.key} className="ml-model-card">
                      <div className="ml-model-info">
                        <span className="ml-model-name">{m.key}</span>
                        <span className="ml-model-meta">
                          {formatSize(m.size)} — {new Date(m.lastModified).toLocaleDateString('fr-FR')} — {m.source}
                        </span>
                      </div>
                      <button
                        className="admin-btn admin-btn-primary"
                        onClick={() => handleDeploy(m.key)}
                        disabled={!!busy}
                      >
                        {busy === 'deploy'
                          ? 'Deploiement...'
                          : `Deployer ${deploySlot === 'beta' ? 'BETA' : 'PROD'}`}
                      </button>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* ── Compare ──────────────────────────────────────── */}
      {section === 'compare' && (
        <div className="ml-compare">
          <>
              <div className="ml-compare-major-section">
              <h3 className="ml-compare-major-heading">Benchmark différés Espace Compétition</h3>
              <div className="admin-section">
                <h4 style={{ marginBottom: 10 }}>Paramétrage</h4>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    alignItems: 'center',
                    marginBottom: 4,
                  }}
                  role="group"
                  aria-label="Outils différés benchmark"
                >
                  {([
                    { tab: 'validation' as const, label: 'Validation bot1' },
                    { tab: 'poolCriteria' as const, label: 'Import critères' },
                    { tab: 'poolList' as const, label: 'Pool différés' },
                    { tab: 'modelPool' as const, label: 'Modèles pool' },
                    { tab: 'runsRankings' as const, label: 'Runs & classements' },
                  ]).map(({ tab, label }) => (
                    <button
                      key={tab}
                      type="button"
                      className="admin-btn admin-btn-sm"
                      onClick={() => {
                        setDiffereToolsModalTab(tab);
                        setDiffereToolsModalOpen(true);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="admin-section">
                <h4 style={{ marginBottom: 10 }}>Benchmark</h4>
                <div
                  className="ml-compare-synth-metric-toggle"
                  role="group"
                  aria-label="Vue du classement benchmark"
                  style={{ marginBottom: 12, flexWrap: 'wrap' }}
                >
                  {([
                    { id: 'global' as const, label: 'Global' },
                    { id: 'attackNoSc' as const, label: 'Attaque (hors s/c)' },
                    { id: 'attackSc' as const, label: 'Attaque (s/c)' },
                    { id: 'defense' as const, label: 'Défense' },
                    { id: 'perDiffere' as const, label: 'Par différé' },
                  ]).map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      className={`ml-section-btn ${differePoolBenchmarkView === id ? 'active' : ''}`}
                      onClick={() => setDifferePoolBenchmarkView(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {differePoolBenchmarkView === 'perDiffere' && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 10,
                      alignItems: 'center',
                      marginBottom: 14,
                    }}
                  >
                    <span style={{ fontSize: '0.82rem', opacity: 0.85 }}>Catégorie</span>
                    <div
                      role="group"
                      aria-label="Catégorie de différé"
                      style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}
                    >
                      {([
                        { id: 'attacker' as const, label: 'Attaque' },
                        { id: 'defender' as const, label: 'Défense' },
                        { id: 'attackSc' as const, label: 'Attaque s/c' },
                      ]).map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          className={`ml-section-btn ${differePerDiffCategory === id ? 'active' : ''}`}
                          onClick={() => setDifferePerDiffCategory(id)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.88rem' }}>
                      Différé
                      <select
                        className="admin-input"
                        style={{ minWidth: 200 }}
                        value={differePerDiffResolvedImportId ?? ''}
                        onChange={e => setDifferePerDiffSelectedImportId(
                          e.target.value ? parseInt(e.target.value, 10) : null,
                        )}
                        disabled={differePerDiffPickerLists[differePerDiffCategory].length === 0}
                      >
                        {differePerDiffPickerLists[differePerDiffCategory].map((d: { importId: number; sourceDiffereId: number }) => (
                          <option key={d.importId} value={d.importId}>
                            #{d.sourceDiffereId} (import {d.importId})
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}

                {showDiffereBenchmarkProgressBar && (
                  <div className="ml-compare-progress" style={{ marginTop: 10, marginBottom: 10 }}>
                    <div className="ml-compare-progress-bar">
                      {differeProgressForBar && differeProgressForBar.tot > 0 && differeProgressForBar.pct !== null ? (
                        <div
                          className="ml-compare-progress-fill"
                          style={{ width: `${differeProgressForBar.pct}%` }}
                        />
                      ) : (
                        <div className="ml-compare-progress-fill ml-compare-progress-fill--indeterminate" />
                      )}
                    </div>
                    <span className="ml-compare-progress-label">
                      {differeBenchmarkProgressLabel}
                    </span>
                  </div>
                )}

                {differeRuns.some((run: any) => run.runKind === 'benchmark' && run.status === 'failed') && (
                  <div className="ml-compare-error" style={{ marginBottom: 12 }}>
                    Certains benchmarks différés ont échoué (voir l’onglet « Runs et classements » du modal pour le détail).
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '8px 14px',
                    marginBottom: 12,
                  }}
                >
                  <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    onClick={() => setDiffereBenchmarkMetricsModalOpen(true)}
                    disabled={differeBenchmarkRunsWithComparisonStats.length === 0 && activeDifferePoolImportIdSet.size === 0}
                    title={
                      differeBenchmarkRunsWithComparisonStats.length === 0 && activeDifferePoolImportIdSet.size === 0
                        ? 'Aucun différé actif dans le pool.'
                        : 'Voir petit au bout, métriques qualitatives, etc. — comme une comparaison ML.'
                    }
                  >
                    Métriques détaillées (benchmark différé)…
                  </button>
                  <button
                    type="button"
                    className="admin-btn"
                    onClick={() => void handleExportDiffereBenchmarkMetrics()}
                    disabled={differeMetricsExportBusy || activeDifferePoolImportIdSet.size === 0}
                    title="Export JSON des agrégats (win rate, petit, qualitatif…) — périmètre « tous » par défaut ; sans drill-down"
                  >
                    {differeMetricsExportBusy ? 'Export…' : 'Exporter JSON (métriques)'}
                  </button>
                  <span style={{ fontSize: '0.82rem', opacity: 0.88, maxWidth: 620 }}>
                    Vue multi-modèles : par défaut agrégat sur tous les différés du pool ; option un différé. Même logique que la comparaison ML ; attaque et défense en sections séparées.
                  </span>
                </div>

                  {!differeRankings ? (
                    <div className="admin-empty">Chargement des classements…</div>
                  ) : (
                    <>
                      {differePoolBenchmarkView === 'global' && (
                        differeRankings.global?.length > 0 ? (
                          <div className="ml-compare-table-wrap">
                            <h5 style={{ marginTop: 0, marginBottom: 8 }}>Classement global (points totaux)</h5>
                            {differeRankings.rankingScopeStats ? (
                              <p style={{ fontSize: '0.82rem', opacity: 0.88, marginBottom: 10 }}>
                                {differeRankings.rankingScopeStats.global.differeCount} différé(s) dans le pool —{' '}
                                {differeRankings.rankingScopeStats.global.dealCountSum} donnes cumulées (toutes barres).
                              </p>
                            ) : null}
                            <table className="ml-compare-table ml-compare-table--compact">
                              <thead>
                                <tr>
                                  <th>Rang</th>
                                  <th>Joueur / IA</th>
                                  <th>Type</th>
                                  <th>Moyenne</th>
                                  <th>Différés joués</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectBenchmarkRankingRows(differeRankings.global).map((row: any) => (
                                  <tr key={row.key}>
                                    <td>{row.rank ?? '—'}</td>
                                    <td>{row.label}</td>
                                    <td>{mlDiffereRankingTypeLabel(row)}</td>
                                    <td>{row.averagePoints.toFixed(1)}</td>
                                    <td>
                                      {row.playedDifferes}/{row.totalDifferes}
                                      {!row.eligible ? ' (sous seuil)' : ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="admin-empty">Aucun classement global (pool vide ou pas encore de données).</div>
                        )
                      )}

                      {differePoolBenchmarkView === 'attackNoSc' && (
                        Array.isArray(differeRankings.globalAttackNoSc) && differeRankings.globalAttackNoSc.length > 0 ? (
                          <div className="ml-compare-table-wrap">
                            <h5 style={{ marginTop: 0, marginBottom: 8 }}>
                              Classement global — attaque hors sur-contrats (Petite, Garde)
                            </h5>
                            {differeRankings.rankingScopeStats ? (
                              <p style={{ fontSize: '0.82rem', opacity: 0.88, marginBottom: 10 }}>
                                Vue attaque : {differeRankings.rankingScopeStats.attackNoSc.differeCount} différé(s) attaque —{' '}
                                {differeRankings.rankingScopeStats.attackNoSc.dealCountSum} donnes cumulées sur ces différés
                                (seuil de participation et moyennes relatives à cette vue).
                              </p>
                            ) : null}
                            <table className="ml-compare-table ml-compare-table--compact">
                              <thead>
                                <tr>
                                  <th>Rang</th>
                                  <th>Joueur / IA</th>
                                  <th>Type</th>
                                  <th>Moyenne att.</th>
                                  <th>Différés joués</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectBenchmarkRankingRows(differeRankings.globalAttackNoSc).map((row: any) => (
                                  <tr key={row.key}>
                                    <td>{row.rank ?? '—'}</td>
                                    <td>{row.label}</td>
                                    <td>{mlDiffereRankingTypeLabel(row)}</td>
                                    <td>{row.averagePoints.toFixed(1)}</td>
                                    <td>
                                      {row.playedDifferes}/{row.totalDifferes}
                                      {!row.eligible ? ' (sous seuil)' : ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="admin-empty">
                            Aucun classement « attaque hors s/c » (ajoutez au moins un différé attaque au pool, ou segment vide).
                          </div>
                        )
                      )}

                      {differePoolBenchmarkView === 'attackSc' && (
                        Array.isArray(differeRankings.globalAttackSc) && differeRankings.globalAttackSc.length > 0 ? (
                          <div className="ml-compare-table-wrap">
                            <h5 style={{ marginTop: 0, marginBottom: 8 }}>
                              Classement global — attaque sur-contrats (Garde sans, Garde contre)
                            </h5>
                            {differeRankings.rankingScopeStats ? (
                              <p style={{ fontSize: '0.82rem', opacity: 0.88, marginBottom: 10 }}>
                                Vue attaque (s/c) : {differeRankings.rankingScopeStats.attackSc.differeCount} différé(s) attaque —{' '}
                                {differeRankings.rankingScopeStats.attackSc.dealCountSum} donnes cumulées sur ces différés
                                (seuil et moyennes sur les seuls différés attaque du pool — les moyennes portent sur les points « sur-contrats »).
                              </p>
                            ) : null}
                            <table className="ml-compare-table ml-compare-table--compact">
                              <thead>
                                <tr>
                                  <th>Rang</th>
                                  <th>Joueur / IA</th>
                                  <th>Type</th>
                                  <th>Moyenne att.</th>
                                  <th>Différés joués</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectBenchmarkRankingRows(differeRankings.globalAttackSc).map((row: any) => (
                                  <tr key={row.key}>
                                    <td>{row.rank ?? '—'}</td>
                                    <td>{row.label}</td>
                                    <td>{mlDiffereRankingTypeLabel(row)}</td>
                                    <td>{row.averagePoints.toFixed(1)}</td>
                                    <td>
                                      {row.playedDifferes}/{row.totalDifferes}
                                      {!row.eligible ? ' (sous seuil)' : ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="admin-empty">
                            Aucun classement « attaque s/c » (pool attaque requis, ou segment vide).
                          </div>
                        )
                      )}

                      {differePoolBenchmarkView === 'defense' && (
                        Array.isArray(differeRankings.globalDefense) && differeRankings.globalDefense.length > 0 ? (
                          <div className="ml-compare-table-wrap">
                            <h5 style={{ marginTop: 0, marginBottom: 8 }}>Classement global — défense</h5>
                            {differeRankings.rankingScopeStats ? (
                              <p style={{ fontSize: '0.82rem', opacity: 0.88, marginBottom: 10 }}>
                                Vue défense : {differeRankings.rankingScopeStats.defense.differeCount} différé(s) défense —{' '}
                                {differeRankings.rankingScopeStats.defense.dealCountSum} donnes cumulées sur ces différés
                                (seuil et moyennes relatives à cette vue).
                              </p>
                            ) : null}
                            <table className="ml-compare-table ml-compare-table--compact">
                              <thead>
                                <tr>
                                  <th>Rang</th>
                                  <th>Joueur / IA</th>
                                  <th>Type</th>
                                  <th>Moyenne déf.</th>
                                  <th>Différés joués</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectBenchmarkRankingRows(differeRankings.globalDefense).map((row: any) => (
                                  <tr key={row.key}>
                                    <td>{row.rank ?? '—'}</td>
                                    <td>{row.label}</td>
                                    <td>{mlDiffereRankingTypeLabel(row)}</td>
                                    <td>{row.averagePoints.toFixed(1)}</td>
                                    <td>
                                      {row.playedDifferes}/{row.totalDifferes}
                                      {!row.eligible ? ' (sous seuil)' : ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="admin-empty">
                            Aucun classement défense (ajoutez au moins un différé défense au pool, ou segment vide).
                          </div>
                        )
                      )}

                      {differePoolBenchmarkView === 'perDiffere' && (() => {
                        const list = differePerDiffPickerLists[differePerDiffCategory];
                        const diff = (differeRankings.differes ?? []).find(
                          (d: { importId: number }) => d.importId === differePerDiffResolvedImportId,
                        ) as any;
                        if (!list.length) {
                          return (
                            <div className="admin-empty">
                              Aucun différé dans cette catégorie pour le pool actif.
                            </div>
                          );
                        }
                        if (!diff?.participants?.length) {
                          return <div className="admin-empty">Sélectionnez un différé dans la liste.</div>;
                        }
                        const debutLabel = mlDiffereFormatDebut(diff.differeDebut);
                        const legacyAttSc = mlDiffereLegacyIsAttaqueSc(diff.legacyType);
                        const useAttScRanks = differePerDiffCategory === 'attackSc' && diff.role === 'attacker';
                        const participantsForRanking = useAttScRanks
                          ? [...diff.participants]
                            .sort(
                              (a: any, b: any) =>
                                (Number(b.attSc) || -9999) - (Number(a.attSc) || -9999),
                            )
                            .map((p: any, i: number) => ({ ...p, rank: i + 1 }))
                          : diff.participants;
                        return (
                          <div className="ml-compare-table-wrap">
                            <h5 style={{ marginTop: 0, marginBottom: 8 }}>
                              Différé #{diff.sourceDiffereId}
                              {' — '}
                              {diff.role === 'attacker' ? 'attaque' : 'défense'}
                              {debutLabel ? ` — début ${debutLabel}` : ''}
                              {legacyAttSc ? ' — différé attaque sur-contrats (type import)' : ''}
                            </h5>
                            {useAttScRanks ? (
                              <p style={{ fontSize: '0.82rem', opacity: 0.88, marginBottom: 8 }}>
                                Extrait du classement : rangs basés sur les points attaque sur-contrats (puis mêmes filtres d’affichage que les vues globales).
                              </p>
                            ) : null}
                            <table className="ml-compare-table ml-compare-table--compact">
                              <thead>
                                <tr>
                                  <th>Rang</th>
                                  <th>Participant</th>
                                  <th>Type</th>
                                  <th>Total</th>
                                  <th>
                                    {diff.role === 'attacker'
                                      ? 'Att h.SC / Att SC / Def'
                                      : 'Att / Def'}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectBenchmarkRankingRows(participantsForRanking).map((p: any) => (
                                  <tr key={p.key}>
                                    <td>{p.rank}</td>
                                    <td>{p.label}</td>
                                    <td>{mlDiffereRankingTypeLabel(p)}</td>
                                    <td>{p.tot}</td>
                                    <td>
                                      {diff.role === 'attacker'
                                        ? `${typeof p.attNoSc === 'number' ? p.attNoSc : (p.att ?? '—')} / ${typeof p.attSc === 'number' ? p.attSc : '—'} / ${p.def}`
                                        : `${p.att} / ${p.def}`}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </>
                  )}
              </div>
              </div>

              <div className="ml-compare-major-section">
              <h3 className="ml-compare-major-heading">Comparaisons entre modèles</h3>
              {runningMlComparison && (
                <div className="ml-compare-running-banner" role="status" aria-live="polite">
                  <div className="ml-compare-running-banner-inner">
                    <span className="ml-compare-running-banner-id">#{runningMlComparison.id}</span>
                    <span className="ml-job-badge ml-job-badge--running">En cours</span>
                    <span className="ml-compare-running-banner-role">
                      {runningMlComparison.config?.compareRole === 'defender' ? 'Défense' : 'Attaque'}
                    </span>
                    {Array.isArray(runningMlComparison.runningSummaries) && runningMlComparison.runningSummaries.length > 0 ? (
                      <div className="ml-compare-running-banner-summaries">
                        {runningMlComparison.runningSummaries.map(s => (
                          <span key={s.versionTag} className="ml-compare-running-banner-wr">
                            <strong>{s.label || s.versionTag}</strong>
                            {' '}
                            {(s.winRate * 100).toFixed(1)}%
                            <span className="ml-compare-running-banner-wr-n"> ({s.dealsPlayed})</span>
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <div className="ml-compare-progress ml-compare-running-banner-progress">
                      <div className="ml-compare-progress-bar">
                        <div
                          className="ml-compare-progress-fill"
                          style={{
                            width: `${runningMlComparison.totalSteps > 0
                              ? (runningMlComparison.progress / runningMlComparison.totalSteps) * 100
                              : 0}%`,
                          }}
                        />
                      </div>
                      <span className="ml-compare-progress-label">
                        {runningMlComparison.progress}/{runningMlComparison.totalSteps}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="admin-btn admin-btn-sm"
                      onClick={() => handleStopComparison(runningMlComparison.id)}
                    >
                      Arrêter
                    </button>
                  </div>
                </div>
              )}

              {/* Nouvelle comparaison — formulaire dans un modal */}
              <div className="admin-section">
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                  <h4 style={{ margin: 0 }}>Nouvelle comparaison</h4>
                  <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    onClick={() => setNewCompareModalOpen(true)}
                    disabled={!!runningMlComparison || busy === 'compare'}
                    title={
                      runningMlComparison
                        ? 'Une comparaison est déjà en cours.'
                        : busy === 'compare'
                          ? 'Lancement en cours…'
                          : undefined
                    }
                  >
                    Configurer et lancer…
                  </button>
                </div>
                {runningMlComparison ? (
                  <p className="admin-help" style={{ marginTop: 10, marginBottom: 0 }}>
                    Une comparaison est en cours — suivez la progression dans le bandeau ci-dessus.
                  </p>
                ) : null}
              </div>

              {newCompareModalOpen && (
                <div
                  className="ml-compare-detail-overlay"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="ml-new-compare-modal-title"
                  style={{ zIndex: 1010 }}
                  onClick={() => setNewCompareModalOpen(false)}
                >
                  <div
                    className="ml-compare-detail-panel"
                    style={{
                      maxWidth: 720,
                      maxHeight: 'min(90vh, 920px)',
                      overflowY: 'auto',
                      margin: '24px auto',
                      borderRadius: 10,
                      paddingBottom: 24,
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
                      <button type="button" className="admin-btn" onClick={() => setNewCompareModalOpen(false)}>
                        Fermer
                      </button>
                      <h4 id="ml-new-compare-modal-title" style={{ margin: 0, flex: 1 }}>
                        Nouvelle comparaison
                      </h4>
                    </div>

                    <div className="ml-compare-form">
                  <div className="ml-compare-row">
                    <label>Rôle comparé :</label>
                    <div className="ml-compare-toggle">
                      <button
                        className={`ml-section-btn ${compareConfig.compareRole === 'defender' ? 'active' : ''}`}
                        onClick={() => setCompareConfig(c => ({ ...c, compareRole: 'defender' }))}
                      >
                        Défense
                      </button>
                      <button
                        className={`ml-section-btn ${compareConfig.compareRole === 'attacker' ? 'active' : ''}`}
                        onClick={() => setCompareConfig(c => ({ ...c, compareRole: 'attacker' }))}
                      >
                        Attaque
                      </button>
                    </div>
                  </div>

                  <div className="ml-compare-row">
                    <label>Nombre de donnes :</label>
                    <input
                      type="number"
                      min={10}
                      max={50000}
                      step={10}
                      value={compareConfig.dealCount}
                      onChange={e => setCompareConfig(c => ({ ...c, dealCount: parseInt(e.target.value, 10) || 200 }))}
                      className="admin-input"
                      style={{ width: 100 }}
                    />
                    <span className="ml-compare-hint" style={{ marginLeft: 8 }}>
                      À ajuster avant le lancement pour limiter la durée ; une fois démarrée, la comparaison utilise « Arrêter » pour tronquer.
                    </span>
                  </div>

                  <div className="ml-compare-row">
                    <label>Score main preneur (barème Chavey) :</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="number"
                        min={0}
                        max={200}
                        value={compareConfig.minBidScore}
                        onChange={e => setCompareConfig(c => ({ ...c, minBidScore: parseInt(e.target.value, 10) || 0 }))}
                        className="admin-input"
                        style={{ width: 70 }}
                      />
                      <span>à</span>
                      <input
                        type="number"
                        min={0}
                        max={200}
                        value={compareConfig.maxBidScore}
                        onChange={e => setCompareConfig(c => ({ ...c, maxBidScore: parseInt(e.target.value, 10) || 200 }))}
                        className="admin-input"
                        style={{ width: 70 }}
                      />
                      <span className="ml-compare-hint">
                        (Passe &lt; 40, Prise 40-55, Garde 56-70, Sans 71-80, Contre &gt; 80)
                      </span>
                    </div>
                  </div>

                  <div className="ml-compare-row">
                    <label>Contrats autorisés :</label>
                    <div className="ml-compare-toggle">
                      {[
                        { value: 2, label: 'Garde' },
                        { value: 3, label: 'Garde Sans' },
                        { value: 4, label: 'Garde Contre' },
                      ].map(c => (
                        <button
                          key={c.value}
                          className={`ml-section-btn ${compareConfig.allowedContracts.includes(c.value) ? 'active' : ''}`}
                          onClick={() => {
                            setCompareConfig(cfg => {
                              const has = cfg.allowedContracts.includes(c.value);
                              const next = has
                                ? cfg.allowedContracts.filter(v => v !== c.value)
                                : [...cfg.allowedContracts, c.value];
                              return { ...cfg, allowedContracts: next.length > 0 ? next : [c.value] };
                            });
                          }}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="ml-compare-row">
                    <label>
                      <input
                        type="checkbox"
                        checked={compareConfig.crossModelAnalysis}
                        onChange={e => setCompareConfig(c => ({ ...c, crossModelAnalysis: e.target.checked }))}
                      />
                      {' '}Analyse inter-modèles (concordance cartes jouées)
                    </label>
                    {compareConfig.crossModelAnalysis && (
                      <div style={{ marginLeft: 24 }}>
                        <label>Donnes échantillon : </label>
                        <input
                          type="number"
                          min={5}
                          max={200}
                          value={compareConfig.crossModelSampleSize}
                          onChange={e => setCompareConfig(c => ({ ...c, crossModelSampleSize: parseInt(e.target.value, 10) || 50 }))}
                          className="admin-input"
                          style={{ width: 70 }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Models to compare */}
                  <div className="ml-compare-models-section">
                    <h5>Modèles à comparer ({compareConfig.compareRole === 'defender' ? 'défense' : 'attaque'})</h5>
                    {compareModels.map((m, idx) => (
                      <div key={idx} className="ml-compare-model-row">
                        <select
                          value={m.versionTag}
                          onChange={e => {
                            if (e.target.value === 'legacy-rv') {
                              setCompareModels(prev => {
                                const next = [...prev];
                                next[idx] = legacyRvCompareModel;
                                return next;
                              });
                              return;
                            }
                            const ver = modelVersions.find(v => v.versionTag === e.target.value);
                            setCompareModels(prev => {
                              const next = [...prev];
                              next[idx] = {
                                slot: ver?.versionTag || e.target.value,
                                label: ver?.notes || ver?.versionTag || '',
                                versionTag: e.target.value,
                              };
                              return next;
                            });
                          }}
                          className="admin-input"
                        >
                          <option value="">— Choisir un modèle —</option>
                          <option value="legacy-rv">Legacy RV (moteur historique)</option>
                          {modelVersions
                            .filter(v => v.modelRole === compareConfig.compareRole && v.s3ModelPath?.endsWith('.pt'))
                            .map(v => (
                              <option key={v.versionTag} value={v.versionTag} title={v.notes || v.versionTag}>
                                {mlShortModelLabel(v.notes || v.versionTag)} ({v.trainingMode}{v.deployedSlot ? ` — ${v.deployedSlot}` : ''})
                              </option>
                            ))}
                        </select>
                        {idx >= 2 && (
                          <button
                            className="admin-btn admin-btn-sm"
                            onClick={() => setCompareModels(prev => prev.filter((_, i) => i !== idx))}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="admin-btn"
                      onClick={() => setCompareModels(prev => [...prev, { slot: '', label: '', versionTag: '' }])}
                      style={{ marginTop: 4 }}
                    >
                      + Ajouter un modèle
                    </button>
                  </div>

                  {/* Opponent model */}
                  <div className="ml-compare-models-section">
                    <h5>Modèle adversaire ({compareConfig.compareRole === 'defender' ? 'attaque' : 'défense'})</h5>
                    <select
                      value={compareOpponent.versionTag}
                      onChange={e => {
                        if (e.target.value === 'legacy-rv') {
                          setCompareOpponent(legacyRvCompareModel);
                          return;
                        }
                        const ver = modelVersions.find(v => v.versionTag === e.target.value);
                        setCompareOpponent({
                          slot: ver?.versionTag || e.target.value,
                          label: ver?.notes || ver?.versionTag || '',
                          versionTag: e.target.value,
                        });
                      }}
                      className="admin-input"
                    >
                      <option value="">— Choisir le modèle adverse —</option>
                      <option value="legacy-rv">Legacy RV (moteur historique)</option>
                      {modelVersions
                        .filter(v => v.modelRole !== compareConfig.compareRole && v.s3ModelPath?.endsWith('.pt'))
                        .map(v => (
                          <option key={v.versionTag} value={v.versionTag} title={v.notes || v.versionTag}>
                            {mlShortModelLabel(v.notes || v.versionTag)} ({v.trainingMode}{v.deployedSlot ? ` — ${v.deployedSlot}` : ''})
                          </option>
                        ))}
                    </select>
                  </div>

                  <button
                    className="admin-btn admin-btn-primary"
                    onClick={handleStartComparison}
                    disabled={!!busy}
                    style={{ marginTop: 16 }}
                  >
                    {busy === 'compare' ? 'Lancement...' : 'Lancer la comparaison'}
                  </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Tableaux de synthèse (plusieurs modèles × plusieurs comparaisons) */}
              <div className="admin-section" style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <h4 style={{ margin: 0, flex: 1 }}>Tableaux de synthèse</h4>
                  <button
                    type="button"
                    className="admin-btn admin-btn-sm"
                    onClick={() =>
                      setMlSynthComposers(prev => [
                        ...prev,
                        { id: mlSynthNewComposerId(), role: 'defender', selectedVersionTags: [], metric: 'avgScore', showModelChips: true },
                      ])
                    }
                  >
                    + défense
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-sm"
                    onClick={() =>
                      setMlSynthComposers(prev => [
                        ...prev,
                        { id: mlSynthNewComposerId(), role: 'attacker', selectedVersionTags: [], metric: 'avgScore', showModelChips: true },
                      ])
                    }
                  >
                    + attaque
                  </button>
                  <label className="ml-compare-synth-toolbar-toggle">
                    <input
                      type="checkbox"
                      checked={mlSynthShowChipFullLabels}
                      onChange={e => setMlSynthShowChipFullLabels(e.target.checked)}
                    />
                    Libellés complets sur les puces
                  </label>
                </div>

                {mlSynthComposers.length === 0 ? (
                  <div className="admin-empty">Ajoutez un tableau avec « + défense » ou « + attaque ».</div>
                ) : (
                  mlSynthComposers.map(block => {
                    const roleComps = mlSynthCompletedByRole[block.role];
                    const catalog = mlSynthBuildCatalogForRole(comparisons, block.role, modelVersions);
                    const catalogKeysSorted = [...catalog.keys()].sort((a, b) =>
                      catalog.get(a)!.displayName.localeCompare(catalog.get(b)!.displayName, 'fr'),
                    );
                    const visibleKeys = mlSynthVisibleTags(
                      catalogKeysSorted,
                      catalog,
                      roleComps,
                      block.selectedVersionTags,
                    );
                    const tableCols = mlSynthTableComparisons(roleComps, block.selectedVersionTags);

                    const blockMetric = block.metric ?? 'avgScore';
                    return (
                      <div key={block.id} className="ml-compare-synth-block">
                        <div className="ml-compare-synth-block-head">
                          <strong>
                            Tableau {block.role === 'defender' ? 'défense' : 'attaque'}
                          </strong>
                          <div className="ml-compare-synth-metric-toggle" role="group" aria-label="Métrique du tableau">
                            <span className="ml-compare-synth-metric-label">Métrique :</span>
                            <button
                              type="button"
                              className={`ml-section-btn ${blockMetric === 'winRate' ? 'active' : ''}`}
                              onClick={() =>
                                setMlSynthComposers(prev =>
                                  prev.map(b => (b.id === block.id ? { ...b, metric: 'winRate' } : b)),
                                )
                              }
                            >
                              Win-rate
                            </button>
                            <button
                              type="button"
                              className={`ml-section-btn ${blockMetric === 'avgScore' ? 'active' : ''}`}
                              onClick={() =>
                                setMlSynthComposers(prev =>
                                  prev.map(b => (b.id === block.id ? { ...b, metric: 'avgScore' } : b)),
                                )
                              }
                            >
                              Score moy.
                            </button>
                          </div>
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm"
                            onClick={() =>
                              setMlSynthComposers(prev =>
                                prev.map(b => {
                                  if (b.id !== block.id) return b;
                                  const visible = b.showModelChips !== false;
                                  return { ...b, showModelChips: !visible };
                                }),
                              )
                            }
                            title="Masquer ou afficher la liste des puces (sélection des modèles)"
                          >
                            {(block.showModelChips !== false) ? 'Masquer les puces' : 'Afficher les puces'}
                          </button>
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm"
                            onClick={() => setMlSynthComposers(prev => prev.filter(b => b.id !== block.id))}
                          >
                            Effacer ce tableau
                          </button>
                        </div>

                        {catalogKeysSorted.length === 0 ? (
                          <p className="admin-help">Aucun modèle dans des comparaisons terminées pour ce rôle.</p>
                        ) : (
                          <>
                            {(block.showModelChips !== false) && (
                            <div className="ml-compare-synth-chips" aria-label="Modèles disponibles">
                              {visibleKeys.map(tag => {
                                const entry = catalog.get(tag)!;
                                const selected = block.selectedVersionTags.includes(tag);
                                return (
                                  <button
                                    key={tag}
                                    type="button"
                                    className={`ml-compare-synth-chip${selected ? ' ml-compare-synth-chip--selected' : ''}`}
                                    onClick={() =>
                                      setMlSynthComposers(prev =>
                                        prev.map(b =>
                                          b.id !== block.id
                                            ? b
                                            : {
                                                ...b,
                                                selectedVersionTags: selected
                                                  ? b.selectedVersionTags.filter(t => t !== tag)
                                                  : [...b.selectedVersionTags, tag],
                                              },
                                        ),
                                      )
                                    }
                                    title={`${tag} — ${entry.displayName}`}
                                  >
                                    {(mlSynthShowChipFullLabels ? entry.displayName : mlShortModelLabel(entry.displayName))}{' '}
                                    <span className="ml-compare-synth-chip-count">({entry.comparisonCount})</span>
                                  </button>
                                );
                              })}
                            </div>
                            )}
                            {(block.showModelChips !== false) && block.selectedVersionTags.length > 0 &&
                              visibleKeys.length < catalogKeysSorted.length && (
                                <p className="admin-help" style={{ marginTop: 8 }}>
                                  Filtre actif : seuls les modèles ayant une comparaison en commun avec la sélection sont
                                  affichés ({visibleKeys.length}/{catalogKeysSorted.length}).
                                </p>
                              )}
                          </>
                        )}

                        {block.selectedVersionTags.length >= 2 && tableCols.length === 0 && (
                          <p className="admin-help" style={{ marginTop: 12 }}>
                            Aucune comparaison terminée ne contient au moins deux des modèles sélectionnés ensemble.
                          </p>
                        )}

                        {block.selectedVersionTags.length >= 2 && tableCols.length > 0 && (() => {
                          const numCompCols = tableCols.length;
                          const showDiffereCol = numCompCols < 5;
                          const showMetricsCol = numCompCols < 3;
                          const differeRankRows = showDiffereCol ? mlSynthDiffereRankRowsForRole(differeRankings, block.role) : null;
                          const rankByCol = tableCols.map(comp =>
                            mlSynthRankByMetric(
                              (comp.modelStats ?? []).map((ms: { versionTag: string; winRate: number; avgScore: number }) => ({
                                versionTag: ms.versionTag,
                                winRate: Number(ms.winRate) || 0,
                                avgScore: Number(ms.avgScore) || 0,
                              })),
                              blockMetric,
                            ),
                          );
                          const rowTagsSorted = [...block.selectedVersionTags].sort((a, b) => {
                            const la = mlShortModelLabel(
                              catalog.get(a)?.displayName ?? mlCompareRowDisplayName(a, undefined, modelVersions),
                            ).toLocaleLowerCase('fr');
                            const lb = mlShortModelLabel(
                              catalog.get(b)?.displayName ?? mlCompareRowDisplayName(b, undefined, modelVersions),
                            ).toLocaleLowerCase('fr');
                            return la.localeCompare(lb, 'fr');
                          });
                          return (
                            <div className="ml-compare-table-wrap" style={{ marginTop: 12 }}>
                              <table className="ml-compare-table ml-compare-table--compact ml-compare-synth-matrix">
                                <thead>
                                  <tr>
                                    <th className="ml-compare-synth-matrix-model">Modèle</th>
                                    {showDiffereCol && (
                                      <th className="ml-compare-synth-matrix-extra">Différé</th>
                                    )}
                                    {showMetricsCol && (
                                      <th className="ml-compare-synth-matrix-extra">Métriques</th>
                                    )}
                                    {tableCols.map(comp => (
                                      <th key={comp.id} className="ml-compare-synth-matrix-col">
                                        <button
                                          type="button"
                                          className="ml-compare-synth-matrix-col-btn"
                                          onClick={() => {
                                            void handleViewComparison(comp.id);
                                            onSectionChange('compare');
                                          }}
                                          title="Ouvrir ce comparatif"
                                        >
                                          <div className="ml-compare-synth-matrix-col-id">#{comp.id}</div>
                                          <div className="ml-compare-synth-matrix-col-sub">
                                            {mlSynthComparisonBidScoreLabel(comp.config)}
                                          </div>
                                          <div className="ml-compare-synth-matrix-col-sub">
                                            {(comp.config?.stoppedEarly && comp.config?.effectiveDealCount != null
                                              ? comp.config.effectiveDealCount
                                              : comp.config?.dealCount) ?? '—'}{' '}
                                            donnes
                                          </div>
                                        </button>
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {rowTagsSorted.map(tag => {
                                    const meta = catalog.get(tag);
                                    const rowLabel =
                                      meta?.displayName ?? mlCompareRowDisplayName(tag, undefined, modelVersions);
                                    const rowShort = mlShortModelLabel(rowLabel);
                                    const ver = modelVersions.find(x => x.versionTag === tag);
                                    const dim = ver ? mlVersionHiddenDim(ver) : null;
                                    return (
                                      <tr key={tag}>
                                        <td className="ml-compare-model-name ml-compare-synth-model-cell">
                                          <button
                                            type="button"
                                            className="ml-compare-synth-model-open"
                                            disabled={!ver?.ovhJobId}
                                            title={
                                              ver?.ovhJobId
                                                ? 'Voir les logs d’entraînement (Graphique)'
                                                : 'Pas de job OVH lié (ovhJobId manquant)'
                                            }
                                            onClick={() => {
                                              if (!ver?.ovhJobId) {
                                                flash('err', 'Aucun job d’entraînement (ovhJobId) associé à cette version.');
                                                return;
                                              }
                                              void handleViewLogs(ver.ovhJobId);
                                              setLogViewMode('summary');
                                            }}
                                          >
                                            <strong>{rowShort}</strong>
                                          </button>
                                          {ver && (
                                            <div className="ml-compare-synth-model-meta">
                                              <span>{mlVersionDatasetLine(ver)}</span>
                                              {dim != null && (
                                                <span className="ml-compare-synth-model-cap"> · Capacité {dim}</span>
                                              )}
                                            </div>
                                          )}
                                        </td>
                                        {showDiffereCol && (
                                          <td className="ml-compare-synth-extra-cell">
                                            {mlSynthDiffereRankForTag(differeRankRows, tag, block.role)}
                                          </td>
                                        )}
                                        {showMetricsCol && (
                                          <td
                                            className="ml-compare-synth-extra-cell ml-compare-synth-metrics-cell"
                                            title={mlSynthModelMetricsSummary(ver)}
                                          >
                                            {mlSynthModelMetricsSummary(ver)}
                                          </td>
                                        )}
                                        {tableCols.map((comp, colIdx) => {
                                          const info = rankByCol[colIdx]?.get(tag);
                                          if (!info) {
                                            return (
                                              <td key={comp.id} className="ml-compare-synth-cell">
                                                —
                                              </td>
                                            );
                                          }
                                          const bubbleCls =
                                            info.rank <= 3
                                              ? `ml-synth-rank-bubble ml-synth-rank-bubble--${info.rank}`
                                              : 'ml-synth-rank-bubble ml-synth-rank-bubble--other';
                                          const primary =
                                            blockMetric === 'avgScore'
                                              ? `${info.avgScore.toFixed(1)}`
                                              : `${(info.winRate * 100).toFixed(1)}%`;
                                          const deltaRaw = info.deltaVsBest;
                                          const deltaText =
                                            blockMetric === 'avgScore'
                                              ? (Math.abs(deltaRaw) < 1e-9
                                                  ? '0'
                                                  : `${deltaRaw > 0 ? '+' : ''}${deltaRaw.toFixed(1)} pts`)
                                              : (Math.abs(deltaRaw) < 1e-9
                                                  ? '0'
                                                  : `${(deltaRaw * 100) > 0 ? '+' : ''}${(deltaRaw * 100).toFixed(1)} pts`);
                                          const deltaCls =
                                            Math.abs(deltaRaw) < 1e-9
                                              ? ''
                                              : deltaRaw > 0
                                                ? ' ml-delta-pos'
                                                : ' ml-delta-neg';
                                          const showDelta = Math.abs(deltaRaw) >= 1e-9;
                                          return (
                                            <td key={comp.id} className="ml-compare-synth-cell ml-compare-synth-cell--ranked">
                                              <span className={bubbleCls} aria-label={`Rang ${info.rank}`}>
                                                {info.rank}
                                              </span>
                                              <div className="ml-compare-synth-cell-body">
                                                <div className="ml-compare-synth-primary">{primary}</div>
                                                {showDelta && (
                                                  <div className={`ml-compare-delta ml-compare-synth-cell-delta${deltaCls}`}>
                                                    Δ {deltaText}
                                                  </div>
                                                )}
                                              </div>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Comparisons history */}
              <div className="admin-section" style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <h4 style={{ margin: 0 }}>Historique des comparaisons</h4>
                  <div
                    className="ml-compare-history-role-switch"
                    role="radiogroup"
                    aria-label="Type de comparaison"
                  >
                    {([
                      { id: 'all' as const, label: 'Toutes' },
                      { id: 'defender' as const, label: 'Défense' },
                      { id: 'attacker' as const, label: 'Attaque' },
                    ]).map(({ id, label }) => (
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={compareHistoryRoleFilter === id}
                        className={`ml-compare-history-role-switch-btn${compareHistoryRoleFilter === id ? ' ml-compare-history-role-switch-btn--active' : ''}`}
                        onClick={() => setCompareHistoryRoleFilter(id)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <input
                    type="search"
                    value={compareHistoryFilter}
                    onChange={e => setCompareHistoryFilter(e.target.value)}
                    placeholder="Filtrer par nom de modèle…"
                    className="admin-input"
                    aria-label="Filtrer les comparaisons par nom de modèle (modèles comparés ou adversaire)"
                    style={{ minWidth: 200, maxWidth: 360, flex: '1 1 200px' }}
                  />
                </div>
                {comparisons.length === 0 ? (
                  <div className="admin-empty">Aucune comparaison</div>
                ) : comparisonsFiltered.length === 0 ? (
                  <div className="admin-empty">Aucune comparaison ne correspond aux filtres.</div>
                ) : (
                  <div className="ml-compare-history-scroll">
                  <div className="ml-compare-list ml-compare-list--compact">
                    {comparisonsFiltered.map(comp => (
                      <div key={comp.id} className={`ml-compare-card ml-compare-card--${comp.status}`}>
                        <div className="ml-compare-card-header">
                          <span className="ml-compare-card-id">#{comp.id}</span>
                          <span className={`ml-job-badge ml-job-badge--${comp.status === 'completed' ? 'done' : comp.status === 'running' ? 'running' : comp.status === 'failed' ? 'failed' : 'pending'}`}>
                            {comp.status === 'completed'
                              ? (comp.config?.stoppedEarly ? 'Terminé (arrêt anticipé)' : 'Terminé')
                              : comp.status === 'running'
                                ? 'En cours'
                                : comp.status === 'failed'
                                  ? 'Échoué'
                                  : comp.status === 'cancelled'
                                    ? 'Annulé'
                                    : 'En attente'}
                          </span>
                          <span className="ml-compare-card-date">{new Date(comp.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div className="ml-compare-card-meta">
                          <span>{comp.config.compareRole === 'defender' ? 'Défense' : 'Attaque'}</span>
                          {Array.isArray(comp.config.models) && comp.config.models.length > 0 ? (
                            <span className="ml-compare-card-models" title="Modèles comparés (même rôle)">
                              {comp.config.models.map((m: { label?: string; versionTag?: string }) =>
                                mlCompareRowDisplayName(String(m.versionTag ?? ''), m.label, modelVersions),
                              ).join(' · ')}
                            </span>
                          ) : (
                            <span>—</span>
                          )}
                          <span>
                            {comp.status === 'completed' && comp.config?.stoppedEarly && comp.config?.effectiveDealCount != null
                              ? `${comp.config.effectiveDealCount} donnes (prévu ${comp.config.dealCount})`
                              : `${comp.config.dealCount} donnes`}
                          </span>
                          {comp.config.minBidScore > 0 || comp.config.maxBidScore < 200 ? (
                            <span>Score {comp.config.minBidScore}–{comp.config.maxBidScore}</span>
                          ) : null}
                        </div>
                        {comp.status === 'running' && Array.isArray(comp.runningSummaries) && comp.runningSummaries.length > 0 ? (
                          <div className="ml-compare-live-wr" style={{ fontSize: 12, opacity: 0.9, marginTop: 6, lineHeight: 1.5 }}>
                            {comp.runningSummaries.map(s => (
                              <span key={s.versionTag} style={{ marginRight: 12 }}>
                                <strong>{s.label || s.versionTag}</strong>
                                {' '}
                                {(s.winRate * 100).toFixed(1)}%
                                <span style={{ opacity: 0.75 }}> ({s.dealsPlayed} donnes)</span>
                              </span>
                            ))}
                          </div>
                        ) : null}
                        {comp.status === 'running' && (
                          <div className="ml-compare-progress">
                            <div className="ml-compare-progress-bar">
                              <div
                                className="ml-compare-progress-fill"
                                style={{ width: `${comp.totalSteps > 0 ? (comp.progress / comp.totalSteps * 100) : 0}%` }}
                              />
                            </div>
                            <span className="ml-compare-progress-label">
                              {comp.progress}/{comp.totalSteps}
                            </span>
                          </div>
                        )}
                        {comp.error && <div className="ml-compare-error">{comp.error}</div>}
                        <div className="ml-compare-card-actions">
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm"
                            onClick={() => handleExportComparisonJson(comp.id)}
                            disabled={compareExportId !== null}
                            title="Télécharge la comparaison avec toutes les donnes et résultats détaillés"
                          >
                            {compareExportId === comp.id ? 'Export...' : 'Exporter JSON'}
                          </button>
                          {comp.status === 'completed' && (
                            <button className="admin-btn admin-btn-primary" onClick={() => handleViewComparison(comp.id)}>
                              Voir les résultats
                            </button>
                          )}
                          {comp.status === 'running' && (
                            <button type="button" className="admin-btn admin-btn-sm" onClick={() => handleStopComparison(comp.id)}>
                              Arrêter
                            </button>
                          )}
                          {['completed', 'failed', 'cancelled'].includes(comp.status) && (
                            <button className="admin-btn admin-btn-sm" onClick={() => handleDeleteComparison(comp.id)}>
                              Supprimer
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  </div>
                )}
              </div>
              </div>
          </>
          {differeToolsModalOpen && (
            <div
              className="ml-compare-detail-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ml-differe-tools-modal-title"
              style={{ zIndex: 1001 }}
              onClick={() => setDiffereToolsModalOpen(false)}
            >
              <div
                className="ml-compare-detail-panel"
                style={{
                  minHeight: 'auto',
                  maxWidth: 980,
                  margin: '28px auto',
                  borderRadius: 10,
                  paddingBottom: 28,
                }}
                onClick={e => e.stopPropagation()}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                  <button type="button" className="admin-btn" onClick={() => setDiffereToolsModalOpen(false)}>
                    Fermer
                  </button>
                  <h4 id="ml-differe-tools-modal-title" style={{ margin: 0, flex: 1 }}>
                    Paramétrage (différés Espace Compétition)
                  </h4>
                </div>
                <div
                  role="tablist"
                  style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    marginBottom: 16,
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                    paddingBottom: 10,
                  }}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={differeToolsModalTab === 'validation'}
                    className={differeToolsModalTab === 'validation' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                    onClick={() => setDiffereToolsModalTab('validation')}
                  >
                    Validation bot1
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={differeToolsModalTab === 'poolCriteria'}
                    className={differeToolsModalTab === 'poolCriteria' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                    onClick={() => setDiffereToolsModalTab('poolCriteria')}
                  >
                    Import critères
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={differeToolsModalTab === 'poolList'}
                    className={differeToolsModalTab === 'poolList' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                    onClick={() => setDiffereToolsModalTab('poolList')}
                  >
                    Pool différés
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={differeToolsModalTab === 'modelPool'}
                    className={differeToolsModalTab === 'modelPool' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                    onClick={() => setDiffereToolsModalTab('modelPool')}
                  >
                    Modèles pool
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={differeToolsModalTab === 'runsRankings'}
                    className={differeToolsModalTab === 'runsRankings' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                    onClick={() => setDiffereToolsModalTab('runsRankings')}
                  >
                    Runs et classements
                  </button>
                </div>

                {differeToolsModalTab === 'validation' && (
                <>
                <p className="admin-help">
                  Premier jalon du benchmark : importer un différé individuel terminé, recalculer les points du différé et rejouer `bot1` avec Legacy RV (binaire différé uptodate).
                  Le statut « OK import » vérifie bot1 présent, totaux cohérents et infra RV — pas la parité replay score/plis (variables d&apos;env `STRICT_BOT1_REPLAY`, `STRICT_BOT1_TRICK_REPLAY`).
                </p>

                <div className="ml-compare-form">
                  <div className="ml-compare-row">
                    <label>ID différé individuel :</label>
                    <input
                      type="number"
                      min={1}
                      value={differeImportId}
                      onChange={e => setDiffereImportId(e.target.value)}
                      className="admin-input"
                      style={{ width: 120 }}
                      placeholder="ex: 1234"
                    />
                    <button
                      className="admin-btn admin-btn-primary"
                      onClick={handleImportDiffere}
                      disabled={!!busy}
                    >
                      {busy === 'differe-import' ? 'Import...' : 'Importer'}
                    </button>
                  </div>
                </div>

                <div className="ml-compare-section">
                  <h5>Différés dans le pool benchmark ({differePool.length})</h5>
                  <p className="admin-help">
                    Ces entrées ne figurent pas dans la liste « hors pool » ci-dessous ; elles sont incluses dans « Valider tous les différés » (avec les importés pas encore au pool).
                  </p>
                  {differePool.length === 0 ? (
                    <div className="admin-empty">Aucun différé dans le pool.</div>
                  ) : (
                    <p style={{ fontSize: '0.85rem', marginTop: 6 }}>
                      {(differePool as Array<{ sourceDiffereId: number }>)
                        .slice()
                        .sort((a, b) => a.sourceDiffereId - b.sourceDiffereId)
                        .map(p => `#${p.sourceDiffereId}`)
                        .join(', ')}
                    </p>
                  )}
                </div>

                <div className="ml-compare-section">
                  <h5>Différés importés — hors pool ({differeImportsNotInPool.length})</h5>
                  <p className="admin-help">
                    Vérification unitaire ou ajout au pool : les différés déjà dans le pool sont masqués ici.
                  </p>
                  {differeImportsNotInPool.length === 0 ? (
                    <div className="admin-empty">
                      {differeImports.length === 0
                        ? 'Aucun différé benchmark importé.'
                        : 'Tous les différés importés sont déjà dans le pool.'}
                    </div>
                  ) : (
                    <div className="ml-compare-list" style={{ marginTop: 8 }}>
                      {differeImportsNotInPool.map(imp => (
                        <div key={imp.id} className="ml-compare-card">
                          <div className="ml-compare-card-header">
                            <span className="ml-compare-card-id">Différé #{imp.sourceDiffereId}</span>
                            <span className="ml-job-badge ml-job-badge--done">{imp.legacyType || 'indiv'}</span>
                            <span className="ml-compare-card-date">
                              importé {new Date(imp.importedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div className="ml-compare-card-meta">
                            <span>{imp.dealCount} donnes</span>
                            <span>{imp.participantCount} participants</span>
                            {imp.metadata?.fin && <span>fin {new Date(imp.metadata.fin).toLocaleDateString('fr-FR')}</span>}
                          </div>
                          <div className="ml-compare-card-actions">
                            <button
                              className="admin-btn admin-btn-primary"
                              onClick={() => handleValidateBot1(imp.id)}
                              disabled={!!busy}
                            >
                              {busy === `bot1-${imp.id}` ? 'Validation...' : 'Valider bot1'}
                            </button>
                            <button
                              className="admin-btn"
                              onClick={() => handleAddDiffereToPool(imp.id)}
                              disabled={!!busy || differePool.some(p => p.importId === imp.id)}
                            >
                              {differePool.some(p => p.importId === imp.id)
                                ? 'Dans le pool'
                                : busy === `differe-pool-${imp.id}` ? 'Ajout...' : 'Ajouter au pool'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="ml-compare-section">
                  <h5>Validation globale</h5>
                  <p className="admin-help">
                    Lance la validation sur l’ensemble des différés importés : ceux du pool et ceux listés ci-dessus (pas encore au pool), comme une vérification préalable avant benchmark.
                  </p>
                  <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    onClick={() => void handleValidateAllBot1()}
                    disabled={!!busy || bot1ValidationQueue.length === 0}
                  >
                    {busy === 'bot1-batch' ? 'Validation en cours...' : `Valider tous les différés (${bot1ValidationQueue.length})`}
                  </button>
                  {busy === 'bot1-batch' && bot1BatchProgress && bot1BatchProgress.total > 0 && (
                    <div className="ml-compare-progress" style={{ marginTop: 12 }}>
                      <div className="ml-compare-progress-bar">
                        <div
                          className="ml-compare-progress-fill"
                          style={{
                            width: `${Math.min(100, Math.round((bot1BatchProgress.current / bot1BatchProgress.total) * 100))}%`,
                          }}
                        />
                      </div>
                      <span className="ml-compare-progress-label">
                        {Math.min(100, Math.round((bot1BatchProgress.current / bot1BatchProgress.total) * 100))}%
                        {' '}({bot1BatchProgress.current}/{bot1BatchProgress.total} différés)
                      </span>
                    </div>
                  )}
                  {bot1BatchNotOkIds.length > 0 && (
                    <div className="ml-compare-error" style={{ marginTop: 12 }}>
                      Différés non conformes (critères import : bot1 absent, totaux recalculés, infra RV) :{' '}
                      <strong>#{bot1BatchNotOkIds.join(', #')}</strong>
                    </div>
                  )}
                </div>

                {bot1Report && (
                  <div className="ml-compare-section">
                    <h5>Rapport validation bot1 — différé #{bot1Report.sourceDiffereId}</h5>
                    <div className="ml-compare-table-wrap">
                      <table className="ml-compare-table ml-compare-table--compact">
                        <thead>
                          <tr>
                            <th>Mesure</th>
                            <th>Officiel bot1</th>
                            <th>Recalcul depuis scores officiels</th>
                            <th>Replay Legacy RV</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Rang</td>
                            <td>{bot1Report.official.rank ?? '—'}</td>
                            <td>{bot1Report.recalculatedOfficial.rank ?? '—'}</td>
                            <td>{bot1Report.replay.rank ?? '—'}</td>
                          </tr>
                          <tr>
                            <td>Total</td>
                            <td>{bot1Report.official.tot ?? '—'}</td>
                            <td>{bot1Report.recalculatedOfficial.tot ?? '—'}</td>
                            <td>{bot1Report.replay.tot ?? '—'}</td>
                          </tr>
                          <tr>
                            <td>Attaque / Défense</td>
                            <td>{bot1Report.official.att ?? '—'} / {bot1Report.official.def ?? '—'}</td>
                            <td>{bot1Report.recalculatedOfficial.att ?? '—'} / {bot1Report.recalculatedOfficial.def ?? '—'}</td>
                            <td>{bot1Report.replay.att ?? '—'} / {bot1Report.replay.def ?? '—'}</td>
                          </tr>
                          <tr>
                            <td>Validation import (pool)</td>
                            <td colSpan={3}>
                              {bot1Report.replay.importValidationOk ? 'OK' : 'KO'}
                              {!bot1Report.replay.importValidationOk && ' — bot1 absent, totaux incohérents ou infra RV'}
                            </td>
                          </tr>
                          <tr>
                            <td>Scores bruts identiques</td>
                            <td colSpan={3}>
                              {bot1Report.replay.rawScoreMatches}/{bot1Report.replay.comparedDeals}
                              {bot1Report.replay.rawScoreMismatches > 0 ? ` — ${bot1Report.replay.rawScoreMismatches} divergences` : ''}
                              {bot1Report.replay.strictScoreReplayOk === false ? ' (parité score non exigée pour le pool)' : ''}
                            </td>
                          </tr>
                          <tr>
                            <td>Plis identiques (vs bot1 officiel)</td>
                            <td colSpan={3}>
                              {(bot1Report.replay.trickMatches ?? 0)}/{(bot1Report.replay.tricksCompared ?? 0)}
                              {(bot1Report.replay.trickMismatches ?? 0) > 0 ? ` — ${bot1Report.replay.trickMismatches} divergences` : ''}
                              {(bot1Report.replay.scoreMatchTrickMismatch ?? 0) > 0
                                ? ` · ${bot1Report.replay.scoreMatchTrickMismatch} score OK mais plis différents`
                                : ''}
                            </td>
                          </tr>
                          <tr>
                            <td>Contrats bot1 officiels utilisés</td>
                            <td colSpan={3}>
                              {bot1Report.replay.contractOverrides ?? 0}/{bot1Report.replay.comparedDeals}
                              {(bot1Report.replay.contractMismatches ?? 0) > 0 ? ` — ${bot1Report.replay.contractMismatches} différents de la source .do2` : ''}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {!bot1Report.recalculatedOfficial.matchingOfficialTotals && (
                      <div className="ml-compare-error">
                        Le recalcul des points officiels ne retombe pas exactement sur les totaux Java : vérifier la logique `pN/tot/att/def` avant d'interpréter le replay.
                      </div>
                    )}
                    {bot1Report.replay.mismatches?.length > 0 && (
                      <details className="ml-compare-section">
                        <summary>Voir les premières divergences de score brut</summary>
                        <div className="ml-compare-table-wrap">
                          <table className="ml-compare-table ml-compare-table--compact">
                            <thead>
                              <tr>
                                <th>Donne</th>
                                <th>ID source</th>
                                <th>Officiel</th>
                                <th>Replay</th>
                                <th>Contrats</th>
                                <th>Entames O/R</th>
                                <th>Premier écart pli/carte</th>
                                <th>Écart replay</th>
                                <th>Premiers plis officiel / replay</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bot1Report.replay.mismatches.map((m: any) => (
                                <tr key={`${m.index1}-${m.idDonne}`}>
                                  <td>{m.index1}</td>
                                  <td>{m.idDonne ?? '—'}</td>
                                  <td>{m.official ?? '—'}{m.officialPartyScore != null && m.officialPartyScore !== m.official ? ` (partie ${m.officialPartyScore})` : ''}</td>
                                  <td>{m.replay ?? '—'}</td>
                                  <td>{m.sourceContract ?? '—'} → {m.officialContract ?? '—'}</td>
                                  <td>
                                    {m.firstLeaderDiff
                                      ? `Pli ${m.firstLeaderDiff.trickIndex1}: ${m.firstLeaderDiff.official ?? '—'} / ${m.firstLeaderDiff.replay ?? '—'}`
                                      : Array.isArray(m.officialEntames) && Array.isArray(m.replayLeaders)
                                        ? `${m.officialEntames.slice(0, 6).join(',')} / ${m.replayLeaders.slice(0, 6).join(',')}`
                                        : '—'}
                                  </td>
                                  <td>
                                    {m.firstTrickDiff
                                      ? `Pli ${m.firstTrickDiff.trickIndex1}, carte ${m.firstTrickDiff.cardIndex1}: ${m.firstTrickDiff.official ?? '—'} / ${m.firstTrickDiff.replay ?? '—'}`
                                      : '—'}
                                  </td>
                                  <td>{Array.isArray(m.replayEcart) && m.replayEcart.length ? m.replayEcart.join(',') : '—'}</td>
                                  <td>
                                    <div>O: {Array.isArray(m.officialFirstTricks) && m.officialFirstTricks.length ? m.officialFirstTricks.map((t: number[]) => `[${t.join(',')}]`).join(' ') : '—'}</div>
                                    <div>R: {Array.isArray(m.replayFirstTricks) && m.replayFirstTricks.length ? m.replayFirstTricks.map((t: number[]) => `[${t.join(',')}]`).join(' ') : '—'}</div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}
                  </div>
                )}
                </>
                )}

                {differeToolsModalTab === 'poolCriteria' && (
                <div className="ml-compare-section">
                  <h5 style={{ marginTop: 0 }}>Import par critères (étapes)</h5>
                  <p className="admin-help">
                    (1) Préparer la sélection — liste des différés retenus et volumes de donnes (attaque hors sur-contrat / sur-contrat / défense).
                    (2) Confirmer — import en base, validation bot1, ajout au pool des conformes.
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: '8px 12px',
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ fontSize: '0.82rem', opacity: 0.85 }}>Du</span>
                    <input
                      type="date"
                      value={differePoolCriteria.dateFrom}
                      onChange={e => setDifferePoolCriteria(c => ({ ...c, dateFrom: e.target.value }))}
                      className="admin-input"
                      disabled={criteriaImportWizardStep === 'preview'}
                    />
                    <span style={{ fontSize: '0.82rem', opacity: 0.85 }}>au</span>
                    <input
                      type="date"
                      value={differePoolCriteria.dateTo}
                      onChange={e => setDifferePoolCriteria(c => ({ ...c, dateTo: e.target.value }))}
                      className="admin-input"
                      disabled={criteriaImportWizardStep === 'preview'}
                    />
                    <select
                      value={differePoolCriteria.legacyType}
                      onChange={e => setDifferePoolCriteria(c => ({ ...c, legacyType: e.target.value }))}
                      className="admin-input"
                      style={{ minWidth: 140 }}
                      disabled={criteriaImportWizardStep === 'preview'}
                    >
                      <option value="">Type : tous</option>
                      <option value="attaque">Attaque</option>
                      <option value="attaqueSC">Att. sur-contrats</option>
                      <option value="defense">Défense</option>
                      <option value="maxiAtt">Maxi attaque</option>
                      <option value="maxiDef">Maxi défense</option>
                    </select>
                    <span style={{ fontSize: '0.82rem', opacity: 0.85 }}>Max</span>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={differePoolCriteria.count}
                      onChange={e => setDifferePoolCriteria(c => ({ ...c, count: e.target.value }))}
                      className="admin-input"
                      style={{ width: 72 }}
                      placeholder="∞"
                      disabled={criteriaImportWizardStep === 'preview'}
                    />
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginLeft: 4 }}>
                      <input
                        type="checkbox"
                        checked={poolCriteriaBypassBot1}
                        onChange={e => setPoolCriteriaBypassBot1(e.target.checked)}
                        disabled={criteriaImportWizardStep === 'preview'}
                      />
                      <span style={{ fontSize: '0.82rem' }}>Sans validation bot1</span>
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={differePoolCriteria.excludeAlreadyInPool}
                        onChange={e => setDifferePoolCriteria(c => ({ ...c, excludeAlreadyInPool: e.target.checked }))}
                        disabled={criteriaImportWizardStep === 'preview'}
                      />
                      <span style={{ fontSize: '0.82rem' }}>Exclure déjà au pool</span>
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={differePoolCriteria.randomSample}
                        onChange={e => setDifferePoolCriteria(c => ({ ...c, randomSample: e.target.checked }))}
                        disabled={criteriaImportWizardStep === 'preview'}
                      />
                      <span style={{ fontSize: '0.82rem' }}>Échantillon aléatoire</span>
                    </label>
                  </div>

                  {criteriaImportWizardStep === 'idle' && (
                    <button
                      type="button"
                      className="admin-btn admin-btn-primary"
                      onClick={() => void handleCriteriaImportPreview()}
                      disabled={!!busy}
                    >
                      {busy === 'differe-criteria-preview' ? 'Analyse…' : '1 — Préparer la sélection'}
                    </button>
                  )}

                  {criteriaImportWizardStep === 'preview' && criteriaPreviewPayload && (
                    <>
                      <div
                        style={{
                          marginTop: 12,
                          padding: '10px 12px',
                          borderRadius: 8,
                          background: 'rgba(255,255,255,0.04)',
                          fontSize: '0.86rem',
                          lineHeight: 1.45,
                        }}
                      >
                        <strong>Synthèse donnes (aperçu)</strong>
                        {' — '}
                        Sélection :{' '}
                        {criteriaPreviewPayload.selectionMode === 'random'
                          ? 'échantillon aléatoire parmi les candidats éligibles'
                          : 'tri par # différé croissant (aperçu reproductible ; souvent les plus petits id / plus anciennes dates en premier)'}.
                        <br />
                        Candidats après filtre type/API :{' '}
                        <strong>{criteriaPreviewPayload.totalMatchingTypeFilter ?? criteriaPreviewPayload.available ?? 0}</strong>
                        {typeof criteriaPreviewPayload.excludedAlreadyInPool === 'number'
                          && criteriaPreviewPayload.excludedAlreadyInPool > 0 ? (
                          <> — dont <strong>{criteriaPreviewPayload.excludedAlreadyInPool}</strong> déjà au pool exclus</>
                        ) : null}
                        {' '}
                        → <strong>{criteriaPreviewPayload.available ?? 0}</strong> éligible(s) pour la coupe ;{' '}
                        <strong>{criteriaPreviewPayload.selectedCount ?? 0}</strong> ligne(s) dans le tableau détaillé.
                        <br />
                        Donnes cumulées : <strong>{criteriaPreviewPayload.aggregates?.totalDeals ?? 0}</strong>
                        {' — '}
                        attaque hors sur-contrat : {criteriaPreviewPayload.aggregates?.attackNoScDeals ?? 0},{' '}
                        sur-contrat : {criteriaPreviewPayload.aggregates?.attackScDeals ?? 0},{' '}
                        défense : {criteriaPreviewPayload.aggregates?.defenseDeals ?? 0}.
                        <br />
                        Par différé : attaque {criteriaPreviewPayload.aggregates?.differeAttacker ?? 0},{' '}
                        défense {criteriaPreviewPayload.aggregates?.differeDefender ?? 0},{' '}
                        mixtes {criteriaPreviewPayload.aggregates?.differeMixed ?? 0}.
                        {criteriaPreviewPayload.aggregates?.rowsFailed ? (
                          <> Échecs lecture source : {criteriaPreviewPayload.aggregates.rowsFailed}.</>
                        ) : null}
                        <p style={{ margin: '10px 0 0', opacity: 0.88 }}>
                          Pour la colonne Att. b. vs Att. SC, on part du contrat enregistré sur chaque donne dans les fichiers source — sauf pour les différés catalogués « attaque sur-contrats » (type attaqueSC) : là, toutes les donnes d’attaque sont comptées en segment sur-contrats, aligné avec le barème du classement (le catalogue impose cette plage même si le fichier garde souvent Garde par défaut).
                        </p>
                      </div>
                      <div className="ml-compare-table-wrap" style={{ marginTop: 10 }}>
                        <table className="ml-compare-table ml-compare-table--compact">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Type</th>
                              <th>Rôle</th>
                              <th>Début</th>
                              <th>Donnes</th>
                              <th title="Attaque — petite / garde">Att. b.</th>
                              <th title="Attaque — garde sans / contre">Att. SC</th>
                              <th>Déf.</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {(criteriaPreviewPayload.rows as any[]).map((row: any) => (
                              <tr key={row.sourceDiffereId}>
                                <td>{row.sourceDiffereId}</td>
                                <td>{row.legacyType ?? '—'}</td>
                                <td>{row.previewError ? '—' : row.role === 'attacker' ? 'Att.' : row.role === 'defender' ? 'Déf.' : 'Mixte'}</td>
                                <td>{formatDiffereDebutCell(row.debut)}</td>
                                <td>{row.previewError ? '—' : row.totalDeals}</td>
                                <td>{row.previewError ? '—' : row.attackNoScDeals}</td>
                                <td>{row.previewError ? '—' : row.attackScDeals}</td>
                                <td>{row.previewError ? '—' : row.defenseDeals}</td>
                                <td style={{ color: '#f87171', fontSize: '0.78rem' }}>
                                  {row.previewError ?? ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
                        <button
                          type="button"
                          className="admin-btn admin-btn-primary"
                          onClick={() => void handleCriteriaImportExecute()}
                          disabled={!!busy}
                        >
                          {busy === 'differe-criteria-execute'
                            ? poolCriteriaBypassBot1
                              ? 'Import au pool…'
                              : 'Import & validation bot1…'
                            : poolCriteriaBypassBot1
                              ? '2 — Importer au pool (sans bot1)'
                              : '2 — Importer et valider bot1, puis ajouter au pool'}
                        </button>
                        <button
                          type="button"
                          className="admin-btn"
                          onClick={() => {
                            setCriteriaImportWizardStep('idle');
                            setCriteriaPreviewPayload(null);
                          }}
                          disabled={!!busy}
                        >
                          Revenir aux critères
                        </button>
                      </div>
                      {busy === 'differe-criteria-execute' && criteriaExecuteProgress && criteriaExecuteProgress.total > 0 && (
                        <div className="ml-compare-progress" style={{ marginTop: 12 }}>
                          <div className="ml-compare-progress-bar">
                            <div
                              className="ml-compare-progress-fill"
                              style={{
                                width: `${Math.min(
                                  100,
                                  Math.round((criteriaExecuteProgress.current / criteriaExecuteProgress.total) * 100),
                                )}%`,
                              }}
                            />
                          </div>
                          <span className="ml-compare-progress-label">
                            {Math.min(
                              100,
                              Math.round((criteriaExecuteProgress.current / criteriaExecuteProgress.total) * 100),
                            )}
                            %
                            {' '}
                            ({criteriaExecuteProgress.current}/{criteriaExecuteProgress.total} différé
                            {criteriaExecuteProgress.total > 1 ? 's' : ''})
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <p className="admin-help" style={{ marginTop: 12 }}>
                    Au moins une date ou un nombre max. Sélection déterministe (tri par # différé). Sans bot1 : case « Sans validation bot1 ».
                  </p>
                </div>
                )}

                {differeToolsModalTab === 'poolList' && (
                <div className="ml-compare-section">
                  <h5 style={{ marginTop: 0 }}>Différés dans le pool benchmark</h5>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 16,
                      marginBottom: 14,
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: 'rgba(255,255,255,0.04)',
                      fontSize: '0.88rem',
                    }}
                  >
                    <span>
                      <strong>Attaque</strong> : {differePoolStatsByRole.attacker.count} différé(s),{' '}
                      {differePoolStatsByRole.attacker.deals.toLocaleString('fr-FR')} donnes
                    </span>
                    <span style={{ opacity: 0.35 }}>|</span>
                    <span>
                      <strong>Défense</strong> : {differePoolStatsByRole.defender.count} différé(s),{' '}
                      {differePoolStatsByRole.defender.deals.toLocaleString('fr-FR')} donnes
                    </span>
                    <span style={{ opacity: 0.35 }}>|</span>
                    <span>
                      <strong>Total</strong> : {differePoolStatsByRole.totalCount} différé(s),{' '}
                      {differePoolStatsByRole.totalDealsAll.toLocaleString('fr-FR')} donnes
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: '0.82rem', opacity: 0.85 }}>Vue liste</span>
                    <div
                      role="group"
                      aria-label="Filtrer les différés du pool par rôle"
                      style={{
                        display: 'inline-flex',
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.15)',
                      }}
                    >
                      <button
                        type="button"
                        className={differePoolModalRoleFilter === 'all' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                        style={{ borderRadius: 0, border: 'none', padding: '6px 12px' }}
                        onClick={() => setDifferePoolModalRoleFilter('all')}
                      >
                        Tous
                      </button>
                      <button
                        type="button"
                        className={differePoolModalRoleFilter === 'attacker' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                        style={{ borderRadius: 0, border: 'none', padding: '6px 12px' }}
                        onClick={() => setDifferePoolModalRoleFilter('attacker')}
                      >
                        Attaque
                      </button>
                      <button
                        type="button"
                        className={differePoolModalRoleFilter === 'defender' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                        style={{ borderRadius: 0, border: 'none', padding: '6px 12px' }}
                        onClick={() => setDifferePoolModalRoleFilter('defender')}
                      >
                        Défense
                      </button>
                    </div>
                    <span className="admin-help" style={{ margin: 0 }}>
                      ({differePoolModalFiltered.length} ligne(s))
                    </span>
                    <button
                      type="button"
                      className="admin-btn admin-btn-sm"
                      onClick={() => void handleReimportDifferePool()}
                      disabled={!!busy || differePool.length === 0}
                      title="Réimporte les snapshots des différés actifs du pool (dont plis Top humains), sans relancer les modèles benchmark."
                    >
                      {busy === 'differe-pool-reimport' ? 'Ré-import…' : 'Ré-importer le pool'}
                    </button>
                  </div>
                  {differePoolReimportProgress && differePoolReimportProgress.total > 0 ? (
                    <div className="ml-compare-progress" style={{ marginBottom: 12 }}>
                      <div className="ml-compare-progress-bar">
                        <div
                          className="ml-compare-progress-fill"
                          style={{
                            width: `${Math.min(
                              100,
                              Math.round((differePoolReimportProgress.current / differePoolReimportProgress.total) * 100),
                            )}%`,
                          }}
                        />
                      </div>
                      <span className="ml-compare-progress-label">
                        {differePoolReimportProgress.current}/{differePoolReimportProgress.total} différé
                        {differePoolReimportProgress.total > 1 ? 's' : ''}
                        {differePoolReimportProgress.failedIds.length > 0
                          ? ` — ${differePoolReimportProgress.failedIds.length} échec(s)`
                          : ''}
                      </span>
                    </div>
                  ) : null}
                  {differePool.length === 0 ? (
                    <div className="admin-empty">Aucun différé dans le pool.</div>
                  ) : differePoolModalFiltered.length === 0 ? (
                    <div className="admin-empty">Aucun différé pour ce filtre.</div>
                  ) : (
                    <div className="ml-compare-table-wrap">
                      <table className="ml-compare-table ml-compare-table--compact">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Rôle</th>
                            <th>Type</th>
                            <th>Donnes</th>
                            <th>Part.</th>
                            <th>Début différé</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {differePoolModalFiltered.map((entry: any) => (
                            <tr key={entry.id}>
                              <td className="ml-compare-card-id">{entry.sourceDiffereId}</td>
                              <td>{entry.role === 'attacker' ? 'Att.' : 'Déf.'}</td>
                              <td>{entry.legacyType || '—'}</td>
                              <td>{entry.dealCount}</td>
                              <td>{entry.participantCount}</td>
                              <td style={{ whiteSpace: 'nowrap', fontSize: '0.85em', opacity: 0.85 }}>
                                {formatDiffereDebutCell(entry.differeDebut)}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="admin-btn admin-btn-sm"
                                  onClick={() => handleRemoveDiffereFromPool(entry.id)}
                                  disabled={!!busy}
                                >
                                  Retirer
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                )}

                {differeToolsModalTab === 'modelPool' && (
                <div className="ml-compare-section">
                  <h5 style={{ marginTop: 0 }}>Modèles actifs du pool benchmark</h5>
                  <p className="admin-help">
                    Le segment <strong>Vue</strong> définit aussi le rôle du modèle à ajouter (aligné sur attaque ou défense).
                    Archiver un modèle l’exclut des sync futures tout en conservant ses résultats au classement.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: '0.82rem', opacity: 0.85 }}>Vue</span>
                    <div
                      role="group"
                      aria-label="Rôle du pool et ajout de modèle"
                      style={{
                        display: 'inline-flex',
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.15)',
                      }}
                    >
                      <button
                        type="button"
                        className={differeModalModelPoolViewRole === 'attacker' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                        style={{ borderRadius: 0, border: 'none', padding: '6px 14px' }}
                        onClick={() => {
                          setDiffereModalModelPoolViewRole('attacker');
                          setDiffereModelRole('attacker');
                        }}
                      >
                        Attaque
                      </button>
                      <button
                        type="button"
                        className={differeModalModelPoolViewRole === 'defender' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                        style={{ borderRadius: 0, border: 'none', padding: '6px 14px' }}
                        onClick={() => {
                          setDiffereModalModelPoolViewRole('defender');
                          setDiffereModelRole('defender');
                        }}
                      >
                        Défense
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <span style={{ fontSize: '0.82rem', opacity: 0.85 }}>Ajouter</span>
                    <select
                      value={differeModelVersionTag}
                      onChange={e => setDiffereModelVersionTag(e.target.value)}
                      className="admin-input"
                      style={{ minWidth: 200, flex: '1 1 200px' }}
                    >
                      <option value="">— Modèle (registre) —</option>
                      <option value="legacy-rv">Legacy RV</option>
                      {differeModalModelPoolViewRole === 'attacker' && (
                        <option value="pimc">PIMC en ligne (recherche)</option>
                      )}
                      {differeModalModelPoolViewRole === 'attacker' && (
                        <option value="hybrid">Hybrid (Loir entame + consensus réponses → IARV)</option>
                      )}
                      {modelVersions
                        .filter(v => v.modelRole === differeModalModelPoolViewRole && v.s3ModelPath?.endsWith('.pt'))
                        .map(v => (
                          <option key={v.versionTag} value={v.versionTag}>
                            {v.notes || v.versionTag} ({v.trainingMode}{v.deployedSlot ? ` — ${v.deployedSlot}` : ''})
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="admin-btn admin-btn-primary"
                      onClick={handleAddDiffereModel}
                      disabled={!!busy}
                    >
                      {busy === 'differe-model-pool' ? 'Ajout...' : 'Ajouter'}
                    </button>
                  </div>
                  {differeModelVersionTag === 'hybrid' && differeModalModelPoolViewRole === 'attacker' && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                      gap: 8,
                      marginBottom: 14,
                      padding: 10,
                      background: 'rgba(0,0,0,0.04)',
                    }}>
                      <label style={{ fontSize: '0.78rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        Entame (Loir)
                        <select
                          className="admin-input"
                          value={differeHybridLeadTag}
                          onChange={e => setDiffereHybridLeadTag(e.target.value)}
                        >
                          <option value="">— version .pt —</option>
                          {modelVersions
                            .filter(v => v.modelRole === 'attacker' && v.s3ModelPath?.endsWith('.pt'))
                            .map(v => (
                              <option key={`lead-${v.versionTag}`} value={v.versionTag}>
                                {v.notes || v.versionTag}
                              </option>
                            ))}
                        </select>
                      </label>
                      <label style={{ fontSize: '0.78rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        Réponses (consensus)
                        <select
                          className="admin-input"
                          value={differeHybridFollowTag}
                          onChange={e => setDiffereHybridFollowTag(e.target.value)}
                        >
                          <option value="">— version .pt —</option>
                          {modelVersions
                            .filter(v => v.modelRole === 'attacker' && v.s3ModelPath?.endsWith('.pt'))
                            .map(v => (
                              <option key={`follow-${v.versionTag}`} value={v.versionTag}>
                                {v.notes || v.versionTag}
                              </option>
                            ))}
                        </select>
                      </label>
                      <label style={{ fontSize: '0.78rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        Prof. openings
                        <input
                          type="number"
                          className="admin-input"
                          min={1}
                          max={18}
                          value={differeHybridDepth}
                          onChange={e => setDiffereHybridDepth(Math.max(1, Math.min(18, Number(e.target.value) || 7)))}
                        />
                      </label>
                      <label style={{ fontSize: '0.78rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        Id pool (hybrid:…)
                        <input
                          className="admin-input"
                          value={differeHybridId}
                          onChange={e => setDiffereHybridId(e.target.value)}
                          placeholder="g-v1"
                        />
                      </label>
                      <div style={{ fontSize: '0.75rem', opacity: 0.75, gridColumn: '1 / -1' }}>
                        Après les N premiers plis → IARV legacy. Réutilisable : d’autres mixes via API
                        {' '}(<code>hybridConfig.lead/follow/late</code>).
                      </div>
                    </div>
                  )}
                  {(differeModelPool as Array<{ active?: boolean }>).filter(m => m.active !== false).length === 0 ? (
                    <div className="admin-empty">Aucun modèle actif dans le pool.</div>
                  ) : differeModelPoolActiveForView.length === 0 ? (
                    <div className="admin-empty">
                      Aucun modèle actif pour le rôle « {differeModalModelPoolViewRole === 'attacker' ? 'attaque' : 'défense'} ».
                    </div>
                  ) : (
                    <div className="ml-compare-table-wrap">
                      <table className="ml-compare-table ml-compare-table--compact">
                        <thead>
                          <tr>
                            <th>Modèle</th>
                            <th>Rôle</th>
                            <th>Slot</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {differeModelPoolActiveForView.map(model => (
                            <tr key={model.id}>
                              <td>
                                {model.label}
                                {(model as { hybridConfig?: { openingsDepth?: number } }).hybridConfig && (
                                  <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>
                                    hybrid depth={(model as { hybridConfig: { openingsDepth: number } }).hybridConfig.openingsDepth}
                                  </div>
                                )}
                              </td>
                              <td>{model.role === 'attacker' ? 'Attaque' : 'Défense'}</td>
                              <td>{model.slot}</td>
                              <td>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-sm"
                                    onClick={() => handleRerunDiffereModel(model.id, model.label || model.versionTag)}
                                    disabled={!!busy}
                                    title="Supprime les anciens runs benchmark de ce modèle, puis les rejoue sur le pool actif compatible."
                                  >
                                    {busy === `differe-model-rerun-${model.id}` ? 'Rejeu…' : 'Rejouer'}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-sm"
                                    onClick={() => handleArchiveDiffereModel(model.id, model.label || model.versionTag)}
                                    disabled={!!busy}
                                    title="Retire le modèle du pool actif (sync) sans effacer l’historique benchmark."
                                  >
                                    {busy === `differe-model-archive-${model.id}` ? 'Archivage…' : 'Archiver'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {differeModelPoolArchivedForView.length > 0 && (
                    <>
                      <h5 style={{ marginTop: 20, marginBottom: 8 }}>Modèles archivés (historique)</h5>
                      <p className="admin-help" style={{ marginTop: 0 }}>
                        Non inclus dans les sync ; leurs runs terminés restent visibles au classement (participation partielle possible).
                      </p>
                      <div className="ml-compare-table-wrap">
                        <table className="ml-compare-table ml-compare-table--compact">
                          <thead>
                            <tr>
                              <th>Modèle</th>
                              <th>Rôle</th>
                              <th>Slot</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {differeModelPoolArchivedForView.map(model => (
                              <tr key={model.id} style={{ opacity: 0.82 }}>
                                <td>{model.label}</td>
                                <td>{model.role === 'attacker' ? 'Attaque' : 'Défense'}</td>
                                <td>{model.slot}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-sm"
                                    onClick={() => handleReactivateDiffereModel(model.id, model.label || model.versionTag)}
                                    disabled={!!busy}
                                  >
                                    {busy === `differe-model-reactivate-${model.id}` ? 'Réactivation…' : 'Réactiver'}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
                )}

                {differeToolsModalTab === 'runsRankings' && (
                <div className="ml-compare-section">
                  <h5 style={{ marginTop: 0 }}>Classements (paramètres) et runs benchmark</h5>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '8px 12px',
                      marginBottom: 10,
                      fontSize: '0.82rem',
                    }}
                  >
                    <span style={{ opacity: 0.88 }}>Rôle export</span>
                    <div
                      role="group"
                      aria-label="Rôle attaque ou défense pour l’export JSON des classements"
                      style={{
                        display: 'inline-flex',
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.15)',
                      }}
                    >
                      <button
                        type="button"
                        className={differeRankingRole === 'attacker' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                        style={{ borderRadius: 0, border: 'none', padding: '5px 12px' }}
                        onClick={() => {
                          setDiffereRankingRole('attacker');
                          void refreshDiffereBenchmark();
                        }}
                      >
                        Attaque
                      </button>
                      <button
                        type="button"
                        className={differeRankingRole === 'defender' ? 'admin-btn admin-btn-primary' : 'admin-btn'}
                        style={{ borderRadius: 0, border: 'none', padding: '5px 12px' }}
                        onClick={() => {
                          setDiffereRankingRole('defender');
                          void refreshDiffereBenchmark();
                        }}
                      >
                        Défense
                      </button>
                    </div>
                    <span style={{ opacity: 0.35, userSelect: 'none' }} aria-hidden>|</span>
                    <label htmlFor="ml-differe-min-participation" style={{ margin: 0, opacity: 0.88 }}>
                      Seuil humains
                    </label>
                    <input
                      id="ml-differe-min-participation"
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      value={differeMinParticipation}
                      onChange={e => {
                        const v = parseFloat(e.target.value);
                        if (Number.isFinite(v)) {
                          setDiffereMinParticipation(Math.min(1, Math.max(0, v)));
                        }
                      }}
                      onBlur={() => void refreshDiffereBenchmark()}
                      className="admin-input"
                      style={{ width: 72 }}
                      title="Part minimale de donnes jouées par les humains pour entrer dans les moyennes globales"
                    />
                    <label htmlFor="ml-differe-fallback-policy" style={{ margin: 0, opacity: 0.88 }}>
                      Fallback runs
                    </label>
                    <select
                      id="ml-differe-fallback-policy"
                      value={differeFallbackPolicy}
                      onChange={e => setDiffereFallbackPolicy(e.target.value as 'none' | 'non_card')}
                      className="admin-input"
                      style={{ minWidth: 200 }}
                      title="Politique Legacy RV lors du lancement des runs benchmark"
                    >
                      <option value="non_card">Autorisé sauf cartes jouées</option>
                      <option value="none">Aucun fallback</option>
                    </select>
                  </div>
                  <p style={{ fontSize: '0.82rem', opacity: 0.88, margin: '0 0 8px', lineHeight: 1.45 }}>
                    {differeWorkerStatus?.ovhDelegationEnabled ? (
                      <>Worker benchmark OVH : <strong style={{ color: '#2e7d32' }}>joignable</strong>
                        {typeof differeWorkerStatus.health?.latencyMs === 'number'
                          ? ` (${differeWorkerStatus.health.latencyMs} ms)`
                          : ''}
                        {' '}— priorité OVH, repli prod sur demande.</>
                    ) : differeWorkerStatus?.ovhConfigured ? (
                      <>Worker benchmark OVH : <strong style={{ color: '#c62828' }}>injoignable</strong>
                        {differeWorkerStatus.health?.error ? ` — ${differeWorkerStatus.health.error}` : ''}
                        {' '}— repli prod proposé au lancement.</>
                    ) : (
                      <>Worker benchmark OVH : <strong>non activé</strong> — exécution sur le serveur Neo prod.</>
                    )}
                  </p>
                  <p style={{ fontSize: '0.82rem', opacity: 0.88, margin: '0 0 12px', lineHeight: 1.45 }}>
                    {differeSyncPreview == null ? (
                      <>Estimation des runs manquants indisponible.</>
                    ) : differeSyncPreview.poolActiveDiffereCount === 0 ? (
                      <>
                        <strong>Aucun différé actif dans le pool benchmark</strong> — l’estimation ci-dessus ne peut pas planifier de runs.
                        {(differeSyncPreview.poolTotalDiffereCount ?? 0) > 0 ? (
                          <>
                            {' '}
                            Vous avez <strong>{differeSyncPreview.poolInactiveDiffereCount ?? 0}</strong> entrée(s) de pool inactive(s) : réactivez-les dans « Pool différés ».
                          </>
                        ) : (
                          <>
                            {' '}
                            Pool vide :
                            {(differeSyncPreview.importedDiffereCount ?? 0) > 0 ? (
                              <>
                                {' '}
                                <strong>{differeSyncPreview.importedDiffereCount}</strong> différé(s) importé(s) en base — ajoutez-les au pool pour lancer les benchmarks (Legacy RV + ML).
                              </>
                            ) : (
                              <> Importez des différés puis ajoutez-les au pool.</>
                            )}
                          </>
                        )}
                        <br />
                        <span style={{ opacity: 0.9 }}>
                          La validation bot1 (rejouer Legacy RV pour contrôle) est une étape à part ; elle ne remplace pas les runs benchmark du bouton « Lancer les runs manquants ».
                        </span>
                      </>
                    ) : differeSyncPreview.missingRuns === 0 ? (
                      <>
                        Prochain sync : <strong>aucun run manquant</strong> pour les{' '}
                        <strong>{differeSyncPreview.poolActiveDiffereCount ?? 0}</strong> différé(s) actif(s) du pool
                        {' '}(Legacy RV +{' '}
                        <strong>{differeSyncPreview.activeMlModelsCount ?? 0}</strong> modèle(s) ML actif(s), hors Legacy RV).
                      </>
                    ) : (
                      <>
                        Runs manquants (prochain « Lancer les runs manquants ») :{' '}
                        <strong>{differeSyncPreview.missingRuns}</strong> run(s),{' '}
                        <strong>{differeSyncPreview.plannedDeals}</strong> donne(s) au total,{' '}
                        sur <strong>{differeSyncPreview.differesWithMissingRuns}</strong> différé(s) du pool avec au moins un run à faire
                        {' '}(chaque différé inclut un benchmark <strong>Legacy RV</strong> si absent, puis les modèles ML actifs du même rôle).
                      </>
                    )}
                  </p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
                    <button
                      type="button"
                      className="admin-btn admin-btn-primary"
                      onClick={() => void handleSyncDifferePool()}
                      disabled={!!busy}
                    >
                      {busy === 'differe-sync' ? 'Sync…' : 'Lancer les runs manquants'}
                    </button>
                    <button
                      type="button"
                      className="admin-btn"
                      onClick={() => void handleClearDiffereBenchmarkResults()}
                      disabled={!!busy}
                    >
                      {busy === 'differe-clear-benchmark-results' ? 'Purge…' : 'Effacer les résultats benchmark'}
                    </button>
                    <button
                      type="button"
                      className="admin-btn"
                      onClick={handleExportDiffereBenchmarkSummary}
                      disabled={!!busy}
                    >
                      Exporter JSON (résumé)
                    </button>
                    <button
                      type="button"
                      className="admin-btn"
                      onClick={() => void handleExportDiffereBenchmarkMetrics()}
                      disabled={differeMetricsExportBusy || !!busy}
                      title="Agrégats win rate / petit / qualitatif (tous segments), sans drill-down"
                    >
                      {differeMetricsExportBusy ? 'Export…' : 'Exporter JSON (métriques)'}
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn-sm"
                      onClick={() => void handleHideDiffereRunsFromAdminList()}
                      disabled={!!busy}
                    >
                      {busy === 'differe-hide-run-list' ? '…' : 'Masquer l’historique (liste)'}
                    </button>
                  </div>

                  {showDiffereBenchmarkProgressBar && (
                    <div className="ml-compare-progress" style={{ marginTop: 0, marginBottom: 12 }}>
                      <div className="ml-compare-progress-bar">
                        {differeProgressForBar && differeProgressForBar.tot > 0 && differeProgressForBar.pct !== null ? (
                          <div
                            className="ml-compare-progress-fill"
                            style={{ width: `${differeProgressForBar.pct}%` }}
                          />
                        ) : (
                          <div className="ml-compare-progress-fill ml-compare-progress-fill--indeterminate" />
                        )}
                      </div>
                      <span className="ml-compare-progress-label">
                        {differeBenchmarkProgressLabel}
                      </span>
                    </div>
                  )}

                  {differeRuns.some((run: any) => run.runKind === 'benchmark' && run.status === 'failed') && (
                    <div className="ml-compare-error" style={{ marginBottom: 12 }}>
                      Un ou plusieurs benchmarks sont en échec — voir le détail dans le tableau ci-dessous.
                    </div>
                  )}

                  <p style={{ fontSize: '0.82rem', opacity: 0.85, marginBottom: 8 }}>
                    Runs benchmark stockés en base (hors « en cours ») : <strong>{differeBenchmarkRunsStored}</strong>
                  </p>

                  {differeRuns.length === 0 ? (
                    <div className="admin-empty">Aucun run dans la liste admin (limite 30).</div>
                  ) : (
                    <div className="ml-compare-table-wrap">
                      <table className="ml-compare-table ml-compare-table--compact">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Type</th>
                            <th>État</th>
                            <th>Modèle</th>
                            <th>Différé</th>
                            <th>Progression</th>
                            <th>Démarré</th>
                            <th>Erreur</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(differeRuns as Array<{
                            id: number;
                            runKind?: string;
                            status?: string;
                            modelLabel?: string;
                            sourceDiffereId?: number;
                            progress?: number;
                            totalDeals?: number;
                            startedAt?: string;
                            error?: string | null;
                          }>).map(run => (
                            <tr key={run.id}>
                              <td>{run.id}</td>
                              <td>{run.runKind === 'benchmark' ? 'Benchmark' : 'Validation'}</td>
                              <td>{run.status}</td>
                              <td>{run.modelLabel ?? '—'}</td>
                              <td>#{run.sourceDiffereId ?? '—'}</td>
                              <td>
                                {run.totalDeals ? `${run.progress ?? 0}/${run.totalDeals}` : '—'}
                              </td>
                              <td style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                                {run.startedAt ? new Date(run.startedAt).toLocaleString('fr-FR') : '—'}
                              </td>
                              <td style={{ maxWidth: 220, fontSize: '0.78rem', wordBreak: 'break-word' }}>
                                {run.error ?? '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
          )}
          {differeBenchmarkMetricsModalOpen && (
            <div
              className="ml-compare-detail-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ml-differe-benchmark-metrics-title"
              style={{ zIndex: 1002 }}
              onClick={() => setDiffereBenchmarkMetricsModalOpen(false)}
            >
              <div
                className="ml-compare-detail-panel"
                style={{
                  minHeight: 'auto',
                  maxWidth: 980,
                  margin: '28px auto',
                  borderRadius: 10,
                  paddingBottom: 28,
                  maxHeight: '90vh',
                  overflow: 'auto',
                }}
                onClick={e => e.stopPropagation()}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                  <button type="button" className="admin-btn" onClick={() => setDiffereBenchmarkMetricsModalOpen(false)}>
                    Fermer
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-sm"
                    onClick={() => void handleExportDiffereBenchmarkMetrics()}
                    disabled={
                      differeMetricsExportBusy
                      || differeBenchmarkMetricsRunsLoading
                      || differeBenchmarkRunsWithComparisonStats.length === 0
                    }
                    title="Exporte tous les segments pour le périmètre sélectionné (sans listes de donnes)"
                  >
                    {differeMetricsExportBusy ? 'Export…' : 'Exporter JSON'}
                  </button>
                  <h4 id="ml-differe-benchmark-metrics-title" style={{ margin: 0, flex: 1 }}>
                    Métriques benchmark différé (comme comparaison ML)
                  </h4>
                </div>
                {differeBenchmarkMetricsRunsLoading ? (
                  <div className="admin-help">Chargement de tous les runs benchmark terminés (hors limite de la liste admin)…</div>
                ) : differeBenchmarkRunsWithComparisonStats.length === 0 ? (
                  <div className="admin-empty">Aucun run benchmark terminé avec agrégats détaillés.</div>
                ) : (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 8,
                        marginBottom: 16,
                      }}
                    >
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 10,
                          flexWrap: 'nowrap',
                          maxWidth: '100%',
                          overflowX: 'auto',
                        }}
                      >
                        <label
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            flex: '0 0 auto',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span style={{ fontSize: '0.86rem', opacity: 0.9 }}>Périmètre</span>
                          <select
                            className="admin-input"
                            style={{ width: 175, padding: '5px 8px', fontSize: '0.82rem' }}
                            value={differeBenchmarkMetricsScope}
                            onChange={e => setDiffereBenchmarkMetricsScope(e.target.value === 'one' ? 'one' : 'all')}
                          >
                            <option value="all">Tous (agrégé)</option>
                            <option value="one" disabled={differeBenchmarkMetricsImportOptions.length === 0}>
                              Un différé
                            </option>
                          </select>
                        </label>
                        {differeBenchmarkMetricsScope === 'one' && (
                          <label
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              flex: '0 0 auto',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <span style={{ fontSize: '0.86rem', opacity: 0.9 }}>Import</span>
                            <select
                              className="admin-input"
                              style={{ width: 210, padding: '5px 8px', fontSize: '0.82rem' }}
                              value={differeBenchmarkMetricsImportId ?? ''}
                              onChange={e =>
                                setDiffereBenchmarkMetricsImportId(
                                  e.target.value ? parseInt(e.target.value, 10) : null,
                                )
                              }
                            >
                              {differeBenchmarkMetricsImportOptions.map(o => (
                                <option key={o.importId} value={o.importId}>
                                  #{o.sourceDiffereId} (import {o.importId})
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                      </div>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 14,
                          flexWrap: 'wrap',
                        }}
                      >
                        <label
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span style={{ fontSize: '0.86rem', opacity: 0.9 }}>Scope</span>
                          <select
                            className="admin-input"
                            style={{ width: 170, padding: '5px 8px', fontSize: '0.82rem' }}
                            value={differeBenchmarkMetricsView}
                            onChange={e =>
                              setDiffereBenchmarkMetricsView(e.target.value as DiffereBenchmarkMetricsView)}
                          >
                            {DIFFERE_BENCHMARK_METRIC_VIEW_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            whiteSpace: 'nowrap',
                            fontSize: '0.84rem',
                            opacity: differeBenchmarkMetricsSelectedView.aiCount <= DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT
                              ? 0.55
                              : 0.92,
                            cursor:
                              differeBenchmarkMetricsSelectedView.aiCount <= DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT
                                ? 'default'
                                : 'pointer',
                          }}
                          title={
                            differeBenchmarkMetricsSelectedView.aiCount <= DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT
                              ? `Moins de ${DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT} IA dans ce périmètre — tout est déjà affiché.`
                              : 'Par défaut : top 5 IA + Bot1 officiel + Top 10 (moy) par donne / par différé. Cochez pour toutes les IA et les autres baselines.'
                          }
                        >
                          <input
                            type="checkbox"
                            checked={
                              differeBenchmarkMetricsShowAllModels
                              || differeBenchmarkMetricsSelectedView.aiCount <= DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT
                            }
                            disabled={
                              differeBenchmarkMetricsSelectedView.aiCount <= DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT
                            }
                            onChange={e => setDiffereBenchmarkMetricsShowAllModels(e.target.checked)}
                          />
                          <span>Tous les modèles</span>
                        </label>
                      </div>
                    </div>

                    <div
                      style={{
                        fontSize: '0.84rem',
                        opacity: 0.9,
                        marginBottom: 16,
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}
                    >
                      {differeBenchmarkMetricsScope === 'all' ? (
                        <div>
                          <strong>Périmètre</strong> : tous les runs benchmark terminés avec{' '}
                          <code>comparisonStats</code> ({differeBenchmarkMetricsAggregated.runsConsidered} run(s) pris en
                          compte).
                        </div>
                      ) : (
                        <div>
                          <strong>Périmètre</strong> : uniquement l’import sélectionné (
                          {differeBenchmarkMetricsAggregated.runsConsidered} run(s)).
                        </div>
                      )}
                      <div style={{ marginTop: 6, fontSize: '0.8rem', opacity: 0.85 }}>
                        Les taux qualitatifs reposent sur la somme des compteurs / dénominateurs issus de chaque
                        run fusionné (approximation cohérente lorsque les taux sont recalculés à partir des stats exportées).
                      </div>
                      <div style={{ marginTop: 6, fontSize: '0.8rem', opacity: 0.85 }}>
                        <strong>Scope</strong> : {differeBenchmarkMetricsSelectedView.label} —{' '}
                        {differeBenchmarkMetricsSelectedView.description}
                      </div>
                      <div style={{ marginTop: 6, fontSize: '0.8rem', opacity: 0.85 }}>
                        Les modèles archivés du pool sont exclus.
                        {differeBenchmarkMetricsSelectedView.limitedToTopAi
                          ? ` Affichage : ${DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT} IA les mieux classées`
                            + (differeBenchmarkMetricsSelectedView.rankingBasedLimit
                              ? ' (classement différé du scope)'
                              : ' (fallback win rate — classement indisponible)')
                            + ' + Bot1 officiel + Top 10 par donne (moy) + Top 10 par différé (moy).'
                            + ' Cochez « Tous les modèles » pour le reste.'
                          : ' Toutes les IA et baselines sont affichées (« Tous les modèles », ou ≤5 IA dans le périmètre).'}
                      </div>
                    </div>

                    {differeBenchmarkMetricsAggregated.runsConsidered === 0 ? (
                      <div className="admin-empty">Aucun run dans ce périmètre (choisissez un autre différé ou élargissez le périmètre).</div>
                    ) : (
                      differeBenchmarkMetricsSelectedView.rows.length > 0 ? (
                        <DiffereBenchmarkSegmentStats
                          title={differeBenchmarkMetricsSelectedView.title}
                          subtitle={
                            (differeBenchmarkMetricsScope === 'all'
                              ? 'Agrégat sur tous les différés'
                              : 'Un différé')
                            + (differeBenchmarkMetricsSelectedView.limitedToTopAi
                              ? ` — top ${DIFFERE_BENCHMARK_METRICS_TOP_AI_LIMIT} IA + baselines réf.`
                              : '')
                            + ` — ${differeBenchmarkMetricsSelectedView.rows.length} modèle(s)`
                            + (differeBenchmarkMetricsSelectedView.limitedToTopAi
                              ? ` (${differeBenchmarkMetricsSelectedView.aiCount} IA au total)`
                              : '')
                          }
                          rows={differeBenchmarkMetricsSelectedView.rows}
                          compareRole={differeBenchmarkMetricsSelectedView.compareRole}
                          segment={differeBenchmarkMetricsSelectedView.segment}
                          onOpenMetric={openDiffereBenchmarkMetricDrilldown}
                        />
                      ) : (
                        <div className="admin-empty">
                          Aucune statistique pour la vue « {differeBenchmarkMetricsSelectedView.label} » dans ce périmètre.
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          {differeMetricDrilldown &&
            createPortal(
              <div
                className="ml-compare-metric-drill-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ml-differe-metric-drill-title"
                onClick={e => {
                  if (e.target !== e.currentTarget) return;
                  closeDiffereMetricDrilldown();
                }}
              >
                <div
                  className="ml-compare-detail-panel"
                  style={{
                    maxWidth:
                      differeDealDetailPanel && differeDealDetailCompareOptions.length > 0
                        ? 'min(1480px, 98vw)'
                        : differeDealDetailPanel
                          ? 'min(980px, 96vw)'
                          : 860,
                    marginTop: 32,
                    marginBottom: 32,
                    maxHeight: '90vh',
                    overflow: 'auto',
                    width: '100%',
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                    <button type="button" className="admin-btn" onClick={closeDiffereMetricDrilldown}>
                      Fermer
                    </button>
                    {differeDealDetailPanel ? (
                      <>
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm"
                          onClick={() => {
                            setDiffereDealDetailPanel(null);
                            setDiffereDealDetailComparePanel(null);
                            setDiffereDealDetailCompareTarget(null);
                            setDiffereDealDetailTrickFocus(null);
                          }}
                        >
                          ← Liste des donnes
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm"
                          disabled={differeDealDetailLoading || !differeDealDetailMetricNav.prev}
                          onClick={() => {
                            const d = differeDealDetailMetricNav.prev;
                            if (d) void openDiffereBenchmarkDealDetail(d.runId, d.dealIndex, undefined, d.baselineKey, d.importId);
                          }}
                        >
                          ← Précédent
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm"
                          disabled={differeDealDetailLoading || !differeDealDetailMetricNav.next}
                          onClick={() => {
                            const d = differeDealDetailMetricNav.next;
                            if (d) void openDiffereBenchmarkDealDetail(d.runId, d.dealIndex, undefined, d.baselineKey, d.importId);
                          }}
                        >
                          Suivant →
                        </button>
                        {differeDealDetailMetricNav.listIndex >= 0 ? (
                          <span style={{ fontSize: '0.78rem', opacity: 0.78, whiteSpace: 'nowrap' }}>
                            {differeDealDetailMetricNav.listIndex + 1} / {differeDealDetailMetricNav.listLen}
                          </span>
                        ) : null}
                      </>
                    ) : null}
                    <h4 id="ml-differe-metric-drill-title" style={{ margin: 0, flex: 1 }}>
                      {ML_COMPARE_METRIC_TITLES[differeMetricDrilldown.metric] ?? differeMetricDrilldown.metric}
                      {' — '}
                      {differeMetricDrilldown.modelLabel}
                    </h4>
                  </div>

                  {differeDealDetailLoading ? (
                    <div className="admin-loading">Chargement du détail…</div>
                  ) : differeDealDetailPanel ? (
                    <div className="ml-compare-deal-detail">
                      <p style={{ fontSize: '0.88rem', opacity: 0.9 }}>
                        {String(differeDealDetailPanel.modelLabel ?? differeMetricDrilldown.modelLabel)} —{' '}
                        Différé #{differeDealDetailPanel.sourceDiffereId as number} — donne{' '}
                        #{Number(differeDealDetailPanel.dealIndex) + 1} —{' '}
                        {(differeDealDetailPanel.contractLabel as string) ?? '—'} — preneur{' '}
                        {ML_COMPARE_SEAT_LABELS[(differeDealDetailPanel.takerPosition as number) ?? 0] ?? '?'}
                      </p>
                      {differeDealDetailCompareOptions.length > 0 ? (
                        <>
                          <div
                            style={{
                              marginBottom: 12,
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 10,
                              fontSize: '0.84rem',
                            }}
                          >
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, flexWrap: 'wrap' }}>
                              <span style={{ opacity: 0.9 }}>Comparer les plis avec :</span>
                              <select
                                value={differeDealDetailCompareTarget ?? ''}
                                onChange={e => {
                                  const v = e.target.value;
                                  if (v) setDiffereDealDetailCompareTarget(v);
                                }}
                                disabled={differeDealDetailCompareLoading}
                                style={{
                                  maxWidth: 380,
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  border: '1px solid rgba(255,255,255,0.15)',
                                  background: 'rgba(0,0,0,0.25)',
                                  color: 'inherit',
                                  fontSize: '0.82rem',
                                }}
                              >
                                {differeDealDetailCompareOptions.map(o => (
                                  <option key={o.key} value={o.key}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <span style={{ fontSize: '0.78rem', opacity: 0.65 }}>
                              Même import et même donne ; surbrillance des plis identiques (4 cartes) entre les deux colonnes.
                            </span>
                          </div>
                          <div className="ml-compare-deal-detail-split">
                            <div className="ml-compare-deal-detail-split-pane">
                              <div className="ml-compare-deal-detail-split-caption">
                                <strong>
                                  {mlShortModelLabel(
                                    String(differeDealDetailPanel.modelLabel ?? differeMetricDrilldown.modelLabel),
                                  )}
                                </strong>
                              </div>
                              <MlCompareDealTricksGrid
                                tricks={(differeDealDetailPanel.tricks as Array<Record<string, unknown>>) ?? []}
                                metricHighlights={
                                  differeDealDetailPanel.metricHighlights as MlCompareMetricHighlightRow[] | undefined
                                }
                                studiedSeatColumnIndexes={differeDealDetailStudiedSeatCols}
                                syncHighlightTrickNumbers={differeDealDetailSyncTrickNums}
                                focusedTrickNum1={differeDealDetailTrickFocus}
                                onTrickNumberCellClick={toggleDiffereDealDetailTrickFocus}
                                takerPosition={Number((differeDealDetailPanel as { takerPosition?: number }).takerPosition ?? 0)}
                                compareRole={
                                  (differeDealDetailPanel as { compareRole?: string }).compareRole === 'defender'
                                    ? 'defender'
                                    : 'attacker'
                                }
                              />
                              <MlCompareDealPostTricksPanel
                                panel={differeDealDetailPanel as Record<string, unknown>}
                                tricks={(differeDealDetailPanel.tricks as Array<Record<string, unknown>>) ?? []}
                                trickFocusTrickNum1={differeDealDetailTrickFocus}
                                ecartHighlightBothModels={differeDealDetailEcartCommonBoth}
                                studiedSeatIndexes={differeDealDetailStudiedSeats}
                              />
                              <MlCompareDealDetailPaneStats
                                panel={differeDealDetailPanel}
                                drillMetricKey={differeMetricDrilldown.metric}
                                onSelectMetric={metric => {
                                  setDiffereMetricDrilldown(prev => (prev ? { ...prev, metric } : prev));
                                  const runId = Number(differeDealDetailPanel.runId);
                                  const dealIndex = Number(differeDealDetailPanel.dealIndex);
                                  const baselineKey = String((differeDealDetailPanel as { versionTag?: unknown }).versionTag ?? '');
                                  const importId = Number((differeDealDetailPanel as { importId?: unknown }).importId);
                                  if (Number.isFinite(runId) && Number.isFinite(dealIndex)) {
                                    void openDiffereBenchmarkDealDetail(
                                      runId,
                                      dealIndex,
                                      metric,
                                      runId === 0 ? baselineKey : null,
                                      runId === 0 && Number.isFinite(importId) ? importId : null,
                                    );
                                  }
                                }}
                              />
                            </div>
                            <div className="ml-compare-deal-detail-split-pane">
                              <div className="ml-compare-deal-detail-split-caption">
                                <strong>
                                  {mlShortModelLabel(
                                    String(
                                      differeDealDetailCompareOptions.find(o => o.key === differeDealDetailCompareTarget)
                                        ?.label
                                        ?? differeDealDetailCompareTarget
                                        ?? '—',
                                    ),
                                  )}
                                </strong>
                              </div>
                              {differeDealDetailCompareLoading ? (
                                <div className="admin-loading ml-compare-deal-detail-compare-loading">
                                  Chargement du 2e jeu de plis…
                                </div>
                              ) : differeDealDetailComparePanel ? (
                                <>
                                  <MlCompareDealTricksGrid
                                    tricks={
                                      (differeDealDetailComparePanel.tricks as Array<Record<string, unknown>>) ?? []
                                    }
                                    metricHighlights={
                                      differeDealDetailComparePanel.metricHighlights as
                                        | MlCompareMetricHighlightRow[]
                                        | undefined
                                    }
                                    studiedSeatColumnIndexes={differeDealDetailStudiedSeatCols}
                                    syncHighlightTrickNumbers={differeDealDetailSyncTrickNums}
                                    focusedTrickNum1={differeDealDetailTrickFocus}
                                    onTrickNumberCellClick={toggleDiffereDealDetailTrickFocus}
                                    takerPosition={Number(
                                      (differeDealDetailComparePanel as { takerPosition?: number }).takerPosition ?? 0,
                                    )}
                                    compareRole={
                                      (differeDealDetailComparePanel as { compareRole?: string }).compareRole ===
                                      'defender'
                                        ? 'defender'
                                        : 'attacker'
                                    }
                                  />
                                  <MlCompareDealPostTricksPanel
                                    panel={differeDealDetailComparePanel as Record<string, unknown>}
                                    tricks={
                                      (differeDealDetailComparePanel.tricks as Array<Record<string, unknown>>) ?? []
                                    }
                                    trickFocusTrickNum1={differeDealDetailTrickFocus}
                                    ecartHighlightBothModels={differeDealDetailEcartCommonBoth}
                                    studiedSeatIndexes={differeDealDetailStudiedSeats}
                                  />
                                  <MlCompareDealDetailPaneStats
                                    panel={differeDealDetailComparePanel}
                                    drillMetricKey={differeMetricDrilldown.metric}
                                    onSelectMetric={metric => {
                                      setDiffereMetricDrilldown(prev => (prev ? { ...prev, metric } : prev));
                                      const runId = Number(differeDealDetailPanel.runId);
                                      const dealIndex = Number(differeDealDetailPanel.dealIndex);
                                      const baselineKey = String(
                                        (differeDealDetailPanel as { versionTag?: unknown }).versionTag ?? '',
                                      );
                                      const importId = Number((differeDealDetailPanel as { importId?: unknown }).importId);
                                      if (Number.isFinite(runId) && Number.isFinite(dealIndex)) {
                                        void openDiffereBenchmarkDealDetail(
                                          runId,
                                          dealIndex,
                                          metric,
                                          runId === 0 ? baselineKey : null,
                                          runId === 0 && Number.isFinite(importId) ? importId : null,
                                        );
                                      }
                                    }}
                                  />
                                </>
                              ) : (
                                <div className="admin-empty" style={{ fontSize: '0.82rem', padding: '12px 0' }}>
                                  Pas de données pour ce 2e joueur (run benchmark manquant sur cet import ?).
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <MlCompareDealTricksGrid
                            tricks={(differeDealDetailPanel.tricks as Array<Record<string, unknown>>) ?? []}
                            metricHighlights={
                              differeDealDetailPanel.metricHighlights as MlCompareMetricHighlightRow[] | undefined
                            }
                            studiedSeatColumnIndexes={differeDealDetailStudiedSeatCols}
                            focusedTrickNum1={differeDealDetailTrickFocus}
                            onTrickNumberCellClick={toggleDiffereDealDetailTrickFocus}
                            takerPosition={Number((differeDealDetailPanel as { takerPosition?: number }).takerPosition ?? 0)}
                            compareRole={
                              (differeDealDetailPanel as { compareRole?: string }).compareRole === 'defender'
                                ? 'defender'
                                : 'attacker'
                            }
                          />
                          <MlCompareDealPostTricksPanel
                            panel={differeDealDetailPanel as Record<string, unknown>}
                            tricks={(differeDealDetailPanel.tricks as Array<Record<string, unknown>>) ?? []}
                            trickFocusTrickNum1={differeDealDetailTrickFocus}
                            studiedSeatIndexes={differeDealDetailStudiedSeats}
                          />
                          <MlCompareDealDetailPaneStats
                            panel={differeDealDetailPanel}
                            drillMetricKey={differeMetricDrilldown.metric}
                            onSelectMetric={metric => {
                              setDiffereMetricDrilldown(prev => (prev ? { ...prev, metric } : prev));
                              const runId = Number(differeDealDetailPanel.runId);
                              const dealIndex = Number(differeDealDetailPanel.dealIndex);
                              const baselineKey = String((differeDealDetailPanel as { versionTag?: unknown }).versionTag ?? '');
                              const importId = Number((differeDealDetailPanel as { importId?: unknown }).importId);
                              if (Number.isFinite(runId) && Number.isFinite(dealIndex)) {
                                void openDiffereBenchmarkDealDetail(
                                  runId,
                                  dealIndex,
                                  metric,
                                  runId === 0 ? baselineKey : null,
                                  runId === 0 && Number.isFinite(importId) ? importId : null,
                                );
                              }
                            }}
                          />
                        </>
                      )}
                    </div>
                  ) : differeMetricDealsLoading ? (
                    <div className="admin-loading">Chargement des donnes…</div>
                  ) : (
                    <>
                      <p style={{ fontSize: '0.86rem', opacity: 0.88 }}>
                        Périmètre :{' '}
                        {differeMetricDrilldown.segment === 'fullRun'
                          ? 'toutes les donnes'
                          : differeMetricDrilldown.segment === 'defense'
                            ? 'donnes défense'
                            : differeMetricDrilldown.segment === 'attackSc'
                              ? 'attaque sur-contrats'
                              : 'attaque hors sur-contrats'}
                        {differeBenchmarkMetricsScope === 'one' && differeBenchmarkMetricsImportId != null
                          ? ` — import ${differeBenchmarkMetricsImportId}`
                          : ' — tous les différés du pool'}
                      </p>
                      {(() => {
                        const compareModels =
                          (differeMetricDealsPayload?.comparisonModels as
                            | Array<{ versionTag: string; modelLabel: string }>
                            | undefined) ?? [];
                        const dealsList =
                          (differeMetricDealsPayload?.deals as Array<Record<string, any>> | undefined) ?? [];
                        const showCompareCol =
                          compareModels.length > 0 && differeBenchmarkDrillCompareTag != null && differeBenchmarkDrillCompareTag !== '';
                        const compareOtherLabel = compareModels.find(
                          m => m.versionTag === differeBenchmarkDrillCompareTag,
                        )?.modelLabel;
                        const tableColSpan = showCompareCol ? 8 : 7;
                        const canInvertDiffereBenchmarkDrill =
                          !!differeMetricDrilldown
                          && !!differeBenchmarkDrillCompareTag
                          && differeBenchmarkIsMlPoolMetricDrillTag(differeMetricDrilldown.versionTag)
                          && differeBenchmarkIsMlPoolMetricDrillTag(differeBenchmarkDrillCompareTag);
                        return (
                          <>
                            {compareModels.length > 0 ? (
                              <div
                                style={{
                                  marginBottom: 12,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  alignItems: 'center',
                                  gap: 10,
                                  fontSize: '0.84rem',
                                }}
                              >
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, flexWrap: 'wrap' }}>
                                  <span style={{ opacity: 0.9 }}>
                                    Même métrique sur cette donne, autre modèle ML (pool) :
                                  </span>
                                  <select
                                    value={differeBenchmarkDrillCompareTag ?? ''}
                                    onChange={e => {
                                      const v = e.target.value;
                                      if (!differeMetricDrilldown) return;
                                      void loadDiffereBenchmarkDrillDeals(differeMetricDrilldown, v || undefined);
                                    }}
                                    disabled={differeMetricDealsLoading}
                                    style={{
                                      maxWidth: 360,
                                      padding: '6px 10px',
                                      borderRadius: 6,
                                      border: '1px solid rgba(255,255,255,0.15)',
                                      background: 'rgba(0,0,0,0.25)',
                                      color: 'inherit',
                                      fontSize: '0.82rem',
                                    }}
                                  >
                                    {compareModels.map(m => (
                                      <option key={m.versionTag} value={m.versionTag}>
                                        {m.modelLabel || m.versionTag}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <button
                                  type="button"
                                  className="admin-btn admin-btn-sm"
                                  title="Échanger la ligne principale (liste + points) et la colonne « Aussi » — uniquement entre deux modèles ML du pool."
                                  disabled={differeMetricDealsLoading || !canInvertDiffereBenchmarkDrill}
                                  onClick={() => void invertDiffereBenchmarkMetricDrill()}
                                >
                                  ⇄ Inverser
                                </button>
                              </div>
                            ) : null}
                            <div className="ml-compare-table-wrap">
                              <table className="ml-compare-table ml-compare-table--compact">
                                <thead>
                                  <tr>
                                    <th>Différé</th>
                                    <th>Run</th>
                                    <th>Joueur/baseline</th>
                                    <th>Donne</th>
                                    <th>Contrat</th>
                                    <th>Points</th>
                                    {showCompareCol ? (
                                      <th
                                        className="ml-compare-metric-drill-deals-points-th"
                                        title="Présence de la même métrique drill sur cette donne pour le modèle ML choisi (même import, même index de donne)."
                                      >
                                        Aussi : {mlShortModelLabel(compareOtherLabel || differeBenchmarkDrillCompareTag || '—')}
                                      </th>
                                    ) : null}
                                    <th>Détail</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dealsList.length > 0 ? (
                                    dealsList.map(row => {
                                      const otherTag = differeBenchmarkDrillCompareTag;
                                      const pr =
                                        showCompareCol && otherTag && row.metricPresenceForOtherModels
                                          ? row.metricPresenceForOtherModels[otherTag]
                                          : undefined;
                                      return (
                                        <tr key={`${row.baselineKey ?? row.runId}-${row.importId}-${row.dealIndex}`}>
                                          <td>#{row.sourceDiffereId ?? row.importId}</td>
                                          <td>{Number(row.runId) === 0 ? 'Baseline' : row.runId}</td>
                                          <td>{row.modelLabel ?? differeMetricDrilldown.modelLabel}</td>
                                          <td>#{Number(row.dealIndex) + 1}</td>
                                          <td>{row.contractLabel ?? '—'}</td>
                                          <td>{formatMlCompareEvaluatedSidePoints(row.evaluatedSidePointsStudied)}</td>
                                          {showCompareCol ? (
                                            <td className="ml-compare-metric-drill-deals-yesno">
                                              {pr === true ? (
                                                <span
                                                  className="ml-compare-metric-drill-yesno-cell"
                                                  title="Oui : ce modèle a aussi la métrique sur cette donne"
                                                  role="img"
                                                  aria-label="Oui"
                                                >
                                                  ✅
                                                </span>
                                              ) : pr === false ? (
                                                <span
                                                  className="ml-compare-metric-drill-yesno-cell"
                                                  title="Non : pas cette métrique pour ce modèle sur cette donne (ou run absent)"
                                                  role="img"
                                                  aria-label="Non"
                                                >
                                                  ❌
                                                </span>
                                              ) : (
                                                <span className="ml-compare-metric-drill-yesno-unknown" title="Indisponible">
                                                  ➖
                                                </span>
                                              )}
                                            </td>
                                          ) : null}
                                          <td>
                                            <button
                                              type="button"
                                              className="admin-btn admin-btn-sm"
                                              onClick={() => void openDiffereBenchmarkDealDetail(
                                                Number(row.runId),
                                                Number(row.dealIndex),
                                                undefined,
                                                row.baselineKey ? String(row.baselineKey) : null,
                                                row.baselineKey ? Number(row.importId) : null,
                                              )}
                                            >
                                              Voir les plis
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    <tr>
                                      <td colSpan={tableColSpan}>Aucune donne trouvée pour cette métrique dans ce périmètre.</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>,
              document.body,
            )}
          {selectedComparison && (
            <div
              ref={compareDetailOverlayRef}
              className="ml-compare-detail-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ml-compare-detail-title"
              onClick={closeComparisonDetail}
            >
              <div className="ml-compare-detail-panel" onClick={e => e.stopPropagation()}>
            <div className="ml-compare-results">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
                <button type="button" className="admin-btn" onClick={closeComparisonDetail}>
                  ← Retour à la liste
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-sm"
                  onClick={() => handleExportComparisonJson(selectedComparison.id)}
                  disabled={compareExportId !== null}
                  title="Même export que dans la liste : toutes les donnes et résultats détaillés"
                >
                  {compareExportId === selectedComparison.id ? 'Export...' : 'Exporter JSON'}
                </button>
              </div>

              <h4 id="ml-compare-detail-title">
                Comparaison #{selectedComparison.id} — {selectedComparison.config.compareRole === 'defender' ? 'Défense' : 'Attaque'}
                <span className="ml-compare-results-meta">
                  {' '}
                  ({selectedComparison.config?.stoppedEarly && selectedComparison.config?.effectiveDealCount != null
                    ? `${selectedComparison.config.effectiveDealCount} donnes (arrêt anticipé, prévu ${selectedComparison.config.dealCount})`
                    : `${selectedComparison.config.dealCount} donnes`}
                  , {new Date(selectedComparison.createdAt).toLocaleDateString('fr-FR')})
                </span>
              </h4>

              {selectedComparison.status === 'completed' && (
                <div className="admin-section" style={{ marginBottom: 20, padding: 12, border: '1px solid var(--admin-border, #333)', borderRadius: 8 }}>
                  <h5 style={{ marginTop: 0 }}>Ajouter un modèle (mêmes donnes)</h5>
                  <p style={{ fontSize: 13, opacity: 0.9, marginBottom: 10 }}>
                    Fait rejouer uniquement le nouveau modèle sur le jeu de donnes déjà utilisé, puis met à jour les statistiques et la matrice de significativité.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <select
                      value={compareAppendModel.versionTag}
                      onChange={e => {
                        if (e.target.value === 'legacy-rv') {
                          setCompareAppendModel(legacyRvCompareModel);
                          return;
                        }
                        const ver = modelVersions.find(v => v.versionTag === e.target.value);
                        setCompareAppendModel({
                          slot: ver?.versionTag || e.target.value,
                          label: ver?.notes || ver?.versionTag || '',
                          versionTag: e.target.value,
                        });
                      }}
                      className="admin-input"
                      style={{ minWidth: 260 }}
                      disabled={busy === 'compare'}
                    >
                      <option value="">— Choisir un modèle à ajouter —</option>
                      <option value="legacy-rv">Legacy RV (moteur historique)</option>
                      {modelVersions
                        .filter(v => v.modelRole === selectedComparison.config.compareRole && v.s3ModelPath?.endsWith('.pt'))
                        .filter(v => !(selectedComparison.config.models ?? []).some((m: { versionTag?: string }) => m.versionTag === v.versionTag))
                        .map(v => (
                          <option key={v.versionTag} value={v.versionTag} title={v.notes || v.versionTag}>
                            {mlShortModelLabel(v.notes || v.versionTag)} ({v.trainingMode}{v.deployedSlot ? ` — ${v.deployedSlot}` : ''})
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="admin-btn admin-btn-primary"
                      onClick={() => void handleAppendModelToComparison()}
                      disabled={busy === 'compare' || !compareAppendModel.versionTag}
                    >
                      {busy === 'compare' ? 'Lancement…' : 'Ajouter à la comparaison'}
                    </button>
                  </div>
                </div>
              )}

              {selectedComparison.modelStats && selectedComparison.modelStats.length > 0 ? (
                <>
                  {/* Summary table */}
                  <div className="ml-compare-table-wrap">
                    <p className="ml-compare-hint" style={{ fontSize: '0.82rem', marginBottom: 8 }}>
                      Colonnes Petit : cliquez sur une valeur pour lister les donnes où le cas se produit (côté modèle évalué).
                    </p>
                    <table className="ml-compare-table">
                      <thead>
                        <tr>
                          <th>Modèle</th>
                          <th title={ML_COMPARE_DENOM_TT.victoires}>Victoires</th>
                          <th title={ML_COMPARE_DENOM_TT.winRate}>Win Rate</th>
                          <th title={ML_COMPARE_DENOM_TT.avgScore}>Score moy.</th>
                          <th title={ML_COMPARE_DENOM_TT.scoreStdDev}>Écart-type</th>
                          <th title={ML_COMPARE_DENOM_TT.petitLostTh}>Petit perdu</th>
                          <th title={ML_COMPARE_DENOM_TT.petitRemporteTh}>Petit remporté</th>
                          <th title={ML_COMPARE_DENOM_TT.petitAuBoutTh}>
                            Petit au bout
                          </th>
                          {selectedComparisonShowRv99Column && <th title={ML_COMPARE_DENOM_TT.legacyRv99Th}>RV 99</th>}
                          <th title="Durée totale des donnes rejouées pour ce modèle (somme, pas un pourcentage).">Durée</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedComparison.modelStats.map((ms: any, idx: number) => {
                          const pt = ms.petitStats;
                          const remporte = pt.petitRemporteByModel ?? 0;
                          const fftB = pt.petitAuBoutScoringBonusCount ?? pt.petitAuBout ?? 0;
                          const evN = pt.petitAuBoutEvaluatedLastTrick ?? 0;
                          const heldDenom = pt.petitAuBoutEvaluatedDenom ?? 0;
                          const noPetitDenom = pt.petitStudiedNoPetitDealCount ?? 0;
                          const evPct = heldDenom > 0 ? ((evN / heldDenom) * 100).toFixed(1) : null;
                          return (
                          <tr key={ms.versionTag} className={idx === 0 ? 'ml-compare-best' : ''}>
                            <td className="ml-compare-model-name">
                              <strong>{ms.modelLabel || ms.versionTag}</strong>
                            </td>
                            <td title={ML_COMPARE_DENOM_TT.victoires}>{ms.wins}/{ms.totalDeals}</td>
                            <td className="ml-compare-winrate" title={ML_COMPARE_DENOM_TT.winRate}>
                              <strong>{(ms.winRate * 100).toFixed(1)}%</strong>
                            </td>
                            <td title={ML_COMPARE_DENOM_TT.avgScore}>{ms.avgScore.toFixed(1)}</td>
                            <td title={ML_COMPARE_DENOM_TT.scoreStdDev}>{ms.scoreStdDev.toFixed(1)}</td>
                            <td
                              role="button"
                              tabIndex={0}
                              style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
                              title={`${ML_COMPARE_DENOM_TT.petitLostTh} — Lister les donnes avec petit perdu.`}
                              onClick={e => {
                                e.stopPropagation();
                                void openCompareMetricDrilldown('petitLost', ms.versionTag, ms.modelLabel || ms.versionTag);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  void openCompareMetricDrilldown('petitLost', ms.versionTag, ms.modelLabel || ms.versionTag);
                                }
                              }}
                            >
                              {heldDenom > 0
                                ? `${pt.petitLostByModel} (${((pt.petitLostByModel / heldDenom) * 100).toFixed(0)}%)`
                                : '—'}
                            </td>
                            <td
                              role="button"
                              tabIndex={0}
                              style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
                              title={`${ML_COMPARE_DENOM_TT.petitRemporteTh} — Lister les donnes — petit remporté.`}
                              onClick={e => {
                                e.stopPropagation();
                                void openCompareMetricDrilldown('petitRemporte', ms.versionTag, ms.modelLabel || ms.versionTag);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  void openCompareMetricDrilldown('petitRemporte', ms.versionTag, ms.modelLabel || ms.versionTag);
                                }
                              }}
                            >
                              {noPetitDenom > 0
                                ? `${remporte} (${((remporte / noPetitDenom) * 100).toFixed(0)}%)`
                                : '—'}
                            </td>
                            <td
                              role="button"
                              tabIndex={0}
                              style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
                              title={
                                heldDenom > 0
                                  ? `${ML_COMPARE_DENOM_TT.petitAuBoutTh} — Lister les donnes : Petit au dernier pli (${evN}/${heldDenom}).`
                                  : 'Lister les donnes avec bonus barème petit au bout'
                              }
                              onClick={e => {
                                e.stopPropagation();
                                void openCompareMetricDrilldown('petitAuBout', ms.versionTag, ms.modelLabel || ms.versionTag);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  void openCompareMetricDrilldown('petitAuBout', ms.versionTag, ms.modelLabel || ms.versionTag);
                                }
                              }}
                            >
                              {heldDenom > 0 ? `${evN} (${evPct}%)` : (fftB > 0 ? String(fftB) : '—')}
                            </td>
                            {selectedComparisonShowRv99Column && (
                              <td
                                role={ms.legacyRvStats ? 'button' : undefined}
                                tabIndex={ms.legacyRvStats ? 0 : undefined}
                                style={ms.legacyRvStats ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 } : undefined}
                                title={ms.legacyRvStats ? `${ML_COMPARE_DENOM_TT.legacyRv99Th} — Lister les donnes avec retour RV 99 (${ms.legacyRvStats.emptyCardReturns}/${ms.legacyRvStats.totalCalls} appels RV ; écart: ${ms.legacyRvStats.ecartEmptyCardReturns}/${ms.legacyRvStats.ecartCalls}, carte: ${ms.legacyRvStats.cardEmptyCardReturns}/${ms.legacyRvStats.cardCalls}).` : undefined}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (ms.legacyRvStats) {
                                    void openCompareMetricDrilldown('legacyRv99', ms.versionTag, ms.modelLabel || ms.versionTag);
                                  }
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (ms.legacyRvStats) {
                                      void openCompareMetricDrilldown('legacyRv99', ms.versionTag, ms.modelLabel || ms.versionTag);
                                    }
                                  }
                                }}
                              >
                                {ms.legacyRvStats ? `${(ms.legacyRvStats.emptyCardRate * 100).toFixed(1)}%` : '—'}
                              </td>
                            )}
                            <td>{Math.round(ms.totalDurationMs / 1000)}s</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* P-values */}
                  {selectedComparison.modelStats.some((ms: any) => ms.pValues) && (
                    <div className="ml-compare-section">
                      <h5>Significativité statistique (p-value, test Z bilatéral)</h5>
                      <div className="ml-compare-pvalues">
                        {selectedComparison.modelStats.map((ms: any) =>
                          ms.pValues && Object.entries(ms.pValues).map(([otherTag, pVal]: [string, any]) => {
                            const otherModel = selectedComparison.modelStats!.find((m: any) => m.versionTag === otherTag);
                            const sig = pVal < 0.01 ? 'très significatif' : pVal < 0.05 ? 'significatif' : 'non significatif';
                            const cls = pVal < 0.01 ? 'sig-high' : pVal < 0.05 ? 'sig-med' : 'sig-low';
                            return (
                              <div key={`${ms.versionTag}-${otherTag}`} className={`ml-compare-pvalue ${cls}`}>
                                <span>{ms.modelLabel} vs {otherModel?.modelLabel || otherTag}</span>
                                <span className="ml-compare-pvalue-val">p = {pVal.toFixed(4)}</span>
                                <span className={`ml-compare-pvalue-sig ml-compare-pvalue-sig--${cls}`}>{sig}</span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}

                  {/* Win rate by strength */}
                  <div className="ml-compare-section">
                    <h5>Taux de victoire par force du jeu du preneur</h5>
                    <div className="ml-compare-table-wrap">
                      <table className="ml-compare-table ml-compare-table--compact">
                        <thead>
                          <tr>
                            <th>Bracket</th>
                            {selectedComparison.modelStats.map((ms: any) => (
                              <th key={ms.versionTag}>{ms.modelLabel || ms.versionTag}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const allBrackets = new Set<string>();
                            for (const ms of selectedComparison.modelStats!) {
                              for (const k of Object.keys(ms.winRateByStrength || {})) allBrackets.add(k);
                            }
                            return [...allBrackets].sort().map(bracket => (
                              <tr key={bracket}>
                                <td>{bracket}</td>
                                {selectedComparison.modelStats!.map((ms: any) => {
                                  const d = ms.winRateByStrength?.[bracket];
                                  return (
                                    <td key={ms.versionTag}>
                                      {d ? `${(d.rate * 100).toFixed(1)}% (${d.wins}/${d.total})` : '—'}
                                    </td>
                                  );
                                })}
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Win rate by contract */}
                  <div className="ml-compare-section">
                    <h5>Taux de victoire par contrat</h5>
                    <div className="ml-compare-table-wrap">
                      <table className="ml-compare-table ml-compare-table--compact">
                        <thead>
                          <tr>
                            <th>Contrat</th>
                            {selectedComparison.modelStats.map((ms: any) => (
                              <th key={ms.versionTag}>{ms.modelLabel || ms.versionTag}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const allContracts = new Set<string>();
                            for (const ms of selectedComparison.modelStats!) {
                              for (const k of Object.keys(ms.winRateByContract || {})) allContracts.add(k);
                            }
                            return [...allContracts].map(contract => (
                              <tr key={contract}>
                                <td>{contract}</td>
                                {selectedComparison.modelStats!.map((ms: any) => {
                                  const d = ms.winRateByContract?.[contract];
                                  return (
                                    <td key={ms.versionTag}>
                                      {d ? `${(d.rate * 100).toFixed(1)}% (${d.wins}/${d.total})` : '—'}
                                    </td>
                                  );
                                })}
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Confidence & Entropy */}
                  <div className="ml-compare-section">
                    <h5>Confiance et entropie par phase de jeu</h5>
                    <div className="ml-compare-table-wrap">
                      <table className="ml-compare-table ml-compare-table--compact">
                        <thead>
                          <tr>
                            <th>Modèle</th>
                            <th>Conf. début</th>
                            <th>Conf. milieu</th>
                            <th>Conf. fin</th>
                            <th>Entropie début</th>
                            <th>Entropie milieu</th>
                            <th>Entropie fin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedComparison.modelStats.map((ms: any) => (
                            <tr key={ms.versionTag}>
                              <td><strong>{ms.modelLabel || ms.versionTag}</strong></td>
                              <td>{ms.avgConfidence?.early != null ? (ms.avgConfidence.early * 100).toFixed(1) + '%' : '—'}</td>
                              <td>{ms.avgConfidence?.mid != null ? (ms.avgConfidence.mid * 100).toFixed(1) + '%' : '—'}</td>
                              <td>{ms.avgConfidence?.late != null ? (ms.avgConfidence.late * 100).toFixed(1) + '%' : '—'}</td>
                              <td>{ms.avgEntropy?.early != null ? ms.avgEntropy.early.toFixed(3) : '—'}</td>
                              <td>{ms.avgEntropy?.mid != null ? ms.avgEntropy.mid.toFixed(3) : '—'}</td>
                              <td>{ms.avgEntropy?.late != null ? ms.avgEntropy.late.toFixed(3) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Entame agreement */}
                  {selectedComparison.modelStats.some((ms: any) => ms.entameAgreement) && (
                    <div className="ml-compare-section">
                      <h5>Concordance des entames entre modèles</h5>
                      <div className="ml-compare-table-wrap">
                        <table className="ml-compare-table ml-compare-table--compact">
                          <thead>
                            <tr>
                              <th>Modèle</th>
                              {selectedComparison.modelStats.map((ms: any) => (
                                <th key={ms.versionTag}>{ms.modelLabel || ms.versionTag}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {selectedComparison.modelStats.map((ms: any) => (
                              <tr key={ms.versionTag}>
                                <td><strong>{ms.modelLabel || ms.versionTag}</strong></td>
                                {selectedComparison.modelStats!.map((other: any) => {
                                  if (ms.versionTag === other.versionTag) return <td key={other.versionTag}>—</td>;
                                  const rate = ms.entameAgreement?.[other.versionTag];
                                  return (
                                    <td key={other.versionTag}>
                                      {rate != null ? `${(rate * 100).toFixed(1)}%` : '—'}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Cross-model card agreement */}
                  {selectedComparison.modelStats.some((ms: any) => ms.cardAgreement?.early) && (
                    <div className="ml-compare-section">
                      <h5>Concordance des cartes jouées (analyse inter-modèles)</h5>
                      <div className="ml-compare-table-wrap">
                        <table className="ml-compare-table ml-compare-table--compact">
                          <thead>
                            <tr>
                              <th>Modèle</th>
                              {selectedComparison.modelStats.map((ms: any) => (
                                <th key={ms.versionTag}>{ms.modelLabel || ms.versionTag}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {selectedComparison.modelStats.map((ms: any) => (
                              <tr key={ms.versionTag}>
                                <td><strong>{ms.modelLabel || ms.versionTag}</strong></td>
                                {selectedComparison.modelStats!.map((other: any) => {
                                  if (ms.versionTag === other.versionTag) return <td key={other.versionTag}>—</td>;
                                  const rate = ms.cardAgreement?.early?.[other.versionTag];
                                  return (
                                    <td key={other.versionTag}>
                                      {rate != null ? `${(rate * 100).toFixed(1)}%` : '—'}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Métriques qualitatives (Phase 0 ml-compare) */}
                  {selectedComparison.modelStats.some((ms: any) => ms.qualitativeStats) && (
                    <div className="ml-compare-section ml-compare-qualitative">
                      <h5>Métriques qualitatives</h5>
                      <p className="ml-compare-hint" style={{ marginTop: 0, marginBottom: 8 }}>
                        Indicateurs comportementaux (preneur uniquement pour la plupart des taux si rôle = attaque).{' '}
                        Cliquez sur une valeur pour lister les donnes où l&apos;événement est comptabilisé (sauf « Écart pts (moy.) »).
                      </p>
                      <div className="ml-compare-table-wrap">
                        <table className="ml-compare-table ml-compare-table--compact">
                          <thead>
                            <tr>
                              <th>Modèle</th>
                              <th title={ML_COMPARE_DENOM_TT.petitKamikazeTh}>Petit kamikaze</th>
                              <th title={ML_COMPARE_DENOM_TT.honneurOffertTh}>Honneur offert</th>
                              <th title={ML_COMPARE_DENOM_TT.pointsDonnesTh}>Points donnés</th>
                              <th title={ML_COMPARE_DENOM_TT.pointsDonnesGapTh}>Écart pts (moy.)</th>
                              <th title={ML_COMPARE_DENOM_TT.petitPrenableTh}>Petit prenable</th>
                              <th title={ML_COMPARE_DENOM_TT.chasseRealiseeTh}>Chasse réalisée</th>
                              <th title={ML_COMPARE_DENOM_TT.chasseIncertaineTh}>Chasse incertaine</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedComparison.modelStats.map((ms: any) => {
                              const q = ms.qualitativeStats;
                              const def = selectedComparison.config?.compareRole === 'defender';
                              const kRate = q?.petitKamikazeRate ?? 0;
                              const hRate = q?.honneurOffertRate ?? 0;
                              const kWarn = kRate > 0.005;
                              const hWarn = hRate > 0.02;
                              const fmtRate = (r: number) => `${(r * 100).toFixed(2)}%`;
                              const dash = '—';
                              const ml = ms.modelLabel || ms.versionTag;
                              const tag = ms.versionTag;
                              const opp = q?.petitPrenableOpportunities ?? 0;
                              const oppDenom = q?.petitPrenableOpportunityDenom ?? 0;
                              const oppPct = !def && oppDenom > 0 ? ((opp / oppDenom) * 100).toFixed(1) : null;
                              const realized = q?.petitPrenableRealized ?? 0;
                              const chasseRate = q?.petitChasseRate ?? 0;
                              const drill = (metric: string, content: React.ReactNode, title: string, cellCls?: string) => (
                                <td
                                  role="button"
                                  tabIndex={0}
                                  className={cellCls}
                                  style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
                                  title={title}
                                  onClick={e => {
                                    e.stopPropagation();
                                    void openCompareMetricDrilldown(metric, tag, ml);
                                  }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      void openCompareMetricDrilldown(metric, tag, ml);
                                    }
                                  }}
                                >
                                  {content}
                                </td>
                              );
                              return (
                                <tr key={ms.versionTag}>
                                  <td className="ml-compare-model-name">
                                    <strong>{ms.modelLabel || ms.versionTag}</strong>
                                  </td>
                                  {def ? (
                                    <td className={kWarn ? 'ml-compare-qualitative-warn' : undefined}>{dash}</td>
                                  ) : drill(
                                    'petitKamikaze',
                                    `${fmtMetricCountBeforeRate(ms, q?.petitKamikazeCount ?? 0)} (${fmtRate(kRate)})`,
                                    `${ML_COMPARE_DENOM_TT.petitKamikazeTh} — Lister les donnes avec Petit kamikaze.`,
                                    kWarn ? 'ml-compare-qualitative-warn' : undefined,
                                  )}
                                  {def ? (
                                    <td className={hWarn ? 'ml-compare-qualitative-warn' : undefined}>{dash}</td>
                                  ) : drill(
                                    'honneurOffert',
                                    `${fmtMetricCountBeforeRate(ms, q?.honneurOffertCount ?? 0)} (${fmtRate(hRate)})`,
                                    `${ML_COMPARE_DENOM_TT.honneurOffertTh} — Lister les donnes avec honneur offert.`,
                                    hWarn ? 'ml-compare-qualitative-warn' : undefined,
                                  )}
                                  {drill(
                                    'pointsDonnes',
                                    `${fmtMetricCountBeforeRate(ms, q?.pointsDonnesCount ?? 0)} (${fmtRate(q?.pointsDonnesRate ?? 0)})`,
                                    `${ML_COMPARE_DENOM_TT.pointsDonnesTh} — Lister les donnes avec points donnés.`,
                                  )}
                                  <td title={ML_COMPARE_DENOM_TT.pointsDonnesGapTh}>
                                    {fmtPointsDonnesAvgGapCell(ms, q?.pointsDonnesAvgGap ?? 0)}
                                  </td>
                                  {def ? (
                                    <td>{dash}</td>
                                  ) : drill(
                                    'petitPrenableOpportunity',
                                    oppPct != null ? `${fmtMetricCountBeforeRate(ms, opp)} (${oppPct}%)` : fmtMetricCountBeforeRate(ms, opp),
                                    oppDenom > 0
                                      ? `${ML_COMPARE_DENOM_TT.petitPrenableTh} — Lister les donnes : ${opp}/${oppDenom} donne(s) avec opportunité.`
                                      : `${ML_COMPARE_DENOM_TT.petitPrenableTh} — Lister les donnes avec opportunité petit prenable.`,
                                  )}
                                  {def ? (
                                    <td>{dash}</td>
                                  ) : drill(
                                    'petitPrenableRealized',
                                    `${fmtMetricCountBeforeRate(ms, realized)} (${fmtRate(chasseRate)})`,
                                    `${ML_COMPARE_DENOM_TT.chasseRealiseeTh} — Lister les donnes où la chasse a mené au Petit posé.`,
                                  )}
                                  {def ? (
                                    <td>{dash}</td>
                                  ) : drill(
                                    'petitChasseIncertaine',
                                    `${fmtMetricCountBeforeRate(ms, q?.petitChasseIncertaineCount ?? 0)} (${fmtRate(q?.petitChasseIncertaineRate ?? 0)})`,
                                    `${ML_COMPARE_DENOM_TT.chasseIncertaineTh} — Lister les donnes avec chasse incertaine.`,
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Slam stats */}
                  {selectedComparison.modelStats.some((ms: any) => ms.slamStats?.succeeded > 0) && (
                    <div className="ml-compare-section">
                      <h5>Chelems</h5>
                      <div className="ml-compare-table-wrap">
                        <table className="ml-compare-table ml-compare-table--compact">
                          <thead>
                            <tr>
                              <th>Modèle</th>
                              <th>Chelems réussis</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedComparison.modelStats.map((ms: any) => (
                              <tr key={ms.versionTag}>
                                <td>{ms.modelLabel || ms.versionTag}</td>
                                <td>{fmtMetricCountBeforeRate(ms, ms.slamStats.succeeded)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="admin-empty">Pas de résultats disponibles</div>
              )}
            </div>
            {compareMetricDrilldown &&
              createPortal(
              <>
              <div
                className="ml-compare-metric-drill-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ml-compare-metric-drill-title"
                onClick={e => {
                  if (e.target !== e.currentTarget) return;
                  closeCompareMetricDrilldown();
                }}
              >
                <div
                  className="ml-compare-detail-panel"
                  style={{
                    maxWidth: compareDealDetailPanel ? 'min(1480px, 98vw)' : 760,
                    marginTop: 32,
                    marginBottom: 32,
                    maxHeight: '90vh',
                    overflow: 'auto',
                    width: '100%',
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                    <button type="button" className="admin-btn" onClick={closeCompareMetricDrilldown}>
                      Fermer
                    </button>
                    {compareDealDetailPanel ? (
                      <>
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm"
                          onClick={() => {
                            setCompareDealDetailPanel(null);
                            setCompareDealDetailComparePanel(null);
                            setCompareDealDetailTrickFocus(null);
                          }}
                        >
                          ← Liste des donnes
                        </button>
                        {(compareMetricDealsPayload?.deals?.length ?? 0) > 0 ? (
                          <>
                            <button
                              type="button"
                              className="admin-btn admin-btn-sm"
                              disabled={
                                compareDealDetailLoading
                                || compareDealDetailMetricNav.prevDealIndex == null
                                || !compareMetricDrilldown
                              }
                              title={
                                compareDealDetailMetricNav.prevDealIndex != null
                                  ? 'Donne précédente dans la liste filtrée (même métrique)'
                                  : 'Pas de donne précédente dans cette liste'
                              }
                              onClick={() => {
                                const di = compareDealDetailMetricNav.prevDealIndex;
                                if (di != null && compareMetricDrilldown) {
                                  void openCompareDealDetail(compareMetricDrilldown.versionTag, di);
                                }
                              }}
                            >
                              ← Précédent
                            </button>
                            <button
                              type="button"
                              className="admin-btn admin-btn-sm"
                              disabled={
                                compareDealDetailLoading
                                || compareDealDetailMetricNav.nextDealIndex == null
                                || !compareMetricDrilldown
                              }
                              title={
                                compareDealDetailMetricNav.nextDealIndex != null
                                  ? 'Donne suivante dans la liste filtrée (même métrique)'
                                  : 'Pas de donne suivante dans cette liste'
                              }
                              onClick={() => {
                                const di = compareDealDetailMetricNav.nextDealIndex;
                                if (di != null && compareMetricDrilldown) {
                                  void openCompareDealDetail(compareMetricDrilldown.versionTag, di);
                                }
                              }}
                            >
                              Suivant →
                            </button>
                            {compareDealDetailMetricNav.listIndex >= 0 ? (
                              <span
                                style={{ fontSize: '0.78rem', opacity: 0.78, whiteSpace: 'nowrap' }}
                                title="Position dans la liste des donnes avec cette métrique"
                              >
                                {compareDealDetailMetricNav.listIndex + 1} /{' '}
                                {compareDealDetailMetricNav.listLen}
                              </span>
                            ) : null}
                          </>
                        ) : null}
                      </>
                    ) : null}
                    <h4 id="ml-compare-metric-drill-title" style={{ margin: 0, flex: 1 }}>
                      {ML_COMPARE_METRIC_TITLES[compareMetricDrilldown.metric] ?? compareMetricDrilldown.metric}
                      {' — '}
                      {compareMetricDrilldown.modelLabel}
                    </h4>
                  </div>

                  {compareDealDetailLoading ? (
                    <div className="admin-loading">Chargement du détail…</div>
                  ) : compareDealDetailPanel ? (
                    <div className="ml-compare-deal-detail">
                      <p style={{ fontSize: '0.88rem', opacity: 0.9 }}>
                        Donne #{compareDealDetailPanel.dealIndex as number} —{' '}
                        {(compareDealDetailPanel.contractLabel as string) ?? '—'} — force{' '}
                        {(compareDealDetailPanel.bidScore as number) ?? '—'} — preneur{' '}
                        {ML_COMPARE_SEAT_LABELS[(compareDealDetailPanel.takerPosition as number) ?? 0] ?? '?'}
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 10,
                          marginBottom: 12,
                        }}
                      >
                        <div style={{ fontSize: '0.82rem', opacity: 0.85, flex: '1 1 220px' }}>
                          Plis enregistrés lors de la comparaison. Replay graphique : même onglet, via l’onglet Entraînement
                          (enchères simplifiées : chacun passe une fois dans l’ordre, puis le preneur annonce le contrat).
                        </div>
                        <button
                          type="button"
                          className="admin-btn admin-btn-primary admin-btn-sm"
                          onClick={() => void openCompareDealInTraining()}
                          disabled={!session.compet || compareBatchTrainingLoading}
                          title={
                            session.compet
                              ? `Charge jusqu’à ${ML_COMPARE_TRAINING_MAX_DEALS} donnes de cette liste et ouvre sur celle-ci (${String((compareDealDetailPanel.contractLabel as string) ?? '')}).`
                              : 'Activez le mode compétition pour accéder à l’onglet Entraînement.'
                          }
                        >
                          {compareBatchTrainingLoading ? 'Chargement…' : 'Ouvrir dans Entraînement'}
                        </button>
                      </div>
                      {compareMetricDrilldown.metric === 'legacyRv99'
                        && (!Array.isArray(compareDealDetailPanel.metricHighlights)
                          || compareDealDetailPanel.metricHighlights.length === 0) ? (
                          <div className="ml-compare-hint" style={{ marginTop: -4, marginBottom: 10 }}>
                            <p style={{ marginTop: 0, marginBottom: 8 }}>
                              Aucun pli ne peut être surligné pour ce RV 99 : soit la donnée est antérieure au stockage des
                              cartes jouées après fallback RV 99, soit le 99 concerne l’écart ou la poignée plutôt qu’une
                              carte effectivement jouée dans les plis.
                            </p>
                            <button
                              type="button"
                              className="admin-btn admin-btn-sm"
                              onClick={() => void runCompareRv99Diagnostic()}
                              disabled={compareRv99DiagnosticLoading}
                            >
                              {compareRv99DiagnosticLoading ? 'Diagnostic RV…' : 'Rejouer IA RV pour localiser les 99'}
                            </button>
                          </div>
                        ) : null}
                      {compareMetricDrilldown.metric === 'legacyRv99' && compareRv99Diagnostic ? (
                        <div className="ml-compare-table-wrap" style={{ marginBottom: 12 }}>
                          <table className="ml-compare-table ml-compare-table--compact">
                            <thead>
                              <tr>
                                <th>Phase</th>
                                <th>Joueur</th>
                                <th>Pli</th>
                                <th>Position</th>
                                <th>Détail</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(((compareRv99Diagnostic.events as Array<Record<string, unknown>> | undefined) ?? [])).length > 0 ? (
                                ((compareRv99Diagnostic.events as Array<Record<string, unknown>> | undefined) ?? []).map((ev, idx) => {
                                  const phase = String(ev.phase ?? '—');
                                  const player = typeof ev.player === 'number'
                                    ? (ML_COMPARE_SEAT_LABELS[ev.player] ?? `J${ev.player}`)
                                    : '—';
                                  const pos = typeof ev.positionInTrick === 'number' ? Number(ev.positionInTrick) + 1 : null;
                                  const fallback = typeof ev.fallbackCard === 'number' ? `carte jouée après fallback: ${ev.fallbackCard}` : '';
                                  const callIdx = typeof ev.callIndex === 'number' ? `appel #${ev.callIndex}` : '';
                                  const message = typeof ev.message === 'string' ? ev.message : '';
                                  return (
                                    <tr key={`rv99-${idx}`}>
                                      <td>{phase}</td>
                                      <td>{player}</td>
                                      <td>{typeof ev.trickNumber === 'number' ? ev.trickNumber : '—'}</td>
                                      <td>{pos ?? '—'}</td>
                                      <td>{[callIdx, fallback, message].filter(Boolean).join(' — ') || 'RV a renvoyé 99'}</td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={5}>Aucun retour 99 détecté au rejeu RV.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                      <div className="ml-compare-deal-detail-split">
                        <div className="ml-compare-deal-detail-split-pane">
                          <div className="ml-compare-deal-detail-split-caption">
                            <strong>
                              {mlShortModelLabel(
                                mlCompareRowDisplayName(
                                  compareMetricDrilldown.versionTag,
                                  compareMetricDrilldown.modelLabel,
                                  modelVersions,
                                ),
                              )}
                            </strong>
                          </div>
                          <MlCompareDealTricksGrid
                            tricks={(compareDealDetailPanel.tricks as Array<Record<string, unknown>>) ?? []}
                            metricHighlights={
                              compareDealDetailPanel.metricHighlights as MlCompareMetricHighlightRow[] | undefined
                            }
                            studiedSeatColumnIndexes={dealDetailStudiedSeatCols}
                            syncHighlightTrickNumbers={dealDetailSyncTrickNums}
                            focusedTrickNum1={compareDealDetailTrickFocus}
                            onTrickNumberCellClick={toggleCompareDealTrickFocus}
                            takerPosition={Number((compareDealDetailPanel as { takerPosition?: number }).takerPosition ?? 0)}
                            compareRole={
                              (compareDealDetailPanel as { compareRole?: string }).compareRole === 'defender'
                                ? 'defender'
                                : 'attacker'
                            }
                          />
                          <MlCompareDealPostTricksPanel
                            panel={compareDealDetailPanel as Record<string, unknown>}
                            tricks={(compareDealDetailPanel.tricks as Array<Record<string, unknown>>) ?? []}
                            trickFocusTrickNum1={compareDealDetailTrickFocus}
                            ecartHighlightBothModels={dealDetailEcartCommonBothModels}
                            studiedSeatIndexes={dealDetailStudiedSeats}
                          />
                          <MlCompareDealDetailPaneStats
                            panel={compareDealDetailPanel}
                            drillMetricKey={compareMetricDrilldown.metric}
                            onSelectMetric={switchCompareDealDrillMetric}
                          />
                        </div>
                        <div className="ml-compare-deal-detail-split-pane">
                          <div className="ml-compare-deal-detail-split-caption">
                            <strong>
                              {compareMetricOtherVersionTag
                                ? mlShortModelLabel(
                                    mlCompareRowDisplayName(
                                      compareMetricOtherVersionTag,
                                      compareDrillOtherModelOptions.find(
                                        o => o.versionTag === compareMetricOtherVersionTag,
                                      )?.modelLabel,
                                      modelVersions,
                                    ),
                                  )
                                : 'Comparaison'}
                            </strong>
                          </div>
                          {compareDealDetailCompareLoading ? (
                            <div className="admin-loading ml-compare-deal-detail-compare-loading">
                              Chargement du même pli (autre modèle)…
                            </div>
                          ) : compareDealDetailComparePanel &&
                            compareMetricOtherVersionTag &&
                            compareMetricOtherVersionTag !== compareMetricDrilldown.versionTag ? (
                            <>
                              <MlCompareDealTricksGrid
                                tricks={
                                  (compareDealDetailComparePanel.tricks as Array<Record<string, unknown>>) ?? []
                                }
                                metricHighlights={
                                  compareDealDetailComparePanel.metricHighlights as
                                    | MlCompareMetricHighlightRow[]
                                    | undefined
                                }
                                studiedSeatColumnIndexes={dealDetailStudiedSeatCols}
                                syncHighlightTrickNumbers={dealDetailSyncTrickNums}
                                focusedTrickNum1={compareDealDetailTrickFocus}
                                onTrickNumberCellClick={toggleCompareDealTrickFocus}
                                takerPosition={Number(
                                  (compareDealDetailComparePanel as { takerPosition?: number }).takerPosition ?? 0,
                                )}
                                compareRole={
                                  (compareDealDetailComparePanel as { compareRole?: string }).compareRole ===
                                  'defender'
                                    ? 'defender'
                                    : 'attacker'
                                }
                              />
                              <MlCompareDealPostTricksPanel
                                panel={compareDealDetailComparePanel as Record<string, unknown>}
                                tricks={
                                  (compareDealDetailComparePanel.tricks as Array<Record<string, unknown>>) ?? []
                                }
                                trickFocusTrickNum1={compareDealDetailTrickFocus}
                                ecartHighlightBothModels={dealDetailEcartCommonBothModels}
                                studiedSeatIndexes={dealDetailStudiedSeats}
                              />
                              <MlCompareDealDetailPaneStats
                                panel={compareDealDetailComparePanel}
                                drillMetricKey={compareMetricDrilldown.metric}
                                onSelectMetric={switchCompareDealDrillMetric}
                              />
                            </>
                          ) : (
                            <div className="admin-empty" style={{ fontSize: '0.82rem', padding: '12px 0' }}>
                              {compareMetricOtherVersionTag &&
                              compareMetricOtherVersionTag === compareMetricDrilldown.versionTag
                                ? 'Le modèle de comparaison est le même que le modèle étudié.'
                                : 'Choisissez un modèle de comparaison dans la liste des donnes (menu déroulant au-dessus).'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : compareMetricDealsLoading ? (
                    <div className="admin-loading">Chargement de la liste…</div>
                  ) : (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 10,
                          marginBottom: 12,
                        }}
                      >
                        <p style={{ fontSize: '0.84rem', opacity: 0.88, margin: 0, flex: '1 1 200px' }}>
                          {(compareMetricDealsPayload?.deals?.length ?? 0)} donne(s). Cliquez une ligne pour le détail des
                          plis (données déjà stockées).
                        </p>
                        <button
                          type="button"
                          className="admin-btn admin-btn-primary admin-btn-sm"
                          disabled={
                            !session.compet
                            || compareBatchTrainingLoading
                            || (compareMetricDealsPayload?.deals?.length ?? 0) === 0
                          }
                          title={
                            session.compet
                              ? (compareMetricDealsPayload?.deals?.length ?? 0) > ML_COMPARE_TRAINING_MAX_DEALS
                                ? `Envoie les ${ML_COMPARE_TRAINING_MAX_DEALS} premières donnes sur ${compareMetricDealsPayload?.deals?.length ?? 0}. Navigation dans l’onglet liste à icône.`
                                : 'Envoie toutes les donnes listées ; navigation entre donnes dans l’onglet liste (icône).'
                              : 'Activez le mode compétition pour accéder à l’onglet Entraînement.'
                          }
                          onClick={() => void openCompareDealsInTraining()}
                        >
                          {compareBatchTrainingLoading
                            ? 'Chargement…'
                            : (() => {
                                const n = compareMetricDealsPayload?.deals?.length ?? 0;
                                const k = Math.min(n, ML_COMPARE_TRAINING_MAX_DEALS);
                                return n > ML_COMPARE_TRAINING_MAX_DEALS
                                  ? `Ouvrir dans Entraînement (${k} / ${n})`
                                  : `Ouvrir dans Entraînement (${n})`;
                              })()}
                        </button>
                      </div>
                      <div
                        style={{
                          marginBottom: 14,
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 10,
                          fontSize: '0.84rem',
                        }}
                      >
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, flexWrap: 'wrap' }}>
                          <span style={{ opacity: 0.9 }}>
                            Même métrique sur cette donne, côté autre modèle (comparaison) :
                          </span>
                          <select
                            value={compareMetricOtherVersionTag ?? ''}
                            onChange={e =>
                              setCompareMetricOtherVersionTag(e.target.value ? e.target.value : null)}
                            disabled={compareDrillOtherModelOptions.length === 0 || compareMetricDealsLoading}
                            style={{
                              maxWidth: 320,
                              padding: '6px 10px',
                              borderRadius: 6,
                              border: '1px solid rgba(255,255,255,0.15)',
                              background: 'rgba(0,0,0,0.25)',
                              color: 'inherit',
                              fontSize: '0.82rem',
                            }}
                          >
                            {compareDrillOtherModelOptions.length === 0 ? (
                              <option value="">—</option>
                            ) : (
                              compareDrillOtherModelOptions.map(m => (
                                <option key={m.versionTag} value={m.versionTag}>
                                  {m.modelLabel || m.versionTag}
                                  {typeof m.avgScore === 'number' ? ` (${m.avgScore.toFixed(1)} pts/donne)` : ''}
                                </option>
                              ))
                            )}
                          </select>
                        </label>
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm"
                          title="Échanger : ce modèle devient celui dont on liste les donnes avec la métrique ; l’actuel analysé passe en colonne comparaison."
                          disabled={
                            compareMetricDealsLoading
                            || !compareMetricOtherVersionTag
                            || compareDrillOtherModelOptions.length === 0
                          }
                          onClick={() => void invertCompareMetricDrillModels()}
                        >
                          ⇄ Inverser les modèles
                        </button>
                        {compareDrillOtherModelOptions.length > 0 && (
                          <span style={{ fontSize: '0.78rem', opacity: 0.55 }}>
                            Par défaut : meilleur score moyen de la comparaison (ou 2e si vous étiez déjà sur ce
                            modèle).
                          </span>
                        )}
                      </div>
                      <div className="ml-compare-table-wrap">
                        <table
                          className="ml-compare-table ml-compare-table--compact ml-compare-metric-drill-deals"
                          style={{ marginTop: 0 }}
                        >
                          <thead>
                            <tr>
                              <th>Donne</th>
                              <th>Contrat / force</th>
                              <th
                                className="ml-compare-metric-drill-deals-points-th"
                                title={
                                  compareMetricDealsPayload?.compareRole === 'defender'
                                    ? `Points du camp défense (modèle étudié) — ${compareMetricDrilldown.modelLabel}`
                                    : `Points du camp attaque (modèle étudié) — ${compareMetricDrilldown.modelLabel}`
                                }
                              >
                                Pts —{' '}
                                {mlShortModelLabel(compareMetricDrilldown.modelLabel)}
                              </th>
                              <th
                                className="ml-compare-metric-drill-deals-points-th"
                                title={
                                  compareMetricDealsPayload?.compareRole === 'defender'
                                    ? `Points du camp défense (modèle comparaison) — ${compareDrillOtherModelOptions.find(o => o.versionTag === compareMetricOtherVersionTag)?.modelLabel ?? compareMetricOtherVersionTag ?? ''}`
                                    : `Points du camp attaque (modèle comparaison) — ${compareDrillOtherModelOptions.find(o => o.versionTag === compareMetricOtherVersionTag)?.modelLabel ?? compareMetricOtherVersionTag ?? ''}`
                                }
                              >
                                Pts —{' '}
                                {(() => {
                                  const lab = compareDrillOtherModelOptions.find(
                                    o => o.versionTag === compareMetricOtherVersionTag,
                                  )?.modelLabel;
                                  return mlShortModelLabel(lab || compareMetricOtherVersionTag || '—');
                                })()}
                              </th>
                              <th>
                                Aussi :{' '}
                                {(() => {
                                  const lab = compareDrillOtherModelOptions.find(
                                    o => o.versionTag === compareMetricOtherVersionTag,
                                  )?.modelLabel;
                                  return lab || compareMetricOtherVersionTag || '—';
                                })()}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(compareMetricDealsPayload?.deals ?? []).map(d => {
                              const otherTag = compareMetricOtherVersionTag;
                              const pr =
                                otherTag && d.metricPresenceForOtherModels
                                  ? d.metricPresenceForOtherModels[otherTag]
                                  : undefined;
                              const studiedPts =
                                typeof d.evaluatedSidePointsStudied === 'number'
                                  ? d.evaluatedSidePointsStudied
                                  : undefined;
                              const otherPtsRaw =
                                otherTag && d.evaluatedSidePointsByOtherTag
                                  ? d.evaluatedSidePointsByOtherTag[otherTag]
                                  : undefined;
                              const { studiedBest, otherBest } = mlCompareMetricDrillRowPointsHighlight(
                                studiedPts,
                                otherPtsRaw,
                              );
                              const openThisDeal = () =>
                                void openCompareDealDetail(compareMetricDrilldown.versionTag, d.dealIndex);
                              return (
                                <tr
                                  key={d.dealIndex}
                                  className="ml-compare-metric-drill-deals-row"
                                  tabIndex={0}
                                  role="button"
                                  title="Afficher le détail de cette donne"
                                  onClick={openThisDeal}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      openThisDeal();
                                    }
                                  }}
                                >
                                  <td className="ml-compare-metric-drill-deals-num">#{d.dealIndex}</td>
                                  <td className="ml-compare-metric-drill-deals-contract">
                                    {d.contractLabel} — force {d.bidScore}
                                  </td>
                                  <td
                                    className={`ml-compare-metric-drill-deals-points-cell${studiedBest ? ' ml-compare-metric-drill-deals-points-best' : ''}`}
                                  >
                                    {formatMlCompareEvaluatedSidePoints(studiedPts)}
                                  </td>
                                  <td
                                    className={`ml-compare-metric-drill-deals-points-cell${otherBest ? ' ml-compare-metric-drill-deals-points-best' : ''}`}
                                  >
                                    {otherTag
                                      ? formatMlCompareEvaluatedSidePoints(otherPtsRaw)
                                      : '—'}
                                  </td>
                                  <td className="ml-compare-metric-drill-deals-yesno">
                                    {otherTag ? (
                                      pr === true ? (
                                        <span
                                          className="ml-compare-metric-drill-yesno-cell"
                                          title="Oui : ce modèle a aussi la métrique sur cette donne"
                                          role="img"
                                          aria-label="Oui"
                                        >
                                          ✅
                                        </span>
                                      ) : pr === false ? (
                                        <span
                                          className="ml-compare-metric-drill-yesno-cell"
                                          title="Non : pas cette métrique pour ce modèle sur cette donne"
                                          role="img"
                                          aria-label="Non"
                                        >
                                          ❌
                                        </span>
                                      ) : (
                                        <span className="ml-compare-metric-drill-yesno-unknown" title="Indisponible">
                                          ➖
                                        </span>
                                      )
                                    ) : (
                                      '—'
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(compareMetricDealsPayload?.deals?.length ?? 0) === 0 ? (
                        <div className="admin-empty">Aucune donne ne correspond à ce filtre pour ce modèle.</div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
              {!metricDrillUiSafe ? (
                <div
                  className="ml-compare-metric-drill-open-shield"
                  aria-hidden
                  onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                />
              ) : null}
              </>,
              document.body,
            )}
              </div>
            </div>
          )}
        </div>
      )}

      {section === 'deductions' && (
        <DeductionsPanel session={session} />
      )}

      {selectedJob && typeof document !== 'undefined' && createPortal(
        <div
          className="ml-log-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Logs d'entraînement"
          onClick={() => setSelectedJob(null)}
        >
          <div className="ml-log-panel" onClick={e => e.stopPropagation()}>
            <div className="ml-log-header">
              <h3 title={selectedJob.name || selectedJob.id}>{selectedJobDisplayName}</h3>
              <span>{stateLabel(selectedJob.state)}</span>
              <div className="ml-log-header-actions">
                <div className="ml-log-view-toggle" aria-label="Mode d'affichage des logs">
                  <button
                    type="button"
                    className={logViewMode === 'raw' ? 'is-active' : ''}
                    onClick={() => setLogViewMode('raw')}
                  >
                    Brut
                  </button>
                  <button
                    type="button"
                    className={logViewMode === 'clean' ? 'is-active' : ''}
                    onClick={() => setLogViewMode('clean')}
                  >
                    Sans timestamps
                  </button>
                  <button
                    type="button"
                    className={logViewMode === 'summary' ? 'is-active' : ''}
                    onClick={() => setLogViewMode('summary')}
                  >
                    Graphique
                  </button>
                  <button
                    type="button"
                    className={logViewMode === 'params' ? 'is-active' : ''}
                    onClick={() => setLogViewMode('params')}
                  >
                    Paramètres
                  </button>
                </div>
                <button type="button" className="admin-btn-sm" onClick={e => { e.stopPropagation(); void handleCopyJobLogs(); }} title="Copie un extrait sans timestamps ni lignes de progression">
                  Copier extrait log
                </button>
                <button type="button" className="admin-btn-sm" onClick={() => setSelectedJob(null)}>✕</button>
              </div>
            </div>
            {logViewMode === 'summary' ? (
              <MLTrainingLogSummaryView
                selfplaySummary={selectedJobSelfplaySummary}
                imitationSummary={selectedJobImitationSummary}
              />
            ) : logViewMode === 'params' ? (
              <MLTrainingLogParametersView
                selfplaySummary={selectedJobSelfplaySummary}
                imitationSummary={selectedJobImitationSummary}
              />
            ) : (
              <textarea
                className="ml-log-content ml-log-textarea"
                readOnly
                spellCheck={false}
                aria-label="Contenu des logs du job"
                value={(logViewMode === 'clean' ? selectedJobCleanLogText : selectedJobLogText) || '(pas de logs disponibles)'}
                onChange={() => {}}
              />
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ============================================
// Deductions Panel
// ============================================

function DeductionsPanel({ session }: { session: Session }) {
  const [verifyConfig, setVerifyConfig] = useState({
    table: 'matchs',
    idcompetition: '1',
    etats: 'fini',
    limit: 100,
  });
  const [verifyResult, setVerifyResult] = useState<any>(null);
  const [evalReport, setEvalReport] = useState<any>(null);
  const [dedVersions, setDedVersions] = useState<Array<{ versionTag: string; notes?: string }>>([]);
  const [selectedDedVersion, setSelectedDedVersion] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const runVerification = async () => {
    setBusy('verify');
    setError('');
    try {
      const resp = await adminFetch(`${BACKEND_URL}/api/admin/ml/verify-deductions`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({
          table: verifyConfig.table,
          idcompetition: verifyConfig.idcompetition,
          etats: verifyConfig.etats,
          limit: verifyConfig.limit,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        let msg: string;
        try { msg = JSON.parse(text).error ?? text; } catch { msg = text; }
        throw new Error(msg);
      }
      const data = await resp.json();
      if (data.error && data.stats == null) {
        throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
      }
      setVerifyResult(data.stats ?? data);
    } catch (err: any) {
      setError(err.message || 'Erreur inconnue');
    } finally {
      setBusy('');
    }
  };

  const fetchDedVersions = async () => {
    try {
      const versResp = await adminFetch(`${BACKEND_URL}/api/admin/ml/versions`, session.pseudo);
      if (!versResp.ok) return;
      const versData = await versResp.json() as { versions?: Array<{ versionTag: string; modelRole: string; trainingMode: string; notes?: string; evalMetrics?: any }> };
      const allVersions = versData.versions ?? [];
      const deds = allVersions
        .filter((v: any) => v.trainingMode === 'deductions' || v.modelRole === 'deductions')
        .sort((a: any, b: any) => (b.versionTag || '').localeCompare(a.versionTag || ''));
      setDedVersions(deds);
      if (deds.length > 0 && !selectedDedVersion) {
        setSelectedDedVersion(deds[0].versionTag);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchDedVersions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvalReport = async () => {
    setBusy('eval');
    setError('');
    try {
      let versionToLoad = selectedDedVersion;
      if (!versionToLoad) {
        const versResp = await adminFetch(`${BACKEND_URL}/api/admin/ml/versions`, session.pseudo);
        if (!versResp.ok) throw new Error('Impossible de charger les versions');
        const versData = await versResp.json() as { versions?: Array<{ versionTag: string; modelRole: string; trainingMode: string; notes?: string }> };
        const deds = (versData.versions ?? [])
          .filter((v: any) => v.trainingMode === 'deductions' || v.modelRole === 'deductions')
          .sort((a: any, b: any) => (b.versionTag || '').localeCompare(a.versionTag || ''));
        if (deds.length === 0) throw new Error('Aucune version de déduction trouvée');
        setDedVersions(deds);
        versionToLoad = deds[0].versionTag;
        setSelectedDedVersion(versionToLoad);
      }

      const evalResp = await adminFetch(
        `${BACKEND_URL}/api/admin/ml/versions/${encodeURIComponent(versionToLoad)}/eval`,
        session.pseudo,
        { method: 'POST' },
      );
      if (!evalResp.ok) {
        const errText = await evalResp.text().catch(() => '');
        throw new Error(errText ? JSON.parse(errText).error || errText : `HTTP ${evalResp.status}`);
      }
      const evalData = await evalResp.json();
      if (evalData.error) throw new Error(evalData.error);

      const report = evalData.metrics?.details ?? evalData.metrics ?? evalData;
      if (!report || (typeof report === 'object' && !report.macro_f1_nontrivial && !report.overall_accuracy)) {
        throw new Error('Rapport d\'évaluation vide ou incomplet. Le job est peut-être encore en cours de synchronisation S3.');
      }
      setEvalReport(report);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="ml-deductions-panel">
      {/* Verification */}
      <div className="admin-section">
        <h3>Vérification des déductions certaines</h3>
        <p style={{ fontSize: '0.85em', color: '#666', marginBottom: 8 }}>
          Rejoue des parties en stock (tirées au hasard selon la table / filtres, sans lien avec votre pseudo),
          vérifie que les déductions « certaines » du BeliefState sont correctes cartes sur table.
          Le paramètre <code>pseudo</code> dans l’URL sert uniquement à l’authentification admin, comme sur les autres routes <code>/api/admin/*</code>.
        </p>

        <div className="ml-deductions-config" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Source :
            <select value={verifyConfig.table} onChange={e => setVerifyConfig(c => ({ ...c, table: e.target.value }))}>
              <option value="matchs">Compétitions / défis libres</option>
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Compétition :
            <select value={verifyConfig.idcompetition} onChange={e => setVerifyConfig(c => ({ ...c, idcompetition: e.target.value }))}>
              <option value="1">Championnat équipes</option>
              <option value="3">Coupe Quadrette</option>
              <option value="null">Défis libres</option>
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            État :
            <select value={verifyConfig.etats} onChange={e => setVerifyConfig(c => ({ ...c, etats: e.target.value }))}>
              <option value="fini">fini</option>
              <option value="fini,abandon">fini + abandon</option>
              <option value="">tous</option>
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Parties :
            <input type="number" min={10} max={5000} step={50} value={verifyConfig.limit}
              onChange={e => setVerifyConfig(c => ({ ...c, limit: +e.target.value }))}
              style={{ width: 80 }}
            />
          </label>
        </div>

        <button className="admin-btn admin-btn-primary" onClick={runVerification} disabled={!!busy}>
          {busy === 'verify' ? 'Vérification en cours...' : 'Lancer la vérification'}
        </button>

        {error && <div className="admin-error" style={{ marginTop: 8 }}>{error}</div>}

        {verifyResult && (
          <div className="ml-deductions-results" style={{ marginTop: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 8 }}>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{verifyResult.total_games}</div>
                <div className="admin-stat-label">Parties vérifiées</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{verifyResult.total_void_checks}</div>
                <div className="admin-stat-label">Vérif. coupes</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value" style={{ color: verifyResult.void_errors > 0 ? '#c00' : '#080' }}>
                  {verifyResult.void_errors}
                </div>
                <div className="admin-stat-label">Erreurs coupes ({(verifyResult.void_error_rate * 100).toFixed(3)}%)</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{verifyResult.total_ceiling_checks}</div>
                <div className="admin-stat-label">Vérif. plafonds</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value" style={{ color: verifyResult.ceiling_errors > 0 ? '#c00' : '#080' }}>
                  {verifyResult.ceiling_errors}
                </div>
                <div className="admin-stat-label">Erreurs plafonds ({(verifyResult.ceiling_error_rate * 100).toFixed(3)}%)</div>
              </div>
            </div>

            {verifyResult.error_details?.length > 0 && (
              <details>
                <summary>Détails des erreurs ({verifyResult.error_details.length})</summary>
                <div style={{ maxHeight: 300, overflow: 'auto', fontSize: '0.8em' }}>
                  <table className="admin-table" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Partie</th>
                        <th>Pli</th>
                        <th>Type</th>
                        <th>Joueur</th>
                        <th>Détail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {verifyResult.error_details.slice(0, 50).map((e: any, i: number) => (
                        <tr key={i}>
                          <td>{e.game_id}</td>
                          <td>{e.trick_number}</td>
                          <td>{e.error_type}</td>
                          <td>J{e.deduced_player} (vu par J{e.perspective_player})</td>
                          <td>{e.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Training Panel */}
      <div className="admin-section" style={{ marginTop: 16 }}>
        <h3>Entraînement du modèle de déductions</h3>
        <p style={{ fontSize: '0.85em', color: '#666', marginBottom: 8 }}>
          Entraîne un réseau DeductionMLP à prédire 210 assertions sur les mains adverses.
          Utilise le même pipeline OVH que l&apos;entraînement par imitation (<code>train_pipeline.py --deductions</code>).
        </p>
        <p style={{ fontSize: '0.85em', color: '#999' }}>
          Onglet <strong>Exporter &amp; Entraîner</strong> → section <strong>Mode d&apos;entraînement</strong> → carte <strong>Déductions</strong> (icône bulle, juste à droite d&apos;<strong>Imitation</strong>). Configurez les données comme pour l&apos;imitation (étapes 1–3), puis <strong>Lancer l&apos;entraînement Déductions (DeductionMLP)</strong>.
        </p>
      </div>

      {/* Evaluation Panel */}
      <div className="admin-section" style={{ marginTop: 16 }}>
        <h3>Évaluation du modèle</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {dedVersions.length > 0 && (
            <select
              value={selectedDedVersion}
              onChange={e => { setSelectedDedVersion(e.target.value); setEvalReport(null); }}
              style={{ minWidth: 200 }}
            >
              {dedVersions.map(v => (
                <option key={v.versionTag} value={v.versionTag}>
                  {v.notes || v.versionTag}
                </option>
              ))}
            </select>
          )}
          <button className="admin-btn admin-btn-primary" onClick={loadEvalReport} disabled={!!busy}>
            {busy === 'eval' ? 'Chargement...' : 'Charger le rapport d\'évaluation'}
          </button>
        </div>
        {error && !evalReport && <div className="admin-error" style={{ marginTop: 8 }}>{error}</div>}

        {evalReport && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
              {evalReport.macro_f1_nontrivial != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{((evalReport.macro_f1_nontrivial as number) * 100).toFixed(1)}%</div>
                  <div className="admin-stat-label">Macro F1 (non-trivial)</div>
                </div>
              )}
              {evalReport.overall_accuracy != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{((evalReport.overall_accuracy as number) * 100).toFixed(1)}%</div>
                  <div className="admin-stat-label">Accuracy globale</div>
                </div>
              )}
              {evalReport.macro_precision != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{((evalReport.macro_precision as number) * 100).toFixed(1)}%</div>
                  <div className="admin-stat-label">Précision</div>
                </div>
              )}
              {evalReport.macro_recall != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{((evalReport.macro_recall as number) * 100).toFixed(1)}%</div>
                  <div className="admin-stat-label">Rappel</div>
                </div>
              )}
              {evalReport.total_games != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{(evalReport.total_games as number).toLocaleString()}</div>
                  <div className="admin-stat-label">Parties</div>
                </div>
              )}
              {evalReport.unique_deals != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{(evalReport.unique_deals as number).toLocaleString()}</div>
                  <div className="admin-stat-label">Distributions uniques</div>
                </div>
              )}
              {evalReport.num_assertions != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{evalReport.num_assertions}</div>
                  <div className="admin-stat-label">Assertions</div>
                </div>
              )}
              {evalReport.train_examples != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{(evalReport.train_examples as number).toLocaleString()}</div>
                  <div className="admin-stat-label">Exemples train</div>
                </div>
              )}
              {evalReport.val_examples != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{(evalReport.val_examples as number).toLocaleString()}</div>
                  <div className="admin-stat-label">Exemples val</div>
                </div>
              )}
              {evalReport.temperature != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{(evalReport.temperature as number).toFixed(3)}</div>
                  <div className="admin-stat-label">Température</div>
                </div>
              )}
              {evalReport.best_epoch != null && (
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{evalReport.best_epoch}</div>
                  <div className="admin-stat-label">Meilleure epoch</div>
                </div>
              )}
            </div>

            {/* Per-trick breakdown */}
            {evalReport.per_trick && (
              <details>
                <summary>Breakdown par pli</summary>
                <table className="admin-table" style={{ width: '100%', fontSize: '0.82em' }}>
                  <thead>
                    <tr><th style={{ textAlign: 'left' }}>Plis</th><th style={{ textAlign: 'right' }}>Macro F1</th><th style={{ textAlign: 'right' }}>Accuracy</th><th style={{ textAlign: 'right' }}>Exemples</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(evalReport.per_trick).map(([group, m]: [string, any]) => (
                      <tr key={group}>
                        <td>{group}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: m.macro_f1 < 0.9 ? '#e53935' : m.macro_f1 < 0.98 ? '#ff9800' : '#4caf50' }}>
                          {(m.macro_f1 * 100).toFixed(1)}%
                        </td>
                        <td style={{ textAlign: 'right' }}>{(m.accuracy * 100).toFixed(1)}%</td>
                        <td style={{ textAlign: 'right' }}>{m.num_examples.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            {/* Worst / best assertions */}
            {evalReport.worst_assertions && (
              <details style={{ marginTop: 8 }}>
                <summary>Pires / meilleures assertions</summary>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8, fontSize: '0.82em' }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4, color: '#e53935' }}>Pires (F1 le plus bas)</div>
                    {evalReport.worst_assertions.slice(0, 10).map((a: any) => (
                      <div key={a.id} style={{ padding: '2px 0', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={a.id}>{a.id}</span>
                        <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>F1={((a.f1 as number) * 100).toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                  {evalReport.best_assertions && (
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 4, color: '#4caf50' }}>Meilleures (F1 le plus haut)</div>
                      {evalReport.best_assertions.slice(0, 10).map((a: any) => (
                        <div key={a.id} style={{ padding: '2px 0', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={a.id}>{a.id}</span>
                          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>F1={((a.f1 as number) * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Sidebar section definitions
// ============================================

interface SidebarSubItemDef {
  id: string;
  label: string;
}

interface SidebarItemDef {
  id: AdminTab;
  label: string;
  icon: (props: { size: number }) => JSX.Element;
  subItems?: SidebarSubItemDef[];
}

interface SidebarSection {
  id: string;
  label: string;
  icon: (props: { size: number }) => JSX.Element;
  color: string;
  items: SidebarItemDef[];
}

const SIDEBAR_SECTIONS: SidebarSection[] = [
  {
    id: 'ml',
    label: 'Machine Learning',
    icon: TrainingIcon,
    color: '#818cf8',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: ChartTrendIcon },
      {
        id: 'feedback',
        label: 'Feedback',
        icon: CommentIcon,
        subItems: [
          { id: 'passive', label: 'Feedback passif' },
          { id: 'manual', label: 'Feedback manuel' },
        ],
      },
      {
        id: 'ml-training',
        label: 'Entraînement',
        icon: TrainingIcon,
        subItems: ML_SECTION_ORDER.map(s => ({ id: s, label: ML_SECTION_LABELS[s] })),
      },
    ],
  },
  {
    id: 'ops',
    label: 'Exploitation Neo',
    icon: WrenchIcon,
    color: '#a78bfa',
    items: [
      { id: 'usage', label: "Statistiques d'usage", icon: ChartTrendIcon },
      {
        id: 'beta-ops',
        label: 'Bêta-test',
        icon: CommentIcon,
        subItems: BETA_SECTION_ORDER.map(s => ({ id: s, label: BETA_SECTION_LABELS[s] })),
      },
      { id: 'maintenance', label: 'Maintenance', icon: WrenchIcon },
    ],
  },
  {
    id: 'data',
    label: 'Exploration données',
    icon: CardsIcon,
    color: '#34d399',
    items: [
      { id: 'differe-stats', label: 'Statistiques différé', icon: CrosshairIcon },
    ],
  },
  {
    id: 'rights',
    label: 'Gestion des droits',
    icon: AdminShieldIcon,
    color: '#f59e0b',
    items: [
      { id: 'admins', label: 'Administrateurs', icon: AdminShieldIcon },
      { id: 'chat-log', label: 'Log chat', icon: CommentIcon },
      {
        id: 'moderation',
        label: 'Modération chat',
        icon: AdminShieldIcon,
        subItems: MODERATION_SECTION_ORDER.map(s => ({ id: s, label: MODERATION_SECTION_LABELS[s] })),
      },
      { id: 'anti-cheat', label: 'Conflits anti-fraude', icon: AdminShieldIcon },
    ],
  },
];

function findSectionAndItem(tab: AdminTab) {
  for (const section of SIDEBAR_SECTIONS) {
    const item = section.items.find(i => i.id === tab);
    if (item) return { section, item };
  }
  return null;
}

// ============================================
// Main AdminPage
// ============================================

export default function AdminPage({ session }: AdminPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isCompact } = useResponsive();
  const activeTab = useMemo(() => parseAdminTabParam(searchParams.get('adminTab')), [searchParams]);
  const resolvedSub = useMemo(() => parseAdminSubParam(searchParams, activeTab), [searchParams, activeTab]);

  const [showMobileDetail, setShowMobileDetail] = useState(false);
  useTabReselect('admin', () => setShowMobileDetail(false));

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    try {
      const sp = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      const tab = parseAdminTabParam(sp.get('adminTab'));
      const found = findSectionAndItem(tab);
      if (found) initial.add(found.section.id);
    } catch {
      const found = findSectionAndItem('dashboard');
      if (found) initial.add(found.section.id);
    }
    return initial;
  });

  useEffect(() => {
    const found = findSectionAndItem(activeTab);
    if (found) setExpandedSections(prev => new Set(prev).add(found.section.id));
  }, [activeTab]);

  useEffect(() => {
    if (!isCompact) setShowMobileDetail(false);
  }, [isCompact]);

  const navigateAdmin = useCallback(
    (tab: AdminTab, sub?: string | null) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('adminTab', tab);
        if (tab === 'feedback') {
          next.set('adminSub', sub === 'manual' ? 'manual' : 'passive');
        } else if (tab === 'ml-training') {
          next.set('adminSub', sub != null && isMLSection(sub) ? sub : 'overview');
        } else if (tab === 'beta-ops') {
          next.set('adminSub', sub != null && isBetaSection(sub) ? sub : 'ops');
        } else if (tab === 'moderation') {
          next.set('adminSub', sub != null && isModerationSection(sub) ? sub : 'active');
        } else {
          next.delete('adminSub');
        }
        return next;
      });
      setShowMobileDetail(true);
    },
    [setSearchParams],
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const feedbackSub: FeedbackSubTab = resolvedSub === 'manual' ? 'manual' : 'passive';
  const mlSection: MLSection = activeTab === 'ml-training' && isMLSection(resolvedSub) ? resolvedSub : 'overview';
  const betaSection: BetaSection = activeTab === 'beta-ops' && isBetaSection(resolvedSub) ? resolvedSub : 'ops';
  const moderationSection: ModerationSectionId =
    activeTab === 'moderation' && isModerationSection(resolvedSub) ? resolvedSub : 'active';

  const adminSidebar = (
    <aside className="admin-sidebar">
      <div className="admin-sidebar-header">
        <span className="admin-sidebar-title">Administration</span>
      </div>
      <nav className="admin-sidebar-nav">
        {SIDEBAR_SECTIONS.map((section) => {
          const isExpanded = expandedSections.has(section.id);
          const sectionActive = section.items.some(i => i.id === activeTab);
          return (
            <div key={section.id} className="admin-sidebar-section">
              <button
                type="button"
                className={`admin-sidebar-section-header ${sectionActive ? 'active' : ''}`}
                onClick={() => toggleSection(section.id)}
                style={{ '--section-color': section.color } as React.CSSProperties}
              >
                <span className="admin-sidebar-section-icon">
                  <section.icon size={16} />
                </span>
                <span className="admin-sidebar-section-label">{section.label}</span>
                <span className={`admin-sidebar-chevron ${isExpanded ? 'expanded' : ''}`}>
                  <ChevronDownIcon size={14} />
                </span>
              </button>
              {isExpanded && (
                <div className="admin-sidebar-subitems">
                  {section.items.map((item) => (
                    item.subItems ? (
                      <div
                        key={item.id}
                        className={`admin-sidebar-l3-root ${activeTab === item.id ? 'admin-sidebar-l3-root--open' : ''}`}
                        style={{ '--section-color': section.color } as React.CSSProperties}
                      >
                        <button
                          type="button"
                          className="admin-sidebar-l3-compact admin-sidebar-subitem"
                          aria-expanded={activeTab === item.id}
                          tabIndex={activeTab === item.id ? -1 : 0}
                          onClick={() => {
                            navigateAdmin(item.id, defaultSubForGroupedTab(item.id));
                            setExpandedSections(prev => new Set(prev).add(section.id));
                          }}
                        >
                          <span className="admin-sidebar-subitem-icon">
                            <item.icon size={14} />
                          </span>
                          <span className="admin-sidebar-subitem-label">{item.label}</span>
                        </button>
                        <div className="admin-sidebar-l3-sheet" aria-hidden={activeTab !== item.id}>
                          <div className="admin-sidebar-l3-sheet-inner">
                            <div className="admin-sidebar-group-label">
                              <span className="admin-sidebar-group-label-icon">
                                <item.icon size={14} />
                              </span>
                              <span>{item.label}</span>
                            </div>
                            <div className="admin-sidebar-nested-list">
                              {item.subItems.map(sub => (
                                <button
                                  key={sub.id}
                                  type="button"
                                  tabIndex={activeTab === item.id ? 0 : -1}
                                  className={`admin-sidebar-nested-item ${
                                    activeTab === item.id && resolvedSub === sub.id ? 'active' : ''
                                  }`}
                                  onClick={() => {
                                    navigateAdmin(item.id, sub.id);
                                    setExpandedSections(prev => new Set(prev).add(section.id));
                                  }}
                                >
                                  <span className="admin-sidebar-nested-item-label">{sub.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        key={item.id}
                        type="button"
                        className={`admin-sidebar-subitem ${activeTab === item.id ? 'active' : ''}`}
                        onClick={() => {
                          navigateAdmin(item.id);
                          setExpandedSections(prev => new Set(prev).add(section.id));
                        }}
                        style={{ '--section-color': section.color } as React.CSSProperties}
                      >
                        <span className="admin-sidebar-subitem-icon">
                          <item.icon size={14} />
                        </span>
                        <span className="admin-sidebar-subitem-label">{item.label}</span>
                      </button>
                    )
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );

  const adminDetail = (
    <div className="admin-main">
      <div className="admin-main-header">
        <h1>{adminPageHeading(activeTab, resolvedSub)}</h1>
      </div>
      <div className="admin-content">
        {activeTab === 'dashboard' && <DashboardTab session={session} />}
        {activeTab === 'feedback' && (
          <AdminErrorBoundary label="Feedback">
            <FeedbackTab
              session={session}
              subTab={feedbackSub}
              onSubTabChange={t => navigateAdmin('feedback', t)}
            />
          </AdminErrorBoundary>
        )}
        {activeTab === 'ml-training' && (
          <MLTrainingTab
            session={session}
            section={mlSection}
            onSectionChange={s => navigateAdmin('ml-training', s)}
          />
        )}
        {activeTab === 'differe-stats' && (
          <AdminErrorBoundary label="Statistiques différé">
            <StatistiquesDiffereePage session={session} />
          </AdminErrorBoundary>
        )}
        {activeTab === 'admins' && <AdminsTab session={session} />}
        {activeTab === 'anti-cheat' && (
          <AdminErrorBoundary label="Conflits anti-fraude">
            <AntiCheatConflictsPage session={session} />
          </AdminErrorBoundary>
        )}
        {activeTab === 'chat-log' && (
          <AdminErrorBoundary label="Log chat">
            <GlobalChatLogPage session={session} />
          </AdminErrorBoundary>
        )}
        {activeTab === 'moderation' && (
          <AdminErrorBoundary label="Modération chat">
            <ModerationPage
              session={session}
              section={moderationSection}
              onSectionChange={s => navigateAdmin('moderation', s)}
            />
          </AdminErrorBoundary>
        )}
        {activeTab === 'usage' && (
          <AdminErrorBoundary label="Statistiques d'usage">
            <AdminUsagePanel session={session} />
          </AdminErrorBoundary>
        )}
        {activeTab === 'beta-ops' && (
          <AdminErrorBoundary label="Bêta-test">
            <AdminBetaOpsPanel
              session={session}
              section={betaSection}
              onSectionChange={s => navigateAdmin('beta-ops', s)}
            />
          </AdminErrorBoundary>
        )}
        {activeTab === 'maintenance' && (
          <AdminErrorBoundary label="Maintenance">
            <AdminMaintenancePanel session={session} />
          </AdminErrorBoundary>
        )}
      </div>
    </div>
  );

  return (
    <div className="admin-page admin-page-masterdetail">
      <MasterDetail
        master={adminSidebar}
        detail={adminDetail}
        detailTitle={adminPageHeading(activeTab, resolvedSub)}
        hasSelection={showMobileDetail}
        onBack={() => setShowMobileDetail(false)}
      />
    </div>
  );
}
